import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import '@/App.css';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileUp, Download, Plus, Pencil, Trash2, Users, FileText, PackageOpen, Eye, EyeOff, CheckSquare, Star, Filter, XCircle, Save, ExternalLink, LogOut, X, Check, ArrowUp, ArrowDown, ArrowUpDown, FileSpreadsheet, Menu, RefreshCw, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import html2canvas from 'html2canvas';
import AuthPage from '@/pages/AuthPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import AdminPanel from '@/components/AdminPanel';
import { AppDosyalari } from '@/components/AppDosyalari';
import { VideoYonetimi } from '@/components/VideoYonetimi';
import { KurulumIndirmeleri } from '@/components/KurulumIndirmeleri';
import { GuncellemeBanner } from '@/components/GuncellemeBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios timeout ve retry ayarları
axios.defaults.timeout = 15000; // 15 saniye timeout

// Retry fonksiyonu - Başarısız istekleri otomatik tekrarla
const retryRequest = async (config, retries = 2, delay = 1000) => {
  for (let i = 0; i <= retries; i++) {
    try {
      const response = await axios.request(config);
      return response;
    } catch (error) {
      if (i === retries) throw error;
      console.warn(`İstek başarısız, ${i + 1}. deneme... (${delay}ms sonra)`);
      await new Promise(resolve => setTimeout(resolve, delay));
      delay *= 1.5; // Exponential backoff
    }
  }
};

// Axios response interceptor - Token ve bağlantı hataları için
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Auth ile ilgili isteklerde 401 kontrolü yapma (normal akış)
    const isAuthRequest = originalRequest?.url?.includes('/auth/login') || 
                          originalRequest?.url?.includes('/auth/register') ||
                          originalRequest?.url?.includes('/auth/me');
    
    // Token süresi dolmuş veya geçersiz (auth istekleri hariç)
    if (error.response?.status === 401 && !isAuthRequest) {
      const token = localStorage.getItem('token');
      if (token && !originalRequest._auth_retry) {
        originalRequest._auth_retry = true;
        // Token'ı yeniden ekle ve tekrar dene
        originalRequest.headers.Authorization = `Bearer ${token}`;
        try {
          return await axios.request(originalRequest);
        } catch (retryError) {
          // İkinci denemede de 401 alırsa sadece log yaz
          // Otomatik çıkış yapmıyoruz - kullanıcı deneyimini bozmamak için
          if (retryError.response?.status === 401) {
            console.warn('API isteği başarısız (401). Token sorunlu olabilir.');
          }
          return Promise.reject(retryError);
        }
      }
      return Promise.reject(error);
    }
    
    // Ağ hatası veya timeout - Otomatik retry (sadece GET istekleri için)
    if ((!error.response || error.code === 'ECONNABORTED') && 
        originalRequest?.method === 'get' && 
        !originalRequest._retry) {
      originalRequest._retry = true;
      console.warn('Bağlantı hatası, otomatik yeniden deneniyor...');
      try {
        return await retryRequest(originalRequest, 2, 1000);
      } catch (retryError) {
        console.error('Yeniden deneme başarısız:', retryError.message);
      }
    }
    
    return Promise.reject(error);
  }
);

// Axios request interceptor - Her istekte token ekle
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Her isteğe cache-control header ekle
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';
    return config;
  },
  (error) => Promise.reject(error)
);

const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

// Tahsilat Dialog Component - Portal ile izole edildi
const TahsilatDialog = memo(({ open, onOpenChange, kayit, onSave }) => {
  const [tutar, setTutar] = useState('0,00');
  const [saving, setSaving] = useState(false);

  // Kayıt değiştiğinde tutarı güncelle
  React.useEffect(() => {
    if (kayit && open) {
      const val = kayit.odenen || 0;
      setTutar(Math.abs(val).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }
  }, [kayit?.id, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!kayit) return;
    
    setSaving(true);
    try {
      const tutarSayi = parseFloat(tutar.replace(/\./g, '').replace(',', '.')) || 0;
      await axios.put(`${API}/vergi-kayit/${kayit.id}`, { odenen: tutarSayi });
      toast.success('Tahsilat kaydedildi');
      onSave();
      onOpenChange(false);
    } catch (err) {
      toast.error('Kayıt hatası');
    } finally {
      setSaving(false);
    }
  };

  const formatParaLocal = (v) => Math.abs(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Dialog kapalıysa hiçbir şey render etme
  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>💰 Tahsilat Girişi</DialogTitle>
        </DialogHeader>
        {kayit && (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm font-semibold text-slate-700">{kayit.ad_soyad}</p>
                <p className="text-xs text-slate-600">{kayit.ay} {kayit.yil}</p>
              </div>
              <div>
                <Label htmlFor="tahsilat-tutar">Ödenen Tutar</Label>
                <Input
                  id="tahsilat-tutar"
                  type="text"
                  value={tutar}
                  onChange={(e) => setTutar(e.target.value)}
                  placeholder="0,00"
                  autoFocus
                  className="text-lg font-semibold"
                />
                <p className="text-xs text-slate-500 mt-1">Format: 1.000,00</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-600">Toplam:</span>
                    <span className="ml-1 font-semibold">{formatParaLocal(kayit.toplam)}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Kalan:</span>
                    <span className="ml-1 font-semibold text-green-600">{formatParaLocal(kayit.kalan)}</span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
              <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving ? 'Kaydediliyor...' : '✓ Kaydet'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}, (prevProps, nextProps) => {
  // Sadece open veya kayit.id değişirse re-render et
  return prevProps.open === nextProps.open && 
         prevProps.kayit?.id === nextProps.kayit?.id;
});

// Para birimi formatı fonksiyonu (Türk formatı: 1.000,00)
// Negatif değerler parantez içinde gösterilir: (1.000,00)
const formatPara = (value) => {
  if (value === null || value === undefined || value === '') return '0,00';
  const num = parseFloat(value);
  if (isNaN(num)) return '0,00';
  
  const formatted = Math.abs(num).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  
  // Negatif değerleri parantez içinde göster
  if (num < 0) {
    return `(${formatted})`;
  }
  
  return formatted;
};

// Para formatını sayıya çevir (1.000,00 -> 1000)
const parsePara = (value) => {
  if (!value) return 0;
  const strValue = value.toString().replace(/\./g, '').replace(',', '.');
  return parseFloat(strValue) || 0;
};

// Vergi alanlarının görünürlük kontrolü
const vergiAlaniGorunurMu = (musteriTuru, stopajPeriyot, ay, alanAdi) => {
  // Stopaj - 3 Aylık ise sadece Mart, Haziran, Eylül, Aralık
  if (alanAdi === 'stopaj' && stopajPeriyot === '3 Aylık') {
    return ['Mart', 'Haziran', 'Eylül', 'Aralık'].includes(ay);
  }
  
  // Geçici Vergi - Sadece Şubat, Mayıs, Ağustos, Kasım
  if (alanAdi === 'gecici_vergi') {
    return ['Şubat', 'Mayıs', 'Ağustos', 'Kasım'].includes(ay);
  }
  
  // Gelir Vergisi - Müşteri türüne göre
  if (alanAdi === 'gelir_vergisi') {
    if (musteriTuru === 'İŞLETME' || musteriTuru === 'BİLANÇO') {
      return ['Mart', 'Temmuz'].includes(ay);
    } else if (musteriTuru === 'ŞİRKET') {
      return ay === 'Nisan';
    }
  }
  
  return true; // Diğer alanlar her zaman görünür
};

function App() {
  // Auth State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Mobil menü state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [musteriler, setMusteriler] = useState([]);
  const [kayitlar, setKayitlar] = useState([]);
  const [allYearKayitlar, setAllYearKayitlar] = useState([]); // Tüm yılın verileri (frontend filtreleme için)
  const [selectedMusteri, setSelectedMusteri] = useState('all');
  
  // Luca Entegrasyonu State'leri
  const [lucaModalOpen, setLucaModalOpen] = useState(false);
  const [lucaMemberNo, setLucaMemberNo] = useState('');
  const [lucaUsername, setLucaUsername] = useState('');
  const [lucaPassword, setLucaPassword] = useState('');
  const [lucaConnected, setLucaConnected] = useState(false);
  const [lucaLoading, setLucaLoading] = useState(false);
  const [lucaTransferModalOpen, setLucaTransferModalOpen] = useState(false);
  const [selectedMusterilerForLuca, setSelectedMusterilerForLuca] = useState([]);
  const [lucaCaptchaModalOpen, setLucaCaptchaModalOpen] = useState(false);
  const [lucaCaptchaImage, setLucaCaptchaImage] = useState(null);
  const [lucaCaptchaInput, setLucaCaptchaInput] = useState('');
  const [lucaSessionId, setLucaSessionId] = useState(null);
  
  // Varsayılan: SADECE bir önceki ay seçili
  const getDefaultAylar = () => {
    const bugun = new Date();
    const buAy = bugun.getMonth(); // 0-11 (0=Ocak, 11=Aralık)
    const oncekiAyIndex = buAy === 0 ? 11 : buAy - 1; // Ocaksa Aralık, değilse bir önceki ay
    return [AYLAR[oncekiAyIndex]]; // Sadece bir önceki ay
  };
  
  // Varsayılan yıl: Ocak ayındaysa bir önceki yıl, değilse mevcut yıl
  const getDefaultYil = () => {
    const bugun = new Date();
    const buAy = bugun.getMonth(); // 0-11 (0=Ocak, 11=Aralık)
    const buYil = bugun.getFullYear();
    // Ocak ayındaysa, bir önceki ay Aralık olduğu için yıl da bir geri gitmeli
    return buAy === 0 ? buYil - 1 : buYil;
  };
  
  const [selectedAylar, setSelectedAylar] = useState(getDefaultAylar());
  const [selectedYil, setSelectedYil] = useState(getDefaultYil());
  const [loading, setLoading] = useState(false);
  
  // Data loading states - Sayfa kopmaları için
  const [dataLoadingStates, setDataLoadingStates] = useState({
    musteriler: false,
    kayitlar: false,
    sgk: false,
    users: false
  });
  const [dataErrors, setDataErrors] = useState({
    musteriler: null,
    kayitlar: null,
    sgk: null,
    users: null
  });
  
  // Yeni: Tüm aktif müşteriler göster seçeneği
  const [tumAktifMusteriler, setTumAktifMusteriler] = useState(false);
  
  // GİB API Token
  const [gibApiToken, setGibApiToken] = useState('');
  const [gibApiTokenSaved, setGibApiTokenSaved] = useState(false);
  
  // GİB Webview state
  const [gibWebviewSessionId, setGibWebviewSessionId] = useState(null);
  const [gibScreenshot, setGibScreenshot] = useState(null);
  const [gibCurrentUrl, setGibCurrentUrl] = useState('');
  const [screenshotInterval, setScreenshotInterval] = useState(null);
  
  // Yüklenen Vergiler
  const [yuklenenVergiler, setYuklenenVergiler] = useState([]);
  const [allYearYuklenenVergiler, setAllYearYuklenenVergiler] = useState([]); // Tüm yılın verileri (frontend filtreleme için)
  const [selectedMusteriForVergiler, setSelectedMusteriForVergiler] = useState(null);
  const [musteriFiltreTuru, setMusteriFiltreTuru] = useState('bilanco-sirket'); // Varsayılan: Bilanço/Şirket
  const [selectedVergiIds, setSelectedVergiIds] = useState([]); // Seçili vergi kayıtlarının ID'leri
  
  // Yüklenen Vergiler Sıralama
  const [yuklenenVergilerSortConfig, setYuklenenVergilerSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Yüklenen Vergiler Filtreleri
  const [yuklenenVergilerSelectedAylar, setYuklenenVergilerSelectedAylar] = useState(getDefaultAylar());
  const [yuklenenVergilerSelectedYil, setYuklenenVergilerSelectedYil] = useState(getDefaultYil());
  const [selectedVergiTurleri, setSelectedVergiTurleri] = useState(['KDV', 'KDV-2', 'Stopaj', 'SSK', 'Geçici Vergi', 'Gelir Vergisi']); // Vergi türleri (çoklu seçim)
  const [vergisiVerilmeyenler, setVergisiVerilmeyenler] = useState(false); // Vergisi verilmeyenler checkbox
  
  // Vergi türü checkbox toggle
  const handleVergiTuruToggle = (tur) => {
    if (selectedVergiTurleri.includes(tur)) {
      setSelectedVergiTurleri(selectedVergiTurleri.filter(t => t !== tur));
    } else {
      setSelectedVergiTurleri([...selectedVergiTurleri, tur]);
    }
  };
  
  // Yüklenen Vergiler Sıralama Fonksiyonu
  const handleYuklenenVergilerSort = (key) => {
    let direction = 'asc';
    if (yuklenenVergilerSortConfig.key === key && yuklenenVergilerSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setYuklenenVergilerSortConfig({ key, direction });
  };
  
  // Sıralanmış Yüklenen Vergiler
  const sortedYuklenenVergiler = React.useMemo(() => {
    // Önce müşteri türüne göre filtrele
    let filteredVergiler = yuklenenVergiler;
    
    if (musteriFiltreTuru === 'bilanco-sirket') {
      // Sadece Bilanço ve Şirket türündeki müşterilerin vergilerini göster
      const bilancoSirketMusteriler = musteriler
        .filter(m => m.musteri_turu === 'BİLANÇO' || m.musteri_turu === 'ŞİRKET')
        .map(m => m.id);
      
      filteredVergiler = yuklenenVergiler.filter(v => bilancoSirketMusteriler.includes(v.musteri_id));
    }
    
    if (!yuklenenVergilerSortConfig.key) return filteredVergiler;
    
    return [...filteredVergiler].sort((a, b) => {
      let aValue = a[yuklenenVergilerSortConfig.key];
      let bValue = b[yuklenenVergilerSortConfig.key];
      
      // null/undefined kontrolü
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';
      
      // Sayısal değerler için
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return yuklenenVergilerSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // String değerler için
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      
      if (aValue < bValue) return yuklenenVergilerSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return yuklenenVergilerSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [yuklenenVergiler, yuklenenVergilerSortConfig, musteriFiltreTuru, musteriler]);
  
  // "Tümü" checkbox'ı - tüm vergi türlerini seç/kaldır
  const handleTumVergiTurleri = (checked) => {
    if (checked) {
      setSelectedVergiTurleri(['KDV', 'KDV-2', 'Stopaj', 'SSK', 'Geçici Vergi', 'Gelir Vergisi']);
    } else {
      setSelectedVergiTurleri([]);
    }
  };
  
  // "Vergisi Verilmeyenler" toggle - müşteri türünü otomatik "Tümü"ye çevir
  const handleVergisiVerilmeyenlerToggle = (checked) => {
    setVergisiVerilmeyenler(checked);
    if (checked) {
      setMusteriFiltreTuru('tumu');
    }
  };
  
  // Yüklenen Vergiler - Checkbox işlemleri
  const handleSelectAllVergiler = (checked) => {
    if (checked) {
      // Sıralanmış listeden ID'leri al (useMemo sonrası)
      const allIds = yuklenenVergiler.map(v => v.id);
      setSelectedVergiIds(allIds);
    } else {
      setSelectedVergiIds([]);
    }
  };
  
  const handleSelectVergi = (vergiId) => {
    if (selectedVergiIds.includes(vergiId)) {
      setSelectedVergiIds(selectedVergiIds.filter(id => id !== vergiId));
    } else {
      setSelectedVergiIds([...selectedVergiIds, vergiId]);
    }
  };
  
  const handleDeleteSelectedVergiler = async () => {
    if (selectedVergiIds.length === 0) return;
    
    if (!window.confirm(`${selectedVergiIds.length} adet vergi kaydını silmek istediğinize emin misiniz?`)) {
      return;
    }
    
    try {
      // Her seçili ID için silme isteği gönder
      await Promise.all(
        selectedVergiIds.map(id => axios.delete(`${API}/yuklenen-vergiler/${id}`))
      );
      
      toast.success(`${selectedVergiIds.length} vergi kaydı silindi`);
      setSelectedVergiIds([]);
      fetchYuklenenVergiler();
    } catch (error) {
      toast.error('Vergi kayıtları silinemedi');
      console.error('Silme hatası:', error);
    }
  };
  
  // Active Tab
  const [activeTab, setActiveTab] = useState('kayitlar');
  
  // Kasa Virman
  const [kasaExcelFile, setKasaExcelFile] = useState(null);
  const [kasaMinLimit, setKasaMinLimit] = useState(1000);
  const [kasaMaxLimit, setKasaMaxLimit] = useState(18000);
  const [kasaLoading, setKasaLoading] = useState(false);
  const [kasaResult, setKasaResult] = useState(null);
  
  // Yeni: Vergi türü seçici (hangi kolonlar gösterilecek - Kayıtlar sekmesi için)
  const [kayitlarVergiKolonlari, setKayitlarVergiKolonlari] = useState({
    kdv: true,
    stopaj: true,
    ssk: true,
    bagkur: true,
    gecici_vergi: true,
    gelir_vergisi: true,
    diger1: true,
    diger2: true,
    diger3: true
  });
  
  // Yeni: Sıralama (Sorting)
  const [sortColumn, setSortColumn] = useState(null); // Hangi kolona göre sıralanıyor
  const [sortDirection, setSortDirection] = useState(null); // 'asc', 'desc', veya null
  
  // Sıralama fonksiyonu
  const handleSort = (columnName) => {
    if (sortColumn === columnName) {
      // Aynı kolona tıklandı: asc → desc → null (döngü)
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      // Farklı kolona tıklandı: asc başlat
      setSortColumn(columnName);
      setSortDirection('asc');
    }
  };
  
  // Müşteri ekleme formu
  const [yeniMusteri, setYeniMusteri] = useState({ 
    vergi_no: '', 
    ad_soyad: '',
    telefon: '',
    musteri_turu: 'İŞLETME',
    stopaj_periyot: 'Aylık',
    aktif: true,
    kdv_kendisi: false,
    stopaj_kendisi: false,
    ssk_kendisi: false,
    bagkur_kendisi: false,
    gecici_vergi_kendisi: false,
    gelir_vergisi_kendisi: false,
    bakiye_gorunur: true,
    muhasebe_gorunur: true,
    kdv_gorunur: true,
    stopaj_gorunur: true,
    ssk_gorunur: true,
    bagkur_gorunur: true,
    gecici_vergi_gorunur: true,
    gelir_vergisi_gorunur: true,
    diger1_gorunur: true,
    diger2_gorunur: true,
    diger3_gorunur: true,
    gib_giris_kodu: '',
    gib_parola: 'maliye',
    gib_sifre: '',
    ortaklar: []
  });
  const [musteriDialogOpen, setMusteriDialogOpen] = useState(false);
  
  // Müşteri düzenleme
  const [editingMusteri, setEditingMusteri] = useState(null);
  const [musteriEditDialogOpen, setMusteriEditDialogOpen] = useState(false);

  // Toplu güncelleme
  const [selectedMusteriler, setSelectedMusteriler] = useState([]);
  const [topluGuncellemeDialogOpen, setTopluGuncellemeDialogOpen] = useState(false);
  
  // Inline editing
  const [editingMusteriId, setEditingMusteriId] = useState(null);
  const [editedMusteriler, setEditedMusteriler] = useState({});
  const [topluGuncellemeData, setTopluGuncellemeData] = useState({
    aktif: null,
    musteri_turu: '',
    stopaj_periyot: '',
    favori: null,
    kdv_kendisi: null,
    stopaj_kendisi: null,
    ssk_kendisi: null,
    bagkur_kendisi: null,
    gecici_vergi_kendisi: null,
    gelir_vergisi_kendisi: null,
    gorunur_aylar: null  // null = değiştirme, [] = tüm aylar, ["Ocak","Mart"] = belirli aylar
  });
  
  
  // Manuel kayıt ekleme formu
  const [yeniKayit, setYeniKayit] = useState({
    vergi_no: '',
    ad_soyad: '',
    ay: '',
    yil: new Date().getFullYear(),
    bakiye: 0,
    muhasebe: 0,
    kdv: 0,
    stopaj: 0,
    ssk: 0,
    bagkur: 0,
    gecici_vergi: 0,
    gelir_vergisi: 0,
    diger1: 0,
    diger2: 0,
    diger3: 0,
    odenen: 0
  });
  const [kayitDialogOpen, setKayitDialogOpen] = useState(false);
  
  // PDF upload

  // Vergi kayıtları için seçim
  const [selectedKayitlar, setSelectedKayitlar] = useState([]);
  
  // Rapor dialog
  const [raporDialogOpen, setRaporDialogOpen] = useState(false);
  const [raporDialogClosing, setRaporDialogClosing] = useState(false); // Dialog kapanıyor mu?
  const [raporData, setRaporData] = useState(null);
  const [raporOdenenInput, setRaporOdenenInput] = useState('0,00');
  const [raporSelectedMusteri, setRaporSelectedMusteri] = useState('');
  const [raporSelectedAy, setRaporSelectedAy] = useState('');
  const [raporSelectedAylar, setRaporSelectedAylar] = useState([]); // Çoklu ay seçimi
  
  // Rapor form düzenleme - STRING olarak tut (virgül korunsun)
  const [raporFormData, setRaporFormData] = useState({});
  const [raporFormInputs, setRaporFormInputs] = useState({}); // Input için string değerler
  
  // Tahsilat Özeti Dialog (yıl seçilebilir)
  const [tahsilatOzetiDialogOpen, setTahsilatOzetiDialogOpen] = useState(false);
  const [tahsilatOzetiYil, setTahsilatOzetiYil] = useState(new Date().getFullYear());
  const [tahsilatOzetiKayitlari, setTahsilatOzetiKayitlari] = useState([]);
  const [tahsilatOzetiLoading, setTahsilatOzetiLoading] = useState(false);
  const [tahsilatOzetiVergiNo, setTahsilatOzetiVergiNo] = useState('');
  const [tahsilatOzetiAdSoyad, setTahsilatOzetiAdSoyad] = useState('');
  
  // Güvenlik / 2FA Ayarları
  const [securityDialogOpen, setSecurityDialogOpen] = useState(false);
  const [twoFAToggleLoading, setTwoFAToggleLoading] = useState(false);
  const [twoFAPasswordInput, setTwoFAPasswordInput] = useState('');
  const [twoFAToggleError, setTwoFAToggleError] = useState('');
  const [profilTelefon, setProfilTelefon] = useState('');
  const [profilTelefonLoading, setProfilTelefonLoading] = useState(false);

  const handleProfilTelefonKaydet = async () => {
    setProfilTelefonLoading(true);
    try {
      const res = await axios.put(`${API}/auth/profil`, { telefon: profilTelefon });
      const updatedUser = { ...currentUser, telefon: res.data.telefon };
      setCurrentUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setProfilTelefon('');
      toast.success('Telefon numaranız kaydedildi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Telefon kaydedilemedi');
    } finally {
      setProfilTelefonLoading(false);
    }
  };
  
  const [digerIsimler, setDigerIsimler] = useState({
    diger1: 'DİĞER 1',
    diger2: 'DİĞER 2', 
    diger3: 'DİĞER 3'
  });
  
  // Ödeme dialog
  const [odemeDialogOpen, setOdemeDialogOpen] = useState(false);
  const [odemeKayit, setOdemeKayit] = useState(null);
  const [odemeTutari, setOdemeTutari] = useState('');
  
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFormData, setPdfFormData] = useState({
    vergi_no: '',
    ad_soyad: '',
    ay: '',
    yil: new Date().getFullYear()
  });
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  
  // Düzenleme
  const [editingKayit, setEditingKayit] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Toplu ZIP upload
  const [zipFile, setZipFile] = useState(null);
  const [zipFormData, setZipFormData] = useState({
    ay: '',
    yil: new Date().getFullYear()
  });
  const [zipDialogOpen, setZipDialogOpen] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  
  // Background Processing için async upload state'leri
  const [asyncUploadJobId, setAsyncUploadJobId] = useState(null);
  const [asyncUploadProgress, setAsyncUploadProgress] = useState(null);
  const [asyncUploadDialogOpen, setAsyncUploadDialogOpen] = useState(false);
  
  // Çakışma yönetimi
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [conflictDecisions, setConflictDecisions] = useState({}); // {index: 'update' | 'keep'}
  const [pendingZipFile, setPendingZipFile] = useState(null); // Onay bekleyen ZIP dosyası
  
  // Tahakkuk PDF seçimi (WhatsApp için)
  const [tahakkukPdfs, setTahakkukPdfs] = useState([]); // Mevcut tahakkuk PDF listesi
  const [selectedTahakkukPdfs, setSelectedTahakkukPdfs] = useState([]); // Seçili PDF'ler

  // Müşteri Excel import
  const [musteriExcelFile, setMusteriExcelFile] = useState(null);
  const [musteriExcelDialogOpen, setMusteriExcelDialogOpen] = useState(false);
  
  
  // Görünürlük
  const [showHidden, setShowHidden] = useState(true);
  
  // Yeni özellikler
  const [aramaMusteri, setAramaMusteri] = useState(''); // Müşteri arama
  const [aramaKayit, setAramaKayit] = useState(''); // Kayıt arama
  const [showFavoriOnly, setShowFavoriOnly] = useState(false); // Varsayılan: Favori müşteriler kapalı
  const [showHiddenMusteriler, setShowHiddenMusteriler] = useState(false); // Gizli müşterileri göster
  const [hideZeroValues, setHideZeroValues] = useState(false); // Sıfır değerleri gizle
  
  // Inline editing için
  const [editingCell, setEditingCell] = useState({ kayitId: null, field: null }); // Hangi hücre düzenleniyor
  const [editingValue, setEditingValue] = useState(''); // Düzenlenen değer
  
  // Vergi Raporu Bakiye inline editing için
  const [editingRaporBakiye, setEditingRaporBakiye] = useState(false);
  const [raporBakiyeValue, setRaporBakiyeValue] = useState('');
  
  // Sütun sıralama ve filtreleme
  const [columnFilters, setColumnFilters] = useState({
    durum: '', // 'aktif', 'pasif', ''
    musteri_turu: '', // 'İŞLETME', 'BİLANÇO', 'ŞİRKET', ''
    stopaj_periyot: '' // 'Aylık', '3 Aylık', ''
  })
  
  // Kayıtlar Tablosu - Gelişmiş Filtreler
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false); // Gelişmiş filtre paneli açık/kapalı
  const [kayitFilters, setKayitFilters] = useState({
    bakiye_min: '',
    bakiye_max: '',
    muhasebe_min: '',
    muhasebe_max: '',
    kdv_min: '',
    kdv_max: '',
    stopaj_min: '',
    stopaj_max: '',
    toplam_min: '',
    toplam_max: '',
    kalan_min: '',
    kalan_max: '',
    odeme_durumu: '', // 'odendi', 'odenmedi', 'kismen', ''
    tahakkuk_yuklenmedi: '', // 'kdv', 'stopaj', 'gecici', 'gelir_kurum', ''
  })
  const [savedFilters, setSavedFilters] = useState([]); // Kaydedilmiş filtreler
  const [filterName, setFilterName] = useState(''); // Yeni filtre adı
  
  // Excel import
  const [excelFile, setExcelFile] = useState(null);
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  
  // Toplu veri girişi
  const [topluVeriDialogOpen, setTopluVeriDialogOpen] = useState(false);
  const [topluVeriKayitlar, setTopluVeriKayitlar] = useState([]);
  const [topluVeriAy, setTopluVeriAy] = useState('');
  const [topluVeriYil, setTopluVeriYil] = useState(new Date().getFullYear());

  // SGK İşyerleri
  const [sgkIsyerleri, setSgkIsyerleri] = useState([]);
  const [selectedSgkMusteri, setSelectedSgkMusteri] = useState('');
  const [sgkDialogOpen, setSgkDialogOpen] = useState(false);
  const [editingSgkIsyeri, setEditingSgkIsyeri] = useState(null);
  const [sgkSortField, setSgkSortField] = useState('musteri_ad');
  const [sgkSortDirection, setSgkSortDirection] = useState('asc');
  const [yeniSgkIsyeri, setYeniSgkIsyeri] = useState({
    vergi_no: '',
    ad_soyad: '',
    isyeri_adi: 'MERKEZ',
    sgk_kullanici_kodu: '',
    isyeri_kodu: '',
    sistem_sifresi: '',
    isyeri_sifresi: '',
    sgk_sicil_no: '',
    aktif: true
  });
  
  // Şifre görünürlüğü - tümü için tek toggle
  const [showPasswordsGib, setShowPasswordsGib] = useState(false);
  const [showPasswordsSgk, setShowPasswordsSgk] = useState(false);
  
  // Mali Müşavir Bilgileri
  const [maliMusavir, setMaliMusavir] = useState({
    kullanici_kodu: '',
    sifre: '',
    parola: ''
  });
  const [showPasswordsMaliMusavir, setShowPasswordsMaliMusavir] = useState(false);
  const [editingMaliMusavirField, setEditingMaliMusavirField] = useState(null); // 'kullanici_kodu', 'sifre', 'parola'
  const [tempMaliMusavirValue, setTempMaliMusavirValue] = useState('');

  // E-Beyanname / GİB Tahakkuk
  const [beyannamesiOlmayanlar, setBeyannamesiOlmayanlar] = useState([]);
  const [tahakkukYil, setTahakkukYil] = useState(new Date().getFullYear());
  const [tahakkukAy, setTahakkukAy] = useState(['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'][new Date().getMonth()]);
  const [tahakkukSonuc, setTahakkukSonuc] = useState(null);

  const fetchMaliMusavir = async () => {
    try {
      const response = await axios.get(`${API}/mali-musavir`);
      setMaliMusavir(response.data);
    } catch (error) {
      console.error('Mali müşavir bilgileri yüklenemedi:', error);
    }
  };

  const fetchBeyannamesiOlmayanlar = async () => {
    try {
      const response = await axios.get(`${API}/gib-tahakkuk/beyannamesi-olmayan`, {
        params: { yil: tahakkukYil, ay: tahakkukAy }
      });
      setBeyannamesiOlmayanlar(response.data);
    } catch (error) {
      console.error('Beyannamesi olmayanlar yüklenemedi:', error);
      toast.error('Liste yüklenemedi');
    }
  };

  // Auth check on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      if (token && user) {
        try {
          // Token'ı axios header'a ekle
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Token'ın geçerli olup olmadığını kontrol et
          const response = await axios.get(`${API}/auth/me`);
          setCurrentUser(response.data);
          setIsAuthenticated(true);
        } catch (err) {
          // Token geçersiz, temizle
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setAuthLoading(false);
    };
    
    checkAuth();
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      if (!isAuthenticated) return;
      
      console.log('🚀 Site açılışı - Veriler yükleniyor...');
      
      // 1. ÖNCE müşterileri yükle (en kritik)
      await fetchMusteriler();
      console.log('✅ Müşteriler yüklendi');
      
      // 2. SONRA diğer verileri paralel yükle
      await Promise.all([
        fetchKayitlar(),
        fetchYuklenenVergiler(),
        fetchSgkIsyerleri(),
        fetchMaliMusavir()
      ]);
      console.log('✅ Tüm veriler yüklendi');
      
      // Kaydedilmiş filtreleri yükle
      const saved = localStorage.getItem('savedKayitFilters');
      if (saved) {
        try {
          setSavedFilters(JSON.parse(saved));
        } catch (e) {
          console.error('Kaydedilmiş filtreler yüklenemedi:', e);
        }
      }
    };
    
    loadInitialData();
  }, [isAuthenticated]);

  // 20 dakika işlem yapılmazsa otomatik çıkış (timestamp bazlı - arka planda da çalışır)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const TIMEOUT_DURATION = window.__SESSION_TIMEOUT_MS || 20 * 60 * 1000; // 20 dakika
    const WARNING_DURATION = TIMEOUT_DURATION - 2 * 60 * 1000; // 2 dk kala uyarı
    let lastActivity = Date.now();
    let warningShown = false;
    
    const forceLogout = () => {
      toast.dismiss('session-warning');
      toast.error('Oturum süresi doldu. Güvenlik için çıkış yapıldı.');
      handleLogout();
    };
    
    // Kullanıcı aktivitesi: süre dolmuşsa çıkış, dolmamışsa sıfırla
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity >= TIMEOUT_DURATION) {
        forceLogout();
        return;
      }
      lastActivity = now;
      if (warningShown) {
        warningShown = false;
        toast.dismiss('session-warning');
      }
    };
    
    // Periyodik kontrol (setInterval arka planda yavaşlasa bile, her tetiklenişte gerçek süreye bakar)
    const checkSession = () => {
      const elapsed = Date.now() - lastActivity;
      if (elapsed >= TIMEOUT_DURATION) {
        forceLogout();
      } else if (elapsed >= WARNING_DURATION && !warningShown) {
        warningShown = true;
        toast.warning('⏰ 2 dakika içinde oturum kapanacak. Devam etmek için bir işlem yapın.', {
          duration: 60000,
          id: 'session-warning'
        });
      }
    };
    
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    
    // 15 saniyede bir kontrol et
    const intervalId = setInterval(checkSession, 15 * 1000);
    
    // Sekme tekrar görünür olunca veya pencere odağa gelince hemen kontrol et
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkSession();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', checkSession);
    
    // Cleanup
    return () => {
      clearInterval(intervalId);
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', checkSession);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    // Sadece giriş yapılmışsa ve yıl veya müşteri değiştiğinde backend'den veri çek
    // NOT: selectedAylar dependency'den çıkarıldı - ay filtreleme artık frontend'de
    if (isAuthenticated) {
      fetchKayitlar();
    }
  }, [selectedMusteri, selectedYil, isAuthenticated]);
  
  // Frontend filtreleme: Ay değiştiğinde backend'e gitmeden filtrele
  useEffect(() => {
    if (allYearKayitlar.length > 0) {
      filterKayitlarByMonth();
    }
  }, [selectedAylar, allYearKayitlar, tumAktifMusteriler]);

  // Ödeme Durumu değiştiğinde Sıfır Değerleri Gizle toggle'ını otomatik aktif et
  useEffect(() => {
    if (kayitFilters.odeme_durumu === 'odendi_tamamen' || kayitFilters.odeme_durumu === 'odenmedi_kismen') {
      setHideZeroValues(true);
    }
  }, [kayitFilters.odeme_durumu]);

  // Klavye kısayolları - F2 ve F3
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F3 - Kayıtlar sekmesinde Vergi Raporu aç
      if (e.key === 'F3') {
        e.preventDefault();
        
        // Sadece Kayıtlar sekmesinde ve rapor açık değilken çalışsın
        if (!raporDialogOpen && musteriler.length > 0) {
          // İlk müşteriyi seç
          const ilkMusteri = musteriler[0];
          handleRaporOlustur(ilkMusteri.vergi_no, ilkMusteri.ad_soyad);
          toast.success(`${ilkMusteri.ad_soyad} için Vergi Raporu açılıyor...`);
        }
      }
      
      // F2 - Vergi Raporu içindeyken Müşteri Düzenleme aç
      if (e.key === 'F2' && raporDialogOpen && raporData) {
        e.preventDefault();
        
        // Rapordaki müşteri bilgisini kullanarak düzenleme formunu aç
        const musteriVergiNo = raporData.vergi_no || raporSelectedMusteri;
        const musteri = musteriler.find(m => m.vergi_no === musteriVergiNo);
        
        if (musteri) {
          setEditingMusteri(musteri);
          setMusteriEditDialogOpen(true);
          toast.success(`${musteri.ad_soyad} düzenleme formu açılıyor...`);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [raporDialogOpen, musteriler, raporData, raporSelectedMusteri]);

  // Scroll senkronizasyonu için useEffect
  useEffect(() => {
    const headerScroll = document.getElementById('header-scroll');
    const bodyScroll = document.getElementById('body-scroll');
    const footerScroll = document.getElementById('footer-scroll');
    
    if (!headerScroll || !bodyScroll || !footerScroll) return;
    
    let isScrolling = false;
    
    // Body scroll olduğunda header ve footer'ı güncelle
    const handleBodyScroll = () => {
      if (isScrolling) return;
      isScrolling = true;
      
      const scrollLeft = bodyScroll.scrollLeft;
      if (headerScroll.scrollLeft !== scrollLeft) {
        headerScroll.scrollLeft = scrollLeft;
      }
      if (footerScroll.scrollLeft !== scrollLeft) {
        footerScroll.scrollLeft = scrollLeft;
      }
      
      requestAnimationFrame(() => {
        isScrolling = false;
      });
    };
    
    // Header scroll olduğunda body ve footer'ı güncelle
    const handleHeaderScroll = () => {
      if (isScrolling) return;
      isScrolling = true;
      
      const scrollLeft = headerScroll.scrollLeft;
      if (bodyScroll.scrollLeft !== scrollLeft) {
        bodyScroll.scrollLeft = scrollLeft;
      }
      if (footerScroll.scrollLeft !== scrollLeft) {
        footerScroll.scrollLeft = scrollLeft;
      }
      
      requestAnimationFrame(() => {
        isScrolling = false;
      });
    };
    
    // Footer scroll olduğunda header ve body'yi güncelle
    const handleFooterScroll = () => {
      if (isScrolling) return;
      isScrolling = true;
      
      const scrollLeft = footerScroll.scrollLeft;
      if (headerScroll.scrollLeft !== scrollLeft) {
        headerScroll.scrollLeft = scrollLeft;
      }
      if (bodyScroll.scrollLeft !== scrollLeft) {
        bodyScroll.scrollLeft = scrollLeft;
      }
      
      requestAnimationFrame(() => {
        isScrolling = false;
      });
    };
    
    bodyScroll.addEventListener('scroll', handleBodyScroll);
    headerScroll.addEventListener('scroll', handleHeaderScroll);
    footerScroll.addEventListener('scroll', handleFooterScroll);
    
    return () => {
      bodyScroll.removeEventListener('scroll', handleBodyScroll);
      headerScroll.removeEventListener('scroll', handleHeaderScroll);
      footerScroll.removeEventListener('scroll', handleFooterScroll);
    };
  }, [kayitlar]); // kayitlar değiştiğinde yeniden attach et

  // Luca Entegrasyonu Fonksiyonları
  const handleLucaLogin = async () => {
    if (!lucaMemberNo || !lucaUsername || !lucaPassword) {
      toast.error('Tüm alanları doldurun');
      return;
    }
    
    setLucaLoading(true);
    try {
      const response = await axios.post(`${API}/luca/giris`, {
        member_no: lucaMemberNo,
        username: lucaUsername,
        password: lucaPassword
      });
      
      if (response.data.success) {
        setLucaConnected(true);
        toast.success('Luca\'ya başarıyla bağlanıldı! ✅');
      } else {
        toast.error(response.data.message || 'Giriş başarısız');
      }
    } catch (error) {
      console.error('Luca giriş hatası:', error);
      toast.error(error.response?.data?.detail || 'Luca bağlantı hatası');
    } finally {
      setLucaLoading(false);
    }
  };

  const handleLucaTransfer = async () => {
    if (selectedMusterilerForLuca.length === 0) {
      toast.error('Lütfen en az bir müşteri seçin');
      return;
    }
    
    // Seçilen vergi türlerini al
    const vergiTurleriCheckboxes = document.querySelectorAll('input[type="checkbox"]');
    const selectedVergiTurleri = [];
    vergiTurleriCheckboxes.forEach((cb) => {
      if (cb.checked && cb.nextSibling && ['KDV', 'Stopaj', 'SSK', 'Bağkur'].includes(cb.nextSibling.textContent.trim())) {
        selectedVergiTurleri.push(cb.nextSibling.textContent.trim());
      }
    });
    
    if (selectedVergiTurleri.length === 0) {
      toast.error('Lütfen en az bir vergi türü seçin');
      return;
    }
    
    setLucaLoading(true);
    setLucaTransferModalOpen(false);
    
    try {
      // 1. Luca session başlat ve CAPTCHA'yı al
      toast.info('Luca\'ya bağlanılıyor...');
      
      const sessionResponse = await axios.post(`${API}/luca/start-session`, {
        member_no: lucaMemberNo,
        username: lucaUsername,
        password: lucaPassword
      });
      
      if (sessionResponse.data.requires_captcha) {
        // CAPTCHA gerekli - modal aç
        setLucaSessionId(sessionResponse.data.session_id);
        setLucaCaptchaImage(sessionResponse.data.captcha_image);
        setLucaCaptchaModalOpen(true);
        setLucaLoading(false);
        return;
      }
      
      // CAPTCHA gerekmiyorsa direkt devam et
      await continueLucaTransfer(sessionResponse.data.session_id, selectedVergiTurleri);
      
    } catch (error) {
      console.error('Luca transfer hatası:', error);
      toast.error(error.response?.data?.detail || 'Transfer hatası oluştu');
      setLucaLoading(false);
    }
  };
  
  const handleCaptchaSubmit = async () => {
    if (!lucaCaptchaInput) {
      toast.error('Lütfen CAPTCHA kodunu girin');
      return;
    }
    
    setLucaLoading(true);
    setLucaCaptchaModalOpen(false);
    
    try {
      // CAPTCHA'yı gönder
      await axios.post(`${API}/luca/submit-captcha`, {
        session_id: lucaSessionId,
        captcha: lucaCaptchaInput
      });
      
      // Seçilen vergi türlerini al (tekrar)
      const vergiTurleriCheckboxes = document.querySelectorAll('input[type="checkbox"]');
      const selectedVergiTurleri = [];
      vergiTurleriCheckboxes.forEach((cb) => {
        if (cb.checked && cb.nextSibling && ['KDV', 'Stopaj', 'SSK', 'Bağkur'].includes(cb.nextSibling.textContent.trim())) {
          selectedVergiTurleri.push(cb.nextSibling.textContent.trim());
        }
      });
      
      // Transfer işlemine devam et
      await continueLucaTransfer(lucaSessionId, selectedVergiTurleri);
      
    } catch (error) {
      console.error('CAPTCHA hatası:', error);
      toast.error(error.response?.data?.detail || 'CAPTCHA doğrulanamadı');
    } finally {
      setLucaLoading(false);
      setLucaCaptchaInput('');
    }
  };
  
  const continueLucaTransfer = async (sessionId, selectedVergiTurleri) => {
    try {
      // Her müşteri için vergi ödemelerini gönder
      const transferPromises = [];
      
      for (const vergiNo of selectedMusterilerForLuca) {
        const musteriKayitlari = kayitlar.filter(k => k.vergi_no === vergiNo);
        
        for (const kayit of musteriKayitlari) {
          for (const vergiTuru of selectedVergiTurleri) {
            // Vergi türüne göre tutarı al
            let tutar = 0;
            if (vergiTuru === 'KDV') tutar = kayit.kdv || 0;
            else if (vergiTuru === 'Stopaj') tutar = kayit.stopaj || 0;
            else if (vergiTuru === 'SSK') tutar = kayit.ssk || 0;
            else if (vergiTuru === 'Bağkur') tutar = kayit.bagkur || 0;
            
            if (tutar > 0) {
              transferPromises.push(
                axios.post(`${API}/luca/vergi-odemesi`, {
                  session_id: sessionId,
                  musteri_adi: kayit.ad_soyad,
                  vergi_no: kayit.vergi_no,
                  ay: kayit.ay,
                  yil: kayit.yil,
                  vergi_turu: vergiTuru,
                  tutar: tutar
                })
              );
            }
          }
        }
      }
      
      toast.info(`${transferPromises.length} vergi ödemesi Luca'ya gönderiliyor...`);
      
      const results = await Promise.all(transferPromises);
      
      const successCount = results.filter(r => r.data.success).length;
      toast.success(`✅ ${successCount} vergi ödemesi Luca'ya başarıyla aktarıldı!`);
      
    } catch (error) {
      console.error('Luca transfer hatası:', error);
      toast.error(error.response?.data?.detail || 'Transfer hatası oluştu');
    }
  };

  const fetchMusteriler = async () => {
    setDataLoadingStates(prev => ({ ...prev, musteriler: true }));
    setDataErrors(prev => ({ ...prev, musteriler: null }));
    try {
      const response = await axios.get(`${API}/musteriler`);
      setMusteriler(response.data);
    } catch (error) {
      console.error('Müşteriler yüklenemedi:', error);
      setDataErrors(prev => ({ ...prev, musteriler: error.message }));
      toast.error('Müşteriler yüklenemedi. Sayfayı yenileyin.');
    } finally {
      setDataLoadingStates(prev => ({ ...prev, musteriler: false }));
    }
  };

  const fetchSgkIsyerleri = async (vergiNo = null) => {
    setDataLoadingStates(prev => ({ ...prev, sgk: true }));
    setDataErrors(prev => ({ ...prev, sgk: null }));
    try {
      const params = vergiNo ? { vergi_no: vergiNo } : {};
      const response = await axios.get(`${API}/sgk-isyerleri`, { params });
      setSgkIsyerleri(response.data);
    } catch (error) {
      console.error('SGK işyerleri yüklenemedi:', error);
      setDataErrors(prev => ({ ...prev, sgk: error.message }));
      toast.error('SGK işyerleri yüklenemedi');
    } finally {
      setDataLoadingStates(prev => ({ ...prev, sgk: false }));
    }
  };

  // Tüm yılın verilerini backend'den çek (ay değiştiğinde tekrar çağrılmaz)
  const fetchKayitlar = async (forceAktifMusteriler = null) => {
    try {
      const params = {};
      if (selectedMusteri && selectedMusteri !== 'all') params.vergi_no = selectedMusteri;
      if (selectedYil) params.yil = selectedYil;
      // NOT: Artık ay parametresi gönderilmiyor - tüm yılın verileri çekiliyor
      
      const response = await axios.get(`${API}/vergi-kayit`, { params });
      
      // Tüm yılın verilerini kaydet
      setAllYearKayitlar(response.data);
      
      console.log(`📊 ${selectedYil} yılı için ${response.data.length} kayıt yüklendi`);
    } catch (error) {
      console.error('Kayıtlar yüklenemedi:', error);
    }
  };
  
  // Frontend'de ay bazlı filtreleme (backend'e gitmeden)
  const filterKayitlarByMonth = async () => {
    const aktifMode = tumAktifMusteriler;
    
    // Ay sırasına göre helper
    const aySirasi = {
      'Ocak': 1, 'Şubat': 2, 'Mart': 3, 'Nisan': 4, 'Mayıs': 5, 'Haziran': 6,
      'Temmuz': 7, 'Ağustos': 8, 'Eylül': 9, 'Ekim': 10, 'Kasım': 11, 'Aralık': 12
    };
    
    // Seçilen aylara göre filtrele
    let filteredData = [...allYearKayitlar];
    if (selectedAylar.length > 0 && selectedAylar.length < AYLAR.length) {
      filteredData = filteredData.filter(kayit => selectedAylar.includes(kayit.ay));
    }
    
    // OTOMATİK AKTARIM: Seçilen ayda kayıt yoksa, önceki aydan otomatik aktar
    if (selectedAylar.length === 1 && filteredData.length === 0 && allYearKayitlar.length > 0) {
      const hedefAy = selectedAylar[0];
      const ayIndex = AYLAR.indexOf(hedefAy);
      
      // Önceki ayı bul
      let kaynakAy, kaynakYil;
      if (ayIndex === 0) {
        kaynakAy = 'Aralık';
        kaynakYil = selectedYil - 1;
      } else {
        kaynakAy = AYLAR[ayIndex - 1];
        kaynakYil = selectedYil;
      }
      
      try {
        // Önceki ayda kayıt var mı kontrol et (önce local cache'de bak)
        let kaynakKayitlar = [];
        if (kaynakYil === selectedYil) {
          kaynakKayitlar = allYearKayitlar.filter(k => k.ay === kaynakAy);
        } else {
          // Önceki yıl için backend'e gitmeliyiz
          const kaynakResponse = await axios.get(`${API}/vergi-kayit`, { 
            params: { yil: kaynakYil, ay: kaynakAy } 
          });
          kaynakKayitlar = kaynakResponse.data;
        }
        
        if (kaynakKayitlar.length > 0) {
          console.log(`🔄 ${hedefAy}/${selectedYil} ayında kayıt yok, ${kaynakAy}/${kaynakYil} ayından otomatik aktarılıyor...`);
          
          await axios.post(`${API}/vergi-kayit/aya-aktar`, null, {
            params: {
              kaynak_ay: kaynakAy,
              kaynak_yil: kaynakYil,
              hedef_ay: hedefAy,
              hedef_yil: selectedYil
            }
          });
          
          // Aktarım sonrası backend'den yeniden yükle
          const params = {};
          if (selectedMusteri && selectedMusteri !== 'all') params.vergi_no = selectedMusteri;
          params.yil = selectedYil;
          
          const yeniResponse = await axios.get(`${API}/vergi-kayit`, { params });
          setAllYearKayitlar(yeniResponse.data);
          filteredData = yeniResponse.data.filter(kayit => selectedAylar.includes(kayit.ay));
          
          toast.success(`✅ ${kaynakAy} ayından ${filteredData.length} kayıt ${hedefAy} ayına aktarıldı`);
        }
      } catch (error) {
        console.error('Otomatik aktarım hatası:', error);
      }
    }
    
    // TÜM AKTİF MÜŞTERİLER MODU
    if (aktifMode) {
      try {
        const musteriResponse = await axios.get(`${API}/musteriler`);
        const aktifMusteriler = musteriResponse.data.filter(m => m.aktif === true);
        
        const tumKayitlar = [];
        
        for (const musteri of aktifMusteriler) {
          for (const ay of selectedAylar) {
            const mevcutKayit = filteredData.find(k => 
              k.vergi_no === musteri.vergi_no && k.ay === ay
            );
            
            if (mevcutKayit) {
              tumKayitlar.push(mevcutKayit);
            } else {
              // Kayıt yok - önceki ayın kalan tutarını bul (local cache'den)
              const ayIndex = AYLAR.indexOf(ay);
              let oncekiAyKalan = 0;
              
              if (ayIndex > 0) {
                const oncekiAy = AYLAR[ayIndex - 1];
                const oncekiKayit = allYearKayitlar.find(k => 
                  k.vergi_no === musteri.vergi_no && k.ay === oncekiAy
                );
                if (oncekiKayit) {
                  oncekiAyKalan = oncekiKayit.kalan || 0;
                }
              } else {
                // Ocak ayı - önceki yılın Aralık ayına bak (backend'e gitmeliyiz)
                try {
                  const oncekiYilResponse = await axios.get(`${API}/vergi-kayit`, { 
                    params: { yil: selectedYil - 1, ay: 'Aralık', vergi_no: musteri.vergi_no } 
                  });
                  if (oncekiYilResponse.data.length > 0) {
                    oncekiAyKalan = oncekiYilResponse.data[0].kalan || 0;
                  }
                } catch (err) {
                  console.error('Önceki yıl bakiye hatası:', err);
                }
              }
              
              const bakiyeDegeri = oncekiAyKalan;
              tumKayitlar.push({
                id: `temp_${musteri.vergi_no}_${ay}`,
                vergi_no: musteri.vergi_no,
                ad_soyad: musteri.ad_soyad,
                ay: ay,
                yil: selectedYil,
                bakiye: bakiyeDegeri,
                muhasebe: 0,
                muhasebe_ucreti: 0,
                kdv: 0,
                stopaj: 0,
                ssk: 0,
                bagkur: 0,
                gecici_vergi: 0,
                kurum_gecici_vergi: 0,
                gelir_vergisi: 0,
                diger1: 0,
                diger2: 0,
                diger3: 0,
                toplam_tutar: bakiyeDegeri,
                odenen: 0,
                kalan: bakiyeDegeri,
                notlar: '',
                gorunur: true,
                _isFrontendOnly: true
              });
            }
          }
        }
        
        filteredData = tumKayitlar;
      } catch (error) {
        console.error('Aktif müşteriler yüklenemedi:', error);
      }
    }
    
    // Ay sırasına göre sırala
    filteredData.sort((a, b) => {
      const adKarsilastir = a.ad_soyad.localeCompare(b.ad_soyad, 'tr');
      if (adKarsilastir !== 0) return adKarsilastir;
      return aySirasi[a.ay] - aySirasi[b.ay];
    });
    
    setKayitlar(filteredData);
    console.log(`🔍 ${selectedAylar.join(', ')} için ${filteredData.length} kayıt filtrelendi (frontend)`);
  };

  // GİB API Token Fonksiyonları
  const saveGibToken = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/gib/token/save`, {
        token: gibApiToken
      });
      
      if (response.data.success) {
        setGibApiTokenSaved(true);
        alert('✅ Token başarıyla kaydedildi!');
      }
    } catch (error) {
      console.error('Token kaydetme hatası:', error);
      alert('❌ Token kaydedilemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const testGibConnection = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/gib/test-connection`);
      
      if (response.data.success) {
        alert('✅ GİB API Bağlantı Başarılı!\n\n' + JSON.stringify(response.data.user_info, null, 2));
      } else {
        alert('❌ Bağlantı Test Edilemedi:\n\n' + response.data.message + '\n\n' + response.data.detail);
      }
    } catch (error) {
      console.error('Bağlantı test hatası:', error);
      alert('❌ Test hatası: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchGibTahakkuklar = async () => {
    try {
      setLoading(true);
      
      // Seçili aydan dönem oluştur (örn: Ocak 2025 → 202501)
      const ayIndex = AYLAR.indexOf(selectedAylar[0]) + 1;
      const donem = `${selectedYil}${ayIndex.toString().padStart(2, '0')}`;
      
      const response = await axios.post(`${API}/gib/tahakkuk/fetch`, null, {
        params: { donem }
      });
      
      if (response.data.success) {
        alert(`✅ ${response.data.message}\n\nTahakkuklar indiriliyor ve parse ediliyor...`);
        
        // TODO: İndirilen tahakkukları parse et ve kayıtlara ekle
        console.log('Tahakkuklar:', response.data.tahakkuklar);
        
        // Kayıtları yenile
        await fetchKayitlar();
      }
    } catch (error) {
      console.error('Tahakkuk indirme hatası:', error);
      
      if (error.response?.status === 401) {
        alert('❌ Token geçersiz veya süresi dolmuş!\n\nLütfen GİB portalından yeni token oluşturun.');
        setGibApiTokenSaved(false);
        setGibApiToken('');
      } else if (error.response?.status === 400) {
        alert('❌ Token bulunamadı!\n\nLütfen önce GİB API Token\'ı kaydedin.');
      } else {
        alert('❌ Tahakkuk indirilemedi: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };


  // GİB Webview Fonksiyonları
  const startGibWebview = async () => {
    try {
      toast.info('GİB sayfası açılıyor... Lütfen bekleyin.');
      
      const response = await axios.post(`${API}/gib/webview/start`);
      
      if (response.data.success) {
        setGibWebviewSessionId(response.data.session_id);
        toast.success('✅ GİB sayfası açıldı! Manuel giriş yapın ve tahakkukları listeleyin.');
        
        // Screenshot polling başlat (her 2 saniyede bir)
        const interval = setInterval(() => {
          fetchGibScreenshot(response.data.session_id);
        }, 2000);
        setScreenshotInterval(interval);
        
        // İlk screenshot'ı hemen al
        fetchGibScreenshot(response.data.session_id);
      }
    } catch (error) {
      console.error('Webview başlatma hatası:', error);
      toast.error('❌ GİB sayfası açılamadı: ' + (error.response?.data?.detail || error.message));
    }
  };

  const fetchGibScreenshot = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/gib/webview/screenshot/${sessionId}`);
      if (response.data.success) {
        setGibScreenshot(response.data.screenshot);
        setGibCurrentUrl(response.data.current_url);
      }
    } catch (error) {
      // Sessiz hata - polling sırasında normal
      console.log('Screenshot alınamadı:', error.message);
    }
  };

  const downloadGibTahakkuklar = async () => {
    try {
      setLoading(true);
      toast.info('Tahakkuklar indiriliyor... Lütfen bekleyin.');
      
      const response = await axios.post(`${API}/gib/webview/download-tahakkuklar/${gibWebviewSessionId}`);
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}\n\nİndirilen: ${response.data.downloaded_count}\nKaydedilen: ${response.data.parsed_count}`);
        
        // Kayıtları ve yüklenen vergileri yenile
        await fetchKayitlar();
        await fetchYuklenenVergiler();
      }
    } catch (error) {
      console.error('Tahakkuk indirme hatası:', error);
      toast.error('❌ Tahakkuk indirme hatası: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const closeGibWebview = async () => {
    try {
      // Polling'i durdur
      if (screenshotInterval) {
        clearInterval(screenshotInterval);
        setScreenshotInterval(null);
      }
      
      await axios.post(`${API}/gib/webview/close/${gibWebviewSessionId}`);
      setGibWebviewSessionId(null);
      setGibScreenshot(null);
      setGibCurrentUrl('');
      toast.success('✅ GİB sayfası kapatıldı');
    } catch (error) {
      console.error('Webview kapatma hatası:', error);
      toast.error('❌ Kapatma hatası: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleScreenshotClick = async (e) => {
    try {
      // Screenshot'un gerçek boyutunu al
      const img = e.target;
      const rect = img.getBoundingClientRect();
      
      // Tıklama pozisyonunu hesapla (görüntü boyutuna göre ölçeklendir)
      const scaleX = 1920 / rect.width;  // Backend'de 1920x1080
      const scaleY = 1080 / rect.height;
      
      const x = Math.round((e.clientX - rect.left) * scaleX);
      const y = Math.round((e.clientY - rect.top) * scaleY);
      
      console.log(`Clicking at: (${x}, ${y})`);
      
      // Backend'e tıklama gönder
      await axios.post(`${API}/gib/webview/click/${gibWebviewSessionId}?x=${x}&y=${y}`);
      
      // Screenshot'ı hemen güncelle
      fetchGibScreenshot(gibWebviewSessionId);
      
    } catch (error) {
      console.error('Tıklama hatası:', error);
      toast.error('Tıklama başarısız');
    }
  };

  const handleKeyPress = async (key) => {
    try {
      await axios.post(`${API}/gib/webview/keyboard/${gibWebviewSessionId}?key=${encodeURIComponent(key)}`);
      fetchGibScreenshot(gibWebviewSessionId);
    } catch (error) {
      console.error('Tuş basma hatası:', error);
    }
  };

  const handleTypeText = async (text) => {
    try {
      await axios.post(`${API}/gib/webview/type/${gibWebviewSessionId}?text=${encodeURIComponent(text)}`);
      fetchGibScreenshot(gibWebviewSessionId);
    } catch (error) {
      console.error('Text yazma hatası:', error);
      toast.error('Yazma başarısız');
    }
  };


  // Yüklenen Vergileri Getir
  // Yüklenen Vergileri yükle - Tüm yılın verilerini çeker (frontend filtreleme için)
  const fetchYuklenenVergiler = async () => {
    try {
      const params = {};
      
      // Sadece yıl parametresi gönder - ay filtreleme frontend'de yapılacak
      if (yuklenenVergilerSelectedYil) {
        params.yil = yuklenenVergilerSelectedYil;
      }
      
      const response = await axios.get(`${API}/yuklenen-vergiler`, { params });
      
      // Tüm yılın verilerini kaydet
      setAllYearYuklenenVergiler(response.data);
      
      console.log(`📊 ${yuklenenVergilerSelectedYil} yılı için ${response.data.length} yüklenen vergi yüklendi`);
    } catch (error) {
      console.error('Yüklenen vergiler getirme hatası:', error);
    }
  };
  
  // Frontend'de yüklenen vergileri filtrele
  const filterYuklenenVergiler = () => {
    let filtered = [...allYearYuklenenVergiler];
    
    // Ay filtresi
    if (yuklenenVergilerSelectedAylar && yuklenenVergilerSelectedAylar.length > 0 && yuklenenVergilerSelectedAylar.length < 12) {
      filtered = filtered.filter(v => yuklenenVergilerSelectedAylar.includes(v.ay));
    }
    
    // Müşteri filtresi
    if (selectedMusteriForVergiler) {
      filtered = filtered.filter(v => v.musteri_id === selectedMusteriForVergiler);
    }
    
    // Vergi türü filtresi
    if (selectedVergiTurleri && selectedVergiTurleri.length > 0) {
      filtered = filtered.filter(v => selectedVergiTurleri.includes(v.vergi_turu));
    }
    
    // Vergisi verilmeyenler filtresi
    if (vergisiVerilmeyenler) {
      filtered = filtered.filter(v => v.vergisini_ben_oduyorum === true);
    }
    
    setYuklenenVergiler(filtered);
    console.log(`🔍 ${yuklenenVergilerSelectedAylar.join(', ')} için ${filtered.length} yüklenen vergi filtrelendi (frontend)`);
  };

  // Yüklenen Vergi Sil
  const deleteYuklenenVergi = async (vergiId) => {
    if (!window.confirm('Bu vergi kaydını silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/yuklenen-vergiler/${vergiId}`);
      await fetchYuklenenVergiler();
      alert('✅ Vergi kaydı silindi');
    } catch (error) {
      console.error('Vergi silme hatası:', error);
      alert('❌ Vergi silinemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Sayfa Yenile - Tüm verileri yeniden yükle (uygulamadan çıkmadan)
  const handleSayfaYenile = async () => {
    try {
      toast.info('Veriler yenileniyor...');
      await Promise.all([
        fetchMusteriler(),
        fetchKayitlar(),
        fetchSgkIsyerleri(),
        fetchYuklenenVergiler()
      ]);
      toast.success('Veriler yenilendi');
    } catch (error) {
      console.error('Yenileme hatası:', error);
      toast.error('Veriler yenilenemedi');
    }
  };

  // SSK Destek Primi Güncelle
  const updateDestekPrimi = async (vergiId, isyeriIndex, yeniDeger) => {
    try {
      await axios.put(`${API}/yuklenen-vergiler/destek-primi`, {
        vergi_id: vergiId,
        isyeri_index: isyeriIndex,
        destek_primi: parseFloat(yeniDeger) || 0
      });
      // Listeyi yenile
      fetchYuklenenVergiler();
    } catch (error) {
      console.error('Destek primi güncelleme hatası:', error);
      alert('❌ Destek primi güncellenemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Token kontrolü (sayfa yüklendiğinde - sadece giriş yapılmışsa)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const checkToken = async () => {
      try {
        const response = await axios.get(`${API}/gib/token/check`);
        if (response.data.has_token) {
          setGibApiTokenSaved(true);
        }
      } catch (error) {
        console.error('Token kontrol hatası:', error);
      }
    };
    checkToken();
  }, [isAuthenticated]);

  // Yüklenen vergileri yükle - Sadece yıl değiştiğinde veya tab açıldığında backend'e git
  useEffect(() => {
    if (isAuthenticated && activeTab === 'vergi-yukle') {
      fetchMusteriler();
      fetchYuklenenVergiler();
    }
  }, [yuklenenVergilerSelectedYil, activeTab, isAuthenticated]);
  
  // Frontend filtreleme - Filtreler değiştiğinde backend'e gitmeden filtrele
  useEffect(() => {
    if (allYearYuklenenVergiler.length > 0) {
      filterYuklenenVergiler();
    }
  }, [allYearYuklenenVergiler, yuklenenVergilerSelectedAylar, selectedMusteriForVergiler, selectedVergiTurleri, vergisiVerilmeyenler]);


  // Kasa Optimizasyonu
  const handleKasaOptimize = async () => {
    if (!kasaExcelFile) {
      alert('Lütfen bir Excel dosyası seçin');
      return;
    }

    // State'i temizle ve loading başlat
    setKasaLoading(true);
    setKasaResult(null);

    try {
      const formData = new FormData();
      formData.append('file', kasaExcelFile);
      
      // Timestamp ekleyerek cache'i bypass et
      const timestamp = Date.now();

      const response = await axios.post(
        `${API}/kasa-virman/optimize?min_limit=${kasaMinLimit}&max_limit=${kasaMaxLimit}&_t=${timestamp}`, 
        formData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000 // 120 saniye - Excel işleme uzun sürebilir
        }
      );

      // Response'u logla
      console.log('Kasa Virman API Response:', response.data);
      console.log('Toplam Virman:', response.data.toplam_virman);
      console.log('Virman Kayıtları:', response.data.virman_kayitlari);

      // State'i güncelle
      setKasaResult(response.data);
      
      // Force re-render
      setTimeout(() => {
        console.log('State güncellendi:', response.data);
      }, 100);

    } catch (error) {
      console.error('Kasa optimizasyon hatası:', error);
      alert('İşlem sırasında bir hata oluştu: ' + (error.response?.data?.detail || error.message));
      setKasaResult(null);
    } finally {
      setKasaLoading(false);
    }
  };

  // Excel İndirme
  const handleKasaExcelDownload = () => {
    if (!kasaResult?.rapor_excel_data) return;

    // Hex string'i byte array'e çevir
    // RAPOR CSV DOSYASINI İNDİR (rapor_excel_data)
    const hexString = kasaResult.rapor_excel_data;
    const bytes = new Uint8Array(hexString.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
    const blob = new Blob([bytes], { type: 'text/csv;charset=utf-8;' });
    
    // İndir
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Kasa_Virman_Rapor_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // ============================================
  // BACKGROUND PROCESSING - ASYNC ZIP UPLOAD
  // ============================================
  
  const handleAsyncZipUpload = async (file) => {
    if (!file) return;
    
    try {
      toast.info('📦 ZIP dosyası yükleniyor...');
      
      const formData = new FormData();
      formData.append('file', file);
      
      // Token'ı localStorage'dan al
      const token = localStorage.getItem('token');
      
      // Async endpoint'e gönder
      const response = await axios.post(`${API}/yuklenen-vergiler/upload-zip-async`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        timeout: 120000 // 120 saniye - dosya yükleme
      });
      
      const { job_id } = response.data;
      setAsyncUploadJobId(job_id);
      setAsyncUploadProgress({
        status: 'pending',
        progress: 0,
        message: 'İşlem başlatılıyor...',
        total_files: 0,
        processed_files: 0,
        current_file: '',
        filename: '',
        basarili: 0,
        hatali: 0
      });
      setAsyncUploadDialogOpen(true);
      setZipDialogOpen(false);
      
      // Polling başlat
      startProgressPolling(job_id);
      
      toast.success('✅ ZIP dosyası alındı, işlem başlatıldı!');
      
    } catch (error) {
      console.error('Async upload hatası:', error);
      toast.error('ZIP yükleme hatası: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const startProgressPolling = (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/upload-status/${jobId}`);
        const jobData = response.data;
        
        setAsyncUploadProgress({
          status: jobData.status,
          progress: jobData.progress || 0,
          message: jobData.message || '',
          total_files: jobData.total_files || 0,
          processed_files: jobData.processed_files || 0,
          current_file: jobData.current_file || '',
          filename: jobData.filename || '',
          basarili: jobData.basarili || 0,
          hatali: jobData.hatali || 0,
          detaylar: jobData.detaylar || []
        });
        
        // İşlem tamamlandıysa veya hata olduysa polling'i durdur
        if (jobData.status === 'completed' || jobData.status === 'failed') {
          clearInterval(pollInterval);
          
          if (jobData.status === 'completed') {
            toast.success(`✅ İşlem tamamlandı! ${jobData.basarili} başarılı, ${jobData.hatali} hatalı`);
            // Verileri yenile
            fetchKayitlar();
            fetchYuklenenVergiler();
          } else {
            toast.error('❌ İşlem başarısız: ' + jobData.message);
          }
        }
        
      } catch (error) {
        console.error('Progress polling hatası:', error);
        // Hata durumunda polling'i durdur
        clearInterval(pollInterval);
      }
    }, 2000); // Her 2 saniyede bir sorgula
    
    // 10 dakika sonra otomatik durdur (güvenlik için)
    setTimeout(() => clearInterval(pollInterval), 600000);
  };


  // Ay seçim fonksiyonları
  const handleAyToggle = (ay) => {
    if (selectedAylar.includes(ay)) {
      setSelectedAylar(selectedAylar.filter(a => a !== ay));
    } else {
      setSelectedAylar([...selectedAylar, ay]);
    }
  };

  const handleTumAylar = (checked) => {
    if (checked) {
      setSelectedAylar(AYLAR); // Tüm ayları seç
    } else {
      setSelectedAylar([]); // Tümünü kaldır
    }
  };
  
  // Yüklenen Vergiler için ay toggle fonksiyonları
  const handleYuklenenVergilerAyToggle = (ay) => {
    if (yuklenenVergilerSelectedAylar.includes(ay)) {
      setYuklenenVergilerSelectedAylar(yuklenenVergilerSelectedAylar.filter(a => a !== ay));
    } else {
      setYuklenenVergilerSelectedAylar([...yuklenenVergilerSelectedAylar, ay]);
    }
  };

  const handleYuklenenVergilerTumAylar = (checked) => {
    if (checked) {
      setYuklenenVergilerSelectedAylar(AYLAR); // Tüm ayları seç
    } else {
      setYuklenenVergilerSelectedAylar([]); // Tümünü kaldır
    }
  };

  // Hangi sütunların gösterileceğini belirle - useMemo ile cache'le
  const visibleColumns = React.useMemo(() => {
    // YENİ: Önce kullanıcının seçtiği vergi türlerini kontrol et
    const userSelected = {
      bakiye: true, // Bakiye ve muhasebe her zaman göster (temel kolonlar)
      muhasebe: true,
      kdv: kayitlarVergiKolonlari.kdv,
      stopaj: kayitlarVergiKolonlari.stopaj,
      ssk: kayitlarVergiKolonlari.ssk,
      bagkur: kayitlarVergiKolonlari.bagkur,
      gecici_vergi: kayitlarVergiKolonlari.gecici_vergi,
      gelir_vergisi: kayitlarVergiKolonlari.gelir_vergisi,
      diger1: kayitlarVergiKolonlari.diger1,
      diger2: kayitlarVergiKolonlari.diger2,
      diger3: kayitlarVergiKolonlari.diger3,
      odenen: true // Ödenen sütunu her zaman görünür
    };
    
    if (!hideZeroValues) {
      return userSelected;
    }
    
    // hideZeroValues aktifse: Kullanıcı seçimi + Sıfır olmayan değer kontrolü
    const hasNonZero = {
      bakiye: kayitlar.some(k => k.bakiye && Number(k.bakiye) > 0),
      muhasebe: kayitlar.some(k => k.muhasebe && Number(k.muhasebe) > 0),
      kdv: kayitlar.some(k => k.kdv && Number(k.kdv) > 0),
      stopaj: kayitlar.some(k => k.stopaj && Number(k.stopaj) > 0),
      ssk: kayitlar.some(k => k.ssk && Number(k.ssk) > 0),
      bagkur: kayitlar.some(k => k.bagkur && Number(k.bagkur) > 0),
      gecici_vergi: kayitlar.some(k => k.gecici_vergi && Number(k.gecici_vergi) > 0),
      gelir_vergisi: kayitlar.some(k => k.gelir_vergisi && Number(k.gelir_vergisi) > 0),
      diger1: kayitlar.some(k => k.diger1 && Number(k.diger1) > 0),
      diger2: kayitlar.some(k => k.diger2 && Number(k.diger2) > 0),
      diger3: kayitlar.some(k => k.diger3 && Number(k.diger3) > 0),
      odenen: true
    };
    
    // Her iki koşulu da sağlamalı: Kullanıcı seçmiş VE sıfır olmayan değer var
    return {
      bakiye: userSelected.bakiye && hasNonZero.bakiye,
      muhasebe: userSelected.muhasebe && hasNonZero.muhasebe,
      kdv: userSelected.kdv && hasNonZero.kdv,
      stopaj: userSelected.stopaj && hasNonZero.stopaj,
      ssk: userSelected.ssk && hasNonZero.ssk,
      bagkur: userSelected.bagkur && hasNonZero.bagkur,
      gecici_vergi: userSelected.gecici_vergi && hasNonZero.gecici_vergi,
      gelir_vergisi: userSelected.gelir_vergisi && hasNonZero.gelir_vergisi,
      diger1: userSelected.diger1 && hasNonZero.diger1,
      diger2: userSelected.diger2 && hasNonZero.diger2,
      diger3: userSelected.diger3 && hasNonZero.diger3,
      odenen: true
    };
  }, [hideZeroValues, kayitlar, kayitlarVergiKolonlari]);

  // Rapor başlığı (Muhasebe Unvanı) - Backend'den al
  const [raporBaslik, setRaporBaslik] = useState('');
  const [baslikDuzenle, setBaslikDuzenle] = useState(false);
  const [baslikYukleniyor, setBaslikYukleniyor] = useState(true);
  
  // Kullanıcı ayarlarını backend'den yükle
  useEffect(() => {
    const fetchKullaniciAyarlari = async () => {
      if (!currentUser) return;
      try {
        const response = await axios.get(`${API}/kullanici-ayarlari`);
        setRaporBaslik(response.data.muhasebe_unvani || '');
      } catch (error) {
        console.error('Kullanıcı ayarları yüklenemedi:', error);
      } finally {
        setBaslikYukleniyor(false);
      }
    };
    fetchKullaniciAyarlari();
  }, [currentUser]);
  
  // Aktif satır için state (tıklanan satır)
  const [activeRowId, setActiveRowId] = useState(null);
  
  const handleBaslikKaydet = async (yeniBaslik) => {
    setRaporBaslik(yeniBaslik);
    setBaslikDuzenle(false);
    
    // Backend'e kaydet
    try {
      await axios.put(`${API}/kullanici-ayarlari`, {
        muhasebe_unvani: yeniBaslik
      });
    } catch (error) {
      console.error('Muhasebe unvanı kaydedilemedi:', error);
      toast.error('Muhasebe unvanı kaydedilemedi');
    }
  };
  
  // WhatsApp ile gönderme fonksiyonu
  const raporRef = useRef(null);
  
  // Tahsilat Özeti açıcı - yıl seçilerek müşterinin yıllık özetini gösterir
  const openTahsilatOzeti = async (vergiNo, adSoyad, yil) => {
    setTahsilatOzetiVergiNo(vergiNo);
    setTahsilatOzetiAdSoyad(adSoyad);
    setTahsilatOzetiYil(yil);
    setTahsilatOzetiDialogOpen(true);
    setTahsilatOzetiLoading(true);
    try {
      const resp = await axios.get(`${API}/vergi-kayit`, { params: { vergi_no: vergiNo, yil } });
      setTahsilatOzetiKayitlari(resp.data || []);
    } catch (e) {
      console.error('Tahsilat özeti yüklenemedi', e);
      toast.error('Tahsilat özeti yüklenemedi');
      setTahsilatOzetiKayitlari([]);
    } finally {
      setTahsilatOzetiLoading(false);
    }
  };
  
  const refreshTahsilatOzeti = async (yeniYil) => {
    if (!tahsilatOzetiVergiNo) return;
    setTahsilatOzetiYil(yeniYil);
    setTahsilatOzetiLoading(true);
    try {
      const resp = await axios.get(`${API}/vergi-kayit`, { params: { vergi_no: tahsilatOzetiVergiNo, yil: yeniYil } });
      setTahsilatOzetiKayitlari(resp.data || []);
    } catch (e) {
      toast.error('Tahsilat özeti yüklenemedi');
      setTahsilatOzetiKayitlari([]);
    } finally {
      setTahsilatOzetiLoading(false);
    }
  };

  // Tahsilat Özeti'ni WhatsApp ile gönder (görsel olarak)
  const handleTahsilatOzetiWhatsApp = async () => {
    try {
      const musteri = musteriler.find(m => m.vergi_no === tahsilatOzetiVergiNo);
      const telefonRaw = musteri?.telefon || '';
      if (!telefonRaw || telefonRaw.trim() === '') {
        toast.error('Müşterinin telefon numarası kayıtlı değil!');
        return;
      }

      const aySirasi = {'Ocak':1,'Şubat':2,'Mart':3,'Nisan':4,'Mayıs':5,'Haziran':6,'Temmuz':7,'Ağustos':8,'Eylül':9,'Ekim':10,'Kasım':11,'Aralık':12};
      const yilKayitlari = (tahsilatOzetiKayitlari || [])
        .filter(k => k.vergi_no === tahsilatOzetiVergiNo)
        .sort((a, b) => (aySirasi[a.ay] || 0) - (aySirasi[b.ay] || 0));

      if (yilKayitlari.length === 0) {
        toast.error('Bu yıl için kayıt bulunamadı');
        return;
      }

      toast.info('Görsel oluşturuluyor...');

      let genelToplam = 0, genelOdenen = 0, genelKalan = 0;
      const rowsHTML = yilKayitlari.map(kayit => {
        const toplam = (Number(kayit.bakiye) || 0) +
          (Number(kayit.muhasebe) || 0) +
          (kayit.kdv_aktif !== false && !kayit.kdv_kendisi_odemis ? (Number(kayit.kdv) || 0) : 0) +
          (kayit.stopaj_aktif !== false && !kayit.stopaj_kendisi_odemis ? (Number(kayit.stopaj) || 0) : 0) +
          (kayit.ssk_aktif !== false && !kayit.ssk_kendisi_odemis ? (Number(kayit.ssk) || 0) : 0) +
          (kayit.bagkur_aktif !== false && !kayit.bagkur_kendisi_odemis ? (Number(kayit.bagkur) || 0) : 0) +
          (kayit.gecici_vergi_aktif !== false && !kayit.gecici_vergi_kendisi_odemis ? (Number(kayit.gecici_vergi) || 0) : 0) +
          (kayit.gelir_vergisi_aktif !== false && !kayit.gelir_vergisi_kendisi_odemis ? (Number(kayit.gelir_vergisi) || 0) : 0) +
          (Number(kayit.diger1) || 0) + (Number(kayit.diger2) || 0) + (Number(kayit.diger3) || 0);
        const odenen = Number(kayit.odenen) || 0;
        const kalan = toplam - odenen;
        genelToplam += toplam;
        genelOdenen += odenen;
        genelKalan += kalan;
        const fmt = (n) => n.toLocaleString('tr-TR', {minimumFractionDigits: 2});
        const kalanColor = kalan > 0 ? '#dc2626' : '#16a34a';
        return `
          <tr style="border-bottom: 1px solid #e5e7eb;">
            <td style="padding: 8px 10px; font-weight: 500; font-size: 13px;">${kayit.ay}</td>
            <td style="padding: 8px 10px; text-align: right; font-size: 13px;">${fmt(toplam)}</td>
            <td style="padding: 8px 10px; text-align: right; font-size: 13px; color: #15803d;">${fmt(odenen)}</td>
            <td style="padding: 8px 10px; text-align: right; font-size: 13px; font-weight: 600; color: ${kalanColor};">${fmt(kalan)}</td>
            <td style="padding: 8px 10px; font-size: 12px; color: #475569; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${(kayit.notlar || '-').replace(/[<>]/g, '')}</td>
          </tr>
        `;
      }).join('');

      const fmt = (n) => n.toLocaleString('tr-TR', {minimumFractionDigits: 2});
      const genelKalanColor = genelKalan > 0 ? '#dc2626' : '#16a34a';

      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.background = 'white';
      tempDiv.style.padding = '20px';
      tempDiv.style.width = '720px';
      tempDiv.style.fontFamily = 'Arial, sans-serif';

      tempDiv.innerHTML = `
        <div style="background: linear-gradient(to right, #7c3aed, #db2777); padding: 14px 16px; border-radius: 8px; margin-bottom: 14px; text-align: center;">
          <div style="color: white; font-weight: bold; font-size: 18px;">${tahsilatOzetiYil} Yılı Tahsilat Özeti</div>
          <div style="color: white; font-size: 14px; margin-top: 6px;">${tahsilatOzetiAdSoyad} &nbsp;•&nbsp; Vergi No: ${tahsilatOzetiVergiNo}</div>
        </div>
        <div style="border: 2px solid #cbd5e1; border-radius: 8px; overflow: hidden;">
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: #1e293b; color: white;">
                <th style="padding: 10px; text-align: left; font-size: 13px;">Ay</th>
                <th style="padding: 10px; text-align: right; font-size: 13px;">Toplam</th>
                <th style="padding: 10px; text-align: right; font-size: 13px;">Tahsilat</th>
                <th style="padding: 10px; text-align: right; font-size: 13px;">Kalan</th>
                <th style="padding: 10px; text-align: left; font-size: 13px;">Not</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHTML}
              <tr style="background: #e2e8f0; font-weight: bold; border-top: 2px solid #94a3b8;">
                <td style="padding: 10px;">TOPLAM</td>
                <td style="padding: 10px; text-align: right;">${fmt(genelToplam)}</td>
                <td style="padding: 10px; text-align: right; color: #15803d;">${fmt(genelOdenen)}</td>
                <td style="padding: 10px; text-align: right; color: ${genelKalanColor};">${fmt(genelKalan)}</td>
                <td style="padding: 10px;"></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style="margin-top: 10px; text-align: right; font-size: 11px; color: #64748b;">${new Date().toLocaleDateString('tr-TR')}</div>
      `;
      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: true,
      });
      document.body.removeChild(tempDiv);

      let telefon = telefonRaw.replace(/\D/g, '');
      if (telefon.startsWith('0')) telefon = '90' + telefon.substring(1);
      else if (!telefon.startsWith('90') && telefon.length === 10) telefon = '90' + telefon;

      canvas.toBlob(async (blob) => {
        try {
          const item = new ClipboardItem({ 'image/png': blob });
          await navigator.clipboard.write([item]);
          toast.success('✅ Görsel panoya kopyalandı!');
          const desktopUrl = `whatsapp://send?phone=${telefon}`;
          const webUrl = `https://web.whatsapp.com/send?phone=${telefon}`;
          const link = document.createElement('a');
          link.href = desktopUrl;
          link.click();
          toast.info('📱 WhatsApp açılıyor... Ctrl+V ile görseli yapıştırın!');
          const webFallbackTimeout = setTimeout(() => {
            if (window.confirm('WhatsApp uygulaması açılmadı mı?\n\nWeb versiyonunu açmak için Tamam\'a tıklayın.')) {
              window.open(webUrl, '_blank');
            }
          }, 2000);
          const handleBlur = () => {
            clearTimeout(webFallbackTimeout);
            window.removeEventListener('blur', handleBlur);
          };
          window.addEventListener('blur', handleBlur);
          setTimeout(() => window.removeEventListener('blur', handleBlur), 5000);
        } catch (err) {
          console.error('Clipboard hatası:', err);
          try {
            const url = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = `${tahsilatOzetiAdSoyad}_${tahsilatOzetiYil}_tahsilat_ozeti.png`;
            downloadLink.click();
            URL.revokeObjectURL(url);
            toast.success('📥 Görsel indirildi! WhatsApp\'ta dosyayı ekleyin.');
            const link = document.createElement('a');
            link.href = `whatsapp://send?phone=${telefon}`;
            link.click();
          } catch (downloadErr) {
            console.error('İndirme hatası:', downloadErr);
            toast.error('Görsel oluşturulamadı');
          }
        }
      }, 'image/png');
    } catch (error) {
      console.error('Tahsilat Özeti WhatsApp hatası:', error);
      toast.error('Görsel oluşturulamadı');
    }
  };
  
  const handleWhatsAppGonder = async () => {
    try {
      if (!raporData || !raporData.telefon || raporData.telefon.trim() === '') {
        toast.error('Müşterinin telefon numarası kayıtlı değil!');
        return;
      }
      
      toast.info('Rapor görseli oluşturuluyor...');
      
      // Seçili tahakkuk PDF'lerini bul
      const secilenPdfler = tahakkukPdfs.filter(pdf => selectedTahakkukPdfs.includes(pdf.id));
      
      // Özel WhatsApp görseli için geçici div oluştur
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.background = 'white';
      tempDiv.style.padding = '20px';
      tempDiv.style.width = '600px';
      
      // Başlık ekle
      const headerHTML = raporBaslik ? `
        <div style="background: linear-gradient(to right, #2563eb, #3b82f6); padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; text-align: center;">
          <div style="color: white; font-weight: bold; font-size: 16px;">
            ${raporBaslik}
          </div>
          <div style="color: white; font-size: 14px; margin-top: 8px;">
            📅 ${raporData.tekAy 
              ? `${raporData.aylar[0]} ${raporData.yil}`
              : `${raporData.aylar[0]} - ${raporData.aylar[raporData.aylar.length - 1]} ${raporData.yil}`
            } - ${raporData.adSoyad} - Vergi No: ${raporData.vergiNo}
          </div>
        </div>
      ` : `
        <div style="background: linear-gradient(to right, #2563eb, #3b82f6); padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; text-align: center;">
          <div style="color: white; font-size: 14px;">
            📅 ${raporData.tekAy 
              ? `${raporData.aylar[0]} ${raporData.yil}`
              : `${raporData.aylar[0]} - ${raporData.aylar[raporData.aylar.length - 1]} ${raporData.yil}`
            } - ${raporData.adSoyad} - Vergi No: ${raporData.vergiNo}
          </div>
        </div>
      `;
      
      // Tablo HTML'i oluştur
      const tableHTML = `
        <div style="border: 2px solid #9ca3af; border-radius: 8px; overflow: hidden; font-family: Arial, sans-serif;">
          <div style="display: flex; border-bottom: 2px solid #9ca3af; background: #e2e8f0;">
            <div style="width: 50%; padding: 12px; font-weight: bold; font-size: 14px; border-right: 2px solid #9ca3af;">BAKİYE</div>
            <div style="width: 50%; padding: 12px; text-align: right; font-size: 14px; font-weight: 600;">${formatPara(raporData.bakiye)}</div>
          </div>
          
          <div style="display: flex; border-bottom: 1px solid #d1d5db;">
            <div style="width: 50%; padding: 10px 12px; font-size: 14px; font-weight: 500; border-right: 1px solid #d1d5db;">MUHASEBE ÜCRETİ</div>
            <div style="width: 50%; padding: 10px 12px; text-align: right; font-size: 14px;">${formatPara(raporFormData.muhasebe)}</div>
          </div>
          
          ${raporFormData.kdv > 0 ? `
          <div style="display: flex; border-bottom: 1px solid #d1d5db; ${raporData.kdv_kendisi_odemis ? 'background: #f0fdf4;' : ''}">
            <div style="width: 50%; padding: 10px 12px; font-size: 14px; font-weight: 500; border-right: 1px solid #d1d5db; ${raporData.kdv_kendisi_odemis ? 'color: #6b7280;' : ''}">KDV${raporData.kdv_kendisi_odemis ? ' <span style="color: #059669; font-size: 12px;">(Kendisi Ödedi)</span>' : ''}</div>
            <div style="width: 50%; padding: 10px 12px; text-align: right; font-size: 14px; ${raporData.kdv_kendisi_odemis ? 'text-decoration: line-through; color: #9ca3af;' : ''}">${formatPara(raporFormData.kdv)}</div>
          </div>
          ` : ''}
          
          ${raporFormData.stopaj > 0 ? `
          <div style="display: flex; border-bottom: 1px solid #d1d5db; ${raporData.stopaj_kendisi_odemis ? 'background: #f0fdf4;' : ''}">
            <div style="width: 50%; padding: 10px 12px; font-size: 14px; font-weight: 500; border-right: 1px solid #d1d5db; ${raporData.stopaj_kendisi_odemis ? 'color: #6b7280;' : ''}">STOPAJ${raporData.stopaj_kendisi_odemis ? ' <span style="color: #059669; font-size: 12px;">(Kendisi Ödedi)</span>' : ''}</div>
            <div style="width: 50%; padding: 10px 12px; text-align: right; font-size: 14px; ${raporData.stopaj_kendisi_odemis ? 'text-decoration: line-through; color: #9ca3af;' : ''}">${formatPara(raporFormData.stopaj)}</div>
          </div>
          ` : ''}
          
          ${raporFormData.ssk > 0 ? `
          <div style="display: flex; border-bottom: 1px solid #d1d5db; ${raporData.ssk_kendisi_odemis ? 'background: #f0fdf4;' : ''}">
            <div style="width: 50%; padding: 10px 12px; font-size: 14px; font-weight: 500; border-right: 1px solid #d1d5db; ${raporData.ssk_kendisi_odemis ? 'color: #6b7280;' : ''}">SSK${raporData.ssk_kendisi_odemis ? ' <span style="color: #059669; font-size: 12px;">(Kendisi Ödedi)</span>' : ''}</div>
            <div style="width: 50%; padding: 10px 12px; text-align: right; font-size: 14px; ${raporData.ssk_kendisi_odemis ? 'text-decoration: line-through; color: #9ca3af;' : ''}">${formatPara(raporFormData.ssk)}</div>
          </div>
          ` : ''}
          
          ${raporFormData.bagkur > 0 ? `
          <div style="display: flex; border-bottom: 1px solid #d1d5db; ${raporData.bagkur_kendisi_odemis ? 'background: #f0fdf4;' : ''}">
            <div style="width: 50%; padding: 10px 12px; font-size: 14px; font-weight: 500; border-right: 1px solid #d1d5db; ${raporData.bagkur_kendisi_odemis ? 'color: #6b7280;' : ''}">BAĞKUR${raporData.bagkur_kendisi_odemis ? ' <span style="color: #059669; font-size: 12px;">(Kendisi Ödedi)</span>' : ''}</div>
            <div style="width: 50%; padding: 10px 12px; text-align: right; font-size: 14px; ${raporData.bagkur_kendisi_odemis ? 'text-decoration: line-through; color: #9ca3af;' : ''}">${formatPara(raporFormData.bagkur)}</div>
          </div>
          ` : ''}
          
          ${raporFormData.gecici_vergi > 0 ? `
          <div style="display: flex; border-bottom: 1px solid #d1d5db; ${raporData.gecici_vergi_kendisi_odemis ? 'background: #f0fdf4;' : ''}">
            <div style="width: 50%; padding: 10px 12px; font-size: 14px; font-weight: 500; border-right: 1px solid #d1d5db; ${raporData.gecici_vergi_kendisi_odemis ? 'color: #6b7280;' : ''}">GEÇİCİ VERGİ${raporData.gecici_vergi_kendisi_odemis ? ' <span style="color: #059669; font-size: 12px;">(Kendisi Ödedi)</span>' : ''}</div>
            <div style="width: 50%; padding: 10px 12px; text-align: right; font-size: 14px; ${raporData.gecici_vergi_kendisi_odemis ? 'text-decoration: line-through; color: #9ca3af;' : ''}">${formatPara(raporFormData.gecici_vergi)}</div>
          </div>
          ` : ''}
          
          ${raporFormData.gelir_vergisi > 0 ? `
          <div style="display: flex; border-bottom: 1px solid #d1d5db; ${raporData.gelir_vergisi_kendisi_odemis ? 'background: #f0fdf4;' : ''}">
            <div style="width: 50%; padding: 10px 12px; font-size: 14px; font-weight: 500; border-right: 1px solid #d1d5db; ${raporData.gelir_vergisi_kendisi_odemis ? 'color: #6b7280;' : ''}">GELİR VERGİSİ${raporData.gelir_vergisi_kendisi_odemis ? ' <span style="color: #059669; font-size: 12px;">(Kendisi Ödedi)</span>' : ''}</div>
            <div style="width: 50%; padding: 10px 12px; text-align: right; font-size: 14px; ${raporData.gelir_vergisi_kendisi_odemis ? 'text-decoration: line-through; color: #9ca3af;' : ''}">${formatPara(raporFormData.gelir_vergisi)}</div>
          </div>
          ` : ''}
          
          ${digerIsimler.diger1 && digerIsimler.diger1.trim() !== '' && raporFormData.diger1 !== 0 ? `
          <div style="display: flex; border-bottom: 1px solid #d1d5db;">
            <div style="width: 50%; padding: 10px 12px; font-size: 14px; font-weight: 500; border-right: 1px solid #d1d5db;">${digerIsimler.diger1.toUpperCase()}</div>
            <div style="width: 50%; padding: 10px 12px; text-align: right; font-size: 14px;">${formatPara(raporFormData.diger1)}</div>
          </div>
          ` : ''}
          
          ${digerIsimler.diger2 && digerIsimler.diger2.trim() !== '' && raporFormData.diger2 !== 0 ? `
          <div style="display: flex; border-bottom: 1px solid #d1d5db;">
            <div style="width: 50%; padding: 10px 12px; font-size: 14px; font-weight: 500; border-right: 1px solid #d1d5db;">${digerIsimler.diger2.toUpperCase()}</div>
            <div style="width: 50%; padding: 10px 12px; text-align: right; font-size: 14px;">${formatPara(raporFormData.diger2)}</div>
          </div>
          ` : ''}
          
          ${digerIsimler.diger3 && digerIsimler.diger3.trim() !== '' && raporFormData.diger3 !== 0 ? `
          <div style="display: flex; border-bottom: 2px solid #9ca3af;">
            <div style="width: 50%; padding: 10px 12px; font-size: 14px; font-weight: 500; border-right: 1px solid #d1d5db;">${digerIsimler.diger3.toUpperCase()}</div>
            <div style="width: 50%; padding: 10px 12px; text-align: right; font-size: 14px;">${formatPara(raporFormData.diger3)}</div>
          </div>
          ` : ''}
          
          <div style="display: flex; background: #dbeafe; border-bottom: 2px solid #3b82f6;">
            <div style="width: 50%; padding: 14px 12px; font-weight: bold; font-size: 14px; color: #1e3a8a; border-right: 2px solid #3b82f6;">TOPLAM</div>
            <div style="width: 50%; padding: 14px 12px; text-align: right; font-size: 14px; font-weight: bold; color: #1e3a8a;">${formatPara(
              raporData.bakiye + 
              raporFormData.muhasebe + 
              (!raporData.kdv_kendisi_odemis ? raporFormData.kdv : 0) + 
              (!raporData.stopaj_kendisi_odemis ? raporFormData.stopaj : 0) + 
              (!raporData.ssk_kendisi_odemis ? raporFormData.ssk : 0) + 
              (!raporData.bagkur_kendisi_odemis ? raporFormData.bagkur : 0) + 
              (!raporData.gecici_vergi_kendisi_odemis ? raporFormData.gecici_vergi : 0) + 
              (!raporData.gelir_vergisi_kendisi_odemis ? raporFormData.gelir_vergisi : 0) + 
              raporFormData.diger1 + 
              raporFormData.diger2 + 
              raporFormData.diger3
            )}</div>
          </div>
          
          <div style="display: flex; background: #fecaca; border-bottom: 2px solid #dc2626;">
            <div style="width: 50%; padding: 12px; font-weight: bold; font-size: 14px; color: #7f1d1d; border-right: 2px solid #dc2626;">ÖDENEN</div>
            <div style="width: 50%; padding: 12px; text-align: right; font-size: 14px; font-weight: bold; color: #7f1d1d;">${formatPara(raporFormData.odenen)}</div>
          </div>
          
          <div style="display: flex; background: #bbf7d0;">
            <div style="width: 50%; padding: 14px 12px; font-weight: bold; font-size: 14px; color: #14532d; border-right: 2px solid #16a34a;">KALAN</div>
            <div style="width: 50%; padding: 14px 12px; text-align: right; font-size: 14px; font-weight: bold; color: #14532d;">${formatPara(
              (raporData.bakiye + 
              raporFormData.muhasebe + 
              (!raporData.kdv_kendisi_odemis ? raporFormData.kdv : 0) + 
              (!raporData.stopaj_kendisi_odemis ? raporFormData.stopaj : 0) + 
              (!raporData.ssk_kendisi_odemis ? raporFormData.ssk : 0) + 
              (!raporData.bagkur_kendisi_odemis ? raporFormData.bagkur : 0) + 
              (!raporData.gecici_vergi_kendisi_odemis ? raporFormData.gecici_vergi : 0) + 
              (!raporData.gelir_vergisi_kendisi_odemis ? raporFormData.gelir_vergisi : 0) + 
              raporFormData.diger1 + 
              raporFormData.diger2 + 
              raporFormData.diger3) - raporFormData.odenen
            )}</div>
          </div>
          
          ${(raporFormInputs.notlar && raporFormInputs.notlar.trim() !== '') ? `
          <div style="display: flex; background: #fef3c7; border-top: 2px solid #f59e0b;">
            <div style="width: 50%; padding: 12px; font-weight: bold; font-size: 14px; color: #92400e; border-right: 2px solid #f59e0b;">NOT</div>
            <div style="width: 50%; padding: 12px; font-size: 13px; color: #92400e;">${raporFormInputs.notlar}</div>
          </div>
          ` : ''}
        </div>
      `;
      
      // Seçili tahakkuklar için görsel bölümü oluştur
      let tahakkukHTML = '';
      if (secilenPdfler.length > 0) {
        const tahakkukRows = secilenPdfler.map(pdf => {
          const vergiAdi = pdf.vergi_turu === 'KDV' ? 'KDV' 
            : pdf.vergi_turu === 'KDV2' ? 'KDV2'
            : pdf.vergi_turu === 'Geçici Vergi' ? 'Geçici Vergi'
            : pdf.vergi_turu === 'Stopaj' ? 'Stopaj'
            : pdf.vergi_turu;
          
          const barkodNo = pdf.barkod_no || '-';
          const tutar = pdf.tutar || 0;
          
          return `
            <div style="display: flex; border-bottom: 1px solid #d1d5db;">
              <div style="width: 60%; padding: 10px 12px; font-size: 13px; font-family: monospace; color: #1f2937; border-right: 1px solid #d1d5db;">
                ${barkodNo}
              </div>
              <div style="width: 40%; padding: 10px 12px; text-align: right; font-size: 13px; font-weight: 600; color: #1f2937;">
                ${vergiAdi}: ${formatPara(tutar)} TL
              </div>
            </div>
          `;
        }).join('');
        
        tahakkukHTML = `
          <div style="margin-top: 16px; border: 2px solid #7c3aed; border-radius: 8px; overflow: hidden;">
            <div style="background: linear-gradient(to right, #7c3aed, #8b5cf6); padding: 10px 12px; text-align: center;">
              <div style="color: white; font-weight: bold; font-size: 14px;">📎 TAHAKKUK BİLGİLERİ</div>
            </div>
            <div style="display: flex; background: #ede9fe; border-bottom: 2px solid #7c3aed;">
              <div style="width: 60%; padding: 8px 12px; font-weight: bold; font-size: 12px; color: #5b21b6; border-right: 2px solid #7c3aed;">BARKOD NO</div>
              <div style="width: 40%; padding: 8px 12px; text-align: right; font-weight: bold; font-size: 12px; color: #5b21b6;">VERGİ TUTARI</div>
            </div>
            ${tahakkukRows}
          </div>
        `;
      }
      
      tempDiv.innerHTML = headerHTML + tableHTML + tahakkukHTML;
      document.body.appendChild(tempDiv);
      
      const raporElement = tempDiv;
      
      // Canvas'a çevir
      const canvas = await html2canvas(raporElement, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: true,
      });
      
      // Geçici div'i temizle
      document.body.removeChild(tempDiv);
      
      // Telefon numarasını düzenle
      let telefon = raporData.telefon.replace(/\D/g, ''); // Sadece rakamlar
      // Türkiye kodu yoksa ekle
      if (telefon.startsWith('0')) {
        telefon = '90' + telefon.substring(1);
      } else if (!telefon.startsWith('90') && telefon.length === 10) {
        telefon = '90' + telefon;
      }
      
      // Canvas'ı blob'a çevir
      canvas.toBlob(async (blob) => {
        try {
          // Clipboard API kullanarak panoya kopyala
          const item = new ClipboardItem({ 'image/png': blob });
          await navigator.clipboard.write([item]);
          
          if (secilenPdfler.length > 0) {
            toast.success(`✅ Rapor ve ${secilenPdfler.length} tahakkuk görseli panoya kopyalandı!`);
          } else {
            toast.success('✅ Rapor görseli panoya kopyalandı!');
          }
          
          // WhatsApp'ı aç - Desktop uygulaması öncelikli
          const desktopUrl = `whatsapp://send?phone=${telefon}`;
          const webUrl = `https://web.whatsapp.com/send?phone=${telefon}`;
          
          // Önce desktop uygulamasını açmayı dene
          const link = document.createElement('a');
          link.href = desktopUrl;
          link.click();
          
          toast.info('📱 WhatsApp açılıyor... Ctrl+V ile görseli yapıştırın!');
          
          // Eğer desktop açılmazsa web'e yönlendir
          const webFallbackTimeout = setTimeout(() => {
            if (window.confirm('WhatsApp uygulaması açılmadı mı?\n\nWeb versiyonunu açmak için Tamam\'a tıklayın.')) {
              window.open(webUrl, '_blank');
            }
          }, 2000);
          
          // Sayfa blur olursa (uygulama açıldıysa) timeout'u iptal et
          const handleBlur = () => {
            clearTimeout(webFallbackTimeout);
            window.removeEventListener('blur', handleBlur);
          };
          window.addEventListener('blur', handleBlur);
          
          // 5 saniye sonra listener'ı temizle
          setTimeout(() => {
            window.removeEventListener('blur', handleBlur);
          }, 5000);
          
        } catch (err) {
          console.error('Clipboard hatası:', err);
          // Fallback: Görseli indir
          try {
            const url = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = `${raporData.adSoyad}_${raporData.aylar[0]}_${raporData.yil}_rapor.png`;
            downloadLink.click();
            URL.revokeObjectURL(url);
            
            toast.success('📥 Görsel indirildi! WhatsApp\'ta görseli göndermek için dosyayı ekleyin.');
            
            // WhatsApp'ı aç
            const desktopUrl = `whatsapp://send?phone=${telefon}`;
            const link = document.createElement('a');
            link.href = desktopUrl;
            link.click();
          } catch (downloadErr) {
            console.error('İndirme hatası:', downloadErr);
            toast.error('Görsel oluşturulamadı');
          }
        }
      }, 'image/png');
      
    } catch (error) {
      console.error('WhatsApp gönderme hatası:', error);
      toast.error('Görsel oluşturulamadı');
    }
  };
  
  // Rapor form güncelleme - en son aya yaz
  // Debounce ref - son kaydetme isteğini takip et
  const saveTimeoutRef = useRef(null);
  
  // Input onBlur handler - OPTIMIZED (state update only on blur for totals)
  const handleRaporInputBlur = (fieldName, value, inputElement) => {
    const parsed = parsePara(value);
    
    // Display değerini Türk para formatına çevir (12500 → 12.500,00)
    const displayValue = Number(parsed).toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    // Input element değerini güncelle
    if (inputElement) {
      inputElement.value = displayValue;
    }
    
    // State'leri güncelle - Toplamların hesaplanması ve UI için gerekli
    setRaporFormData(prev => ({...prev, [fieldName]: parsed}));
    setRaporFormInputs(prev => ({...prev, [fieldName]: displayValue}));
    
    // ÇOKLU AY SEÇİMİNDE KAYDETME - veriler korunmalı, sadece görüntüleme amaçlı
    if (raporData && !raporData.tekAy) {
      console.log('Çoklu ay seçimi - kayıt yapılmadı (sadece görüntüleme)');
      return;
    }
    
    // Önceki kaydetme isteğini iptal et
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // 50ms sonra kaydet - fire-and-forget
    saveTimeoutRef.current = setTimeout(() => {
      if (!raporData || !raporData.kayitlar || raporData.kayitlar.length === 0) return;
      
      const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
      
      // Sadece değişen alanı gönder
      axios.put(`${API}/vergi-kayit/${sonKayit.id}`, {
        [fieldName]: parsed
      }).catch(err => console.error('Otomatik kayıt hatası:', err));
    }, 50);
  };
  
  const handleRaporFormKaydet = async (autoSave = false, customFormData = null, fastMode = false) => {
    if (!raporData || !raporData.kayitlar || raporData.kayitlar.length === 0) return;
    
    // ÇOKLU AY SEÇİMİNDE KAYDETME DEVRE DIŞI - veriler korunmalı
    if (!raporData.tekAy) {
      if (!autoSave) {
        toast.warning('Çoklu ay seçiminde kayıt yapılamaz. Tek ay seçerek düzenleme yapabilirsiniz.');
      }
      console.log('Çoklu ay seçimi - kayıt yapılmadı (sadece görüntüleme)');
      return;
    }
    
    // Kullanılacak form data'yı belirle
    const dataToSave = customFormData || raporFormData;
    
    try {
      if (!autoSave) setLoading(true);
      
      // En son ayın kaydını bul
      const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
      
      // _aktif flag'lerini hesapla: Eğer değer > 0 ise aktif, değilse müşterinin kendisi_oduyor flag'ine göre
      const kdv_aktif = dataToSave.kdv > 0 ? true : !raporData.musteri?.kdv_kendisi;
      const stopaj_aktif = dataToSave.stopaj > 0 ? true : !raporData.musteri?.stopaj_kendisi;
      const ssk_aktif = dataToSave.ssk > 0 ? true : !raporData.musteri?.ssk_kendisi;
      const bagkur_aktif = dataToSave.bagkur > 0 ? true : !raporData.musteri?.bagkur_kendisi;
      const gecici_vergi_aktif = dataToSave.gecici_vergi > 0 ? true : !raporData.musteri?.gecici_vergi_kendisi;
      const gelir_vergisi_aktif = dataToSave.gelir_vergisi > 0 ? true : !raporData.musteri?.gelir_vergisi_kendisi;
      
      console.log('DEBUG handleRaporFormKaydet - digerIsimler gönderilecek:', {
        diger1_isim: digerIsimler.diger1 !== 'DİĞER 1' ? digerIsimler.diger1 : null,
        diger2_isim: digerIsimler.diger2 !== 'DİĞER 2' ? digerIsimler.diger2 : null,
        diger3_isim: digerIsimler.diger3 !== 'DİĞER 3' ? digerIsimler.diger3 : null
      });
      
      // Form verilerini güncelle
      await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, {
        muhasebe: dataToSave.muhasebe,
        kdv: dataToSave.kdv,
        stopaj: dataToSave.stopaj,
        ssk: dataToSave.ssk,
        bagkur: dataToSave.bagkur,
        gecici_vergi: dataToSave.gecici_vergi,
        gelir_vergisi: dataToSave.gelir_vergisi,
        diger1: dataToSave.diger1,
        diger2: dataToSave.diger2,
        diger3: dataToSave.diger3,
        odenen: dataToSave.odenen,
        // Diğer alan isimlerini kaydet
        diger1_isim: digerIsimler.diger1 !== 'DİĞER 1' ? digerIsimler.diger1 : null,
        diger2_isim: digerIsimler.diger2 !== 'DİĞER 2' ? digerIsimler.diger2 : null,
        diger3_isim: digerIsimler.diger3 !== 'DİĞER 3' ? digerIsimler.diger3 : null,
        // SSK/Bağkur elle değiştirildiyse flag'leri false yap
        ssk_onceki_aydan: false,
        bagkur_onceki_aydan: false,
        // _aktif flag'lerini gönder
        kdv_aktif,
        stopaj_aktif,
        ssk_aktif,
        bagkur_aktif,
        gecici_vergi_aktif,
        gelir_vergisi_aktif
      });
      
      // Kayıtları yeniden fetch et
      await fetchKayitlar();
      
      // Mevcut diğer isimlerini koru (kaydetme sonrası resetlenmesin)
      const mevcutDigerIsimler = {...digerIsimler};
      
      // Raporu yeniden oluştur
      await handleRaporOlustur(raporData.vergiNo, raporData.adSoyad);
      
      // Diğer isimleri geri yükle (eğer değiştirilmişse)
      if (mevcutDigerIsimler.diger1 !== 'DİĞER 1' || 
          mevcutDigerIsimler.diger2 !== 'DİĞER 2' || 
          mevcutDigerIsimler.diger3 !== 'DİĞER 3') {
        setDigerIsimler(mevcutDigerIsimler);
      }
      
      if (!autoSave) {
        toast.success('Rapor kaydedildi');
      }
    } catch (error) {
      console.error('Rapor güncellenemedi:', error);
      if (!autoSave) {
        toast.error('Rapor güncellenemedi');
      }
    } finally {
      if (!autoSave) setLoading(false);
    }
  };
  
  // Rapor ödenen güncelleme - en son aya yaz
  const handleRaporOdenenKaydet = async () => {
    if (!raporData || !raporData.kayitlar || raporData.kayitlar.length === 0) return;
    
    // ÇOKLU AY SEÇİMİNDE KAYDETME DEVRE DIŞI
    if (!raporData.tekAy) {
      toast.warning('Çoklu ay seçiminde kayıt yapılamaz. Tek ay seçerek düzenleme yapabilirsiniz.');
      console.log('Çoklu ay seçimi - ödeme kaydedilmedi (sadece görüntüleme)');
      return;
    }
    
    try {
      const yeniOdenen = parsePara(raporOdenenInput);
      
      // En son ayın kaydını bul
      const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
      
      await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, {
        odenen: yeniOdenen
      });
      
      // Raporu yeniden oluştur
      await handleRaporOlustur(raporData.vergiNo, raporData.adSoyad);
      toast.success('Ödeme kaydedildi');
    } catch (error) {
      console.error('Ödeme güncellenemedi:', error);
      toast.error('Ödeme güncellenemedi');
    }
  };

  // Rapor oluştur
  // Önceki aydan SSK ve Bağkur verilerini kopyala
  const handleOncekiVeriKopyala = async () => {
    try {
      if (!raporSelectedMusteri || !raporSelectedAy) {
        toast.error('Müşteri ve ay seçili değil');
        return;
      }
      
      const aySirasi = {
        'Ocak': 1, 'Şubat': 2, 'Mart': 3, 'Nisan': 4, 'Mayıs': 5, 'Haziran': 6,
        'Temmuz': 7, 'Ağustos': 8, 'Eylül': 9, 'Ekim': 10, 'Kasım': 11, 'Aralık': 12
      };
      
      const mevcutAyIndex = aySirasi[raporSelectedAy];
      if (mevcutAyIndex === 1) {
        toast.error('Ocak ayı için önceki ay yok');
        return;
      }
      
      // Önceki ayı bul
      const oncekiAyIndex = mevcutAyIndex - 1;
      const oncekiAy = Object.keys(aySirasi).find(key => aySirasi[key] === oncekiAyIndex);
      
      // Önceki ayın verilerini getir
      const response = await axios.get(`${API}/vergi-kayit`, {
        params: {
          vergi_no: raporSelectedMusteri,
          yil: selectedYil
        }
      });
      
      const oncekiKayit = response.data.find(k => k.ay === oncekiAy);
      
      if (!oncekiKayit) {
        toast.error(`${oncekiAy} ayında kayıt bulunamadı`);
        return;
      }
      
      // Sadece SSK ve Bağkur verilerini kopyala
      setRaporFormInputs(prev => ({
        ...prev,
        ssk: formatPara(oncekiKayit.ssk || 0),
        bagkur: formatPara(oncekiKayit.bagkur || 0)
      }));
      
      setRaporFormData(prev => ({
        ...prev,
        ssk: oncekiKayit.ssk || 0,
        bagkur: oncekiKayit.bagkur || 0
      }));
      
      toast.success(`${oncekiAy} ayından SSK ve Bağkur verileri kopyalandı`);
    } catch (error) {
      console.error('Veri kopyalama hatası:', error);
      toast.error('Veri kopyalanamadı');
    }
  };

  const handleRaporOlustur = async (vergiNo, adSoyad, seciliAy = null) => {
    // Dialog kapanıyorsa, yeni açmayı engelle
    if (raporDialogClosing) {
      return;
    }
    
    // Eğer seciliAy parametresi verilmişse onu kullan, yoksa selectedAylar'ı kullan
    // seciliAy array veya string olabilir
    const aylarToUse = Array.isArray(seciliAy) ? seciliAy : (seciliAy ? [seciliAy] : selectedAylar);
    
    try {
      // Müşteri bilgisini mevcut listeden al (API çağrısı yapmadan)
      const musteri = musteriler.find(m => m.vergi_no === vergiNo);
      
      // Seçili aylara göre kayıtları al
      const response = await axios.get(`${API}/vergi-kayit`, {
        params: {
          vergi_no: vergiNo,
          yil: selectedYil
        }
      });
      
      // Seçili aylara göre filtrele - aylarToUse kullan
      let mevcutKayitlar = response.data.filter(k => aylarToUse.includes(k.ay));
      
      // Eğer kayıt yoksa
      if (mevcutKayitlar.length === 0) {
        // Çoklu ay seçiminde kayıt oluşturma - sadece görüntüleme
        if (aylarToUse.length > 1) {
          // Boş raporData set et
          setRaporData({
            adSoyad,
            vergiNo,
            telefon: musteri?.telefon || '',
            aylar: aylarToUse,
            yil: selectedYil,
            bakiye: 0, muhasebe: 0, kdv: 0, stopaj: 0, ssk: 0, bagkur: 0,
            gecici_vergi: 0, kurum_gecici_vergi: 0, gelir_vergisi: 0,
            diger1: 0, diger2: 0, diger3: 0, odenen: 0, toplam: 0, kalan: 0,
            tekAy: false, kayitlar: [], notlar: '',
            musteri: { kdv_kendisi: false, stopaj_kendisi: false, ssk_kendisi: false, bagkur_kendisi: false, gecici_vergi_kendisi: false, gelir_vergisi_kendisi: false }
          });
          setRaporFormData({ muhasebe: 0, kdv: 0, stopaj: 0, ssk: 0, bagkur: 0, gecici_vergi: 0, gelir_vergisi: 0, diger1: 0, diger2: 0, diger3: 0, odenen: 0 });
          setRaporFormInputs({ muhasebe: '0,00', kdv: '0,00', stopaj: '0,00', ssk: '0,00', bagkur: '0,00', gecici_vergi: '0,00', gelir_vergisi: '0,00', diger1: '0,00', diger2: '0,00', diger3: '0,00', odenen: '0,00', notlar: '' });
          setRaporSelectedMusteri(vergiNo);
          setRaporSelectedAy(aylarToUse[0]);
          setRaporSelectedAylar(aylarToUse);
          setRaporDialogOpen(true);
          toast.info('Seçilen aylarda kayıt bulunamadı');
          return;
        }
        // Tek ay seçiminde yeni kayıt oluştur
        toast.info('Yeni kayıt oluşturuluyor...');
        
        const olusturulanKayitlar = [];
        for (const ay of aylarToUse) {
          const yeniKayit = {
            vergi_no: vergiNo,
            ad_soyad: adSoyad,
            ay: ay,
            yil: selectedYil,
            bakiye: 0,
            muhasebe: 0,
            kdv: 0,
            stopaj: 0,
            ssk: 0,
            bagkur: 0,
            gecici_vergi: 0,
            gelir_vergisi: 0,
            diger1: 0,
            diger2: 0,
            diger3: 0,
            odenen: 0
          };
          
          // Backend'e kayıt oluştur ve dönen kaydı sakla
          const kayitResponse = await axios.post(`${API}/vergi-kayit`, yeniKayit);
          olusturulanKayitlar.push(kayitResponse.data);
        }
        
        // Oluşturulan kayıtları kullan
        mevcutKayitlar = olusturulanKayitlar;
        
        // Kayıtlar tablosunu da güncelle
        await fetchKayitlar();
        
        toast.success('Kayıt oluşturuldu, şimdi veri girebilirsiniz');
      }
      
      // Ay sırasına göre sırala
      const aySirasi = {
        'Ocak': 1, 'Şubat': 2, 'Mart': 3, 'Nisan': 4, 'Mayıs': 5, 'Haziran': 6,
        'Temmuz': 7, 'Ağustos': 8, 'Eylül': 9, 'Ekim': 10, 'Kasım': 11, 'Aralık': 12
      };
      mevcutKayitlar.sort((a, b) => aySirasi[a.ay] - aySirasi[b.ay]);
      
      // Kayıt yoksa hata ver ve çık
      if (mevcutKayitlar.length === 0) {
        toast.error('Kayıt oluşturulamadı. Lütfen tekrar deneyin.');
        return;
      }
      
      // Rapor hesapla
      const ilkKayit = mevcutKayitlar[0];
      const ilkAyIndex = aySirasi[ilkKayit.ay];
      
      // Eğer seçili ilk ay Ocak değilse, önceki ayların kalanını hesapla
      let bakiye = ilkKayit.bakiye || 0;
      
      if (ilkAyIndex > 1) {
        // Önceki ayların toplamını hesapla
        const oncekiKayitlar = response.data.filter(k => {
          const kayitAyIndex = aySirasi[k.ay];
          return kayitAyIndex < ilkAyIndex;
        });
        
        if (oncekiKayitlar.length > 0) {
          // Önceki ayların son kalanını bul (ay sırasına göre son ay)
          oncekiKayitlar.sort((a, b) => aySirasi[b.ay] - aySirasi[a.ay]); // Tersten sırala
          const sonOncekiKayit = oncekiKayitlar[0];
          // DB'deki stale kalan yerine, Kayıtlar tablosuyla aynı formülle canlı hesapla
          const hesaplananToplam = 
            (Number(sonOncekiKayit.bakiye) || 0) +
            (Number(sonOncekiKayit.muhasebe) || 0) +
            (sonOncekiKayit.kdv_aktif !== false && !sonOncekiKayit.kdv_kendisi_odemis ? (Number(sonOncekiKayit.kdv) || 0) : 0) +
            (sonOncekiKayit.stopaj_aktif !== false && !sonOncekiKayit.stopaj_kendisi_odemis ? (Number(sonOncekiKayit.stopaj) || 0) : 0) +
            (sonOncekiKayit.ssk_aktif !== false && !sonOncekiKayit.ssk_kendisi_odemis ? (Number(sonOncekiKayit.ssk) || 0) : 0) +
            (sonOncekiKayit.bagkur_aktif !== false && !sonOncekiKayit.bagkur_kendisi_odemis ? (Number(sonOncekiKayit.bagkur) || 0) : 0) +
            (sonOncekiKayit.gecici_vergi_aktif !== false && !sonOncekiKayit.gecici_vergi_kendisi_odemis ? (Number(sonOncekiKayit.gecici_vergi) || 0) : 0) +
            (sonOncekiKayit.gelir_vergisi_aktif !== false && !sonOncekiKayit.gelir_vergisi_kendisi_odemis ? (Number(sonOncekiKayit.gelir_vergisi) || 0) : 0) +
            (Number(sonOncekiKayit.diger1) || 0) +
            (Number(sonOncekiKayit.diger2) || 0) +
            (Number(sonOncekiKayit.diger3) || 0);
          bakiye = hesaplananToplam - (Number(sonOncekiKayit.odenen) || 0);
        }
      }
      
      const toplamlar = {
        muhasebe: 0,
        kdv: 0,
        stopaj: 0,
        ssk: 0,
        bagkur: 0,
        gecici_vergi: 0,
        kurum_gecici_vergi: 0,
        gelir_vergisi: 0,
        diger1: 0,
        diger2: 0,
        diger3: 0,
        odenen: 0
      };
      
      mevcutKayitlar.forEach(kayit => {
        toplamlar.muhasebe += kayit.muhasebe || 0;
        toplamlar.kdv += kayit.kdv || 0;
        toplamlar.stopaj += kayit.stopaj || 0;
        toplamlar.ssk += kayit.ssk || 0;
        toplamlar.bagkur += kayit.bagkur || 0;
        toplamlar.gecici_vergi += kayit.gecici_vergi || 0;
        toplamlar.kurum_gecici_vergi += kayit.kurum_gecici_vergi || 0;
        toplamlar.gelir_vergisi += kayit.gelir_vergisi || 0;
        toplamlar.diger1 += kayit.diger1 || 0;
        toplamlar.diger2 += kayit.diger2 || 0;
        toplamlar.diger3 += kayit.diger3 || 0;
        toplamlar.odenen += kayit.odenen || 0;
      });
      
      // ÖDENEN: Tek ay seçiliyse son ayın ödemesi, çoklu ay seçiliyse toplam ödenen
      const sonKayit = mevcutKayitlar[mevcutKayitlar.length - 1];
      const sadeceSonAyOdenen = aylarToUse.length === 1 ? (sonKayit.odenen || 0) : toplamlar.odenen;
      
      // Toplam hesapla - "kendisi_odemis" olan vergileri hariç tut
      const toplam = bakiye + 
        toplamlar.muhasebe + 
        (!sonKayit.kdv_kendisi_odemis ? toplamlar.kdv : 0) + 
        (!sonKayit.stopaj_kendisi_odemis ? toplamlar.stopaj : 0) + 
        (!sonKayit.ssk_kendisi_odemis ? toplamlar.ssk : 0) + 
        (!sonKayit.bagkur_kendisi_odemis ? toplamlar.bagkur : 0) + 
        (!sonKayit.gecici_vergi_kendisi_odemis ? toplamlar.gecici_vergi : 0) + 
        toplamlar.kurum_gecici_vergi +
        (!sonKayit.gelir_vergisi_kendisi_odemis ? toplamlar.gelir_vergisi : 0) + 
        toplamlar.diger1 + 
        toplamlar.diger2 + 
        toplamlar.diger3;
      
      const kalan = toplam - sadeceSonAyOdenen;
      
      setRaporData({
        adSoyad,
        vergiNo,
        telefon: musteri?.telefon || '',
        aylar: aylarToUse, // Tüm seçilen ayları göster (kayıt olmayanlar dahil)
        yil: selectedYil,
        bakiye,
        ...toplamlar,
        odenen: sadeceSonAyOdenen, // Sadece en son ayın ödemesi
        toplam,
        kalan,
        tekAy: aylarToUse.length === 1,
        kayitlar: mevcutKayitlar,
        tumYilKayitlari: response.data, // Tüm yılın kayıtları (yıllık özet için)
        notlar: sonKayit?.notlar || '', // Son kaydın notları
        // SSK/Bağkur önceki aydan aktarıldı mı?
        ssk_onceki_aydan: sonKayit?.ssk_onceki_aydan || false,
        bagkur_onceki_aydan: sonKayit?.bagkur_onceki_aydan || false,
        // "Kendisi Ödemiştir" - Bu ay için
        kdv_kendisi_odemis: sonKayit?.kdv_kendisi_odemis || false,
        stopaj_kendisi_odemis: sonKayit?.stopaj_kendisi_odemis || false,
        ssk_kendisi_odemis: sonKayit?.ssk_kendisi_odemis || false,
        bagkur_kendisi_odemis: sonKayit?.bagkur_kendisi_odemis || false,
        gecici_vergi_kendisi_odemis: sonKayit?.gecici_vergi_kendisi_odemis || false,
        gelir_vergisi_kendisi_odemis: sonKayit?.gelir_vergisi_kendisi_odemis || false,
        // Müşteri "kendisi ödüyor" bilgileri
        musteri: {
          kdv_kendisi: musteri?.kdv_kendisi || false,
          stopaj_kendisi: musteri?.stopaj_kendisi || false,
          ssk_kendisi: musteri?.ssk_kendisi || false,
          bagkur_kendisi: musteri?.bagkur_kendisi || false,
          gecici_vergi_kendisi: musteri?.gecici_vergi_kendisi || false,
          gelir_vergisi_kendisi: musteri?.gelir_vergisi_kendisi || false
        }
      });
      
      // Diğer alan isimlerini kayıttan yükle (eğer varsa)
      console.log('DEBUG sonKayit diger isimleri:', {
        diger1_isim: sonKayit?.diger1_isim,
        diger2_isim: sonKayit?.diger2_isim,
        diger3_isim: sonKayit?.diger3_isim
      });
      const kayitDigerIsimler = {
        diger1: sonKayit?.diger1_isim || 'DİĞER 1',
        diger2: sonKayit?.diger2_isim || 'DİĞER 2',
        diger3: sonKayit?.diger3_isim || 'DİĞER 3'
      };
      console.log('DEBUG kayitDigerIsimler:', kayitDigerIsimler);
      setDigerIsimler(kayitDigerIsimler);
      
      // Rapor form verilerini set et (sayı olarak - hesaplama için)
      // Müşterinin kendi ödediği vergiler 0 olarak başlasın
      setRaporFormData({
        muhasebe: toplamlar.muhasebe,
        kdv: musteri?.kdv_kendisi ? 0 : toplamlar.kdv,
        stopaj: musteri?.stopaj_kendisi ? 0 : toplamlar.stopaj,
        ssk: musteri?.ssk_kendisi ? 0 : toplamlar.ssk,
        bagkur: musteri?.bagkur_kendisi ? 0 : toplamlar.bagkur,
        gecici_vergi: musteri?.gecici_vergi_kendisi ? 0 : toplamlar.gecici_vergi,
        gelir_vergisi: musteri?.gelir_vergisi_kendisi ? 0 : toplamlar.gelir_vergisi,
        diger1: toplamlar.diger1,
        diger2: toplamlar.diger2,
        diger3: toplamlar.diger3,
        odenen: sadeceSonAyOdenen
      });
      
      // Input değerleri (string - virgül korunsun)
      // Müşterinin kendi ödediği vergiler boş gösterilsin
      setRaporFormInputs({
        muhasebe: formatPara(toplamlar.muhasebe),
        kdv: musteri?.kdv_kendisi ? '0,00' : formatPara(toplamlar.kdv),
        stopaj: musteri?.stopaj_kendisi ? '0,00' : formatPara(toplamlar.stopaj),
        ssk: musteri?.ssk_kendisi ? '0,00' : formatPara(toplamlar.ssk),
        bagkur: musteri?.bagkur_kendisi ? '0,00' : formatPara(toplamlar.bagkur),
        gecici_vergi: musteri?.gecici_vergi_kendisi ? '0,00' : formatPara(toplamlar.gecici_vergi),
        gelir_vergisi: musteri?.gelir_vergisi_kendisi ? '0,00' : formatPara(toplamlar.gelir_vergisi),
        diger1: formatPara(toplamlar.diger1),
        diger2: formatPara(toplamlar.diger2),
        diger3: formatPara(toplamlar.diger3),
        odenen: formatPara(sadeceSonAyOdenen),
        notlar: sonKayit?.notlar || ''
      });
      
      // Ödenen input'unu set et - SADECE en son ayın ödenen tutarı (düzenlenecek ay)
      setRaporOdenenInput(formatPara(sadeceSonAyOdenen));
      
      // Seçili müşteri ve ay'ı state'e kaydet
      setRaporSelectedMusteri(vergiNo);
      setRaporSelectedAy(aylarToUse[0]); // İlk seçili ay
      setRaporSelectedAylar(aylarToUse); // Çoklu ay seçimi
      
      // Tahakkuk PDF'lerini getir (WhatsApp için)
      try {
        const pdfResponse = await axios.get(`${API}/tahakkuk-pdfs/${vergiNo}/${aylarToUse[0]}/${selectedYil}`);
        setTahakkukPdfs(pdfResponse.data.pdfs || []);
        setSelectedTahakkukPdfs([]); // Seçimleri sıfırla
      } catch (pdfError) {
        console.log('Tahakkuk PDF bulunamadı:', pdfError);
        setTahakkukPdfs([]);
        setSelectedTahakkukPdfs([]);
      }
      
      setRaporDialogOpen(true);
    } catch (error) {
      console.error('Rapor oluşturulamadı:', error);
      toast.error('Rapor oluşturulamadı');
    }
  };

  const handleMusteriEkle = async (e) => {
    e.preventDefault();
    
    // Müşteri türü zorunlu kontrol
    if (!yeniMusteri.musteri_turu) {
      toast.error('Lütfen müşteri türünü seçin');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/musteriler`, yeniMusteri);
      toast.success('Müşteri başarıyla eklendi');
      setYeniMusteri({ 
        vergi_no: '', 
        ad_soyad: '',
        musteri_turu: 'İŞLETME',
        stopaj_periyot: 'Aylık',
        bakiye_gorunur: true,
        muhasebe_gorunur: true,
        kdv_gorunur: true,
        stopaj_gorunur: true,
        ssk_gorunur: true,
        bagkur_gorunur: true,
        gecici_vergi_gorunur: true,
        gelir_vergisi_gorunur: true,
        diger1_gorunur: true,
        diger2_gorunur: true,
        diger3_gorunur: true
      });
      setMusteriDialogOpen(false);
      fetchMusteriler();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Müşteri eklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleKayitEkle = async (e) => {
    e.preventDefault();
    
    // Zorunlu alan kontrolü
    if (!yeniKayit.vergi_no) {
      toast.error('Lütfen müşteri seçin');
      return;
    }
    if (!yeniKayit.ay) {
      toast.error('Lütfen ay seçin');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/vergi-kayit`, yeniKayit);
      toast.success('Kayıt başarıyla eklendi');
      setYeniKayit({
        vergi_no: '',
        ad_soyad: '',
        ay: '',
        yil: new Date().getFullYear(),
        bakiye: 0,
        muhasebe: 0,
        kdv: 0,
        stopaj: 0,
        ssk: 0,
        bagkur: 0,
        gecici_vergi: 0,
        gelir_vergisi: 0,
        diger1: 0,
        diger2: 0,
        diger3: 0,
        odenen: 0
      });
      setKayitDialogOpen(false);
      fetchKayitlar();
    } catch (error) {
      toast.error('Kayıt eklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handlePdfUpload = async (e) => {
    e.preventDefault();
    if (!pdfFile) {
      toast.error('Lütfen bir PDF dosyası seçin');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      
      const response = await axios.post(
        `${API}/vergi-kayit/pdf-upload?vergi_no=${pdfFormData.vergi_no}&ad_soyad=${pdfFormData.ad_soyad}&ay=${pdfFormData.ay}&yil=${pdfFormData.yil}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('PDF başarıyla işlendi ve kayıt oluşturuldu');
      setPdfFile(null);
      setPdfFormData({
        vergi_no: '',
        ad_soyad: '',
        ay: '',
        yil: new Date().getFullYear()
      });
      setPdfDialogOpen(false);
      fetchKayitlar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF işlenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleKayitGuncelle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/vergi-kayit/${editingKayit.id}`, {
        bakiye: parseFloat(editingKayit.bakiye),
        muhasebe: parseFloat(editingKayit.muhasebe),
        kdv: parseFloat(editingKayit.kdv),
        stopaj: parseFloat(editingKayit.stopaj),
        ssk: parseFloat(editingKayit.ssk),
        bagkur: parseFloat(editingKayit.bagkur),
        gecici_vergi: parseFloat(editingKayit.gecici_vergi),
        gelir_vergisi: parseFloat(editingKayit.gelir_vergisi),
        diger1: parseFloat(editingKayit.diger1),
        diger2: parseFloat(editingKayit.diger2),
        diger3: parseFloat(editingKayit.diger3),
        odenen: parseFloat(editingKayit.odenen)
      });
      toast.success('Kayıt güncellendi');
      setEditDialogOpen(false);
      setEditingKayit(null);
      fetchKayitlar();
    } catch (error) {
      toast.error('Kayıt güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Ödeme ekleme - sadece dialog'u aç
  const handleOdemeAc = useCallback((kayit) => {
    setOdemeKayit(kayit);
    setOdemeDialogOpen(true);
  }, []);

  // Tahsilat kaydedildiğinde çağrılacak
  const handleTahsilatSaved = useCallback(() => {
    setOdemeKayit(null);
    fetchKayitlar();
  }, [selectedAylar, selectedYil]);

  const handleOdemeKaydet = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const tutarSayi = parsePara(odemeTutari);
      
      await axios.put(`${API}/vergi-kayit/${odemeKayit.id}`, {
        odenen: tutarSayi
      });
      
      toast.success('Ödeme kaydedildi');
      setOdemeDialogOpen(false);
      setOdemeKayit(null);
      setOdemeTutari('');
      fetchKayitlar();
    } catch (error) {
      toast.error('Ödeme kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleKayitSil = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/vergi-kayit/${id}`);
      toast.success('Kayıt silindi');
      fetchKayitlar();
    } catch (error) {
      toast.error('Kayıt silinemedi');
    } finally {
      setLoading(false);
    }
  };

  // Inline editing fonksiyonları
  const handleCellDoubleClick = (kayitId, field, currentValue) => {
    setEditingCell({ kayitId, field });
    setEditingValue(currentValue || 0);
  };

  const handleCellBlur = async () => {
    if (editingCell.kayitId && editingCell.field) {
      await handleInlineSave();
    }
    setEditingCell({ kayitId: null, field: null });
  };

  const handleCellKeyDown = async (e) => {
    if (e.key === 'Enter') {
      await handleInlineSave();
      setEditingCell({ kayitId: null, field: null });
    } else if (e.key === 'Escape') {
      setEditingCell({ kayitId: null, field: null });
    }
  };

  const handleInlineSave = async () => {
    if (!editingCell.kayitId || !editingCell.field) return;

    try {
      const updatedValue = parseFloat(editingValue) || 0;
      await axios.put(`${API}/vergi-kayit/${editingCell.kayitId}`, {
        [editingCell.field]: updatedValue
      });
      toast.success('Değer güncellendi');
      fetchKayitlar();
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('Değer güncellenemedi');
    }
  };

  // Sesli Komut İşleme
  const handleTopluZipUpload = async (e) => {
    e.preventDefault();
    if (!zipFile) {
      toast.error('Lütfen bir ZIP dosyası seçin');
      return;
    }
    
    // GİB ZIP upload - Ay seçimi GEREKMEZ, PDF içeriğinden otomatik alınır!
    
    setLoading(true);
    try {
      // Önce çakışmaları kontrol et
      const previewFormData = new FormData();
      previewFormData.append('file', zipFile);
      
      const previewResponse = await axios.post(
        `${API}/yuklenen-vergiler/upload-zip-preview`,
        previewFormData,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000 // 5 dakika timeout
        }
      );
      
      // Çakışma varsa dialog aç
      if (previewResponse.data.conflicts && previewResponse.data.conflicts.length > 0) {
        setConflicts(previewResponse.data.conflicts);
        setPendingZipFile(zipFile);
        setConflictDialogOpen(true);
        setZipDialogOpen(false);
        setLoading(false);
        return;
      }
      
      // Çakışma yoksa direkt yükle
      await performZipUpload(zipFile);
      
    } catch (error) {
      console.error('handleTopluZipUpload error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'GİB ZIP yükleme başarısız';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // ZIP yükleme işlemini gerçekleştir (çakışma çözüldükten sonra da çağrılır)
  const performZipUpload = async (file) => {
    setLoading(true);
    toast.info('ZIP dosyası yükleniyor, lütfen bekleyin...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // YENİ: Yüklenen Vergiler ZIP endpoint'i kullan (SSK, Stopaj, KDV2 destekli)
      const response = await axios.post(
        `${API}/yuklenen-vergiler/upload-zip`,
        formData,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000 // 5 dakika timeout
        }
      );
      
      setUploadResults(response.data);
      setResultsDialogOpen(true);
      setZipFile(null);
      setPendingZipFile(null);
      setZipFormData({
        ay: '',
        yil: new Date().getFullYear()
      });
      setZipDialogOpen(false);
      
      toast.success(`GİB ZIP yükleme tamamlandı! ${response.data.basarili} başarılı, ${response.data.hatali} hatalı`);
      fetchKayitlar();
      fetchMusteriler();
      fetchYuklenenVergiler(); // Yüklenen Vergiler sekmesini de yenile
    } catch (error) {
      console.error('performZipUpload error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'GİB ZIP yükleme başarısız';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Çakışmaları çöz ve yüklemeye devam et
  const handleResolveConflicts = async () => {
    setLoading(true);
    try {
      // Kullanıcının kararlarını uygula
      const resolveData = conflicts.map((conflict, index) => ({
        kayit_id: conflict.kayit_id,
        vergi_turu: conflict.vergi_turu,
        action: conflictDecisions[index] || 'keep', // Varsayılan: mevcut değeri koru
        yeni_tutar: conflict.yeni_tutar
      }));
      
      // Güncelleme kararlarını backend'e gönder
      const updateConflicts = resolveData.filter(c => c.action === 'update');
      if (updateConflicts.length > 0) {
        await axios.post(`${API}/vergi-kayit/resolve-conflicts`, updateConflicts);
      }
      
      // Çakışma dialogunu kapat
      setConflictDialogOpen(false);
      setConflicts([]);
      setConflictDecisions({});
      
      // ZIP yüklemeye devam et
      if (pendingZipFile) {
        await performZipUpload(pendingZipFile);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Çakışma çözme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleExcelExport = async () => {
    try {
      const params = {};
      if (selectedMusteri && selectedMusteri !== 'all') params.vergi_no = selectedMusteri;
      // Seçili ayları gönder
      if (selectedAylar && selectedAylar.length > 0) {
        params.aylar = selectedAylar.join(','); // Virgülle ayrılmış string olarak gönder
      }
      if (selectedYil) params.yil = selectedYil;
      
      console.log('Excel export params:', params);
      
      const response = await axios.get(`${API}/vergi-kayit/excel-export`, {
        params,
        responseType: 'blob',
        timeout: 120000 // 120 saniye - Excel oluşturma uzun sürebilir
      });
      
      console.log('Excel response:', response);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vergi_kayitlari_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel dosyası indirildi');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Excel dışa aktarılamadı: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleGorunurlukDegistir = async (kayitId, yeniDurum) => {
    try {
      await axios.put(`${API}/vergi-kayit/${kayitId}`, {
        gorunur: yeniDurum
      });
      toast.success(yeniDurum ? 'Kayıt görünür yapıldı' : 'Kayıt gizlendi');
      fetchKayitlar();
    } catch (error) {
      toast.error('Görünürlük değiştirilemedi');
    }
  };
  
  const handleMusteriSil = async (musteriId) => {
    if (!window.confirm('Bu müşteri ve tüm kayıtları silinecek. Emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/musteriler/${musteriId}`);
      toast.success('Müşteri silindi');
      fetchMusteriler();
      fetchKayitlar();
    } catch (error) {
      toast.error('Müşteri silinemedi');
    }
  };
  
  const handleMusteriGuncelle = async (e) => {
    e.preventDefault();
    
    // Müşteri türü zorunlu kontrol
    if (!editingMusteri.musteri_turu) {
      toast.error('Lütfen müşteri türünü seçin');
      return;
    }
    
    setLoading(true);
    try {
      // Seçili ay ve yıl'ı effective olarak gönder - geçmiş kayıtlar etkilenmesin
      const params = {};
      if (selectedAylar && selectedAylar.length > 0) {
        // Seçili ayların en erken olanını kullan (ay sırasına göre)
        const aySira = { 'Ocak': 1, 'Şubat': 2, 'Mart': 3, 'Nisan': 4, 'Mayıs': 5, 'Haziran': 6, 'Temmuz': 7, 'Ağustos': 8, 'Eylül': 9, 'Ekim': 10, 'Kasım': 11, 'Aralık': 12 };
        const sortedAylar = [...selectedAylar].sort((a, b) => (aySira[a] || 0) - (aySira[b] || 0));
        params.effective_yil = selectedYil;
        params.effective_ay = sortedAylar[0];
      }
      await axios.put(`${API}/musteriler/${editingMusteri.id}`, editingMusteri, { params });
      toast.success('Müşteri güncellendi (değişiklikler seçili aydan itibaren geçerli)');
      setMusteriEditDialogOpen(false);
      setEditingMusteri(null);
      fetchMusteriler();
      fetchKayitlar();
    } catch (error) {
      toast.error('Müşteri güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Sesli komut için müşteri güncelleme (event olmadan)
  // Toplu seçim toggle
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedMusteriler(musteriler.map(m => m.id));
    } else {
      setSelectedMusteriler([]);
    }
  };

  const handleSelectMusteri = (musteriId, checked) => {
    if (checked) {
      setSelectedMusteriler([...selectedMusteriler, musteriId]);
    } else {
      setSelectedMusteriler(selectedMusteriler.filter(id => id !== musteriId));
    }
  };

  // Favori toggle
  const handleFavoriToggle = async (musteriId, currentFavori) => {
    try {
      await axios.patch(`${API}/musteri/${musteriId}`, {
        favori: !currentFavori
      });
      fetchMusteriler();
      toast.success(!currentFavori ? 'Favorilere eklendi' : 'Favorilerden çıkarıldı');
    } catch (error) {
      console.error('Favori güncellenemedi:', error);
      toast.error('Favori güncellenemedi');
    }
  };

  // Toplu güncelleme
  const handleTopluGuncelleme = async () => {
    if (selectedMusteriler.length === 0) {
      toast.error('Lütfen en az bir müşteri seçin');
      return;
    }

    setLoading(true);
    let basarili = 0;
    let hatali = 0;
    const hatalar = [];
    
    try {
      for (const musteriId of selectedMusteriler) {
        const musteri = musteriler.find(m => m.id === musteriId);
        if (!musteri) continue;

        // Sadece gerekli alanları içeren güncelleme verisi hazırla (id, user_id, created_at hariç)
        const updateData = {
          vergi_no: musteri.vergi_no,
          ad_soyad: musteri.ad_soyad,
          telefon: musteri.telefon || '',
          musteri_turu: musteri.musteri_turu || 'İŞLETME',
          stopaj_periyot: musteri.stopaj_periyot || 'Aylık',
          aktif: musteri.aktif !== undefined ? musteri.aktif : true,
          kdv_kendisi: musteri.kdv_kendisi || false,
          stopaj_kendisi: musteri.stopaj_kendisi || false,
          ssk_kendisi: musteri.ssk_kendisi || false,
          bagkur_kendisi: musteri.bagkur_kendisi || false,
          gecici_vergi_kendisi: musteri.gecici_vergi_kendisi || false,
          gelir_vergisi_kendisi: musteri.gelir_vergisi_kendisi || false,
          bakiye_gorunur: musteri.bakiye_gorunur !== undefined ? musteri.bakiye_gorunur : true,
          muhasebe_gorunur: musteri.muhasebe_gorunur !== undefined ? musteri.muhasebe_gorunur : true,
          kdv_gorunur: musteri.kdv_gorunur !== undefined ? musteri.kdv_gorunur : true,
          stopaj_gorunur: musteri.stopaj_gorunur !== undefined ? musteri.stopaj_gorunur : true,
          ssk_gorunur: musteri.ssk_gorunur !== undefined ? musteri.ssk_gorunur : true,
          bagkur_gorunur: musteri.bagkur_gorunur !== undefined ? musteri.bagkur_gorunur : true,
          gecici_vergi_gorunur: musteri.gecici_vergi_gorunur !== undefined ? musteri.gecici_vergi_gorunur : true,
          kurum_gecici_vergi_gorunur: musteri.kurum_gecici_vergi_gorunur !== undefined ? musteri.kurum_gecici_vergi_gorunur : true,
          gelir_vergisi_gorunur: musteri.gelir_vergisi_gorunur !== undefined ? musteri.gelir_vergisi_gorunur : true,
          diger1_gorunur: musteri.diger1_gorunur !== undefined ? musteri.diger1_gorunur : true,
          diger2_gorunur: musteri.diger2_gorunur !== undefined ? musteri.diger2_gorunur : true,
          diger3_gorunur: musteri.diger3_gorunur !== undefined ? musteri.diger3_gorunur : true,
          favori: musteri.favori || false,
          gib_giris_kodu: musteri.gib_giris_kodu || '',
          gib_parola: musteri.gib_parola || 'maliye',
          gib_sifre: musteri.gib_sifre || '',
          ortaklar: musteri.ortaklar || [],
          ssk_isyerleri: musteri.ssk_isyerleri || [],
          gorunur_aylar: musteri.gorunur_aylar || []
        };
        
        // Toplu güncelleme seçeneklerini uygula
        if (topluGuncellemeData.aktif !== null) {
          updateData.aktif = topluGuncellemeData.aktif;
        }
        if (topluGuncellemeData.musteri_turu) {
          updateData.musteri_turu = topluGuncellemeData.musteri_turu;
        }
        if (topluGuncellemeData.stopaj_periyot) {
          updateData.stopaj_periyot = topluGuncellemeData.stopaj_periyot;
        }
        if (topluGuncellemeData.favori !== null) {
          updateData.favori = topluGuncellemeData.favori;
        }
        if (topluGuncellemeData.gorunur_aylar !== null) {
          updateData.gorunur_aylar = topluGuncellemeData.gorunur_aylar;
        }
        
        // Vergi "kendisi ödüyor" alanlarını güncelle
        if (topluGuncellemeData.kdv_kendisi !== null) {
          updateData.kdv_kendisi = topluGuncellemeData.kdv_kendisi;
        }
        if (topluGuncellemeData.stopaj_kendisi !== null) {
          updateData.stopaj_kendisi = topluGuncellemeData.stopaj_kendisi;
        }
        if (topluGuncellemeData.ssk_kendisi !== null) {
          updateData.ssk_kendisi = topluGuncellemeData.ssk_kendisi;
        }
        if (topluGuncellemeData.bagkur_kendisi !== null) {
          updateData.bagkur_kendisi = topluGuncellemeData.bagkur_kendisi;
        }
        if (topluGuncellemeData.gecici_vergi_kendisi !== null) {
          updateData.gecici_vergi_kendisi = topluGuncellemeData.gecici_vergi_kendisi;
        }
        if (topluGuncellemeData.gelir_vergisi_kendisi !== null) {
          updateData.gelir_vergisi_kendisi = topluGuncellemeData.gelir_vergisi_kendisi;
        }

        // Retry mekanizması ile güncelleme
        let retries = 3;
        let success = false;
        
        while (retries > 0 && !success) {
          try {
            await axios.put(`${API}/musteriler/${musteriId}`, updateData);
            basarili++;
            success = true;
          } catch (err) {
            retries--;
            if (retries === 0) {
              hatali++;
              hatalar.push(`${musteri.ad_soyad}: ${err.response?.data?.detail || err.message}`);
            } else {
              // Kısa bekleme ve tekrar dene
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }
      }

      // Sonuçları göster
      if (basarili > 0 && hatali === 0) {
        toast.success(`${basarili} müşteri güncellendi`);
      } else if (basarili > 0 && hatali > 0) {
        toast.warning(`${basarili} müşteri güncellendi, ${hatali} müşteri güncellenemedi`);
        console.error('Güncelleme hataları:', hatalar);
      } else {
        toast.error(`Güncelleme başarısız: ${hatalar.join(', ')}`);
      }
      
      if (basarili > 0) {
        setTopluGuncellemeDialogOpen(false);
        setSelectedMusteriler([]);
        setTopluGuncellemeData({
          aktif: null,
          musteri_turu: '',
          stopaj_periyot: '',
          favori: null,
          kdv_kendisi: null,
          stopaj_kendisi: null,
          ssk_kendisi: null,
          bagkur_kendisi: null,
          gecici_vergi_kendisi: null,
          gelir_vergisi_kendisi: null,
          gorunur_aylar: null
        });
        fetchMusteriler();
      }
    } catch (error) {
      console.error('Toplu güncelleme hatası:', error);
      toast.error('Toplu güncelleme başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Sütun filtresi değiştir
  const handleColumnFilter = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  // Filtreleri temizle
  const handleClearFilters = () => {
    setColumnFilters({
      durum: '',
      musteri_turu: '',
      stopaj_periyot: ''
    });
    setSortColumn(null);
    setSortDirection('asc');
  };
  
  // Kayıt Filtrelerini Temizle
  const handleClearKayitFilters = () => {
    setKayitFilters({
      bakiye_min: '',
      bakiye_max: '',
      muhasebe_min: '',
      muhasebe_max: '',
      kdv_min: '',
      kdv_max: '',
      stopaj_min: '',
      stopaj_max: '',
      toplam_min: '',
      toplam_max: '',
      kalan_min: '',
      kalan_max: '',
      odeme_durumu: '',
    });
  };
  
  // Hızlı Filtre Uygula
  const applyQuickFilter = (filterType) => {
    handleClearKayitFilters();
    const now = new Date();
    const currentMonth = now.toLocaleDateString('tr-TR', { month: 'long' });
    
    switch(filterType) {
      case 'odenmemis':
        setKayitFilters(prev => ({ ...prev, odeme_durumu: 'odenmedi' }));
        break;
      case 'bu_ay_odenmemis':
        setKayitFilters(prev => ({ ...prev, odeme_durumu: 'odenmedi' }));
        setSelectedAylar([currentMonth]);
        break;
      case 'buyuk_borc':
        setKayitFilters(prev => ({ ...prev, kalan_min: '10000' }));
        break;
      case 'kucuk_borc':
        setKayitFilters(prev => ({ ...prev, kalan_min: '0.01', kalan_max: '1000' }));
        break;
      case 'yuksek_kdv':
        setKayitFilters(prev => ({ ...prev, kdv_min: '5000' }));
        break;
      default:
        break;
    }
    toast.success('Hızlı filtre uygulandı');
  };
  
  // Filtre Kaydet
  const handleSaveFilter = () => {
    if (!filterName.trim()) {
      toast.error('Lütfen filtre adı girin');
      return;
    }
    
    const newFilter = {
      id: Date.now(),
      name: filterName,
      filters: { ...kayitFilters },
      aylar: [...selectedAylar],
      yil: selectedYil,
      createdAt: new Date().toISOString()
    };
    
    const updated = [...savedFilters, newFilter];
    setSavedFilters(updated);
    localStorage.setItem('savedKayitFilters', JSON.stringify(updated));
    setFilterName('');
    toast.success(`"${filterName}" filtresi kaydedildi`);
  };
  
  // Kayıtlı Filtreyi Yükle
  const handleLoadFilter = (filter) => {
    setKayitFilters(filter.filters);
    setSelectedAylar(filter.aylar);
    setSelectedYil(filter.yil);
    toast.success(`"${filter.name}" filtresi yüklendi`);
  };
  
  // Kayıtlı Filtreyi Sil
  const handleDeleteFilter = (filterId) => {
    const updated = savedFilters.filter(f => f.id !== filterId);
    setSavedFilters(updated);
    localStorage.setItem('savedKayitFilters', JSON.stringify(updated));
    toast.success('Filtre silindi');
  };

  // Müşteri Listesi Excel'e Aktar
  const handleMusteriListesiExport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/musteriler/excel-export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `musteri_listesi_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Müşteri listesi Excel\'e aktarıldı');
    } catch (error) {
      toast.error('Excel aktarımı başarısız');
    } finally {
      setLoading(false);
    }
  };

  // Müşteri Excel Şablon İndir
  const handleMusteriSablonIndir = async () => {
    try {
      const response = await axios.get(`${API}/musteriler/excel-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'musteriler_sablon.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Müşteri şablonu indirildi');
    } catch (error) {
      toast.error('Şablon indirilemedi');
    }
  };

  // Müşteri Excel Import
  const handleMusteriExcelImport = async (e) => {
    e.preventDefault();
    if (!musteriExcelFile) {
      toast.error('Lütfen bir Excel dosyası seçin');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', musteriExcelFile);
      
      const response = await axios.post(`${API}/musteriler/excel-import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${response.data.eklenen} müşteri başarıyla eklendi`);
      setMusteriExcelDialogOpen(false);
      setMusteriExcelFile(null);
      fetchMusteriler();
    } catch (error) {
      toast.error('Müşteri yükleme başarısız');
    } finally {
      setLoading(false);
    }
  };

  // Toplu müşteri silme
  const handleTopluMusteriSil = async () => {
    if (selectedMusteriler.length === 0) {
      toast.error('Lütfen silmek için müşteri seçin');
      return;
    }

    if (!window.confirm(`${selectedMusteriler.length} müşteri ve ilgili tüm vergi kayıtları silinecek. Emin misiniz?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/musteriler/toplu-sil`, selectedMusteriler);
      const { silinen_musteri, silinen_kayit } = response.data;
      
      toast.success(`${silinen_musteri} müşteri ve ${silinen_kayit} kayıt silindi`);
      setSelectedMusteriler([]);
      fetchMusteriler();
      fetchKayitlar();
    } catch (error) {
      toast.error('Toplu silme başarısız');
    } finally {
      setLoading(false);
    }
  };

  // Vergi kayıtları için seçim fonksiyonları
  const handleSelectAllKayitlar = (checked) => {
    if (checked) {
      setSelectedKayitlar(kayitlar.map(k => k.id));
    } else {
      setSelectedKayitlar([]);
    }
  };

  const handleSelectKayit = (kayitId, checked) => {
    if (checked) {
      setSelectedKayitlar([...selectedKayitlar, kayitId]);
    } else {
      setSelectedKayitlar(selectedKayitlar.filter(id => id !== kayitId));
    }
  };

  // Sonraki Aya Aktar
  const handleSonrakiAyaAktar = async () => {
    // Seçili ay(lar)ı kontrol et
    if (selectedAylar.length === 0) {
      toast.error('Lütfen aktarılacak kaynak ayı seçin');
      return;
    }
    
    if (selectedAylar.length > 1) {
      toast.error('Lütfen sadece bir ay seçin');
      return;
    }
    
    const kaynakAy = selectedAylar[0];
    const kaynakYil = selectedYil;
    
    // Sonraki ayı hesapla
    const ayIndex = AYLAR.indexOf(kaynakAy);
    let hedefAy, hedefYil;
    
    if (ayIndex === 11) { // Aralık
      hedefAy = 'Ocak';
      hedefYil = kaynakYil + 1;
    } else {
      hedefAy = AYLAR[ayIndex + 1];
      hedefYil = kaynakYil;
    }
    
    // Seçili müşterileri belirle
    let seciliVergiNolar = [];
    if (selectedKayitlar.length > 0) {
      // Seçili kayıtlardan vergi_no'ları çıkar
      seciliVergiNolar = [...new Set(
        kayitlar
          .filter(k => selectedKayitlar.includes(k.id))
          .map(k => k.vergi_no)
      )];
    }
    
    const musteriSayisi = seciliVergiNolar.length > 0 ? seciliVergiNolar.length : 'TÜM';
    
    const onay = window.confirm(
      `${kaynakAy} ${kaynakYil} → ${hedefAy} ${hedefYil}\n\n` +
      `Müşteri: ${musteriSayisi === 'TÜM' ? 'Tüm müşteriler' : `${musteriSayisi} seçili müşteri`}\n\n` +
      `• Kalan tutarları → Bakiye olarak\n` +
      `• Muhasebe ücretleri → Muhasebe olarak\n` +
      `• SSK ve Bağkur tutarları → SSK/Bağkur olarak\n\n` +
      `aktarılacak. Onaylıyor musunuz?`
    );
    
    if (!onay) return;
    
    setLoading(true);
    try {
      const params = {
        kaynak_ay: kaynakAy,
        kaynak_yil: kaynakYil,
        hedef_ay: hedefAy,
        hedef_yil: hedefYil
      };
      
      // Seçili müşteriler varsa vergi_nolar parametresi ekle
      const response = await axios.post(`${API}/vergi-kayit/sonraki-aya-aktar`, 
        seciliVergiNolar.length > 0 ? seciliVergiNolar : null, 
        { params }
      );
      
      const { olusturulan, guncellenen, ssk_bagkur_aktarilan, toplam_islem } = response.data;
      
      let mesaj = `Aktarım tamamlandı!\n${olusturulan} yeni kayıt oluşturuldu\n${guncellenen} kayıt güncellendi`;
      if (ssk_bagkur_aktarilan > 0) {
        mesaj += `\n\n⚠️ ${ssk_bagkur_aktarilan} kayıtta SSK/Bağkur verisi aktarıldı`;
      }
      
      toast.success(mesaj);
      
      // Hedef aya geç ve verileri yenile
      setSelectedAylar([hedefAy]);
      if (hedefYil !== selectedYil) {
        setSelectedYil(hedefYil);
      }
      fetchKayitlar();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aktarım başarısız');
    } finally {
      setLoading(false);
    }
  };

  // Toplu kayıt silme
  const handleTopluKayitSil = async () => {
    if (selectedKayitlar.length === 0) {
      toast.error('Lütfen silmek için kayıt seçin');
      return;
    }

    if (!window.confirm(`${selectedKayitlar.length} vergi kaydı silinecek. Emin misiniz?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/vergi-kayit/toplu-sil`, selectedKayitlar);
      const { silinen } = response.data;
      
      toast.success(`${silinen} kayıt silindi`);
      setSelectedKayitlar([]);
      fetchKayitlar();
    } catch (error) {
      toast.error('Toplu silme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleExcelImport = async (e) => {
    e.preventDefault();
    if (!excelFile) {
      toast.error('Lütfen bir Excel dosyası seçin');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      
      const response = await axios.post(`${API}/vergi-kayit/excel-import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Excel import tamamlandı! ${response.data.basarili} başarılı, ${response.data.hatali} hatalı`);
      setExcelFile(null);
      setExcelDialogOpen(false);
      fetchKayitlar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Excel import başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleSablonIndir = async () => {
    try {
      const response = await axios.get(`${API}/excel-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vergi_kayitlari_sablon.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Şablon indirildi');
    } catch (error) {
      toast.error('Şablon indirilemedi');
    }
  };
  
  
  const handleTopluVeriGirisiAc = () => {
    // Tüm müşteriler için boş kayıt formu oluştur
    const kayitlar = musteriler.map(musteri => ({
      musteri_id: musteri.id,
      vergi_no: musteri.vergi_no,
      ad_soyad: musteri.ad_soyad,
      bakiye: 0,
      muhasebe: 0,
      kdv: 0,
      stopaj: 0,
      ssk: 0,
      bagkur: 0,
      gecici_vergi: 0,
      gelir_vergisi: 0,
      diger1: 0,
      diger2: 0,
      diger3: 0,
      odenen: 0,
      notlar: ''
    }));
    setTopluVeriKayitlar(kayitlar);
    setTopluVeriAy('');
    setTopluVeriYil(new Date().getFullYear());
    setTopluVeriDialogOpen(true);
  };
  
  const handleTopluVeriAyDegisti = async (ay) => {
    setTopluVeriAy(ay);
    
    if (!ay) return;
    
    // O ay için mevcut kayıtları çek
    try {
      const response = await axios.get(`${API}/vergi-kayit`, {
        params: { ay, yil: topluVeriYil }
      });
      
      const mevcutKayitlar = response.data;
      
      // Müşteriler ile eşleştir
      const guncellenmisKayitlar = await Promise.all(musteriler.map(async (musteri) => {
        const mevcutKayit = mevcutKayitlar.find(k => k.vergi_no === musteri.vergi_no);
        
        if (mevcutKayit) {
          // Mevcut kayıt varsa onun verilerini getir (ID'yi de sakla)
          return {
            kayit_id: mevcutKayit.id, // UPDATE için gerekli
            musteri_id: musteri.id,
            vergi_no: musteri.vergi_no,
            ad_soyad: musteri.ad_soyad,
            bakiye: mevcutKayit.bakiye || 0,
            muhasebe: mevcutKayit.muhasebe || 0,
            kdv: mevcutKayit.kdv || 0,
            stopaj: mevcutKayit.stopaj || 0,
            ssk: mevcutKayit.ssk || 0,
            bagkur: mevcutKayit.bagkur || 0,
            gecici_vergi: mevcutKayit.gecici_vergi || 0,
            gelir_vergisi: mevcutKayit.gelir_vergisi || 0,
            diger1: mevcutKayit.diger1 || 0,
            diger2: mevcutKayit.diger2 || 0,
            diger3: mevcutKayit.diger3 || 0,
            odenen: mevcutKayit.odenen || 0,
            notlar: mevcutKayit.notlar || ''
          };
        } else {
          // Mevcut kayıt yoksa yeni - önceki ayın kalanını bakiyeye ekle
          // Önceki ayın kalanını bul
          let oncekiKalan = 0;
          const aylar = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
          const currentAyIndex = aylar.indexOf(ay);
          
          if (currentAyIndex > 0) {
            const oncekiAy = aylar[currentAyIndex - 1];
            const oncekiKayit = mevcutKayitlar.find(k => k.vergi_no === musteri.vergi_no && k.ay === oncekiAy);
            if (oncekiKayit) {
              oncekiKalan = oncekiKayit.kalan || 0;
            } else {
              // Önceki ay bu yılda yoksa, tüm kayıtlarda ara
              const tumKayitlar = await axios.get(`${API}/vergi-kayit`, {
                params: { vergi_no: musteri.vergi_no, ay: oncekiAy, yil: topluVeriYil }
              });
              if (tumKayitlar.data.length > 0) {
                oncekiKalan = tumKayitlar.data[0].kalan || 0;
              }
            }
          }
          
          return {
            kayit_id: null,
            musteri_id: musteri.id,
            vergi_no: musteri.vergi_no,
            ad_soyad: musteri.ad_soyad,
            bakiye: oncekiKalan, // Önceki ayın kalan tutarı göster
            muhasebe: 0,
            kdv: 0,
            stopaj: 0,
            ssk: 0,
            bagkur: 0,
            gecici_vergi: 0,
            gelir_vergisi: 0,
            diger1: 0,
            diger2: 0,
            diger3: 0,
            odenen: 0,
            notlar: ''
          };
        }
      }));
      
      setTopluVeriKayitlar(guncellenmisKayitlar);
      
      if (mevcutKayitlar.length > 0) {
        toast.info(`${ay} ayı için ${mevcutKayitlar.length} kayıt yüklendi`);
      }
    } catch (error) {
      console.error('Kayıtlar yüklenemedi:', error);
    }
  };
  
  const handleTopluVeriKaydet = async () => {
    if (!topluVeriAy) {
      toast.error('Lütfen ay seçin');
      return;
    }
    
    setLoading(true);
    let basarili = 0;
    let hatali = 0;
    let guncellenen = 0;
    let eklenen = 0;
    
    for (const kayit of topluVeriKayitlar) {
      try {
        if (kayit.kayit_id) {
          // Mevcut kayıt - UPDATE yap
          await axios.put(`${API}/vergi-kayit/${kayit.kayit_id}`, {
            bakiye: kayit.bakiye,
            muhasebe: kayit.muhasebe,
            kdv: kayit.kdv,
            stopaj: kayit.stopaj,
            ssk: kayit.ssk,
            bagkur: kayit.bagkur,
            gecici_vergi: kayit.gecici_vergi,
            gelir_vergisi: kayit.gelir_vergisi,
            diger1: kayit.diger1,
            diger2: kayit.diger2,
            diger3: kayit.diger3,
            odenen: kayit.odenen,
            notlar: kayit.notlar
          });
          guncellenen++;
        } else {
          // Yeni kayıt - INSERT yap
          const payload = {
            vergi_no: kayit.vergi_no,
            ad_soyad: kayit.ad_soyad,
            ay: topluVeriAy,
            yil: topluVeriYil,
            bakiye: kayit.bakiye,
            muhasebe: kayit.muhasebe,
            kdv: kayit.kdv,
            stopaj: kayit.stopaj,
            ssk: kayit.ssk,
            bagkur: kayit.bagkur,
            gecici_vergi: kayit.gecici_vergi,
            gelir_vergisi: kayit.gelir_vergisi,
            diger1: kayit.diger1,
            diger2: kayit.diger2,
            diger3: kayit.diger3,
            odenen: kayit.odenen,
            notlar: kayit.notlar
          };
          await axios.post(`${API}/vergi-kayit`, payload);
          eklenen++;
        }
        basarili++;
      } catch (error) {
        hatali++;
        console.error('Kayıt hatası:', error);
      }
    }
    
    setLoading(false);
    toast.success(`Toplu kayıt tamamlandı! ${eklenen} yeni, ${guncellenen} güncelleme, ${hatali} hatalı`);
    setTopluVeriDialogOpen(false);
    fetchKayitlar();
  };

  // Currency formatter - cache'lenmiş
  const currencyFormatter = React.useMemo(() => {
    return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, []);

  const formatCurrency = useCallback((value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '0,00';
    
    const formatted = currencyFormatter.format(Math.abs(num));
    
    // Negatif değerleri parantez içinde göster
    if (num < 0) {
      return `(${formatted})`;
    }
    
    return formatted;
  }, [currencyFormatter]);

  const handleLoginSuccess = (user) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Auth required
  if (!isAuthenticated) {
    // Şifre sıfırlama sayfası ayrı route
    if (typeof window !== 'undefined' && window.location.pathname === '/reset-password') {
      return <ResetPasswordPage />;
    }
    return <AuthPage onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo ve Başlık */}
            <div className="flex-1 min-w-0 flex items-center gap-3 sm:gap-4">
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate">Vergi Takip</h1>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Hoşgeldin, {currentUser?.ad_soyad}</p>
              </div>
              <AppDosyalari isAdmin={Boolean(currentUser?.is_admin)} />
            </div>
            
            {/* Masaüstü Butonlar */}
            <div className="hidden md:flex gap-2 desktop-header-buttons">
              <Button 
                variant="outline"
                onClick={handleSayfaYenile}
                size="sm"
                title="Verileri Yenile"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Yenile
              </Button>
              <Button 
                variant={lucaConnected ? "default" : "outline"}
                onClick={() => setLucaModalOpen(true)}
                className={lucaConnected ? "bg-green-600 hover:bg-green-700" : ""}
                size="sm"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {lucaConnected ? "Luca ✓" : "Luca"}
              </Button>
              <Dialog open={musteriDialogOpen} onOpenChange={setMusteriDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="add-customer-btn">
                    <Users className="w-4 h-4 mr-2" />
                    Yeni Müşteri
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="customer-dialog">
                  <DialogHeader>
                    <DialogTitle>Yeni Müşteri Ekle</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleMusteriEkle}>
                    <div className="py-4">
                      {/* İlk Satır - Temel Bilgiler (3 Kolon) */}
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div>
                          <Label htmlFor="vergi_no">Vergi Numarası *</Label>
                          <Input
                            id="vergi_no"
                            data-testid="customer-tax-no-input"
                            value={yeniMusteri.vergi_no}
                            onChange={(e) => setYeniMusteri({...yeniMusteri, vergi_no: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="ad_soyad">Ad Soyad *</Label>
                          <Input
                            id="ad_soyad"
                            data-testid="customer-name-input"
                            value={yeniMusteri.ad_soyad}
                            onChange={(e) => setYeniMusteri({...yeniMusteri, ad_soyad: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="telefon">Telefon (WhatsApp)</Label>
                          <Input
                            id="telefon"
                            placeholder="905551234567"
                            value={yeniMusteri.telefon}
                            onChange={(e) => setYeniMusteri({...yeniMusteri, telefon: e.target.value})}
                          />
                        </div>
                      </div>
                      
                      {/* İkinci Satır - Seçimler (3 Kolon) */}
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div>
                          <Label className="font-semibold">Müşteri Türü *</Label>
                          <div className="flex flex-col gap-2 mt-2">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="musteri_turu"
                                checked={yeniMusteri.musteri_turu === 'İŞLETME'}
                                onChange={() => setYeniMusteri({...yeniMusteri, musteri_turu: 'İŞLETME'})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">İŞLETME</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="musteri_turu"
                                checked={yeniMusteri.musteri_turu === 'BİLANÇO'}
                                onChange={() => setYeniMusteri({...yeniMusteri, musteri_turu: 'BİLANÇO'})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">BİLANÇO</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="musteri_turu"
                                checked={yeniMusteri.musteri_turu === 'ŞİRKET'}
                                onChange={() => setYeniMusteri({...yeniMusteri, musteri_turu: 'ŞİRKET'})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">ŞİRKET</span>
                            </label>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="font-semibold">Stopaj & Durum</Label>
                          <div className="flex flex-col gap-2 mt-2">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={yeniMusteri.stopaj_periyot === '3 Aylık'}
                                onChange={(e) => setYeniMusteri({...yeniMusteri, stopaj_periyot: e.target.checked ? '3 Aylık' : 'Aylık'})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Stopaj 3 Aylık</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={yeniMusteri.aktif !== false}
                                onChange={(e) => setYeniMusteri({...yeniMusteri, aktif: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Aktif</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={yeniMusteri.favori || false}
                                onChange={(e) => setYeniMusteri({...yeniMusteri, favori: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Favori ⭐</span>
                            </label>
                          </div>
                        </div>
                        <div>
                          <Label className="font-semibold">Kendi Vergisini Ödeyen</Label>
                          <label className="flex items-center space-x-2 cursor-pointer mt-2 mb-2 p-1 bg-blue-50 rounded">
                            <input
                              type="checkbox"
                              checked={yeniMusteri.kdv_kendisi && yeniMusteri.stopaj_kendisi && yeniMusteri.ssk_kendisi && yeniMusteri.bagkur_kendisi && yeniMusteri.gecici_vergi_kendisi && yeniMusteri.gelir_vergisi_kendisi}
                              onChange={(e) => {
                                const all = e.target.checked;
                                setYeniMusteri({...yeniMusteri, kdv_kendisi: all, stopaj_kendisi: all, ssk_kendisi: all, bagkur_kendisi: all, gecici_vergi_kendisi: all, gelir_vergisi_kendisi: all});
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-bold text-blue-700">Tümü</span>
                          </label>
                          <div className="grid grid-cols-2 gap-1 text-xs">
                            <label className="flex items-center space-x-1"><input type="checkbox" checked={yeniMusteri.kdv_kendisi || false} onChange={(e) => setYeniMusteri({...yeniMusteri, kdv_kendisi: e.target.checked})} className="w-3 h-3"/><span>KDV</span></label>
                            <label className="flex items-center space-x-1"><input type="checkbox" checked={yeniMusteri.stopaj_kendisi || false} onChange={(e) => setYeniMusteri({...yeniMusteri, stopaj_kendisi: e.target.checked})} className="w-3 h-3"/><span>Stopaj</span></label>
                            <label className="flex items-center space-x-1"><input type="checkbox" checked={yeniMusteri.ssk_kendisi || false} onChange={(e) => setYeniMusteri({...yeniMusteri, ssk_kendisi: e.target.checked})} className="w-3 h-3"/><span>SSK</span></label>
                            <label className="flex items-center space-x-1"><input type="checkbox" checked={yeniMusteri.bagkur_kendisi || false} onChange={(e) => setYeniMusteri({...yeniMusteri, bagkur_kendisi: e.target.checked})} className="w-3 h-3"/><span>Bağkur</span></label>
                            <label className="flex items-center space-x-1"><input type="checkbox" checked={yeniMusteri.gecici_vergi_kendisi || false} onChange={(e) => setYeniMusteri({...yeniMusteri, gecici_vergi_kendisi: e.target.checked})} className="w-3 h-3"/><span>Geçici V</span></label>
                            <label className="flex items-center space-x-1"><input type="checkbox" checked={yeniMusteri.gelir_vergisi_kendisi || false} onChange={(e) => setYeniMusteri({...yeniMusteri, gelir_vergisi_kendisi: e.target.checked})} className="w-3 h-3"/><span>Gelir V</span></label>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-base font-semibold">Görünecek Vergi Kayıtları</Label>
                        <p className="text-sm text-muted-foreground mb-3">Bu müşteri için hangi vergi türlerinin görüneceğini seçin</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="bakiye_gorunur"
                              checked={yeniMusteri.bakiye_gorunur}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, bakiye_gorunur: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="bakiye_gorunur" className="cursor-pointer">Bakiye</Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="muhasebe_gorunur"
                              checked={yeniMusteri.muhasebe_gorunur}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, muhasebe_gorunur: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="muhasebe_gorunur" className="cursor-pointer">Muhasebe</Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="kdv_gorunur"
                              checked={yeniMusteri.kdv_gorunur}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, kdv_gorunur: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="kdv_gorunur" className="cursor-pointer">KDV</Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="stopaj_gorunur"
                              checked={yeniMusteri.stopaj_gorunur}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, stopaj_gorunur: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="stopaj_gorunur" className="cursor-pointer">Stopaj</Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="ssk_gorunur"
                              checked={yeniMusteri.ssk_gorunur}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, ssk_gorunur: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="ssk_gorunur" className="cursor-pointer">SSK</Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="bagkur_gorunur"
                              checked={yeniMusteri.bagkur_gorunur}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, bagkur_gorunur: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="bagkur_gorunur" className="cursor-pointer">BAĞKUR</Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="gecici_vergi_gorunur"
                              checked={yeniMusteri.gecici_vergi_gorunur}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, gecici_vergi_gorunur: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="gecici_vergi_gorunur" className="cursor-pointer">Geçici Vergi</Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="gelir_vergisi_gorunur"
                              checked={yeniMusteri.gelir_vergisi_gorunur}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, gelir_vergisi_gorunur: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="gelir_vergisi_gorunur" className="cursor-pointer">Gelir Vergisi</Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="diger1_gorunur"
                              checked={yeniMusteri.diger1_gorunur}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, diger1_gorunur: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="diger1_gorunur" className="cursor-pointer">Diğer 1</Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="diger2_gorunur"
                              checked={yeniMusteri.diger2_gorunur}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, diger2_gorunur: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="diger2_gorunur" className="cursor-pointer">Diğer 2</Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="diger3_gorunur"
                              checked={yeniMusteri.diger3_gorunur}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, diger3_gorunur: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="diger3_gorunur" className="cursor-pointer">Diğer 3</Label>
                          </div>
                        </div>
                      </div>
                      
                      {/* GİB Bilgileri */}
                      <div className="pt-4 border-t">
                        <h4 className="font-semibold mb-3">GİB Bilgileri</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                          <div>
                            <Label htmlFor="gib_giris_kodu">GİB Giriş Kodu</Label>
                            <Input
                              id="gib_giris_kodu"
                              value={yeniMusteri.gib_giris_kodu}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, gib_giris_kodu: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label htmlFor="gib_parola">GİB Parola</Label>
                            <Input
                              id="gib_parola"
                              value={yeniMusteri.gib_parola}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, gib_parola: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label htmlFor="gib_sifre">GİB Şifre</Label>
                            <Input
                              id="gib_sifre"
                              value={yeniMusteri.gib_sifre}
                              onChange={(e) => setYeniMusteri({...yeniMusteri, gib_sifre: e.target.value})}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Ortak Bilgileri */}
                      <div className="pt-4 border-t">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-semibold">Ortak Bilgileri</h4>
                            <p className="text-xs text-gray-500">Geçici Vergi ve Gelir Vergisi ortakların TC'sine çıkıyorsa</p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                              const ortaklar = yeniMusteri.ortaklar || [];
                              setYeniMusteri({
                                ...yeniMusteri,
                                ortaklar: [...ortaklar, { tc_no: '', ad_soyad: '' }]
                              });
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Ortak Ekle
                          </Button>
                        </div>
                        
                        {yeniMusteri.ortaklar && yeniMusteri.ortaklar.length > 0 && (
                          <div className="space-y-2">
                            {yeniMusteri.ortaklar.map((ortak, index) => (
                              <div key={index} className="grid grid-cols-12 gap-2 items-end p-2 border rounded">
                                <div className="col-span-5">
                                  <Label className="text-xs">TC Kimlik No</Label>
                                  <Input
                                    placeholder="12345678901"
                                    maxLength={11}
                                    value={ortak.tc_no || ''}
                                    onChange={(e) => {
                                      const newOrtaklar = [...yeniMusteri.ortaklar];
                                      newOrtaklar[index].tc_no = e.target.value;
                                      setYeniMusteri({...yeniMusteri, ortaklar: newOrtaklar});
                                    }}
                                  />
                                </div>
                                <div className="col-span-6">
                                  <Label className="text-xs">Ad Soyad</Label>
                                  <Input
                                    placeholder="Ahmet Yılmaz"
                                    value={ortak.ad_soyad || ''}
                                    onChange={(e) => {
                                      const newOrtaklar = [...yeniMusteri.ortaklar];
                                      newOrtaklar[index].ad_soyad = e.target.value;
                                      setYeniMusteri({...yeniMusteri, ortaklar: newOrtaklar});
                                    }}
                                  />
                                </div>
                                <div className="col-span-1">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => {
                                      const newOrtaklar = yeniMusteri.ortaklar.filter((_, i) => i !== index);
                                      setYeniMusteri({...yeniMusteri, ortaklar: newOrtaklar});
                                    }}
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={loading} data-testid="save-customer-btn">
                        Kaydet
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              
              {/* Admin Panel Butonu - Sadece admin kullanıcılar için */}
              {currentUser?.is_admin && (
                <Button 
                  variant="outline"
                  onClick={() => setActiveTab('admin')}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 border-0 shadow-md"
                >
                  🔐 Admin Panel
                </Button>
              )}
              
              {/* Güvenlik / 2FA Ayarları */}
              <Button
                variant="outline"
                onClick={() => setSecurityDialogOpen(true)}
                className="bg-gradient-to-r from-slate-600 to-slate-700 text-white hover:from-slate-700 hover:to-slate-800 border-0 shadow-md"
                size="sm"
                data-testid="security-settings-btn"
                title="Güvenlik Ayarları"
              >
                🛡️ Güvenlik
              </Button>
              
              {/* Çıkış Butonu - Masaüstü */}
              <Button 
                variant="outline" 
                onClick={() => {
                  if (window.confirm('Çıkış yapmak istediğinizden emin misiniz?')) {
                    handleLogout();
                  }
                }}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                size="sm"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Çıkış
              </Button>
            </div>
            
            {/* Mobil Hamburger Menü Butonu */}
            <div className="flex md:hidden items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="mobile-menu-btn p-2"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </div>
          </div>
          
          {/* Mobil Menü (Açılır) */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pt-4 border-t space-y-2 animate-in slide-in-from-top duration-200">
              <Button 
                variant="outline"
                onClick={() => { handleSayfaYenile(); setMobileMenuOpen(false); }}
                className="w-full justify-start"
                size="sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Sayfayı Yenile
              </Button>
              
              <Button 
                variant={lucaConnected ? "default" : "outline"}
                onClick={() => { setLucaModalOpen(true); setMobileMenuOpen(false); }}
                className={`w-full justify-start ${lucaConnected ? "bg-green-600 hover:bg-green-700" : ""}`}
                size="sm"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {lucaConnected ? "Luca Bağlı ✓" : "Luca Entegrasyonu"}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => { setMusteriDialogOpen(true); setMobileMenuOpen(false); }}
                className="w-full justify-start"
                size="sm"
              >
                <Users className="w-4 h-4 mr-2" />
                Yeni Müşteri
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => {
                  setMobileMenuOpen(false);
                  if (window.confirm('Çıkış yapmak istediğinizden emin misiniz?')) {
                    handleLogout();
                  }
                }}
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                size="sm"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Çıkış Yap
              </Button>
            </div>
          )}
        </div>
      </header>
      <GuncellemeBanner />
              
              {/* Luca Entegrasyonu Modal */}
              <Dialog open={lucaModalOpen} onOpenChange={setLucaModalOpen}>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <ExternalLink className="w-5 h-5" />
                      Luca Muhasebe Entegrasyonu
                    </DialogTitle>
                  </DialogHeader>
                  
                  {!lucaConnected ? (
                    <div className="space-y-4 py-4">
                      <Alert>
                        <AlertDescription>
                          Luca muhasebe programına bağlanmak için kullanıcı adı ve şifrenizi girin.
                        </AlertDescription>
                      </Alert>
                      
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="luca-member-no">Üye Numarası</Label>
                          <Input
                            id="luca-member-no"
                            type="text"
                            value={lucaMemberNo}
                            onChange={(e) => setLucaMemberNo(e.target.value)}
                            placeholder="Üye numaranızı girin"
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="luca-username">Kullanıcı Adı</Label>
                          <Input
                            id="luca-username"
                            type="text"
                            value={lucaUsername}
                            onChange={(e) => setLucaUsername(e.target.value)}
                            placeholder="Kullanıcı adınızı girin"
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="luca-password">Parola</Label>
                          <Input
                            id="luca-password"
                            type="password"
                            value={lucaPassword}
                            onChange={(e) => setLucaPassword(e.target.value)}
                            placeholder="Parolanızı girin"
                            className="mt-1"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleLucaLogin();
                              }
                            }}
                          />
                        </div>
                      </div>
                      
                      <DialogFooter>
                        <Button
                          onClick={handleLucaLogin}
                          disabled={lucaLoading || !lucaMemberNo || !lucaUsername || !lucaPassword}
                          className="w-full"
                        >
                          {lucaLoading ? 'Bağlanıyor...' : 'Luca\'ya Bağlan'}
                        </Button>
                      </DialogFooter>
                    </div>
                  ) : (
                    <div className="space-y-4 py-4">
                      <Alert className="bg-green-50 border-green-200">
                        <AlertDescription className="text-green-800">
                          ✅ Luca muhasebe programına başarıyla bağlandınız!
                        </AlertDescription>
                      </Alert>
                      
                      <div className="space-y-2">
                        <p className="text-sm text-slate-600">
                          Artık vergi ödemelerini otomatik olarak Luca'ya aktarabilirsiniz.
                        </p>
                        
                        <Separator className="my-4" />
                        
                        <p className="text-sm font-semibold text-slate-700">Sonraki Adımlar:</p>
                        <ol className="text-sm text-slate-600 list-decimal list-inside space-y-1">
                          <li>Kayıtlar sekmesine gidin</li>
                          <li>Müşteri seçin veya tüm müşterileri işaretleyin</li>
                          <li>"Luca'ya Aktar" butonuna tıklayın</li>
                        </ol>
                      </div>
                      
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setLucaConnected(false);
                            setLucaMemberNo('');
                            setLucaUsername('');
                            setLucaPassword('');
                            setLucaModalOpen(false);
                          }}
                          className="w-full"
                        >
                          Bağlantıyı Kes
                        </Button>
                      </DialogFooter>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              
              {/* Luca Transfer Modal */}
              <Dialog open={lucaTransferModalOpen} onOpenChange={setLucaTransferModalOpen}>
                <DialogContent className="sm:max-w-[600px]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <ExternalLink className="w-5 h-5 text-green-600" />
                      Luca'ya Vergi Ödemesi Aktar
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <Alert>
                      <AlertDescription>
                        Seçilen müşterilerin <strong>{selectedAylar.join(', ')}</strong> ayı vergi ödemelerini Luca'ya aktarın.
                      </AlertDescription>
                    </Alert>
                    
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-semibold">Müşteri Seçimi</Label>
                        <div className="mt-2 max-h-[200px] overflow-y-auto border rounded p-3 space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedMusterilerForLuca.length === kayitlar.length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMusterilerForLuca(kayitlar.map(k => k.vergi_no));
                                } else {
                                  setSelectedMusterilerForLuca([]);
                                }
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-semibold text-blue-600">Tüm Müşteriler</span>
                          </label>
                          {Array.from(new Set(kayitlar.map(k => k.vergi_no))).map((vergi_no) => {
                            const kayit = kayitlar.find(k => k.vergi_no === vergi_no);
                            return (
                              <label key={vergi_no} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedMusterilerForLuca.includes(vergi_no)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedMusterilerForLuca([...selectedMusterilerForLuca, vergi_no]);
                                    } else {
                                      setSelectedMusterilerForLuca(selectedMusterilerForLuca.filter(v => v !== vergi_no));
                                    }
                                  }}
                                  className="w-4 h-4"
                                />
                                <span className="text-sm">{kayit.ad_soyad}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-sm font-semibold">Vergi Türü Seçimi</Label>
                        <div className="mt-2 space-y-2">
                          {['KDV', 'Stopaj', 'SSK', 'Bağkur'].map((tur) => (
                            <label key={tur} className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" className="w-4 h-4" defaultChecked={tur === 'KDV'} />
                              <span className="text-sm">{tur}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <Separator />
                      
                      <div className="bg-blue-50 p-3 rounded">
                        <p className="text-sm text-blue-800">
                          <strong>📌 Not:</strong> CAPTCHA girişi için Luca sayfası açılacak. Kalan işlemler otomatik yapılacaktır.
                        </p>
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setLucaTransferModalOpen(false)}
                      >
                        İptal
                      </Button>
                      <Button
                        onClick={handleLucaTransfer}
                        disabled={selectedMusterilerForLuca.length === 0 || lucaLoading}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {lucaLoading ? 'Gönderiliyor...' : 'Luca\'ya Gönder'}
                      </Button>
                    </DialogFooter>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* CAPTCHA Modal */}
              <Dialog open={lucaCaptchaModalOpen} onOpenChange={setLucaCaptchaModalOpen}>
                <DialogContent className="sm:max-w-[400px]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      🔐 CAPTCHA Doğrulama
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <Alert>
                      <AlertDescription>
                        Luca güvenlik kontrolü için lütfen aşağıdaki CAPTCHA kodunu girin.
                      </AlertDescription>
                    </Alert>
                    
                    {lucaCaptchaImage && (
                      <div className="flex justify-center p-4 bg-gray-100 rounded">
                        <img 
                          src={`data:image/png;base64,${lucaCaptchaImage}`} 
                          alt="CAPTCHA" 
                          className="border-2 border-gray-300"
                        />
                      </div>
                    )}
                    
                    <div>
                      <Label htmlFor="captcha-input">CAPTCHA Kodu</Label>
                      <Input
                        id="captcha-input"
                        type="text"
                        value={lucaCaptchaInput}
                        onChange={(e) => setLucaCaptchaInput(e.target.value)}
                        placeholder="Kodu girin"
                        className="mt-1 text-center text-lg font-mono"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleCaptchaSubmit();
                          }
                        }}
                      />
                    </div>
                    
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setLucaCaptchaModalOpen(false);
                          setLucaCaptchaInput('');
                          setLucaLoading(false);
                        }}
                      >
                        İptal
                      </Button>
                      <Button
                        onClick={handleCaptchaSubmit}
                        disabled={!lucaCaptchaInput || lucaLoading}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {lucaLoading ? 'Doğrulanıyor...' : 'Doğrula'}
                      </Button>
                    </DialogFooter>
                  </div>
                </DialogContent>
              </Dialog>

      <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          {/* Mobil için yatay scroll yapılabilir tabs */}
          <div className="mobile-tabs-scroll -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="flex sm:grid sm:grid-cols-7 gap-1 sm:gap-1.5 bg-gradient-to-r from-slate-100 to-slate-200 p-2.5 sm:p-3 rounded-lg sm:rounded-xl shadow-lg min-w-max sm:min-w-0 w-max sm:w-full sm:max-w-5xl h-auto min-h-[48px] sm:min-h-[52px]">
              <TabsTrigger 
                value="kayitlar" 
                data-testid="records-tab"
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-md sm:rounded-lg font-semibold text-xs sm:text-sm py-2.5 px-3 sm:py-3 sm:px-4 transition-all duration-200 hover:bg-blue-100 whitespace-nowrap"
              >
                📋 <span className="hidden sm:inline">Kayıtlar</span><span className="sm:hidden">Kayıtlar</span>
              </TabsTrigger>
              <TabsTrigger 
                value="musteriler" 
                data-testid="customers-tab"
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-md sm:rounded-lg font-semibold text-xs sm:text-sm py-2.5 px-3 sm:py-3 sm:px-4 transition-all duration-200 hover:bg-emerald-100 whitespace-nowrap"
              >
                👥 <span className="hidden sm:inline">Müşteriler</span><span className="sm:hidden">Müşteriler</span>
              </TabsTrigger>
              <TabsTrigger 
                value="vergi-yukle"
                className="hidden sm:flex data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-md sm:rounded-lg font-semibold text-xs sm:text-sm py-2.5 px-3 sm:py-3 sm:px-4 transition-all duration-200 hover:bg-purple-100 whitespace-nowrap"
              >
                📊 <span className="hidden sm:inline">Yüklenen Vergiler</span><span className="sm:hidden">Vergiler</span>
              </TabsTrigger>
              <TabsTrigger 
                value="kasa-virman"
                className="hidden sm:flex data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-amber-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-md sm:rounded-lg font-semibold text-xs sm:text-sm py-2.5 px-3 sm:py-3 sm:px-4 transition-all duration-200 hover:bg-amber-100 whitespace-nowrap"
              >
                💰 <span className="hidden sm:inline">Kasa Virman</span><span className="sm:hidden">Kasa</span>
              </TabsTrigger>
              <TabsTrigger 
                value="gib" 
                data-testid="gib-tab"
                className="hidden sm:flex data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-red-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-md sm:rounded-lg font-semibold text-xs sm:text-sm py-2.5 px-3 sm:py-3 sm:px-4 transition-all duration-200 hover:bg-red-100 whitespace-nowrap"
              >
                🏛️ GİB
              </TabsTrigger>
              <TabsTrigger 
                value="sgk" 
                data-testid="sgk-tab"
                className="hidden sm:flex data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-cyan-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-md sm:rounded-lg font-semibold text-xs sm:text-sm py-2.5 px-3 sm:py-3 sm:px-4 transition-all duration-200 hover:bg-cyan-100 whitespace-nowrap"
              >
                🏥 SGK
              </TabsTrigger>
              <TabsTrigger 
                value="ebeyanname" 
                data-testid="ebeyanname-tab"
                className="hidden sm:flex data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-md sm:rounded-lg font-semibold text-xs sm:text-sm py-2.5 px-3 sm:py-3 sm:px-4 transition-all duration-200 hover:bg-indigo-100 whitespace-nowrap"
              >
                📝 <span className="hidden sm:inline">E-Beyanname</span><span className="sm:hidden">E-Bey</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="kayitlar" className="space-y-3">
            {/* Filtreler */}
            <Card>
              <CardContent className="py-2">
                <div className="space-y-1">
                  {/* Başlık, Müşteri, Yıl, Excel İndir - Tek Satırda */}
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-semibold min-w-[60px]">Filtreler:</div>
                    <div className="flex-1">
                      <Select value={selectedMusteri} onValueChange={setSelectedMusteri}>
                        <SelectTrigger data-testid="filter-customer-select" className="h-8 text-xs">
                          <SelectValue placeholder="Tüm Müşteriler" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tüm Müşteriler</SelectItem>
                          {musteriler.map((m) => (
                            <SelectItem key={m.id} value={m.vergi_no}>
                              {m.ad_soyad} ({m.vergi_no})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-[100px]">
                      <Input
                        type="number"
                        data-testid="filter-year-input"
                        value={selectedYil}
                        onChange={(e) => setSelectedYil(parseInt(e.target.value))}
                        className="h-8 text-xs"
                      />
                    </div>
                    <Button onClick={handleExcelExport} variant="outline" className="hidden sm:flex h-8 text-xs px-3" data-testid="export-excel-btn">
                      <Download className="w-4 h-4 mr-1" />
                      Excel İndir
                    </Button>
                    {lucaConnected && (
                      <Button 
                        onClick={() => setLucaTransferModalOpen(true)} 
                        variant="default" 
                        className="h-8 text-xs px-3 bg-green-600 hover:bg-green-700"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Luca'ya Aktar
                      </Button>
                    )}
                  </div>
                  
                  {/* Ay Seçimi - 4 Sütunlu Grid */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs">Aylar</Label>
                      {/* Tüm Aylar Checkbox */}
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedAylar.length === AYLAR.length}
                          onChange={(e) => handleTumAylar(e.target.checked)}
                          className="w-3 h-3"
                        />
                        <span className="text-xs font-semibold text-blue-600">Tüm Aylar</span>
                      </label>
                    </div>
                    
                    {/* 4 Sütunlu Grid - Her sütunda 3 ay */}
                    <div className="grid grid-cols-4 gap-2">
                      {/* Sütun 1: Ocak, Şubat, Mart */}
                      <div className="space-y-1">
                        {['Ocak', 'Şubat', 'Mart'].map((ay) => (
                          <label 
                            key={ay} 
                            className="flex items-center gap-1 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedAylar.includes(ay)}
                              onChange={() => handleAyToggle(ay)}
                              className="w-3 h-3"
                            />
                            <span className="text-xs">{ay}</span>
                          </label>
                        ))}
                      </div>
                      
                      {/* Sütun 2: Nisan, Mayıs, Haziran */}
                      <div className="space-y-1">
                        {['Nisan', 'Mayıs', 'Haziran'].map((ay) => (
                          <label 
                            key={ay} 
                            className="flex items-center gap-1 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedAylar.includes(ay)}
                              onChange={() => handleAyToggle(ay)}
                              className="w-3 h-3"
                            />
                            <span className="text-xs">{ay}</span>
                          </label>
                        ))}
                      </div>
                      
                      {/* Sütun 3: Temmuz, Ağustos, Eylül */}
                      <div className="space-y-2">
                        {['Temmuz', 'Ağustos', 'Eylül'].map((ay) => (
                          <label 
                            key={ay} 
                            className="flex items-center gap-1 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedAylar.includes(ay)}
                              onChange={() => handleAyToggle(ay)}
                              className="w-3 h-3"
                            />
                            <span className="text-xs">{ay}</span>
                          </label>
                        ))}
                      </div>
                      
                      {/* Sütun 4: Ekim, Kasım, Aralık */}
                      <div className="space-y-2">
                        {['Ekim', 'Kasım', 'Aralık'].map((ay) => (
                          <label 
                            key={ay} 
                            className="flex items-center gap-1 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedAylar.includes(ay)}
                              onChange={() => handleAyToggle(ay)}
                              className="w-3 h-3"
                            />
                            <span className="text-xs">{ay}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Kayıt Ekleme Butonları ve Ödeme Durumu Filtresi */}
            <div className="hidden sm:flex gap-4 flex-wrap items-center">
              <Dialog open={zipDialogOpen} onOpenChange={setZipDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-bulk-zip-btn" className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg">
                    <PackageOpen className="w-4 h-4 mr-2" />
                    Toplu PDF Yükleme (ZIP)
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="bulk-zip-dialog">
                  <DialogHeader>
                    <DialogTitle>Toplu PDF Yükleme - ZIP</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <Alert className="bg-green-50 border-green-200">
                      <AlertDescription>
                        <strong>🎯 GİB Tahakkuk ZIP Yükleme - Akıllı Sistem</strong><br />
                        <strong>✅ Otomatik Tespit:</strong><br />
                        • Vergi Kimlik No / TC No (PDF içeriğinden)<br />
                        • Ana Vergi Kodu (PDF içeriğinden)<br />
                        • Dönem (PDF içeriğinden - Ay/Yıl otomatik)<br />
                        • Tahakkuk Tutarı<br />
                        • Ortak Sistemi (Geçici/Gelir Vergisi için)
                      </AlertDescription>
                    </Alert>
                    
                    <Alert className="bg-blue-50 border-blue-200">
                      <AlertDescription className="text-sm">
                        <strong>🚀 Arka Plan İşleme:</strong> Büyük ZIP dosyaları arka planda işlenir. Sayfa kapatılsa bile işlem devam eder.
                      </AlertDescription>
                    </Alert>
                    
                    <div>
                      <Label>GİB Tahakkuk ZIP/RAR Dosyası</Label>
                      <Input
                        type="file"
                        accept=".zip,.rar"
                        data-testid="zip-file-input"
                        onChange={(e) => setZipFile(e.target.files[0])}
                        required
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        GİB'den indirilen tahakkuk PDF'lerini içeren ZIP veya RAR dosyasını yükleyin
                      </p>
                    </div>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => setZipDialogOpen(false)}
                    >
                      İptal
                    </Button>
                    <Button 
                      type="button"
                      disabled={!zipFile || loading}
                      onClick={() => handleAsyncZipUpload(zipFile)}
                      className="bg-gradient-to-r from-purple-600 to-purple-700"
                      data-testid="upload-zip-btn"
                    >
                      {loading ? 'Yükleniyor...' : '🚀 Arka Planda Yükle'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              {/* Async Upload Progress Dialog */}
              <Dialog open={asyncUploadDialogOpen} onOpenChange={setAsyncUploadDialogOpen}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                  <DialogHeader>
                    <DialogTitle>
                      {asyncUploadProgress?.status === 'completed' ? '✅ İşlem Tamamlandı' :
                       asyncUploadProgress?.status === 'failed' ? '❌ İşlem Başarısız' :
                       '📦 ZIP Dosyası İşleniyor...'}
                    </DialogTitle>
                    {asyncUploadProgress?.filename && (
                      <p className="text-sm text-gray-500 mt-1">
                        📁 <span className="font-medium">{asyncUploadProgress.filename}</span>
                      </p>
                    )}
                  </DialogHeader>
                  <div className="space-y-4 py-4 flex-1 overflow-hidden flex flex-col">
                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>İlerleme</span>
                        <span className="font-bold">{asyncUploadProgress?.progress || 0}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            asyncUploadProgress?.status === 'completed' ? 'bg-green-500' :
                            asyncUploadProgress?.status === 'failed' ? 'bg-red-500' :
                            'bg-gradient-to-r from-blue-500 to-purple-500'
                          }`}
                          style={{ width: `${asyncUploadProgress?.progress || 0}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Durum Mesajı ve İşlenen Dosya */}
                    <div className="text-center space-y-2">
                      <div className="text-sm text-gray-600">
                        {asyncUploadProgress?.status === 'processing' || asyncUploadProgress?.status === 'pending' ? (
                          <>
                            <span>{asyncUploadProgress?.processed_files || 0}/{asyncUploadProgress?.total_files || 0} dosya işleniyor</span>
                          </>
                        ) : (
                          asyncUploadProgress?.message || 'İşlem başlatılıyor...'
                        )}
                      </div>
                      {asyncUploadProgress?.current_file && asyncUploadProgress?.status === 'processing' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                          <p className="text-xs text-blue-500 font-medium mb-1">📄 İşlenen Dosya:</p>
                          <p className="text-sm text-blue-700 font-semibold truncate" title={asyncUploadProgress.current_file}>
                            {asyncUploadProgress.current_file}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* İstatistikler */}
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-2xl font-bold text-blue-600">
                          {asyncUploadProgress?.processed_files || 0}/{asyncUploadProgress?.total_files || 0}
                        </p>
                        <p className="text-xs text-gray-500">Dosya</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3">
                        <p className="text-2xl font-bold text-green-600">
                          {asyncUploadProgress?.basarili || 0}
                        </p>
                        <p className="text-xs text-gray-500">Başarılı</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3">
                        <p className="text-2xl font-bold text-red-600">
                          {asyncUploadProgress?.hatali || 0}
                        </p>
                        <p className="text-xs text-gray-500">Hatalı</p>
                      </div>
                    </div>
                    
                    {/* Tamamlandığında Detaylar - Hatalılar Üstte */}
                    {(asyncUploadProgress?.status === 'completed' || asyncUploadProgress?.status === 'failed') && asyncUploadProgress?.detaylar?.length > 0 && (
                      <div className="flex-1 overflow-hidden flex flex-col border rounded-lg">
                        <div className="bg-gray-100 px-3 py-2 border-b flex justify-between items-center">
                          <p className="text-sm font-semibold">📋 İşlem Detayları</p>
                          <p className="text-xs text-gray-500">
                            {asyncUploadProgress.detaylar.filter(d => d.durum === 'hata').length} hatalı, {asyncUploadProgress.detaylar.filter(d => d.durum === 'başarılı').length} başarılı
                          </p>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2 max-h-[300px]">
                          {/* Önce hatalıları göster */}
                          {asyncUploadProgress.detaylar
                            .sort((a, b) => {
                              if (a.durum === 'hata' && b.durum !== 'hata') return -1;
                              if (a.durum !== 'hata' && b.durum === 'hata') return 1;
                              return 0;
                            })
                            .map((detay, idx) => (
                              <div 
                                key={idx} 
                                className={`text-xs py-2 px-2 mb-1 rounded ${
                                  detay.durum === 'başarılı' 
                                    ? 'bg-green-50 border-l-4 border-green-500' 
                                    : 'bg-red-50 border-l-4 border-red-500'
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  <span className={detay.durum === 'başarılı' ? 'text-green-600' : 'text-red-600'}>
                                    {detay.durum === 'başarılı' ? '✓' : '✗'}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate" title={detay.dosya}>
                                      {detay.dosya}
                                    </p>
                                    {detay.durum === 'başarılı' ? (
                                      <p className="text-green-700">
                                        {detay.tip && `Tür: ${detay.tip}`} 
                                        {detay.musteri && ` • Müşteri: ${detay.musteri}`}
                                      </p>
                                    ) : (
                                      <p className="text-red-700 font-medium">
                                        ⚠️ {detay.mesaj || 'Bilinmeyen hata'}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button 
                      onClick={() => {
                        setAsyncUploadDialogOpen(false);
                        setAsyncUploadProgress(null);
                        setAsyncUploadJobId(null);
                      }}
                      variant={asyncUploadProgress?.status === 'completed' || asyncUploadProgress?.status === 'failed' ? 'default' : 'outline'}
                    >
                      {asyncUploadProgress?.status === 'completed' || asyncUploadProgress?.status === 'failed' ? 'Kapat' : 'Arka Planda Devam Et'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              <Button 
                onClick={handleTopluVeriGirisiAc} 
                data-testid="bulk-input-btn"
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg"
              >
                <FileText className="w-4 h-4 mr-2" />
                Toplu Veri Girişi
              </Button>
              
              <Dialog open={excelDialogOpen} onOpenChange={setExcelDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="excel-import-btn"
                    className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg"
                  >
                    <FileUp className="w-4 h-4 mr-2" />
                    Excel'den İçe Aktar
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="excel-import-dialog">
                  <DialogHeader>
                    <DialogTitle>Excel'den Kayıt İçe Aktar</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleExcelImport}>
                    <div className="space-y-4 py-4">
                      <Alert className="bg-blue-50 border-blue-200">
                        <AlertDescription>
                          <div className="flex items-center justify-between">
                            <span className="text-sm">Excel şablonunu indirip doldurun, sonra yükleyin</span>
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm"
                              onClick={handleSablonIndir}
                              className="ml-4"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Örnek Şablon İndir
                            </Button>
                          </div>
                        </AlertDescription>
                      </Alert>
                      
                      <Alert>
                        <AlertDescription className="text-xs">
                          Excel formatı: Vergi No, Ad Soyad, Ay, Yıl, Bakiye, Muhasebe, KDV, Stopaj, SSK, BAĞKUR, Geçici Vergi, Gelir Vergisi, Diğer 1-2-3, Ödenen, Notlar
                        </AlertDescription>
                      </Alert>
                      
                      <div>
                        <Label>Excel Dosyası</Label>
                        <Input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={(e) => setExcelFile(e.target.files[0])}
                          required
                        />
                        <p className="text-sm text-muted-foreground mt-1">
                          Şablonu indirip doldurup yükleyin (.xlsx veya .xls)
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={loading}>
                        <FileUp className="w-4 h-4 mr-2" />
                        {loading ? 'Yükleniyor...' : 'İçe Aktar'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              
              <Dialog open={kayitDialogOpen} onOpenChange={setKayitDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="add-manual-record-btn"
                    className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white shadow-lg"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Manuel Kayıt Ekle
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="manual-record-dialog">
                  <DialogHeader>
                    <div className="flex items-center justify-between">
                      <DialogTitle>Manuel Kayıt Ekle</DialogTitle>
                      <Badge variant="outline" className="text-sm font-semibold py-1 px-3 bg-blue-50 border-blue-300">
                        <Users className="w-4 h-4 mr-1" />
                        Toplam Müşteri: {musteriler.length}
                      </Badge>
                    </div>
                  </DialogHeader>
                  <form onSubmit={handleKayitEkle}>
                    <div className="grid grid-cols-2 gap-4 py-4">
                      <div>
                        <Label>Müşteri <span className="text-red-500">*</span></Label>
                        <Select
                          value={yeniKayit.vergi_no}
                          onValueChange={(value) => {
                            const musteri = musteriler.find(m => m.vergi_no === value);
                            setYeniKayit({...yeniKayit, vergi_no: value, ad_soyad: musteri?.ad_soyad || ''});
                          }}
                        >
                          <SelectTrigger data-testid="manual-customer-select">
                            <SelectValue placeholder="Müşteri Seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {musteriler.map((m) => (
                              <SelectItem key={m.id} value={m.vergi_no}>
                                {m.ad_soyad} ({m.vergi_no})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Ay <span className="text-red-500">*</span></Label>
                        <Select value={yeniKayit.ay} onValueChange={(value) => setYeniKayit({...yeniKayit, ay: value})}>
                          <SelectTrigger data-testid="manual-month-select">
                            <SelectValue placeholder="Ay Seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {AYLAR.map((ay) => (
                              <SelectItem key={ay} value={ay}>{ay}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Yıl</Label>
                        <Input
                          type="number"
                          data-testid="manual-year-input"
                          value={yeniKayit.yil}
                          onChange={(e) => setYeniKayit({...yeniKayit, yil: parseInt(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>Bakiye</Label>
                        <Input
                          type="number"
                          step="0.01"
                          data-testid="manual-bakiye-input"
                          value={yeniKayit.bakiye}
                          onChange={(e) => setYeniKayit({...yeniKayit, bakiye: parseFloat(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>Muhasebe</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={yeniKayit.muhasebe}
                          onChange={(e) => setYeniKayit({...yeniKayit, muhasebe: parseFloat(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>KDV</Label>
                        <Input
                          type="number"
                          step="0.01"
                          data-testid="manual-kdv-input"
                          value={yeniKayit.kdv}
                          onChange={(e) => setYeniKayit({...yeniKayit, kdv: parseFloat(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>Stopaj</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={yeniKayit.stopaj}
                          onChange={(e) => setYeniKayit({...yeniKayit, stopaj: parseFloat(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>SSK</Label>
                        <Input
                          type="number"
                          step="0.01"
                          data-testid="manual-ssk-input"
                          value={yeniKayit.ssk}
                          onChange={(e) => setYeniKayit({...yeniKayit, ssk: parseFloat(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>BAĞKUR</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={yeniKayit.bagkur}
                          onChange={(e) => setYeniKayit({...yeniKayit, bagkur: parseFloat(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>Geçici Vergi</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={yeniKayit.gecici_vergi}
                          onChange={(e) => setYeniKayit({...yeniKayit, gecici_vergi: parseFloat(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>Gelir Vergisi</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={yeniKayit.gelir_vergisi}
                          onChange={(e) => setYeniKayit({...yeniKayit, gelir_vergisi: parseFloat(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>Diğer 1</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={yeniKayit.diger1}
                          onChange={(e) => setYeniKayit({...yeniKayit, diger1: parseFloat(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>Diğer 2</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={yeniKayit.diger2}
                          onChange={(e) => setYeniKayit({...yeniKayit, diger2: parseFloat(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>Diğer 3</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={yeniKayit.diger3}
                          onChange={(e) => setYeniKayit({...yeniKayit, diger3: parseFloat(e.target.value)})}
                        />
                      </div>
                      
                      <div>
                        <Label>Ödenen</Label>
                        <Input
                          type="number"
                          step="0.01"
                          data-testid="manual-odenen-input"
                          value={yeniKayit.odenen}
                          onChange={(e) => setYeniKayit({...yeniKayit, odenen: parseFloat(e.target.value)})}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={loading} data-testid="save-manual-record-btn">
                        Kaydet
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              
              {/* Container: Sol (Gösterilecek Vergiler + Gösterim) | Sağ (Ödeme Durumu) */}
              <div className="flex flex-wrap items-start gap-3">
                {/* SOL: Gösterilecek Vergiler + Gösterim (Alt Alta) */}
                <div className="flex flex-col gap-3">
                  {/* Gösterilecek Vergi Türleri */}
                  <div className="flex flex-wrap items-center gap-2 border-2 border-purple-200 rounded-lg px-4 py-2 bg-purple-50">
                    <Label className="text-sm font-semibold">Gösterilecek Vergiler:</Label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Object.values(kayitlarVergiKolonlari).every(v => v)}
                        onChange={(e) => {
                          const yeniDurum = e.target.checked;
                          setKayitlarVergiKolonlari({
                            kdv: yeniDurum,
                            stopaj: yeniDurum,
                            ssk: yeniDurum,
                            bagkur: yeniDurum,
                            gecici_vergi: yeniDurum,
                            gelir_vergisi: yeniDurum,
                            diger1: yeniDurum,
                            diger2: yeniDurum,
                            diger3: yeniDurum
                          });
                        }}
                        className="w-3 h-3"
                      />
                      <span className="text-xs font-bold">Tümü</span>
                    </label>
                    <Separator orientation="vertical" className="h-4" />
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={kayitlarVergiKolonlari.kdv}
                        onChange={(e) => setKayitlarVergiKolonlari({...kayitlarVergiKolonlari, kdv: e.target.checked})}
                        className="w-3 h-3"
                      />
                      <span className="text-xs text-blue-600 font-semibold">KDV</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={kayitlarVergiKolonlari.stopaj}
                        onChange={(e) => setKayitlarVergiKolonlari({...kayitlarVergiKolonlari, stopaj: e.target.checked})}
                        className="w-3 h-3"
                      />
                      <span className="text-xs text-orange-600 font-semibold">Stopaj</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={kayitlarVergiKolonlari.ssk}
                        onChange={(e) => setKayitlarVergiKolonlari({...kayitlarVergiKolonlari, ssk: e.target.checked})}
                        className="w-3 h-3"
                      />
                      <span className="text-xs text-green-600 font-semibold">SSK</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={kayitlarVergiKolonlari.bagkur}
                        onChange={(e) => setKayitlarVergiKolonlari({...kayitlarVergiKolonlari, bagkur: e.target.checked})}
                        className="w-3 h-3"
                      />
                      <span className="text-xs text-yellow-600 font-semibold">Bağkur</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={kayitlarVergiKolonlari.gecici_vergi}
                        onChange={(e) => setKayitlarVergiKolonlari({...kayitlarVergiKolonlari, gecici_vergi: e.target.checked})}
                        className="w-3 h-3"
                      />
                      <span className="text-xs text-purple-600 font-semibold">Geçici V.</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={kayitlarVergiKolonlari.gelir_vergisi}
                        onChange={(e) => setKayitlarVergiKolonlari({...kayitlarVergiKolonlari, gelir_vergisi: e.target.checked})}
                        className="w-3 h-3"
                      />
                      <span className="text-xs text-pink-600 font-semibold">Gelir V.</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={kayitlarVergiKolonlari.diger1}
                        onChange={(e) => setKayitlarVergiKolonlari({...kayitlarVergiKolonlari, diger1: e.target.checked})}
                        className="w-3 h-3"
                      />
                      <span className="text-xs font-semibold">Diğer1</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={kayitlarVergiKolonlari.diger2}
                        onChange={(e) => setKayitlarVergiKolonlari({...kayitlarVergiKolonlari, diger2: e.target.checked})}
                        className="w-3 h-3"
                      />
                      <span className="text-xs font-semibold">Diğer2</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={kayitlarVergiKolonlari.diger3}
                        onChange={(e) => setKayitlarVergiKolonlari({...kayitlarVergiKolonlari, diger3: e.target.checked})}
                        className="w-3 h-3"
                      />
                      <span className="text-xs font-semibold">Diğer3</span>
                    </label>
                  </div>
                  
                  {/* Gösterim Toggle */}
                  <div className="flex items-center gap-4 border-2 border-green-200 rounded-lg px-4 py-2 bg-green-50">
                    <Label className="text-sm font-semibold">Gösterim:</Label>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={tumAktifMusteriler} 
                        onCheckedChange={(checked) => {
                          setTumAktifMusteriler(checked);
                          fetchKayitlar(checked);
                        }}
                      />
                      <span className="text-xs font-medium">
                        {tumAktifMusteriler ? 'Tüm Aktif Müşteriler' : 'Sadece Vergi Girişi Olanlar'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
            </div>
            
            {/* Toplu Yükleme Sonuçları Dialog */}
            <Dialog open={resultsDialogOpen} onOpenChange={setResultsDialogOpen}>
              <DialogContent className="max-w-2xl" data-testid="upload-results-dialog">
                <DialogHeader>
                  <DialogTitle>Toplu Yükleme Sonuçları</DialogTitle>
                </DialogHeader>
                {uploadResults && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-3xl font-bold text-green-600">{uploadResults.basarili}</p>
                            <p className="text-sm text-muted-foreground">Başarılı</p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-3xl font-bold text-red-600">{uploadResults.hatali}</p>
                            <p className="text-sm text-muted-foreground">Hatalı</p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-3xl font-bold text-blue-600">{uploadResults.yeni_musteri}</p>
                            <p className="text-sm text-muted-foreground">Yeni Müşteri</p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                    
                    {uploadResults.detaylar && uploadResults.detaylar.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Detaylar:</h4>
                        <div className="max-h-60 overflow-y-auto space-y-2">
                          {uploadResults.detaylar.map((detay, idx) => (
                            <Alert key={idx} variant={detay.durum === 'hata' ? 'destructive' : 'default'}>
                              <AlertDescription>
                                <strong>{detay.dosya || detay.vergi_no}:</strong> {detay.mesaj}
                              </AlertDescription>
                            </Alert>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter>
                  <Button onClick={() => setResultsDialogOpen(false)}>Kapat</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Çakışma Uyarı Dialogu */}
            <Dialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-orange-600 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Mevcut Vergi Değerleri Tespit Edildi
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <Alert className="bg-orange-50 border-orange-200">
                    <AlertDescription>
                      <strong>⚠️ Dikkat:</strong> Aşağıdaki vergi kayıtlarında mevcut değerler bulundu. 
                      Her kayıt için <strong>"Değiştir"</strong> veya <strong>"Koru"</strong> seçimi yapabilirsiniz.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="space-y-3">
                    {conflicts.map((conflict, index) => (
                      <Card key={index} className="border-2 border-orange-200">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <p className="font-semibold text-lg">{conflict.musteri_adi}</p>
                              <p className="text-sm text-gray-600">
                                {conflict.ay} {conflict.yil} - <span className="font-medium text-blue-600">{conflict.vergi_turu_label}</span>
                              </p>
                              <div className="flex gap-4 mt-2">
                                <div className="bg-gray-100 px-3 py-2 rounded">
                                  <p className="text-xs text-gray-500">Mevcut Tutar</p>
                                  <p className="font-bold text-gray-700">₺{conflict.mevcut_tutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</p>
                                </div>
                                <div className="flex items-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                  </svg>
                                </div>
                                <div className="bg-blue-100 px-3 py-2 rounded">
                                  <p className="text-xs text-blue-500">Yeni Tutar</p>
                                  <p className="font-bold text-blue-700">₺{conflict.yeni_tutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant={conflictDecisions[index] === 'update' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setConflictDecisions({...conflictDecisions, [index]: 'update'})}
                                className={conflictDecisions[index] === 'update' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                              >
                                Değiştir
                              </Button>
                              <Button
                                variant={conflictDecisions[index] === 'keep' || !conflictDecisions[index] ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setConflictDecisions({...conflictDecisions, [index]: 'keep'})}
                                className={conflictDecisions[index] === 'keep' || !conflictDecisions[index] ? 'bg-gray-600 hover:bg-gray-700' : ''}
                              >
                                Koru
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
                
                <DialogFooter className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setConflictDialogOpen(false);
                      setConflicts([]);
                      setConflictDecisions({});
                      setPendingZipFile(null);
                    }}
                  >
                    İptal
                  </Button>
                  <Button 
                    onClick={handleResolveConflicts}
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? 'İşleniyor...' : 'Devam Et ve Yükle'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Kayıtlar Tablosu */}
            <Card className="relative" style={{ minHeight: '700px' }}>
              <CardHeader>
                <div className="space-y-3">
                  {/* Üst satır - Arama ve Toggle'lar */}
                  <div className="flex justify-between items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-4">
                      <Input
                        type="text"
                        placeholder="Kayıt Ara (Müşteri Adı)..."
                        value={aramaKayit}
                        onChange={(e) => setAramaKayit(e.target.value)}
                        className="max-w-sm"
                      />
                      
                      {/* Müşteri Sayıları Badge'leri */}
                      <div className="flex items-center gap-2">
                        {(() => {
                          const filteredKayitlar = kayitlar.filter(kayit => {
                            if (!showHidden && kayit.gorunur === false) return false;
                            if (aramaKayit) {
                              const searchLower = aramaKayit.toLowerCase();
                              if (!kayit.ad_soyad?.toLowerCase().includes(searchLower)) return false;
                            }
                            if (showFavoriOnly) {
                              const musteri = musteriler.find(m => m.vergi_no === kayit.vergi_no);
                              if (!musteri?.favori) return false;
                            }
                            // Özel filtreleme: Ödeme durumuna göre
                            if (hideZeroValues) {
                              const toplam = Number(kayit.toplam_tutar) || 0;
                              const odenen = Number(kayit.odenen) || 0;
                              const kalan = Number(kayit.kalan) || 0;
                              
                              // Ödendi (Tamamen) seçildiğinde: TAM TAHSİLAT (Toplam=Ödenen VE Kalan=0)
                              if (kayitFilters.odeme_durumu === 'odendi_tamamen') {
                                // Önce sıfır kayıtları ele
                                if (toplam === 0 && odenen === 0 && kalan === 0) {
                                  return false; // Sıfır kayıtları gösterme
                                }
                                // Tam tahsilat kontrolü: Toplam = Ödenen VE Kalan = 0 olmalı
                                if (toplam !== odenen || kalan !== 0) {
                                  return false; // Tam ödenmemişleri gösterme
                                }
                              }
                              // Ödenmedi/Kısmen seçildiğinde: Toplam=0 VE Ödenen=0 VE Kalan=0 ise gösterme
                              else if (kayitFilters.odeme_durumu === 'odenmedi_kismen') {
                                if (toplam === 0 && odenen === 0 && kalan === 0) {
                                  return false;
                                }
                              }
                              // Diğer durumlar için eski mantık
                              else if (!kayit.odenen || Number(kayit.odenen) === 0) {
                                const tumDegerlerSifir = 
                                  (!kayit.bakiye || Number(kayit.bakiye) === 0) &&
                                  (!kayit.muhasebe || Number(kayit.muhasebe) === 0) &&
                                  (!kayit.kdv || Number(kayit.kdv) === 0) &&
                                  (!kayit.stopaj || Number(kayit.stopaj) === 0) &&
                                  (!kayit.ssk || Number(kayit.ssk) === 0) &&
                                  (!kayit.bagkur || Number(kayit.bagkur) === 0) &&
                                  (!kayit.gecici_vergi || Number(kayit.gecici_vergi) === 0) &&
                                  (!kayit.gelir_vergisi || Number(kayit.gelir_vergisi) === 0) &&
                                  (!kayit.diger1 || Number(kayit.diger1) === 0) &&
                                  (!kayit.diger2 || Number(kayit.diger2) === 0) &&
                                  (!kayit.diger3 || Number(kayit.diger3) === 0);
                                if (tumDegerlerSifir) return false;
                              }
                            }
                            if (kayitFilters.bakiye_min && kayit.bakiye < parseFloat(kayitFilters.bakiye_min)) return false;
                            if (kayitFilters.bakiye_max && kayit.bakiye > parseFloat(kayitFilters.bakiye_max)) return false;
                            if (kayitFilters.muhasebe_min && kayit.muhasebe < parseFloat(kayitFilters.muhasebe_min)) return false;
                            if (kayitFilters.muhasebe_max && kayit.muhasebe > parseFloat(kayitFilters.muhasebe_max)) return false;
                            if (kayitFilters.kdv_min && kayit.kdv < parseFloat(kayitFilters.kdv_min)) return false;
                            if (kayitFilters.kdv_max && kayit.kdv > parseFloat(kayitFilters.kdv_max)) return false;
                            if (kayitFilters.stopaj_min && kayit.stopaj < parseFloat(kayitFilters.stopaj_min)) return false;
                            if (kayitFilters.stopaj_max && kayit.stopaj > parseFloat(kayitFilters.stopaj_max)) return false;
                            if (kayitFilters.toplam_min && kayit.toplam_tutar < parseFloat(kayitFilters.toplam_min)) return false;
                            if (kayitFilters.toplam_max && kayit.toplam_tutar > parseFloat(kayitFilters.toplam_max)) return false;
                            if (kayitFilters.kalan_min && kayit.kalan < parseFloat(kayitFilters.kalan_min)) return false;
                            if (kayitFilters.kalan_max && kayit.kalan > parseFloat(kayitFilters.kalan_max)) return false;
                            if (kayitFilters.odeme_durumu) {
                              if (kayitFilters.odeme_durumu === 'odendi_tamamen' && kayit.kalan > 0) return false;
                              if (kayitFilters.odeme_durumu === 'odenmedi_kismen' && kayit.kalan === 0) return false;
                            }
                            
                            // Tahakkuk Yüklenmeyenler Filtresi
                            if (kayitFilters.tahakkuk_yuklenmedi) {
                              const musteri = musteriler.find(m => m.vergi_no === kayit.vergi_no);
                              if (!musteri) return true; // Müşteri bulunamazsa atla
                              
                              const ay = kayit.ay;
                              const musteriTuru = musteri.musteri_turu;
                              const stopajPeriyot = musteri.stopaj_periyot || 'Aylık';
                              
                              // KDV Filtresi - Her ay zorunlu
                              if (kayitFilters.tahakkuk_yuklenmedi === 'kdv') {
                                // KDV değeri 0 veya boş ise göster
                                if (!kayit.kdv || Number(kayit.kdv) === 0) {
                                  return true;
                                }
                                return false; // KDV dolu ise gizle
                              }
                              
                              // Stopaj Filtresi
                              if (kayitFilters.tahakkuk_yuklenmedi === 'stopaj') {
                                let stopajGerekli = false;
                                
                                if (stopajPeriyot === 'Aylık') {
                                  stopajGerekli = true; // Her ay
                                } else if (stopajPeriyot === '3 Aylık') {
                                  // Mart, Haziran, Eylül, Aralık
                                  stopajGerekli = ['Mart', 'Haziran', 'Eylül', 'Aralık'].includes(ay);
                                }
                                
                                if (stopajGerekli) {
                                  // Stopaj değeri 0 veya boş ise göster
                                  if (!kayit.stopaj || Number(kayit.stopaj) === 0) {
                                    return true;
                                  }
                                }
                                return false;
                              }
                              
                              // Geçici Vergi Filtresi - Şubat, Mayıs, Ağustos, Kasım
                              if (kayitFilters.tahakkuk_yuklenmedi === 'gecici') {
                                const geciciAylar = ['Şubat', 'Mayıs', 'Ağustos', 'Kasım'];
                                if (geciciAylar.includes(ay)) {
                                  // Geçici vergi değeri 0 veya boş ise göster
                                  if (!kayit.gecici_vergi || Number(kayit.gecici_vergi) === 0) {
                                    return true;
                                  }
                                }
                                return false;
                              }
                              
                              // Gelir/Kurumlar Vergisi Filtresi
                              if (kayitFilters.tahakkuk_yuklenmedi === 'gelir_kurum') {
                                // İşletme veya Bilanço - Mart ayı - Gelir Vergisi
                                if ((musteriTuru === 'İŞLETME' || musteriTuru === 'BİLANÇO') && ay === 'Mart') {
                                  if (!kayit.gelir_vergisi || Number(kayit.gelir_vergisi) === 0) {
                                    return true;
                                  }
                                }
                                
                                // Şirket - Nisan ayı - Kurumlar Vergisi (gelir_vergisi alanında tutuluyor)
                                if (musteriTuru === 'ŞİRKET' && ay === 'Nisan') {
                                  if (!kayit.gelir_vergisi || Number(kayit.gelir_vergisi) === 0) {
                                    return true;
                                  }
                                }
                                
                                return false;
                              }
                            }
                            
                            return true;
                          });
                          
                          // Aktif Müşteri: Toplam müşteri sayısı (filtrelerden bağımsız)
                          const aktifMusteri = musteriler.length;
                          
                          // Favori Müşteri: Her zaman sadece favori müşteri sayısı (toggle durumundan bağımsız)
                          const favoriMusteri = musteriler.filter(m => m.favori === true).length;
                          
                          // Gizli Müşteri: Filtrelenmiş kayıtlar içindeki gizli kayıtları olan müşteriler
                          const gizliKayitlar = filteredKayitlar.filter(k => k.gorunur === false);
                          const gizliMusteriVergiNos = new Set(gizliKayitlar.map(k => k.vergi_no));
                          const gizliMusteri = gizliMusteriVergiNos.size;
                          
                          return (
                            <>
                              <Badge variant="outline" className="text-sm font-semibold py-1 px-3 bg-green-50 border-green-300">
                                <Users className="w-4 h-4 mr-1" />
                                Aktif: {aktifMusteri}
                              </Badge>
                              <Badge variant="outline" className="text-sm font-semibold py-1 px-3 bg-yellow-50 border-yellow-300">
                                <Star className="w-4 h-4 mr-1 fill-yellow-500 text-yellow-500" />
                                Favori: {favoriMusteri}
                              </Badge>
                              <Badge variant="outline" className="hidden sm:flex text-sm font-semibold py-1 px-3 bg-gray-50 border-gray-300">
                                <EyeOff className="w-4 h-4 mr-1" />
                                Gizli: {gizliMusteri}
                              </Badge>
                            </>
                          );
                        })()}
                        
                        {/* Vergi Ödeme Raporu Butonu - Mobilde Gizli */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              toast.info('Vergi Ödeme Raporu hazırlanıyor...');
                              const ay = selectedAylar[0] || 'Kasım';
                              const response = await axios.get(`${API}/vergi-odeme-raporu/excel?ay=${encodeURIComponent(ay)}&yil=${selectedYil}`, {
                                responseType: 'blob'
                              });
                              
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `Vergi_Odeme_Raporu_${ay}_${selectedYil}.xlsx`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                              
                              toast.success('Vergi Ödeme Raporu indirildi!');
                            } catch (error) {
                              console.error('Rapor hatası:', error);
                              toast.error('Rapor oluşturulamadı');
                            }
                          }}
                          className="hidden sm:flex text-purple-600 border-purple-300 hover:bg-purple-50"
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-1" />
                          Vergi Ödeme Raporu
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Gizli Kayıtları Göster - Mobilde Gizli */}
                      <div className="hidden sm:flex items-center space-x-2">
                        <Eye className="w-4 h-4 text-green-600" />
                        <Label htmlFor="show-hidden" className="text-sm whitespace-nowrap">Gizli Kayıtları Göster</Label>
                        <Switch
                          id="show-hidden"
                          checked={showHidden}
                          onCheckedChange={setShowHidden}
                          data-testid="toggle-hidden-records"
                          className="data-[state=checked]:bg-green-600"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Star className="w-4 h-4 text-green-600 fill-green-600" />
                        <Label htmlFor="favori-only" className="text-sm whitespace-nowrap hidden sm:inline">Favori Müşteriler</Label>
                        <Switch
                          id="favori-only"
                          checked={showFavoriOnly}
                          onCheckedChange={setShowFavoriOnly}
                          className="data-[state=checked]:bg-green-600"
                        />
                      </div>
                      
                      {/* Gizli Müşterileri Göster */}
                      <div className="flex items-center space-x-2">
                        <Eye className="w-4 h-4 text-orange-500" />
                        <Label htmlFor="show-hidden" className="text-sm whitespace-nowrap hidden sm:inline">Gizli</Label>
                        <Switch
                          id="show-hidden"
                          checked={showHiddenMusteriler}
                          onCheckedChange={setShowHiddenMusteriler}
                          className="data-[state=checked]:bg-orange-500"
                        />
                      </div>
                      
                      {/* Sıfır Değerleri Gizle - Mobilde Gizli */}
                      <div className="hidden sm:flex items-center space-x-2">
                        <XCircle className="w-4 h-4 text-green-600" />
                        <Label htmlFor="hide-zero" className="text-xs whitespace-nowrap">Sıfır Değerleri Gizle</Label>
                        <Switch
                          id="hide-zero"
                          checked={hideZeroValues}
                          onCheckedChange={setHideZeroValues}
                          className="data-[state=checked]:bg-green-600 scale-90"
                        />
                      </div>
                      
                      {/* Ay Navigasyonu - Kompakt */}
                      <div className="flex items-center gap-1 bg-gradient-to-r from-green-100 to-emerald-200 rounded-full px-1.5 py-0.5 shadow-sm border border-green-300">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 rounded-full bg-white/60 hover:bg-white text-green-700 transition-all duration-200 hover:scale-105"
                          onClick={() => {
                            const currentAyIndex = selectedAylar.length === 1 ? AYLAR.indexOf(selectedAylar[0]) : -1;
                            if (currentAyIndex !== -1) {
                              let newAyIndex = currentAyIndex - 1;
                              let newYil = selectedYil;
                              if (newAyIndex < 0) {
                                newAyIndex = 11;
                                newYil = selectedYil - 1;
                              }
                              setSelectedYil(newYil);
                              setSelectedAylar([AYLAR[newAyIndex]]);
                            } else {
                              const firstAyIndex = Math.min(...selectedAylar.map(a => AYLAR.indexOf(a)));
                              let newAyIndex = firstAyIndex - 1;
                              let newYil = selectedYil;
                              if (newAyIndex < 0) {
                                newAyIndex = 11;
                                newYil = selectedYil - 1;
                              }
                              setSelectedYil(newYil);
                              setSelectedAylar([AYLAR[newAyIndex]]);
                            }
                          }}
                          title="Önceki Ay"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <div className="flex items-center gap-1 min-w-[70px] justify-center">
                          <span className="text-green-800 font-bold text-xs">
                            {selectedAylar.length === 1 ? selectedAylar[0] : `${selectedAylar.length} ay`}
                          </span>
                          <span className="text-green-600 text-xs font-medium">
                            {selectedYil}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 rounded-full bg-white/60 hover:bg-white text-green-700 transition-all duration-200 hover:scale-105"
                          onClick={() => {
                            const currentAyIndex = selectedAylar.length === 1 ? AYLAR.indexOf(selectedAylar[0]) : -1;
                            if (currentAyIndex !== -1) {
                              let newAyIndex = currentAyIndex + 1;
                              let newYil = selectedYil;
                              if (newAyIndex > 11) {
                                newAyIndex = 0;
                                newYil = selectedYil + 1;
                              }
                              setSelectedYil(newYil);
                              setSelectedAylar([AYLAR[newAyIndex]]);
                            } else {
                              const lastAyIndex = Math.max(...selectedAylar.map(a => AYLAR.indexOf(a)));
                              let newAyIndex = lastAyIndex + 1;
                              let newYil = selectedYil;
                              if (newAyIndex > 11) {
                                newAyIndex = 0;
                                newYil = selectedYil + 1;
                              }
                              setSelectedYil(newYil);
                              setSelectedAylar([AYLAR[newAyIndex]]);
                            }
                          }}
                          title="Sonraki Ay"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      {/* Sonraki Aya Aktar Butonu */}
                      <Button 
                        onClick={handleSonrakiAyaAktar} 
                        variant="outline" 
                        size="sm"
                        className="text-blue-600 hover:bg-blue-50 h-7 text-xs"
                        title="Seçili ayın Kalan ve Muhasebe tutarlarını sonraki aya aktar"
                      >
                        <ArrowDown className="w-3 h-3 mr-1" />
                        <span className="hidden sm:inline">Sonraki Aya Aktar</span>
                        <span className="sm:hidden">Aktar</span>
                      </Button>
                      
                      {selectedKayitlar.length > 0 && (
                        <Button onClick={handleTopluKayitSil} variant="outline" size="sm" className="text-red-600 hover:bg-red-50 h-7 text-xs">
                          <Trash2 className="w-3 h-3 mr-1" />
                          Sil ({selectedKayitlar.length})
                        </Button>
                      )}
                    </div>

                    {/* Ödeme Durumu Filtresi - Alt Satır */}
                    <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-200">
                      <Label className="text-sm font-semibold">{selectedYil}</Label>
                      <Label className="text-sm font-semibold">Ödeme Durumu:</Label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="odeme_durumu_quick"
                          checked={kayitFilters.odeme_durumu === ''}
                          onChange={() => setKayitFilters({...kayitFilters, odeme_durumu: ''})}
                          className="w-3 h-3"
                        />
                        <span className="text-xs">Tümü</span>
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="odeme_durumu_quick"
                          checked={kayitFilters.odeme_durumu === 'odendi_tamamen'}
                          onChange={() => setKayitFilters({...kayitFilters, odeme_durumu: 'odendi_tamamen'})}
                          className="w-3 h-3"
                        />
                        <span className="text-xs text-green-600 font-semibold">Ödendi</span>
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="odeme_durumu_quick"
                          checked={kayitFilters.odeme_durumu === 'odenmedi_kismen'}
                          onChange={() => setKayitFilters({...kayitFilters, odeme_durumu: 'odenmedi_kismen'})}
                          className="w-3 h-3"
                        />
                        <span className="text-xs text-red-600 font-semibold">Ödenmedi/Kısmen</span>
                      </label>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="relative">
                  {/* VERİLER - Scrollable Container (Başlık ve veri BERABER) */}
                  <style>
                    {`
                      #excel-freeze-container td,
                      #excel-freeze-container th {
                        padding: 4px 8px !important;
                        height: 32px;
                      }
                      #excel-freeze-container .records-table {
                        width: 100%;
                        min-width: 1400px;
                        table-layout: fixed;
                      }
                      #excel-freeze-container thead,
                      #excel-freeze-container thead tr {
                        position: sticky !important;
                        top: 0 !important;
                        z-index: 50 !important;
                      }
                      #excel-freeze-container thead th {
                        position: sticky !important;
                        top: 0 !important;
                        background: #f1f5f9 !important;
                        z-index: 40 !important;
                      }
                      #excel-freeze-container tfoot,
                      #excel-freeze-container tfoot tr {
                        position: sticky !important;
                        bottom: 0 !important;
                        z-index: 50 !important;
                      }
                      /* Scrollbar her zaman görünür */
                      #excel-freeze-container {
                        scrollbar-width: auto !important;
                        -webkit-overflow-scrolling: touch;
                      }
                      #excel-freeze-container::-webkit-scrollbar {
                        width: 12px !important;
                        height: 12px !important;
                        display: block !important;
                      }
                      #excel-freeze-container::-webkit-scrollbar-track {
                        background: #f1f5f9;
                        border-radius: 6px;
                      }
                      #excel-freeze-container::-webkit-scrollbar-thumb {
                        background: #94a3b8;
                        border-radius: 6px;
                        border: 2px solid #f1f5f9;
                      }
                      #excel-freeze-container::-webkit-scrollbar-thumb:hover {
                        background: #64748b;
                      }
                      /* Mobilde scrollbar daha büyük */
                      @media (max-width: 768px) {
                        #excel-freeze-container::-webkit-scrollbar {
                          width: 16px !important;
                          height: 16px !important;
                        }
                        #excel-freeze-container .records-table {
                          width: max-content !important;
                          min-width: 0 !important;
                          table-layout: auto !important;
                        }
                        #excel-freeze-container .records-table th,
                        #excel-freeze-container .records-table td {
                          width: auto !important;
                          min-width: 0 !important;
                          max-width: none !important;
                        }
                      }
                      @media (min-width: 768px) {
                        #excel-freeze-container th[data-col="bakiye-kapat"] {
                          width: 24px !important;
                          min-width: 24px !important;
                          max-width: 24px !important;
                        }
                      }
                    `}
                  </style>
                  <div 
                    className="overflow-auto max-h-[calc(100vh-300px)] md:max-h-[calc(100vh-250px)] focus:outline-2 focus:outline-blue-500 focus:outline-offset-0 border-2 border-transparent focus:border-blue-400" 
                    id="excel-freeze-container"
                    tabIndex={0}
                    onClick={(e) => {
                      e.currentTarget.focus();
                    }}
                    onKeyDown={(e) => {
                      console.log('Key pressed:', e.key);
                      const container = e.currentTarget;
                      const scrollAmount = 100;
                      
                      if (['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'PageDown', 'PageUp', 'Home', 'End'].includes(e.key)) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                      
                      switch(e.key) {
                        case 'ArrowDown':
                          container.scrollTop += scrollAmount;
                          break;
                        case 'ArrowUp':
                          container.scrollTop -= scrollAmount;
                          break;
                        case 'ArrowRight':
                          container.scrollLeft += scrollAmount;
                          break;
                        case 'ArrowLeft':
                          container.scrollLeft -= scrollAmount;
                          break;
                        case 'PageDown':
                          container.scrollTop += container.clientHeight;
                          break;
                        case 'PageUp':
                          container.scrollTop -= container.clientHeight;
                          break;
                        case 'Home':
                          if (e.ctrlKey) container.scrollTop = 0;
                          else container.scrollLeft = 0;
                          break;
                        case 'End':
                          if (e.ctrlKey) container.scrollTop = container.scrollHeight;
                          else container.scrollLeft = container.scrollWidth;
                          break;
                      }
                    }}
                  >
                    <Table className="records-table md:min-w-[1400px]">
                    {/* BAŞLIKLAR - Sticky Top (Yatay scroll'da veri ile beraber kayar) */}
                    <TableHeader className="sticky top-0 z-20 bg-slate-100 shadow-md border-b-2 border-slate-300">
                      <TableRow className="hover:bg-slate-100">
                        {/* EXCEL FREEZE PANES: SOL SABİT KOLONLAR */}
                        {/* Checkbox - Mobilde gizli */}
                        <TableHead className="text-xs font-bold text-slate-700 sticky bg-slate-100 z-10 border-r-2 border-slate-400 hidden md:table-cell" style={{ width: '30px', minWidth: '30px', left: '0' }}>
                          <input
                            type="checkbox"
                            checked={selectedKayitlar.length === kayitlar.length && kayitlar.length > 0}
                            onChange={(e) => handleSelectAllKayitlar(e.target.checked)}
                            className="w-3 h-3"
                          />
                        </TableHead>
                        {/* Göz ikonu - Mobilde gizli */}
                        <TableHead className="text-xs font-bold text-slate-700 text-center sticky bg-slate-100 z-10 border-r-2 border-slate-400 hidden md:table-cell" style={{ width: '30px', minWidth: '30px', left: '30px' }}>
                          <Eye className="w-3.5 h-3.5 mx-auto text-slate-600" />
                        </TableHead>
                        {/* Bakiye Kapat - Mobilede de görünür, dar genişlik */}
                        <TableHead data-col="bakiye-kapat" className="text-xs font-bold text-center text-slate-700 sticky bg-orange-50 z-10 border-r-2 border-orange-300" style={{ width: '35px', minWidth: '35px', maxWidth: '35px', padding: '1px' }} title="Bakiye Kapat">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mx-auto text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </TableHead>
                        {/* Müşteri - Mobilede ve masaüstünde görünür, mobilde çok dar */}
                        <TableHead className="text-xs font-bold text-slate-700 sticky bg-slate-100 z-10 border-r-2 border-slate-400 sm:w-[150px] w-[90px]" style={{ minWidth: '90px', maxWidth: '180px' }}>
                          <span className="md:hidden" style={{ position: 'sticky', left: '0' }}>Müş.</span>
                          <span className="hidden md:inline" style={{ position: 'sticky', left: '90px' }}>Müşteri</span>
                        </TableHead>
                        {/* Ay - Mobilede de görünür, çok dar */}
                        <TableHead className="text-xs font-bold text-slate-700 sticky bg-slate-100 z-10 border-r-2 border-slate-400 sm:w-[40px] w-[28px]" style={{ minWidth: '25px', maxWidth: '45px' }}>
                          <span className="hidden sm:inline">Ay</span>
                          <span className="sm:hidden text-[9px]">Ay</span>
                        </TableHead>
                        {/* MOBİL: Kalan (önce) - Sadece mobilde göster */}
                        <TableHead 
                          className="text-xs text-right bg-red-100 sticky z-10 border-x-2 border-red-300 font-extrabold text-red-900 shadow-lg cursor-pointer hover:bg-red-200 md:hidden w-[68px]" 
                          style={{ minWidth: '68px', padding: '1px 2px' }}
                          onClick={() => handleSort('kalan')}
                        >
                          <div className="flex items-center justify-end gap-0">
                            <span className="text-[9px]">Kal</span>
                            {sortColumn === 'kalan' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        {/* MOBİL: Ödenen (ortada) - Sadece mobilde göster */}
                        <TableHead 
                          className="text-xs text-right bg-green-100 sticky z-10 border-x-2 border-green-300 font-extrabold text-green-900 cursor-pointer hover:bg-green-200 md:hidden w-[68px]" 
                          style={{ minWidth: '68px', padding: '1px 2px' }}
                          onClick={() => handleSort('odenen')}
                        >
                          <div className="flex items-center justify-end gap-0">
                            <span className="text-[9px]">Öd</span>
                            {sortColumn === 'odenen' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        {/* MOBİL: Toplam (sonda) - Sadece mobilde göster */}
                        <TableHead 
                          className="text-xs text-right bg-amber-100 sticky z-10 border-x-2 border-amber-300 font-extrabold text-amber-900 cursor-pointer hover:bg-amber-200 md:hidden w-[68px]" 
                          style={{ minWidth: '68px', padding: '1px 2px' }}
                          onClick={() => handleSort('toplam_tutar')}
                        >
                          <div className="flex items-center justify-end gap-0">
                            <span className="text-[9px]">Top</span>
                            {sortColumn === 'toplam_tutar' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        {/* Toplam - Her zaman görünür, mobilde çok dar */}
                        <TableHead 
                          className="text-xs text-right bg-amber-100 sticky z-10 border-x-2 border-amber-300 font-extrabold text-amber-900 cursor-pointer hover:bg-amber-200 hidden md:table-cell md:w-[100px]" 
                          style={{ minWidth: '68px', padding: '1px 2px' }}
                          onClick={() => handleSort('toplam_tutar')}
                        >
                          <div className="flex items-center justify-end gap-0">
                            <span className="hidden md:inline">Toplam</span>
                            <span className="md:hidden text-[9px]">Top</span>
                            {sortColumn === 'toplam_tutar' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        {/* Ödenen - Her zaman görünür, mobilde çok dar */}
                        <TableHead 
                          className="text-xs text-right bg-green-100 sticky z-10 border-x-2 border-green-300 font-extrabold text-green-900 cursor-pointer hover:bg-green-200 hidden md:table-cell md:w-[100px]" 
                          style={{ minWidth: '68px', padding: '1px 2px' }}
                          onClick={() => handleSort('odenen')}
                        >
                          <div className="flex items-center justify-end gap-0">
                            <span className="hidden md:inline">Ödenen</span>
                            <span className="md:hidden text-[9px]">Öd</span>
                            {sortColumn === 'odenen' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        {/* Kalan - Her zaman görünür, mobilde çok dar */}
                        <TableHead 
                          className="text-xs text-right bg-red-100 sticky z-10 border-x-2 border-red-300 font-extrabold text-red-900 shadow-lg cursor-pointer hover:bg-red-200 hidden md:table-cell md:w-[100px]" 
                          style={{ minWidth: '68px', padding: '1px 2px' }}
                          onClick={() => handleSort('kalan')}
                        >
                          <div className="flex items-center justify-end gap-0">
                            <span className="hidden md:inline">Kalan</span>
                            <span className="md:hidden text-[9px]">Kal</span>
                            {sortColumn === 'kalan' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        {/* SAĞ SCROLLABLE KOLONLAR - SIRALANABİLİR - Mobilde gizli */}
                        <TableHead 
                          className={`text-xs text-right font-bold text-slate-700 cursor-pointer hover:bg-slate-200 ${visibleColumns.bakiye ? 'hidden md:table-cell' : 'hidden'}`} 
                          style={{ width: '85px', minWidth: '85px' }}
                          onClick={() => handleSort('bakiye')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Bakiye
                            {sortColumn === 'bakiye' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className={`text-xs text-right bg-purple-100 font-bold text-slate-700 cursor-pointer hover:bg-purple-200 ${visibleColumns.muhasebe ? 'hidden md:table-cell' : 'hidden'}`} 
                          style={{ width: '90px', minWidth: '90px' }}
                          onClick={() => handleSort('muhasebe')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Muhasebe
                            {sortColumn === 'muhasebe' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className={`text-xs text-right bg-blue-100 font-bold text-slate-700 cursor-pointer hover:bg-blue-200 ${visibleColumns.kdv ? 'hidden md:table-cell' : 'hidden'}`} 
                          style={{ width: '85px', minWidth: '85px' }}
                          onClick={() => handleSort('kdv')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            KDV
                            {sortColumn === 'kdv' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className={`text-xs text-right bg-orange-100 font-bold text-slate-700 cursor-pointer hover:bg-orange-200 ${visibleColumns.stopaj ? 'hidden md:table-cell' : 'hidden'}`} 
                          style={{ width: '85px', minWidth: '85px' }}
                          onClick={() => handleSort('stopaj')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Stopaj
                            {sortColumn === 'stopaj' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className={`text-xs text-right bg-teal-100 font-bold text-slate-700 cursor-pointer hover:bg-teal-200 ${visibleColumns.ssk ? 'hidden md:table-cell' : 'hidden'}`} 
                          style={{ width: '85px', minWidth: '85px' }}
                          onClick={() => handleSort('ssk')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            SSK
                            {sortColumn === 'ssk' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className={`text-xs text-right bg-pink-100 font-bold text-slate-700 cursor-pointer hover:bg-pink-200 ${visibleColumns.bagkur ? 'hidden md:table-cell' : 'hidden'}`} 
                          style={{ width: '85px', minWidth: '85px' }}
                          onClick={() => handleSort('bagkur')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            BAĞKUR
                            {sortColumn === 'bagkur' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className={`text-xs text-right bg-indigo-100 font-bold text-slate-700 cursor-pointer hover:bg-indigo-200 ${visibleColumns.gecici_vergi ? 'hidden md:table-cell' : 'hidden'}`} 
                          style={{ width: '85px', minWidth: '85px' }}
                          onClick={() => handleSort('gecici_vergi')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            G.Vergi
                            {sortColumn === 'gecici_vergi' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className={`text-xs text-right bg-cyan-100 font-bold text-slate-700 cursor-pointer hover:bg-cyan-200 ${visibleColumns.gelir_vergisi ? 'hidden md:table-cell' : 'hidden'}`} 
                          style={{ width: '85px', minWidth: '85px' }}
                          onClick={() => handleSort('gelir_vergisi')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Gel.Verg.
                            {sortColumn === 'gelir_vergisi' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className={`text-xs text-right bg-lime-100 font-bold text-slate-700 cursor-pointer hover:bg-lime-200 ${visibleColumns.diger1 ? 'hidden md:table-cell' : 'hidden'}`} 
                          style={{ width: '80px', minWidth: '80px' }}
                          onClick={() => handleSort('diger1')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Diğer 1
                            {sortColumn === 'diger1' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className={`text-xs text-right bg-emerald-100 font-bold text-slate-700 cursor-pointer hover:bg-emerald-200 ${visibleColumns.diger2 ? 'hidden md:table-cell' : 'hidden'}`} 
                          style={{ width: '80px', minWidth: '80px' }}
                          onClick={() => handleSort('diger2')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Diğer 2
                            {sortColumn === 'diger2' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className={`text-xs text-right bg-rose-100 font-bold text-slate-700 cursor-pointer hover:bg-rose-200 ${visibleColumns.diger3 ? 'hidden md:table-cell' : 'hidden'}`} 
                          style={{ width: '80px', minWidth: '80px' }}
                          onClick={() => handleSort('diger3')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Diğer 3
                            {sortColumn === 'diger3' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                          </div>
                        </TableHead>
                        {/* Kendisi Ödemiştir Sütunu */}
                        <TableHead 
                          className="text-xs text-center bg-emerald-200 font-bold text-emerald-800 hidden md:table-cell" 
                          style={{ width: '90px', minWidth: '90px' }}
                          title="Vergileri kendisi ödedi işareti"
                        >
                          <div className="flex items-center justify-center gap-1">
                            K.Ödedi
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    
                    {/* VERİLER - TableBody (Başlık ile aynı Table içinde) */}
                    <TableBody>
                      {(() => {
                        // Filtrelenmiş kayıtları hesapla
                        const filteredKayitlar = kayitlar.filter(kayit => {
                            // Gizli kayıt filtresi
                            if (!showHidden && kayit.gorunur === false) return false;
                            
                            // Arama filtresi
                            if (aramaKayit) {
                              const searchLower = aramaKayit.toLowerCase();
                              if (!kayit.ad_soyad?.toLowerCase().includes(searchLower)) {
                                return false;
                              }
                            }
                            
                            // Favori müşteri filtresi
                            if (showFavoriOnly) {
                              const musteri = musteriler.find(m => m.vergi_no === kayit.vergi_no);
                              if (!musteri?.favori) return false;
                            }
                            
                            // Görünür aylar filtresi (müşteri bazında)
                            if (selectedAylar.length > 0 && !showHiddenMusteriler) {
                              const musteri = musteriler.find(m => m.vergi_no === kayit.vergi_no);
                              if (musteri?.gorunur_aylar && musteri.gorunur_aylar.length > 0) {
                                // Müşterinin görünür ayları tanımlıysa, seçili aylarla kesişim kontrol et
                                const seciliAylarKesisim = selectedAylar.some(ay => musteri.gorunur_aylar.includes(ay));
                                if (!seciliAylarKesisim) return false;
                              }
                            }
                            
                            // Özel filtreleme: Ödeme durumuna göre
                            if (hideZeroValues) {
                              const toplam = Number(kayit.toplam_tutar) || 0;
                              const odenen = Number(kayit.odenen) || 0;
                              const kalan = Number(kayit.kalan) || 0;
                              
                              // Ödendi (Tamamen) seçildiğinde: TAM TAHSİLAT (Toplam=Ödenen VE Kalan=0)
                              if (kayitFilters.odeme_durumu === 'odendi_tamamen') {
                                // Önce sıfır kayıtları ele
                                if (toplam === 0 && odenen === 0 && kalan === 0) {
                                  return false; // Sıfır kayıtları gösterme
                                }
                                // Tam tahsilat kontrolü: Toplam = Ödenen VE Kalan = 0 olmalı
                                if (toplam !== odenen || kalan !== 0) {
                                  return false; // Tam ödenmemişleri gösterme
                                }
                              }
                              // Ödenmedi/Kısmen seçildiğinde: Toplam=0 VE Ödenen=0 VE Kalan=0 ise gösterme
                              else if (kayitFilters.odeme_durumu === 'odenmedi_kismen') {
                                if (toplam === 0 && odenen === 0 && kalan === 0) {
                                  return false;
                                }
                              }
                              // Diğer durumlar için eski mantık
                              else if (!kayit.odenen || Number(kayit.odenen) === 0) {
                                const tumDegerlerSifir = 
                                  (!kayit.bakiye || Number(kayit.bakiye) === 0) &&
                                  (!kayit.muhasebe || Number(kayit.muhasebe) === 0) &&
                                  (!kayit.kdv || Number(kayit.kdv) === 0) &&
                                  (!kayit.stopaj || Number(kayit.stopaj) === 0) &&
                                  (!kayit.ssk || Number(kayit.ssk) === 0) &&
                                  (!kayit.bagkur || Number(kayit.bagkur) === 0) &&
                                  (!kayit.gecici_vergi || Number(kayit.gecici_vergi) === 0) &&
                                  (!kayit.gelir_vergisi || Number(kayit.gelir_vergisi) === 0) &&
                                  (!kayit.diger1 || Number(kayit.diger1) === 0) &&
                                  (!kayit.diger2 || Number(kayit.diger2) === 0) &&
                                  (!kayit.diger3 || Number(kayit.diger3) === 0);
                                if (tumDegerlerSifir) return false;
                              }
                            }
                            
                            // Gelişmiş Filtreler - Tutar Filtreleri
                            if (kayitFilters.bakiye_min && kayit.bakiye < parseFloat(kayitFilters.bakiye_min)) return false;
                            if (kayitFilters.bakiye_max && kayit.bakiye > parseFloat(kayitFilters.bakiye_max)) return false;
                            if (kayitFilters.muhasebe_min && kayit.muhasebe < parseFloat(kayitFilters.muhasebe_min)) return false;
                            if (kayitFilters.muhasebe_max && kayit.muhasebe > parseFloat(kayitFilters.muhasebe_max)) return false;
                            if (kayitFilters.kdv_min && kayit.kdv < parseFloat(kayitFilters.kdv_min)) return false;
                            if (kayitFilters.kdv_max && kayit.kdv > parseFloat(kayitFilters.kdv_max)) return false;
                            if (kayitFilters.stopaj_min && kayit.stopaj < parseFloat(kayitFilters.stopaj_min)) return false;
                            if (kayitFilters.stopaj_max && kayit.stopaj > parseFloat(kayitFilters.stopaj_max)) return false;
                            if (kayitFilters.toplam_min && kayit.toplam_tutar < parseFloat(kayitFilters.toplam_min)) return false;
                            if (kayitFilters.toplam_max && kayit.toplam_tutar > parseFloat(kayitFilters.toplam_max)) return false;
                            if (kayitFilters.kalan_min && kayit.kalan < parseFloat(kayitFilters.kalan_min)) return false;
                            if (kayitFilters.kalan_max && kayit.kalan > parseFloat(kayitFilters.kalan_max)) return false;
                            
                            // Ödeme Durumu Filtresi
                            if (kayitFilters.odeme_durumu) {
                              if (kayitFilters.odeme_durumu === 'odendi_tamamen' && kayit.kalan > 0) return false;
                              if (kayitFilters.odeme_durumu === 'odenmedi_kismen' && kayit.kalan === 0) return false;
                            }
                            
                            return true;
                          });
                          
                          // SIRALAMA (Sorting) - Filtrelemeden sonra
                          if (sortColumn && sortDirection) {
                            filteredKayitlar.sort((a, b) => {
                              let valueA, valueB;
                              
                              // toplam_tutar ve kalan için özel hesaplama (kendisi_odemis olanları hariç tut)
                              if (sortColumn === 'toplam_tutar') {
                                valueA = (Number(a.bakiye) || 0) + (Number(a.muhasebe) || 0) +
                                  (a.kdv_aktif !== false && !a.kdv_kendisi_odemis ? (Number(a.kdv) || 0) : 0) +
                                  (a.stopaj_aktif !== false && !a.stopaj_kendisi_odemis ? (Number(a.stopaj) || 0) : 0) +
                                  (a.ssk_aktif !== false && !a.ssk_kendisi_odemis ? (Number(a.ssk) || 0) : 0) +
                                  (a.bagkur_aktif !== false && !a.bagkur_kendisi_odemis ? (Number(a.bagkur) || 0) : 0) +
                                  (a.gecici_vergi_aktif !== false && !a.gecici_vergi_kendisi_odemis ? (Number(a.gecici_vergi) || 0) : 0) +
                                  (a.gelir_vergisi_aktif !== false && !a.gelir_vergisi_kendisi_odemis ? (Number(a.gelir_vergisi) || 0) : 0) +
                                  (Number(a.diger1) || 0) + (Number(a.diger2) || 0) + (Number(a.diger3) || 0);
                                valueB = (Number(b.bakiye) || 0) + (Number(b.muhasebe) || 0) +
                                  (b.kdv_aktif !== false && !b.kdv_kendisi_odemis ? (Number(b.kdv) || 0) : 0) +
                                  (b.stopaj_aktif !== false && !b.stopaj_kendisi_odemis ? (Number(b.stopaj) || 0) : 0) +
                                  (b.ssk_aktif !== false && !b.ssk_kendisi_odemis ? (Number(b.ssk) || 0) : 0) +
                                  (b.bagkur_aktif !== false && !b.bagkur_kendisi_odemis ? (Number(b.bagkur) || 0) : 0) +
                                  (b.gecici_vergi_aktif !== false && !b.gecici_vergi_kendisi_odemis ? (Number(b.gecici_vergi) || 0) : 0) +
                                  (b.gelir_vergisi_aktif !== false && !b.gelir_vergisi_kendisi_odemis ? (Number(b.gelir_vergisi) || 0) : 0) +
                                  (Number(b.diger1) || 0) + (Number(b.diger2) || 0) + (Number(b.diger3) || 0);
                              } else if (sortColumn === 'kalan') {
                                const toplamA = (Number(a.bakiye) || 0) + (Number(a.muhasebe) || 0) +
                                  (a.kdv_aktif !== false && !a.kdv_kendisi_odemis ? (Number(a.kdv) || 0) : 0) +
                                  (a.stopaj_aktif !== false && !a.stopaj_kendisi_odemis ? (Number(a.stopaj) || 0) : 0) +
                                  (a.ssk_aktif !== false && !a.ssk_kendisi_odemis ? (Number(a.ssk) || 0) : 0) +
                                  (a.bagkur_aktif !== false && !a.bagkur_kendisi_odemis ? (Number(a.bagkur) || 0) : 0) +
                                  (a.gecici_vergi_aktif !== false && !a.gecici_vergi_kendisi_odemis ? (Number(a.gecici_vergi) || 0) : 0) +
                                  (a.gelir_vergisi_aktif !== false && !a.gelir_vergisi_kendisi_odemis ? (Number(a.gelir_vergisi) || 0) : 0) +
                                  (Number(a.diger1) || 0) + (Number(a.diger2) || 0) + (Number(a.diger3) || 0);
                                const toplamB = (Number(b.bakiye) || 0) + (Number(b.muhasebe) || 0) +
                                  (b.kdv_aktif !== false && !b.kdv_kendisi_odemis ? (Number(b.kdv) || 0) : 0) +
                                  (b.stopaj_aktif !== false && !b.stopaj_kendisi_odemis ? (Number(b.stopaj) || 0) : 0) +
                                  (b.ssk_aktif !== false && !b.ssk_kendisi_odemis ? (Number(b.ssk) || 0) : 0) +
                                  (b.bagkur_aktif !== false && !b.bagkur_kendisi_odemis ? (Number(b.bagkur) || 0) : 0) +
                                  (b.gecici_vergi_aktif !== false && !b.gecici_vergi_kendisi_odemis ? (Number(b.gecici_vergi) || 0) : 0) +
                                  (b.gelir_vergisi_aktif !== false && !b.gelir_vergisi_kendisi_odemis ? (Number(b.gelir_vergisi) || 0) : 0) +
                                  (Number(b.diger1) || 0) + (Number(b.diger2) || 0) + (Number(b.diger3) || 0);
                                valueA = toplamA - (Number(a.odenen) || 0);
                                valueB = toplamB - (Number(b.odenen) || 0);
                              } else {
                                valueA = a[sortColumn];
                                valueB = b[sortColumn];
                              }
                              
                              // Sayısal değerlere dönüştür
                              valueA = Number(valueA) || 0;
                              valueB = Number(valueB) || 0;
                              
                              if (sortDirection === 'asc') {
                                return valueA - valueB; // Küçükten büyüğe
                              } else {
                                return valueB - valueA; // Büyükten küçüğe
                              }
                            });
                          }
                          
                          // Müşteri adına göre alfabetik sırala (eğer özel sıralama yoksa)
                          if (!sortColumn) {
                            filteredKayitlar.sort((a, b) => {
                              const nameA = (a.ad_soyad || '').toLowerCase();
                              const nameB = (b.ad_soyad || '').toLowerCase();
                              return nameA.localeCompare(nameB, 'tr');
                            });
                          }
                          
                          // "Gizli Müşterileri Göster" toggle açıkken, gizli müşterileri listenin EN ÜSTÜNE getir
                          if (showHiddenMusteriler && selectedAylar.length > 0) {
                            filteredKayitlar.sort((a, b) => {
                              const musteriA = musteriler.find(m => m.vergi_no === a.vergi_no);
                              const musteriB = musteriler.find(m => m.vergi_no === b.vergi_no);
                              const aHidden = musteriA?.gorunur_aylar && musteriA.gorunur_aylar.length > 0 &&
                                !selectedAylar.some(ay => musteriA.gorunur_aylar.includes(ay));
                              const bHidden = musteriB?.gorunur_aylar && musteriB.gorunur_aylar.length > 0 &&
                                !selectedAylar.some(ay => musteriB.gorunur_aylar.includes(ay));
                              if (aHidden && !bHidden) return -1;
                              if (!aHidden && bHidden) return 1;
                              return 0;
                            });
                          }
                        
                        // Toplamları hesapla - sadece aktif vergileri dahil et (gri ve üstü çizili olanları hariç tut)
                        const totals = filteredKayitlar.reduce((acc, kayit) => {
                          // Gizli müşteriler: switch kapalıysa ve bu ayda gizliyse toplama dahil etme
                          if (!showHiddenMusteriler) {
                            const musteriForTotal = musteriler.find(m => m.vergi_no === kayit.vergi_no);
                            if (musteriForTotal?.gorunur_aylar && musteriForTotal.gorunur_aylar.length > 0) {
                              const ayKesisim = selectedAylar.some(ay => musteriForTotal.gorunur_aylar.includes(ay));
                              if (!ayKesisim) return acc;
                            }
                          }
                          acc.bakiye += Number(kayit.bakiye) || 0;
                          acc.muhasebe += Number(kayit.muhasebe) || 0;
                          // Sadece aktif olan VE "kendisi_odemis" olmayan vergileri topla
                          if (kayit.kdv_aktif !== false && !kayit.kdv_kendisi_odemis) acc.kdv += Number(kayit.kdv) || 0;
                          if (kayit.stopaj_aktif !== false && !kayit.stopaj_kendisi_odemis) acc.stopaj += Number(kayit.stopaj) || 0;
                          if (kayit.ssk_aktif !== false && !kayit.ssk_kendisi_odemis) acc.ssk += Number(kayit.ssk) || 0;
                          if (kayit.bagkur_aktif !== false && !kayit.bagkur_kendisi_odemis) acc.bagkur += Number(kayit.bagkur) || 0;
                          if (kayit.gecici_vergi_aktif !== false && !kayit.gecici_vergi_kendisi_odemis) acc.gecici_vergi += Number(kayit.gecici_vergi) || 0;
                          if (kayit.gelir_vergisi_aktif !== false && !kayit.gelir_vergisi_kendisi_odemis) acc.gelir_vergisi += Number(kayit.gelir_vergisi) || 0;
                          acc.diger1 += Number(kayit.diger1) || 0;
                          acc.diger2 += Number(kayit.diger2) || 0;
                          acc.diger3 += Number(kayit.diger3) || 0;
                          // Toplam tutar ve kalan için sadece aktif ve "kendisi_odemis" olmayan vergileri hesapla
                          const aktivToplam = 
                            (Number(kayit.bakiye) || 0) +
                            (Number(kayit.muhasebe) || 0) +
                            (kayit.kdv_aktif !== false && !kayit.kdv_kendisi_odemis ? (Number(kayit.kdv) || 0) : 0) +
                            (kayit.stopaj_aktif !== false && !kayit.stopaj_kendisi_odemis ? (Number(kayit.stopaj) || 0) : 0) +
                            (kayit.ssk_aktif !== false && !kayit.ssk_kendisi_odemis ? (Number(kayit.ssk) || 0) : 0) +
                            (kayit.bagkur_aktif !== false && !kayit.bagkur_kendisi_odemis ? (Number(kayit.bagkur) || 0) : 0) +
                            (kayit.gecici_vergi_aktif !== false && !kayit.gecici_vergi_kendisi_odemis ? (Number(kayit.gecici_vergi) || 0) : 0) +
                            (kayit.gelir_vergisi_aktif !== false && !kayit.gelir_vergisi_kendisi_odemis ? (Number(kayit.gelir_vergisi) || 0) : 0) +
                            (Number(kayit.diger1) || 0) +
                            (Number(kayit.diger2) || 0) +
                            (Number(kayit.diger3) || 0);
                          acc.toplam_tutar += aktivToplam;
                          acc.odenen += Number(kayit.odenen) || 0;
                          acc.kalan += aktivToplam - (Number(kayit.odenen) || 0);
                          return acc;
                        }, {
                          bakiye: 0, muhasebe: 0, kdv: 0, stopaj: 0, ssk: 0, bagkur: 0,
                          gecici_vergi: 0, gelir_vergisi: 0, diger1: 0, diger2: 0, diger3: 0,
                          toplam_tutar: 0, odenen: 0, kalan: 0
                        });
                        
                        return (
                          <>
                            {filteredKayitlar.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={20} className="text-center py-8 text-muted-foreground">
                                  Henüz kayıt bulunmuyor
                                </TableCell>
                              </TableRow>
                            ) : (
                              <>
                                {filteredKayitlar.map((kayit) => {
                          // "Kendisi Ödemiştir" kontrolü - herhangi bir vergi için işaretliyse
                          const hasKendisiOdemis = kayit.kdv_kendisi_odemis || kayit.stopaj_kendisi_odemis || 
                            kayit.ssk_kendisi_odemis || kayit.bagkur_kendisi_odemis || 
                            kayit.gecici_vergi_kendisi_odemis || kayit.gelir_vergisi_kendisi_odemis;
                          
                          // Bu müşteri seçili ay(lar)da gizli mi? (Görünür aylar tanımlı ama bu ay listede değil)
                          const musteriForRow = musteriler.find(m => m.vergi_no === kayit.vergi_no);
                          const isHiddenForCurrentMonth = showHiddenMusteriler && selectedAylar.length > 0 &&
                            musteriForRow?.gorunur_aylar && musteriForRow.gorunur_aylar.length > 0 &&
                            !selectedAylar.some(ay => musteriForRow.gorunur_aylar.includes(ay));
                          
                          return (
                          <TableRow 
                            key={kayit.id} 
                            data-testid={`record-row-${kayit.id}`}
                            onClick={() => setActiveRowId(kayit.id)}
                            className={`cursor-pointer transition-colors ${
                              !kayit.gorunur 
                                ? 'opacity-50 bg-slate-50' 
                                : isHiddenForCurrentMonth
                                ? 'bg-amber-50 border-l-4 border-amber-400 hover:bg-amber-100'
                                : hasKendisiOdemis
                                ? 'bg-emerald-100 border-l-4 border-emerald-500'
                                : activeRowId === kayit.id 
                                ? 'bg-blue-100 border-l-4 border-blue-500' 
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            {/* EXCEL FREEZE PANES: SOL SABİT KOLONLAR - Dinamik Arka Plan */}
                            {/* Checkbox - Mobilde gizli */}
                            <TableCell className={`text-xs sticky left-0 z-10 border-r-2 border-slate-300 hidden md:table-cell ${
                              !kayit.gorunur 
                                ? 'bg-slate-50' 
                                : activeRowId === kayit.id 
                                ? 'bg-blue-100' 
                                : 'bg-white'
                            }`} style={{ width: '30px', minWidth: '30px' }}>
                              <input
                                type="checkbox"
                                checked={selectedKayitlar.includes(kayit.id)}
                                onChange={(e) => handleSelectKayit(kayit.id, e.target.checked)}
                                className="w-3 h-3"
                              />
                            </TableCell>
                            {/* Göz ikonu - Mobilde gizli */}
                            <TableCell className={`text-center text-xs sticky border-r-2 border-slate-300 z-10 hidden md:table-cell ${
                              !kayit.gorunur 
                                ? 'bg-slate-50' 
                                : activeRowId === kayit.id 
                                ? 'bg-blue-100' 
                                : 'bg-white'
                            }`} style={{ width: '30px', minWidth: '30px', left: '30px' }}>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="p-0 h-6 w-6"
                                onClick={() => handleGorunurlukDegistir(kayit.id, !kayit.gorunur)}
                                data-testid={`visibility-btn-${kayit.id}`}
                              >
                                {kayit.gorunur !== false ? (
                                  <Eye className="w-3 h-3 text-green-600" />
                                ) : (
                                  <EyeOff className="w-3 h-3 text-gray-400" />
                                )}
                              </Button>
                            </TableCell>
                            
                            {/* YENİ SÜTUN: Bakiye Kapat İşlem Butonu - Mobilede de görünür, dar */}
                   <TableCell
                        className={`sticky border-r-2 border-orange-200 z-10 flex items-center justify-start ${
                          !kayit.gorunur 
                            ? 'bg-slate-50' 
                            : activeRowId === kayit.id 
                            ? 'bg-orange-100' 
                            : 'bg-orange-50'
                        }`}
                        style={{ width: '35px', minWidth: '35px', maxWidth: '35px', padding: '1px' }}
                      >
                        {(() => {
                          // Bakiye Kapat için toplam ve kalan hesapla (kendisi_odemis olanları hariç tut)
                          const bakiyeKapatToplam = 
                            (Number(kayit.bakiye) || 0) +
                            (Number(kayit.muhasebe) || 0) +
                            (kayit.kdv_aktif !== false && !kayit.kdv_kendisi_odemis ? (Number(kayit.kdv) || 0) : 0) +
                            (kayit.stopaj_aktif !== false && !kayit.stopaj_kendisi_odemis ? (Number(kayit.stopaj) || 0) : 0) +
                            (kayit.ssk_aktif !== false && !kayit.ssk_kendisi_odemis ? (Number(kayit.ssk) || 0) : 0) +
                            (kayit.bagkur_aktif !== false && !kayit.bagkur_kendisi_odemis ? (Number(kayit.bagkur) || 0) : 0) +
                            (kayit.gecici_vergi_aktif !== false && !kayit.gecici_vergi_kendisi_odemis ? (Number(kayit.gecici_vergi) || 0) : 0) +
                            (kayit.gelir_vergisi_aktif !== false && !kayit.gelir_vergisi_kendisi_odemis ? (Number(kayit.gelir_vergisi) || 0) : 0) +
                            (Number(kayit.diger1) || 0) +
                            (Number(kayit.diger2) || 0) +
                            (Number(kayit.diger3) || 0);
                          const bakiyeKapatKalan = bakiyeKapatToplam - (Number(kayit.odenen) || 0);
                          
                          return bakiyeKapatKalan > 0 && !kayit._isTemp && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="p-0 h-5 w-5 md:h-7 md:w-7 hover:bg-orange-200"
                            onClick={async (e) => {
                              e.stopPropagation();

                              if (window.confirm(
                                `${kayit.ad_soyad} - ${kayit.ay} ${kayit.yil}\n\nToplam Borç: ${formatCurrency(bakiyeKapatToplam)}\nÖdenen: ${formatCurrency(kayit.odenen)}\nKalan: ${formatCurrency(bakiyeKapatKalan)}\n\nBakiye kapatılacaktır. Onaylıyor musunuz?`
                              )) {
                                try {
                                  await axios.put(`${API}/vergi-kayit/${kayit.id}`, {
                                    odenen: bakiyeKapatToplam
                                  });
                                  toast.success(`${kayit.ad_soyad} - Bakiye kapatıldı`);
                                  await fetchKayitlar();
                                } catch (error) {
                                  console.error('Bakiye kapatma hatası:', error);
                                  toast.error('Bakiye kapatılamadı');
                                }
                              }
                            }}
                            title="Bakiye Kapat"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-4 h-4 text-orange-600"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </Button>
                        );
                        })()}
                      </TableCell>

                            
                            {/* Müşteri Adı - Mobilede left:0, masaüstünde left:90px */}
                            <TableCell className={`text-xs sticky border-r-2 border-slate-300 z-10 ${
                              !kayit.gorunur 
                                ? 'bg-slate-50' 
                                : activeRowId === kayit.id 
                                ? 'bg-blue-100' 
                                : 'bg-white'
                            }`} style={{ width: '150px', minWidth: '135px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <span
                                className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer hover:underline text-[10px] md:text-xs"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRaporOlustur(kayit.vergi_no, kayit.ad_soyad);
                                }}
                                title={kayit.ad_soyad}
                              >
                                {kayit.ad_soyad.split(' ').slice(0, 3).join(' ')}
                              </span>
                            </TableCell>
                            {/* Ay - Mobilede de görünür, dar */}
                            <TableCell className={`text-[10px] md:text-xs sticky border-r-2 border-slate-300 z-10 ${
                              !kayit.gorunur 
                                ? 'bg-slate-50' 
                                : activeRowId === kayit.id 
                                ? 'bg-blue-100' 
                                : 'bg-white'
                            }`} style={{ width: '40px', minWidth: '35px', maxWidth: '45px', padding: '2px' }}>{kayit.ay?.substring(0, 3)}</TableCell>
                            {/* Satır için hesaplanan toplam - kendisi_odemis olanları hariç tut */}
                            {(() => {
                              const satirToplam = 
                                (Number(kayit.bakiye) || 0) +
                                (Number(kayit.muhasebe) || 0) +
                                (kayit.kdv_aktif !== false && !kayit.kdv_kendisi_odemis ? (Number(kayit.kdv) || 0) : 0) +
                                (kayit.stopaj_aktif !== false && !kayit.stopaj_kendisi_odemis ? (Number(kayit.stopaj) || 0) : 0) +
                                (kayit.ssk_aktif !== false && !kayit.ssk_kendisi_odemis ? (Number(kayit.ssk) || 0) : 0) +
                                (kayit.bagkur_aktif !== false && !kayit.bagkur_kendisi_odemis ? (Number(kayit.bagkur) || 0) : 0) +
                                (kayit.gecici_vergi_aktif !== false && !kayit.gecici_vergi_kendisi_odemis ? (Number(kayit.gecici_vergi) || 0) : 0) +
                                (kayit.gelir_vergisi_aktif !== false && !kayit.gelir_vergisi_kendisi_odemis ? (Number(kayit.gelir_vergisi) || 0) : 0) +
                                (Number(kayit.diger1) || 0) +
                                (Number(kayit.diger2) || 0) +
                                (Number(kayit.diger3) || 0);
                              const satirKalan = satirToplam - (Number(kayit.odenen) || 0);
                              return (
                                <>
                            {/* MOBİL: Kalan (önce) */}
                            <TableCell className={`text-right font-extrabold text-[10px] sticky border-x-2 border-red-200 shadow-lg z-10 md:hidden ${
                              satirKalan < 0 ? 'text-red-700' : 'text-red-900'
                            } ${
                              !kayit.gorunur 
                                ? 'bg-slate-50' 
                                : activeRowId === kayit.id 
                                ? 'bg-red-200' 
                                : 'bg-red-50'
                            }`} style={{ width: '68px', minWidth: '68px', padding: '2px 4px' }}>{formatCurrency(satirKalan)}</TableCell>
                            {/* MOBİL: Ödenen (ortada) */}
                            <TableCell 
                              className={`text-right text-[10px] cursor-pointer sticky border-x-2 border-green-200 font-bold text-green-900 z-10 md:hidden ${
                                !kayit.gorunur 
                                  ? 'bg-slate-50' 
                                  : activeRowId === kayit.id 
                                  ? 'bg-green-200' 
                                  : 'bg-green-50 hover:bg-green-100'
                              }`}
                              style={{ width: '68px', minWidth: '68px', padding: '2px 4px' }}
                              onDoubleClick={() => handleOdemeAc(kayit)}
                              title="Çift tıklayarak ödeme ekleyin"
                            >
                              {formatCurrency(kayit.odenen)}
                            </TableCell>
                            {/* MOBİL: Toplam (sonda) */}
                            <TableCell className={`text-right font-extrabold text-[10px] sticky border-x-2 border-amber-200 z-10 md:hidden ${
                              satirToplam < 0 ? 'text-red-700' : 'text-amber-900'
                            } ${
                              !kayit.gorunur 
                                ? 'bg-slate-50' 
                                : activeRowId === kayit.id 
                                ? 'bg-amber-200' 
                                : 'bg-amber-50'
                            }`} style={{ width: '68px', minWidth: '68px', padding: '2px 4px' }}>{formatCurrency(satirToplam)}</TableCell>
                            {/* MASAÜSTÜ: Toplam - Sadece md+ */}
                            <TableCell className={`text-right font-extrabold text-xs sticky border-x-2 border-amber-200 z-10 hidden md:table-cell ${
                              satirToplam < 0 ? 'text-red-700' : 'text-amber-900'
                            } ${
                              !kayit.gorunur 
                                ? 'bg-slate-50' 
                                : activeRowId === kayit.id 
                                ? 'bg-amber-200' 
                                : 'bg-amber-50'
                            }`} style={{ width: '100px', minWidth: '105px', padding: '2px 4px' }}>{formatCurrency(satirToplam)}</TableCell>
                            {/* MASAÜSTÜ: Ödenen - Sadece md+ */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer sticky border-x-2 border-green-200 font-bold text-green-900 z-10 hidden md:table-cell ${
                                !kayit.gorunur 
                                  ? 'bg-slate-50' 
                                  : activeRowId === kayit.id 
                                  ? 'bg-green-200' 
                                  : 'bg-green-50 hover:bg-green-100'
                              }`}
                              style={{ width: '100px', minWidth: '105px', padding: '2px 4px' }}
                              onDoubleClick={() => handleOdemeAc(kayit)}
                              title="Çift tıklayarak ödeme ekleyin"
                            >
                              {formatCurrency(kayit.odenen)}
                            </TableCell>
                            {/* MASAÜSTÜ: Kalan - Sadece md+ */}
                            <TableCell className={`text-right font-extrabold text-xs sticky border-x-2 border-red-200 shadow-lg z-10 hidden md:table-cell ${
                              satirKalan < 0 ? 'text-red-700' : 'text-red-900'
                            } ${
                              !kayit.gorunur 
                                ? 'bg-slate-50' 
                                : activeRowId === kayit.id 
                                ? 'bg-red-200' 
                                : 'bg-red-50'
                            }`} style={{ width: '100px', minWidth: '105px', padding: '2px 4px' }}>{formatCurrency(satirKalan)}</TableCell>
                                </>
                              );
                            })()}
                            {/* Bakiye - Mobilde gizli */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer hover:bg-blue-50 hidden md:table-cell ${
                                kayit.bakiye < 0 ? 'text-red-700 font-bold' : ''
                              } ${!visibleColumns.bakiye ? 'hidden' : ''}`} 
                              style={{ width: '85px', minWidth: '85px' }}
                              onDoubleClick={() => handleCellDoubleClick(kayit.id, 'bakiye', kayit.bakiye)}
                            >
                              {editingCell.kayitId === kayit.id && editingCell.field === 'bakiye' ? (
                                <Input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  className="h-6 text-xs text-right"
                                />
                              ) : (
                                formatCurrency(kayit.bakiye)
                              )}
                            </TableCell>
                            {/* Muhasebe - Mobilde gizli */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer hover:bg-purple-200 bg-purple-100 ${visibleColumns.muhasebe ? 'hidden md:table-cell' : 'hidden'}`} 
                              style={{ width: '90px', minWidth: '90px' }}
                              onDoubleClick={() => handleCellDoubleClick(kayit.id, 'muhasebe', kayit.muhasebe)}
                            >
                              {editingCell.kayitId === kayit.id && editingCell.field === 'muhasebe' ? (
                                <Input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  className="h-6 text-xs text-right"
                                />
                              ) : (
                                formatCurrency(kayit.muhasebe)
                              )}
                            </TableCell>
                            {/* KDV - Mobilde gizli */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer hover:bg-blue-200 bg-blue-100 ${visibleColumns.kdv ? 'hidden md:table-cell' : 'hidden'} ${kayit.kdv_aktif === false ? 'text-gray-400 line-through' : ''} ${kayit.kdv_kendisi_odemis ? 'text-gray-400 line-through opacity-60' : ''}`} 
                              style={{ width: '85px', minWidth: '85px' }}
                              onDoubleClick={() => handleCellDoubleClick(kayit.id, 'kdv', kayit.kdv)}
                            >
                              {editingCell.kayitId === kayit.id && editingCell.field === 'kdv' ? (
                                <Input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  className="h-6 text-xs text-right"
                                />
                              ) : (
                                formatCurrency(kayit.kdv)
                              )}
                            </TableCell>
                            {/* Stopaj - Mobilde gizli */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer hover:bg-orange-200 bg-orange-100 ${visibleColumns.stopaj ? 'hidden md:table-cell' : 'hidden'} ${kayit.stopaj_aktif === false ? 'text-gray-400 line-through' : ''} ${kayit.stopaj_kendisi_odemis ? 'text-gray-400 line-through opacity-60' : ''}`} 
                              style={{ width: '85px', minWidth: '85px' }}
                              onDoubleClick={() => handleCellDoubleClick(kayit.id, 'stopaj', kayit.stopaj)}
                            >
                              {editingCell.kayitId === kayit.id && editingCell.field === 'stopaj' ? (
                                <Input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  className="h-6 text-xs text-right"
                                />
                              ) : (
                                formatCurrency(kayit.stopaj)
                              )}
                            </TableCell>
                            {/* SSK - Mobilde gizli */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer hover:bg-teal-200 bg-teal-100 ${visibleColumns.ssk ? 'hidden md:table-cell' : 'hidden'} ${kayit.ssk_aktif === false ? 'text-gray-400 line-through' : ''} ${kayit.ssk_kendisi_odemis ? 'text-gray-400 line-through opacity-60' : ''}`} 
                              style={{ width: '85px', minWidth: '85px' }}
                              onDoubleClick={() => handleCellDoubleClick(kayit.id, 'ssk', kayit.ssk)}
                            >
                              {editingCell.kayitId === kayit.id && editingCell.field === 'ssk' ? (
                                <Input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  className="h-6 text-xs text-right"
                                />
                              ) : (
                                formatCurrency(kayit.ssk)
                              )}
                            </TableCell>
                            {/* BAĞKUR - Mobilde gizli */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer hover:bg-pink-200 bg-pink-100 ${visibleColumns.bagkur ? 'hidden md:table-cell' : 'hidden'} ${kayit.bagkur_aktif === false ? 'text-gray-400 line-through' : ''} ${kayit.bagkur_kendisi_odemis ? 'text-gray-400 line-through opacity-60' : ''}`} 
                              style={{ width: '85px', minWidth: '85px' }}
                              onDoubleClick={() => handleCellDoubleClick(kayit.id, 'bagkur', kayit.bagkur)}
                            >
                              {editingCell.kayitId === kayit.id && editingCell.field === 'bagkur' ? (
                                <Input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  className="h-6 text-xs text-right"
                                />
                              ) : (
                                formatCurrency(kayit.bagkur)
                              )}
                            </TableCell>
                            {/* Geçici Vergi - Mobilde gizli */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer hover:bg-indigo-200 bg-indigo-100 ${visibleColumns.gecici_vergi ? 'hidden md:table-cell' : 'hidden'} ${kayit.gecici_vergi_aktif === false ? 'text-gray-400 line-through' : ''} ${kayit.gecici_vergi_kendisi_odemis ? 'text-gray-400 line-through opacity-60' : ''}`} 
                              style={{ width: '85px', minWidth: '85px' }}
                              onDoubleClick={() => handleCellDoubleClick(kayit.id, 'gecici_vergi', kayit.gecici_vergi)}
                            >
                              {editingCell.kayitId === kayit.id && editingCell.field === 'gecici_vergi' ? (
                                <Input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  className="h-6 text-xs text-right"
                                />
                              ) : (
                                formatCurrency(kayit.gecici_vergi)
                              )}
                            </TableCell>
                            {/* Gelir Vergisi - Mobilde gizli */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer hover:bg-cyan-200 bg-cyan-100 ${visibleColumns.gelir_vergisi ? 'hidden md:table-cell' : 'hidden'} ${kayit.gelir_vergisi_aktif === false ? 'text-gray-400 line-through' : ''} ${kayit.gelir_vergisi_kendisi_odemis ? 'text-gray-400 line-through opacity-60' : ''}`} 
                              style={{ width: '85px', minWidth: '85px' }}
                              onDoubleClick={() => handleCellDoubleClick(kayit.id, 'gelir_vergisi', kayit.gelir_vergisi)}
                            >
                              {editingCell.kayitId === kayit.id && editingCell.field === 'gelir_vergisi' ? (
                                <Input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  className="h-6 text-xs text-right"
                                />
                              ) : (
                                formatCurrency(kayit.gelir_vergisi)
                              )}
                            </TableCell>
                            {/* Diğer 1 - Mobilde gizli */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer hover:bg-lime-200 bg-lime-100 ${visibleColumns.diger1 ? 'hidden md:table-cell' : 'hidden'}`} 
                              style={{ width: '80px', minWidth: '80px' }}
                              onDoubleClick={() => handleCellDoubleClick(kayit.id, 'diger1', kayit.diger1)}
                            >
                              {editingCell.kayitId === kayit.id && editingCell.field === 'diger1' ? (
                                <Input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  className="h-6 text-xs text-right"
                                />
                              ) : (
                                formatCurrency(kayit.diger1)
                              )}
                            </TableCell>
                            {/* Diğer 2 - Mobilde gizli */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer hover:bg-emerald-200 bg-emerald-100 ${visibleColumns.diger2 ? 'hidden md:table-cell' : 'hidden'}`} 
                              style={{ width: '80px', minWidth: '80px' }}
                              onDoubleClick={() => handleCellDoubleClick(kayit.id, 'diger2', kayit.diger2)}
                            >
                              {editingCell.kayitId === kayit.id && editingCell.field === 'diger2' ? (
                                <Input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  className="h-6 text-xs text-right"
                                />
                              ) : (
                                formatCurrency(kayit.diger2)
                              )}
                            </TableCell>
                            {/* Diğer 3 - Mobilde gizli */}
                            <TableCell 
                              className={`text-right text-xs cursor-pointer hover:bg-rose-200 bg-rose-100 ${visibleColumns.diger3 ? 'hidden md:table-cell' : 'hidden'}`} 
                              style={{ width: '80px', minWidth: '80px' }}
                              onDoubleClick={() => handleCellDoubleClick(kayit.id, 'diger3', kayit.diger3)}
                            >
                              {editingCell.kayitId === kayit.id && editingCell.field === 'diger3' ? (
                                <Input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  className="h-6 text-xs text-right"
                                />
                              ) : (
                                formatCurrency(kayit.diger3)
                              )}
                            </TableCell>
                            {/* Kendisi Ödemiştir Sütunu */}
                            <TableCell 
                              className={`text-center text-xs hidden md:table-cell ${hasKendisiOdemis ? 'bg-emerald-200' : 'bg-emerald-50'}`}
                              style={{ width: '90px', minWidth: '90px' }}
                            >
                              <Button
                                size="sm"
                                variant={hasKendisiOdemis ? "default" : "outline"}
                                className={`h-6 px-2 text-xs ${hasKendisiOdemis ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-100'}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  // Toggle - eğer herhangi biri işaretliyse hepsini kaldır, değilse hepsini işaretle
                                  const newValue = !hasKendisiOdemis;
                                  try {
                                    await axios.put(`${API}/vergi-kayit/${kayit.id}`, {
                                      kdv_kendisi_odemis: newValue,
                                      stopaj_kendisi_odemis: newValue,
                                      ssk_kendisi_odemis: newValue,
                                      bagkur_kendisi_odemis: newValue,
                                      gecici_vergi_kendisi_odemis: newValue,
                                      gelir_vergisi_kendisi_odemis: newValue
                                    });
                                    toast.success(newValue ? 'Kendisi ödedi olarak işaretlendi' : 'İşaret kaldırıldı');
                                    fetchKayitlar();
                                  } catch (error) {
                                    console.error('Güncelleme hatası:', error);
                                    toast.error('Güncelleme hatası');
                                  }
                                }}
                                title={hasKendisiOdemis ? 'Tüm vergilerde "Kendisi Ödedi" işaretini kaldır' : 'Tüm vergileri "Kendisi Ödedi" olarak işaretle'}
                              >
                                {hasKendisiOdemis ? '✓ Ödedi' : 'İşaretle'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                        })}
                      </>
                    )}
                  </>
                );
              })()}
                    </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
              
              {/* TOPLAM SATIRI - Sticky Footer - CardContent Dışında */}
              <div 
                id="footer-scroll"
                className="sticky bottom-0 border-t-4 border-blue-400 bg-blue-100 overflow-x-auto shadow-md z-20"
                style={{ 
                  scrollbarWidth: 'none', 
                  msOverflowStyle: 'none'
                }}
              >
                <style>
                  {`#footer-scroll::-webkit-scrollbar { display: none; }`}
                </style>
                
                {/* Başlık Satırı - Mobil */}
                <div style={{ minWidth: '0', fontSize: '0.75rem' }} className="flex md:hidden font-semibold border-b border-blue-300 bg-blue-50">
                  <div style={{ width: '214px', minWidth: '214px' }} className="py-1 px-2 bg-blue-50"></div>
                  <div style={{ width: '68px', minWidth: '68px' }} className="py-1 px-2 text-center text-amber-800 bg-amber-100">Toplam</div>
                  <div style={{ width: '68px', minWidth: '68px' }} className="py-1 px-2 text-center text-green-800 bg-green-100">Ödenen</div>
                  <div style={{ width: '68px', minWidth: '68px' }} className="py-1 px-2 text-center text-red-800 bg-red-100">Kalan</div>
                </div>
                    
                {/* Başlık Satırı - Masaüstü */}
                <div style={{ minWidth: '1600px', fontSize: '0.75rem' }} className="hidden md:flex font-semibold border-b border-blue-300 bg-blue-50">
                  <div style={{ width: '150px', minWidth: '150px', left: '65px' }} className="py-1 px-2 text-blue-700 sticky bg-blue-50 z-10"></div>
                  <div style={{ width: '90px', minWidth: '90px' }} className="py-1 px-2 text-center text-amber-800 sticky bg-amber-100 z-10">Toplam</div>
                  <div style={{ width: '90px', minWidth: '90px' }} className="py-1 px-2 text-center text-green-800 sticky bg-green-100 z-10">Ödenen</div>
                  <div style={{ width: '90px', minWidth: '90px' }} className="py-1 px-2 text-center text-red-800 sticky bg-red-100 z-10">Kalan</div>
                  <div style={{ width: '85px', minWidth: '85px' }} className={`py-1 px-2 text-center text-blue-700 ${!visibleColumns.bakiye ? 'hidden' : ''}`}>Bakiye</div>
                  <div style={{ width: '90px', minWidth: '90px' }} className={`py-1 px-2 text-center text-blue-700 ${!visibleColumns.muhasebe ? 'hidden' : ''}`}>Muhasebe</div>
                  <div style={{ width: '85px', minWidth: '85px' }} className={`py-1 px-2 text-center text-blue-700 ${!visibleColumns.kdv ? 'hidden' : ''}`}>KDV</div>
                  <div style={{ width: '70px', minWidth: '70px' }} className={`py-1 px-2 text-center text-blue-700 ${!visibleColumns.stopaj ? 'hidden' : ''}`}>Stopaj</div>
                  <div style={{ width: '70px', minWidth: '70px' }} className={`py-1 px-2 text-center text-blue-700 ${!visibleColumns.ssk ? 'hidden' : ''}`}>SSK</div>
                  <div style={{ width: '70px', minWidth: '70px' }} className={`py-1 px-2 text-center text-blue-700 ${!visibleColumns.bagkur ? 'hidden' : ''}`}>Bağkur</div>
                  <div style={{ width: '70px', minWidth: '70px' }} className={`py-1 px-2 text-center text-blue-700 ${!visibleColumns.gecici_vergi ? 'hidden' : ''}`}>Geçici V.</div>
                  <div style={{ width: '70px', minWidth: '70px' }} className={`py-1 px-2 text-center text-blue-700 ${!visibleColumns.gelir_vergisi ? 'hidden' : ''}`}>Gelir V.</div>
                  <div style={{ width: '70px', minWidth: '70px' }} className={`py-1 px-2 text-center text-blue-700 ${!visibleColumns.diger1 ? 'hidden' : ''}`}>Diğer 1</div>
                  <div style={{ width: '70px', minWidth: '70px' }} className={`py-1 px-2 text-center text-blue-700 ${!visibleColumns.diger2 ? 'hidden' : ''}`}>Diğer 2</div>
                  <div style={{ width: '70px', minWidth: '70px' }} className={`py-1 px-2 text-center text-blue-700 ${!visibleColumns.diger3 ? 'hidden' : ''}`}>Diğer 3</div>
                  <div style={{ width: '100px', minWidth: '100px' }} className="py-1 px-2"></div>
                </div>
                
                {/* Değerler Satırı */}
                <div style={{ minWidth: '1600px', display: 'flex', fontSize: '0.875rem' }} className="font-bold">
                      {(() => {
                        const filteredKayitlar = kayitlar.filter(kayit => {
                          if (!showHidden && kayit.gorunur === false) return false;
                          if (aramaKayit) {
                            const searchLower = aramaKayit.toLowerCase();
                            if (!kayit.ad_soyad?.toLowerCase().includes(searchLower)) return false;
                          }
                          if (showFavoriOnly) {
                            const musteri = musteriler.find(m => m.vergi_no === kayit.vergi_no);
                            if (!musteri?.favori) return false;
                          }
                          // Özel filtreleme: Ödeme durumuna göre
                          if (hideZeroValues) {
                            const toplam = Number(kayit.toplam_tutar) || 0;
                            const odenen = Number(kayit.odenen) || 0;
                            const kalan = Number(kayit.kalan) || 0;
                            
                            // Ödendi (Tamamen) seçildiğinde: TAM TAHSİLAT (Toplam=Ödenen VE Kalan=0)
                            if (kayitFilters.odeme_durumu === 'odendi_tamamen') {
                              // Önce sıfır kayıtları ele
                              if (toplam === 0 && odenen === 0 && kalan === 0) {
                                return false; // Sıfır kayıtları gösterme
                              }
                              // Tam tahsilat kontrolü: Toplam = Ödenen VE Kalan = 0 olmalı
                              if (toplam !== odenen || kalan !== 0) {
                                return false; // Tam ödenmemişleri gösterme
                              }
                            }
                            // Ödenmedi/Kısmen seçildiğinde: Toplam=0 VE Ödenen=0 VE Kalan=0 ise gösterme
                            else if (kayitFilters.odeme_durumu === 'odenmedi_kismen') {
                              if (toplam === 0 && odenen === 0 && kalan === 0) {
                                return false;
                              }
                            }
                            // Diğer durumlar için eski mantık
                            else if (!kayit.odenen || Number(kayit.odenen) === 0) {
                              const tumDegerlerSifir = 
                                (!kayit.bakiye || Number(kayit.bakiye) === 0) &&
                                (!kayit.muhasebe || Number(kayit.muhasebe) === 0) &&
                                (!kayit.kdv || Number(kayit.kdv) === 0) &&
                                (!kayit.stopaj || Number(kayit.stopaj) === 0) &&
                                (!kayit.ssk || Number(kayit.ssk) === 0) &&
                                (!kayit.bagkur || Number(kayit.bagkur) === 0) &&
                                (!kayit.gecici_vergi || Number(kayit.gecici_vergi) === 0) &&
                                (!kayit.gelir_vergisi || Number(kayit.gelir_vergisi) === 0) &&
                                (!kayit.diger1 || Number(kayit.diger1) === 0) &&
                                (!kayit.diger2 || Number(kayit.diger2) === 0) &&
                                (!kayit.diger3 || Number(kayit.diger3) === 0);
                              if (tumDegerlerSifir) return false;
                            }
                          }
                          if (kayitFilters.bakiye_min && kayit.bakiye < parseFloat(kayitFilters.bakiye_min)) return false;
                          if (kayitFilters.bakiye_max && kayit.bakiye > parseFloat(kayitFilters.bakiye_max)) return false;
                          if (kayitFilters.muhasebe_min && kayit.muhasebe < parseFloat(kayitFilters.muhasebe_min)) return false;
                          if (kayitFilters.muhasebe_max && kayit.muhasebe > parseFloat(kayitFilters.muhasebe_max)) return false;
                          if (kayitFilters.kdv_min && kayit.kdv < parseFloat(kayitFilters.kdv_min)) return false;
                          if (kayitFilters.kdv_max && kayit.kdv > parseFloat(kayitFilters.kdv_max)) return false;
                          if (kayitFilters.stopaj_min && kayit.stopaj < parseFloat(kayitFilters.stopaj_min)) return false;
                          if (kayitFilters.stopaj_max && kayit.stopaj > parseFloat(kayitFilters.stopaj_max)) return false;
                          if (kayitFilters.toplam_min && kayit.toplam_tutar < parseFloat(kayitFilters.toplam_min)) return false;
                          if (kayitFilters.toplam_max && kayit.toplam_tutar > parseFloat(kayitFilters.toplam_max)) return false;
                          if (kayitFilters.kalan_min && kayit.kalan < parseFloat(kayitFilters.kalan_min)) return false;
                          if (kayitFilters.kalan_max && kayit.kalan > parseFloat(kayitFilters.kalan_max)) return false;
                          if (kayitFilters.odeme_durumu) {
                            if (kayitFilters.odeme_durumu === 'odendi_tamamen' && kayit.kalan > 0) return false;
                            if (kayitFilters.odeme_durumu === 'odenmedi_kismen' && kayit.kalan === 0) return false;
                          }
                          return true;
                        });
                        
                        const totals = filteredKayitlar.reduce((acc, kayit) => {
                          // Gizli müşteriler: switch kapalıysa ve bu ayda gizliyse toplama dahil etme
                          if (!showHiddenMusteriler) {
                            const musteriForTotal2 = musteriler.find(m => m.vergi_no === kayit.vergi_no);
                            if (musteriForTotal2?.gorunur_aylar && musteriForTotal2.gorunur_aylar.length > 0) {
                              const ayKesisim2 = selectedAylar.some(ay => musteriForTotal2.gorunur_aylar.includes(ay));
                              if (!ayKesisim2) return acc;
                            }
                          }
                          acc.bakiye += Number(kayit.bakiye) || 0;
                          acc.muhasebe += Number(kayit.muhasebe) || 0;
                          // Sadece aktif VE "kendisi_odemis" olmayan vergileri topla
                          if (kayit.kdv_aktif !== false && !kayit.kdv_kendisi_odemis) acc.kdv += Number(kayit.kdv) || 0;
                          if (kayit.stopaj_aktif !== false && !kayit.stopaj_kendisi_odemis) acc.stopaj += Number(kayit.stopaj) || 0;
                          if (kayit.ssk_aktif !== false && !kayit.ssk_kendisi_odemis) acc.ssk += Number(kayit.ssk) || 0;
                          if (kayit.bagkur_aktif !== false && !kayit.bagkur_kendisi_odemis) acc.bagkur += Number(kayit.bagkur) || 0;
                          if (kayit.gecici_vergi_aktif !== false && !kayit.gecici_vergi_kendisi_odemis) acc.gecici_vergi += Number(kayit.gecici_vergi) || 0;
                          if (kayit.gelir_vergisi_aktif !== false && !kayit.gelir_vergisi_kendisi_odemis) acc.gelir_vergisi += Number(kayit.gelir_vergisi) || 0;
                          acc.diger1 += Number(kayit.diger1) || 0;
                          acc.diger2 += Number(kayit.diger2) || 0;
                          acc.diger3 += Number(kayit.diger3) || 0;
                          // Toplam tutar ve kalan için sadece aktif ve "kendisi_odemis" olmayan vergileri hesapla
                          const aktivToplam = 
                            (Number(kayit.bakiye) || 0) +
                            (Number(kayit.muhasebe) || 0) +
                            (kayit.kdv_aktif !== false && !kayit.kdv_kendisi_odemis ? (Number(kayit.kdv) || 0) : 0) +
                            (kayit.stopaj_aktif !== false && !kayit.stopaj_kendisi_odemis ? (Number(kayit.stopaj) || 0) : 0) +
                            (kayit.ssk_aktif !== false && !kayit.ssk_kendisi_odemis ? (Number(kayit.ssk) || 0) : 0) +
                            (kayit.bagkur_aktif !== false && !kayit.bagkur_kendisi_odemis ? (Number(kayit.bagkur) || 0) : 0) +
                            (kayit.gecici_vergi_aktif !== false && !kayit.gecici_vergi_kendisi_odemis ? (Number(kayit.gecici_vergi) || 0) : 0) +
                            (kayit.gelir_vergisi_aktif !== false && !kayit.gelir_vergisi_kendisi_odemis ? (Number(kayit.gelir_vergisi) || 0) : 0) +
                            (Number(kayit.diger1) || 0) +
                            (Number(kayit.diger2) || 0) +
                            (Number(kayit.diger3) || 0);
                          acc.toplam_tutar += aktivToplam;
                          acc.odenen += Number(kayit.odenen) || 0;
                          acc.kalan += aktivToplam - (Number(kayit.odenen) || 0);
                          return acc;
                        }, {
                          bakiye: 0, muhasebe: 0, kdv: 0, stopaj: 0, ssk: 0, bagkur: 0,
                          gecici_vergi: 0, gelir_vergisi: 0, diger1: 0, diger2: 0, diger3: 0,
                          toplam_tutar: 0, odenen: 0, kalan: 0
                        });
                        
                        return (
                          <>
                            {/* Mobil */}
                            <div className="flex md:hidden">
                              <div style={{ width: '214px', minWidth: '214px' }} className="py-3 px-2 bg-blue-100"></div>
                              <div style={{ width: '68px', minWidth: '68px' }} className="py-3 px-2 text-right bg-red-200 border-x-2 border-red-400 text-red-950 font-semibold text-sm">{formatCurrency(totals.kalan)}</div>
                              <div style={{ width: '68px', minWidth: '68px' }} className="py-3 px-2 text-right bg-green-200 border-x-2 border-green-400 text-green-950 font-semibold text-sm">{formatCurrency(totals.odenen)}</div>
                              <div style={{ width: '68px', minWidth: '68px' }} className="py-3 px-2 text-right bg-amber-200 border-x-2 border-amber-400 text-amber-950 font-semibold text-sm">{formatCurrency(totals.toplam_tutar)}</div>
                            </div>

                            {/* Masaüstü */}
                            <div className="hidden md:flex">
                              <div style={{ width: '150px', minWidth: '150px', left: '65px' }} className="py-3 px-2 text-blue-800 font-extrabold text-base sticky bg-blue-100 z-10">TOPLAM</div>
                              <div style={{ width: '90px', minWidth: '90px' }} className="py-3 px-2 text-right bg-amber-200 border-x-2 border-amber-400 text-amber-950 font-semibold text-sm sticky z-10">{formatCurrency(totals.toplam_tutar)}</div>
                              <div style={{ width: '90px', minWidth: '90px' }} className="py-3 px-2 text-right bg-green-200 border-x-2 border-green-400 text-green-950 font-semibold text-sm sticky z-10">{formatCurrency(totals.odenen)}</div>
                              <div style={{ width: '90px', minWidth: '90px' }} className="py-3 px-2 text-right bg-red-200 border-x-2 border-red-400 text-red-950 font-semibold text-sm sticky z-10">{formatCurrency(totals.kalan)}</div>
                              <div style={{ width: '85px', minWidth: '85px' }} className={`py-3 px-2 text-right text-blue-800 font-semibold ${!visibleColumns.bakiye ? 'hidden' : ''}`}>{formatCurrency(totals.bakiye)}</div>
                              <div style={{ width: '90px', minWidth: '90px' }} className={`py-3 px-2 text-right text-blue-800 font-semibold ${!visibleColumns.muhasebe ? 'hidden' : ''}`}>{formatCurrency(totals.muhasebe)}</div>
                              <div style={{ width: '85px', minWidth: '85px' }} className={`py-3 px-2 text-right text-blue-800 font-semibold ${!visibleColumns.kdv ? 'hidden' : ''}`}>{formatCurrency(totals.kdv)}</div>
                              <div style={{ width: '70px', minWidth: '70px' }} className={`py-3 px-2 text-right text-blue-800 font-semibold ${!visibleColumns.stopaj ? 'hidden' : ''}`}>{formatCurrency(totals.stopaj)}</div>
                              <div style={{ width: '70px', minWidth: '70px' }} className={`py-3 px-2 text-right text-blue-800 font-semibold ${!visibleColumns.ssk ? 'hidden' : ''}`}>{formatCurrency(totals.ssk)}</div>
                              <div style={{ width: '70px', minWidth: '70px' }} className={`py-3 px-2 text-right text-blue-800 font-semibold ${!visibleColumns.bagkur ? 'hidden' : ''}`}>{formatCurrency(totals.bagkur)}</div>
                              <div style={{ width: '70px', minWidth: '70px' }} className={`py-3 px-2 text-right text-blue-800 font-semibold ${!visibleColumns.gecici_vergi ? 'hidden' : ''}`}>{formatCurrency(totals.gecici_vergi)}</div>
                              <div style={{ width: '70px', minWidth: '70px' }} className={`py-3 px-2 text-right text-blue-800 font-semibold ${!visibleColumns.gelir_vergisi ? 'hidden' : ''}`}>{formatCurrency(totals.gelir_vergisi)}</div>
                              <div style={{ width: '70px', minWidth: '70px' }} className={`py-3 px-2 text-right text-blue-800 font-semibold ${!visibleColumns.diger1 ? 'hidden' : ''}`}>{formatCurrency(totals.diger1)}</div>
                              <div style={{ width: '70px', minWidth: '70px' }} className={`py-3 px-2 text-right text-blue-800 font-semibold ${!visibleColumns.diger2 ? 'hidden' : ''}`}>{formatCurrency(totals.diger2)}</div>
                              <div style={{ width: '70px', minWidth: '70px' }} className={`py-3 px-2 text-right text-blue-800 font-semibold ${!visibleColumns.diger3 ? 'hidden' : ''}`}>{formatCurrency(totals.diger3)}</div>
                              <div style={{ width: '100px', minWidth: '100px' }} className="py-3 px-2"></div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
            </Card>
          </TabsContent>

          <TabsContent value="musteriler">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center mb-4">
                  <CardTitle>Müşteri Listesi ({musteriler.length})</CardTitle>
                  <div className="flex gap-2">
                    {Object.keys(editedMusteriler).length > 0 && (
                      <Button 
                        onClick={async () => {
                          try {
                            for (const [id, data] of Object.entries(editedMusteriler)) {
                              await axios.put(`${API}/musteriler/${id}`, data);
                            }
                            toast.success(`${Object.keys(editedMusteriler).length} müşteri güncellendi`);
                            setEditedMusteriler({});
                            setEditingMusteriId(null);
                            fetchMusteriler();
                          } catch (error) {
                            toast.error('Güncelleme hatası');
                          }
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckSquare className="w-4 h-4 mr-2" />
                        Toplu Kaydet ({Object.keys(editedMusteriler).length})
                      </Button>
                    )}
                    {selectedMusteriler.length > 0 && (
                      <>
                        <Button onClick={() => setTopluGuncellemeDialogOpen(true)} variant="outline">
                          <CheckSquare className="w-4 h-4 mr-2" />
                          Toplu Güncelle ({selectedMusteriler.length})
                        </Button>
                        <Button onClick={handleTopluMusteriSil} variant="outline" className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Toplu Sil ({selectedMusteriler.length})
                        </Button>
                      </>
                    )}
                    <Button onClick={handleMusteriListesiExport} variant="outline" className="bg-green-50 hover:bg-green-100">
                      <Download className="w-4 h-4 mr-2" />
                      Excel İndir
                    </Button>
                    <Button onClick={() => setMusteriExcelDialogOpen(true)} variant="outline">
                      <FileUp className="w-4 h-4 mr-2" />
                      Toplu Müşteri Ekle
                    </Button>
                  </div>
                </div>
                
                {/* Arama Kutusu ve Filtre Temizleme */}
                <div className="flex gap-2 items-center">
                  <Input
                    type="text"
                    placeholder="Müşteri Ara (Ad Soyad veya Vergi No)..."
                    value={aramaMusteri}
                    onChange={(e) => setAramaMusteri(e.target.value)}
                    className="max-w-md"
                  />
                  {(columnFilters.durum || columnFilters.musteri_turu || columnFilters.stopaj_periyot || sortColumn) && (
                    <Button
                      onClick={handleClearFilters}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Filtreleri Temizle
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedMusteriler.length === musteriler.length && musteriler.length > 0}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="w-4 h-4"
                        />
                      </TableHead>
                      <TableHead className="w-12 text-center">⭐</TableHead>
                      
                      {/* Durum - Filtreleme */}
                      <TableHead>
                        <div className="flex items-center gap-2">
                          <span>Durum</span>
                          <select
                            value={columnFilters.durum}
                            onChange={(e) => handleColumnFilter('durum', e.target.value)}
                            className="text-xs border rounded px-1 py-0.5 bg-white"
                          >
                            <option value="">Tümü</option>
                            <option value="aktif">Aktif</option>
                            <option value="pasif">Pasif</option>
                          </select>
                        </div>
                      </TableHead>
                      
                      {/* Vergi No - Sıralama */}
                      <TableHead>
                        <button
                          onClick={() => handleSort('vergi_no')}
                          className="flex items-center gap-1 hover:text-blue-600"
                        >
                          Vergi No
                          {sortColumn === 'vergi_no' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </TableHead>
                      
                      {/* Ad Soyad - Sıralama */}
                      <TableHead>
                        <button
                          onClick={() => handleSort('ad_soyad')}
                          className="flex items-center gap-1 hover:text-blue-600"
                        >
                          Ad Soyad
                          {sortColumn === 'ad_soyad' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </TableHead>
                      
                      <TableHead>Telefon</TableHead>
                      
                      {/* Müşteri Türü - Filtreleme */}
                      <TableHead>
                        <div className="flex items-center gap-2">
                          <span>Müşteri Türü</span>
                          <select
                            value={columnFilters.musteri_turu}
                            onChange={(e) => handleColumnFilter('musteri_turu', e.target.value)}
                            className="text-xs border rounded px-1 py-0.5 bg-white"
                          >
                            <option value="">Tümü</option>
                            <option value="İŞLETME">İŞLETME</option>
                            <option value="BİLANÇO">BİLANÇO</option>
                            <option value="ŞİRKET">ŞİRKET</option>
                          </select>
                        </div>
                      </TableHead>
                      
                      {/* Stopaj Periyodu - Filtreleme */}
                      <TableHead>
                        <div className="flex items-center gap-2">
                          <span>Stopaj Periyodu</span>
                          <select
                            value={columnFilters.stopaj_periyot}
                            onChange={(e) => handleColumnFilter('stopaj_periyot', e.target.value)}
                            className="text-xs border rounded px-1 py-0.5 bg-white"
                          >
                            <option value="">Tümü</option>
                            <option value="Aylık">Aylık</option>
                            <option value="3 Aylık">3 Aylık</option>
                          </select>
                        </div>
                      </TableHead>
                      
                      <TableHead className="text-center">Kendi Vergisi</TableHead>
                      <TableHead className="text-center">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {musteriler.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          Henüz müşteri bulunmuyor
                        </TableCell>
                      </TableRow>
                    ) : (
                      musteriler
                        .filter(musteri => {
                          // Arama filtresi
                          if (aramaMusteri) {
                            const searchLower = aramaMusteri.toLowerCase();
                            if (!(musteri.ad_soyad?.toLowerCase().includes(searchLower) ||
                                musteri.vergi_no?.toLowerCase().includes(searchLower))) {
                              return false;
                            }
                          }
                          
                          // Durum filtresi
                          if (columnFilters.durum) {
                            if (columnFilters.durum === 'aktif' && musteri.aktif !== true) return false;
                            if (columnFilters.durum === 'pasif' && musteri.aktif !== false) return false;
                          }
                          
                          // Müşteri türü filtresi
                          if (columnFilters.musteri_turu && musteri.musteri_turu !== columnFilters.musteri_turu) {
                            return false;
                          }
                          
                          // Stopaj periyot filtresi
                          if (columnFilters.stopaj_periyot && musteri.stopaj_periyot !== columnFilters.stopaj_periyot) {
                            return false;
                          }
                          
                          return true;
                        })
                        .sort((a, b) => {
                          // Sıralama
                          if (!sortColumn) return 0;
                          
                          let aValue = a[sortColumn] || '';
                          let bValue = b[sortColumn] || '';
                          
                          // String karşılaştırması
                          if (typeof aValue === 'string') {
                            aValue = aValue.toLowerCase();
                            bValue = bValue.toLowerCase();
                          }
                          
                          if (sortDirection === 'asc') {
                            return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
                          } else {
                            return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
                          }
                        })
                        .map((musteri) => {
                          const isSelected = selectedMusteriler.includes(musteri.id);
                          const editedData = editedMusteriler[musteri.id] || musteri;
                          
                          return (
                        <TableRow 
                          key={musteri.id} 
                          data-testid={`customer-row-${musteri.id}`}
                          className={`${musteri.aktif === false ? 'bg-gray-100' : ''} ${isSelected ? 'bg-blue-50' : ''}`}
                        >
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedMusteriler.includes(musteri.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectMusteri(musteri.id, e.target.checked);
                              }}
                              className="w-4 h-4"
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFavoriToggle(musteri.id, musteri.favori);
                              }}
                              className="hover:scale-110 transition-transform"
                            >
                              <Star className={`w-5 h-5 ${
                                // Pasif müşteriler KIRMIZI
                                musteri.aktif === false
                                  ? 'fill-red-500 text-red-500'
                                  // İŞLETME müşterileri MAVİ (favori olsun veya olmasın)
                                  : musteri.musteri_turu === 'İŞLETME'
                                  ? 'fill-blue-500 text-blue-500'
                                  // Diğer türler (BİLANÇO, ŞİRKET)
                                  : musteri.favori 
                                    ? musteri.musteri_turu === 'BİLANÇO'
                                      ? 'fill-green-500 text-green-500'
                                      : 'fill-purple-500 text-purple-500'
                                    : 'text-gray-300'
                              }`} />
                            </button>
                          </TableCell>
                          <TableCell>
                            {isSelected ? (
                              <select 
                                value={editedData.aktif === false ? 'false' : 'true'}
                                onChange={(e) => {
                                  const newData = {...editedData, aktif: e.target.value === 'true'};
                                  setEditedMusteriler({...editedMusteriler, [musteri.id]: newData});
                                }}
                                className="w-20 px-2 py-1 text-xs border rounded bg-white"
                              >
                                <option value="true">Aktif</option>
                                <option value="false">Pasif</option>
                              </select>
                            ) : (
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${musteri.aktif === false ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700'}`}>
                                {musteri.aktif === false ? 'Pasif' : 'Aktif'}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">{musteri.vergi_no}</TableCell>
                          <TableCell>
                            {isSelected ? (
                              <Input 
                                value={editedData.ad_soyad}
                                onChange={(e) => {
                                  const newData = {...editedData, ad_soyad: e.target.value};
                                  setEditedMusteriler({...editedMusteriler, [musteri.id]: newData});
                                }}
                                className="h-8 text-sm"
                              />
                            ) : musteri.ad_soyad}
                          </TableCell>
                          <TableCell>
                            {isSelected ? (
                              <Input 
                                value={editedData.telefon || ''}
                                onChange={(e) => {
                                  const newData = {...editedData, telefon: e.target.value};
                                  setEditedMusteriler({...editedMusteriler, [musteri.id]: newData});
                                }}
                                className="h-8 text-sm"
                                placeholder="905551234567"
                              />
                            ) : (musteri.telefon || '-')}
                          </TableCell>
                          <TableCell>
                            {isSelected ? (
                              <select 
                                value={editedData.musteri_turu || 'İŞLETME'}
                                onChange={(e) => {
                                  const newData = {...editedData, musteri_turu: e.target.value};
                                  setEditedMusteriler({...editedMusteriler, [musteri.id]: newData});
                                }}
                                className="w-28 px-2 py-1 text-xs border rounded bg-white"
                              >
                                <option value="İŞLETME">İŞLETME</option>
                                <option value="BİLANÇO">BİLANÇO</option>
                                <option value="ŞİRKET">ŞİRKET</option>
                              </select>
                            ) : (musteri.musteri_turu || 'İŞLETME')}
                          </TableCell>
                          <TableCell>
                            {isSelected ? (
                              <select 
                                value={editedData.stopaj_periyot || 'Aylık'}
                                onChange={(e) => {
                                  const newData = {...editedData, stopaj_periyot: e.target.value};
                                  setEditedMusteriler({...editedMusteriler, [musteri.id]: newData});
                                }}
                                className="w-24 px-2 py-1 text-xs border rounded bg-white"
                              >
                                <option value="Aylık">Aylık</option>
                                <option value="3 Aylık">3 Aylık</option>
                              </select>
                            ) : (musteri.stopaj_periyot || 'Aylık')}
                          </TableCell>
                          <TableCell className="text-center">
                            {isSelected ? (
                              <div className="flex gap-1 justify-center text-xs flex-wrap">
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input 
                                    type="checkbox" 
                                    checked={editedData.kdv_kendisi || false}
                                    onChange={(e) => {
                                      const newData = {...editedData, kdv_kendisi: e.target.checked};
                                      setEditedMusteriler({...editedMusteriler, [musteri.id]: newData});
                                    }}
                                    className="w-3 h-3"
                                  />
                                  <span className="text-xs">KDV</span>
                                </label>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input 
                                    type="checkbox" 
                                    checked={editedData.stopaj_kendisi || false}
                                    onChange={(e) => {
                                      const newData = {...editedData, stopaj_kendisi: e.target.checked};
                                      setEditedMusteriler({...editedMusteriler, [musteri.id]: newData});
                                    }}
                                    className="w-3 h-3"
                                  />
                                  <span className="text-xs">St</span>
                                </label>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input 
                                    type="checkbox" 
                                    checked={editedData.ssk_kendisi || false}
                                    onChange={(e) => {
                                      const newData = {...editedData, ssk_kendisi: e.target.checked};
                                      setEditedMusteriler({...editedMusteriler, [musteri.id]: newData});
                                    }}
                                    className="w-3 h-3"
                                  />
                                  <span className="text-xs">SSK</span>
                                </label>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input 
                                    type="checkbox" 
                                    checked={editedData.bagkur_kendisi || false}
                                    onChange={(e) => {
                                      const newData = {...editedData, bagkur_kendisi: e.target.checked};
                                      setEditedMusteriler({...editedMusteriler, [musteri.id]: newData});
                                    }}
                                    className="w-3 h-3"
                                  />
                                  <span className="text-xs">Bğ</span>
                                </label>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input 
                                    type="checkbox" 
                                    checked={editedData.gecici_vergi_kendisi || false}
                                    onChange={(e) => {
                                      const newData = {...editedData, gecici_vergi_kendisi: e.target.checked};
                                      setEditedMusteriler({...editedMusteriler, [musteri.id]: newData});
                                    }}
                                    className="w-3 h-3"
                                  />
                                  <span className="text-xs">GV</span>
                                </label>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input 
                                    type="checkbox" 
                                    checked={editedData.gelir_vergisi_kendisi || false}
                                    onChange={(e) => {
                                      const newData = {...editedData, gelir_vergisi_kendisi: e.target.checked};
                                      setEditedMusteriler({...editedMusteriler, [musteri.id]: newData});
                                    }}
                                    className="w-3 h-3"
                                  />
                                  <span className="text-xs">GeV</span>
                                </label>
                              </div>
                            ) : (
                              <div className="flex gap-1 justify-center text-xs">
                                {musteri.kdv_kendisi && <span title="KDV" className="px-1 bg-orange-100 text-orange-700 rounded">K</span>}
                                {musteri.stopaj_kendisi && <span title="Stopaj" className="px-1 bg-blue-100 text-blue-700 rounded">S</span>}
                                {musteri.ssk_kendisi && <span title="SSK" className="px-1 bg-purple-100 text-purple-700 rounded">SK</span>}
                                {musteri.bagkur_kendisi && <span title="Bağkur" className="px-1 bg-pink-100 text-pink-700 rounded">B</span>}
                                {musteri.gecici_vergi_kendisi && <span title="Geçici Vergi" className="px-1 bg-indigo-100 text-indigo-700 rounded">GV</span>}
                                {musteri.gelir_vergisi_kendisi && <span title="Gelir Vergisi" className="px-1 bg-red-100 text-red-700 rounded">GelV</span>}
                                {!musteri.kdv_kendisi && !musteri.stopaj_kendisi && !musteri.ssk_kendisi && !musteri.bagkur_kendisi && !musteri.gecici_vergi_kendisi && !musteri.gelir_vergisi_kendisi && <span className="text-gray-400">-</span>}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingMusteri(musteri);
                                  setMusteriEditDialogOpen(true);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMusteriSil(musteri.id);
                                }}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Yüklenen Vergiler Sekmesi */}
          <TabsContent value="vergi-yukle" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      📊 Yüklenen Vergiler
                    </CardTitle>
                    <p className="text-sm text-gray-600 mt-2">
                      Toplu ZIP yükleme ile parse edilen vergi tahakkukları burada görünür
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      if (window.confirm('⚠️ TÜM yüklenen vergi kayıtları silinecek!\n\nBu işlem geri alınamaz. Devam etmek istiyor musunuz?')) {
                        try {
                          const response = await axios.delete(`${API}/yuklenen-vergiler/delete-all/confirm`);
                          alert(`✅ ${response.data.deleted_count} adet kayıt silindi`);
                          fetchYuklenenVergiler(); // Listeyi yenile
                        } catch (error) {
                          alert('❌ Silme işlemi başarısız: ' + (error.response?.data?.detail || error.message));
                        }
                      }
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2"
                  >
                    🗑️ Tümünü Sil
                  </button>
                  
                  {/* Excel'e Aktar Butonu */}
                  <button
                    onClick={async () => {
                      try {
                        const params = new URLSearchParams();
                        if (selectedMusteriForVergiler) {
                          params.append('musteri_id', selectedMusteriForVergiler);
                        }
                        if (yuklenenVergilerSelectedYil) {
                          params.append('yil', yuklenenVergilerSelectedYil);
                        }
                        if (yuklenenVergilerSelectedAylar && yuklenenVergilerSelectedAylar.length > 0) {
                          params.append('ay', yuklenenVergilerSelectedAylar.join(','));
                        }
                        
                        const response = await axios.get(`${API}/yuklenen-vergiler/excel-export?${params.toString()}`, {
                          responseType: 'blob',
                          timeout: 120000
                        });
                        
                        // Dosyayı indir
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        const tarih = new Date().toISOString().slice(0,10).replace(/-/g,'');
                        link.setAttribute('download', `yuklenen_vergiler_${tarih}.xlsx`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        alert('❌ Excel export başarısız: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
                  >
                    📊 Excel'e Aktar
                  </button>
                  
                  {/* Seçilenleri Sil Butonu - Sadece seçim varsa göster */}
                  {selectedVergiIds.length > 0 && (
                    <button
                      onClick={handleDeleteSelectedVergiler}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium flex items-center gap-2"
                    >
                      🗑️ Seçilenleri Sil ({selectedVergiIds.length})
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Müşteri Filtreleme ve Seçim */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
                  {/* Müşteri Türü Filtresi */}
                  <div className="flex items-center gap-6">
                    <label className="text-sm font-medium text-gray-700">
                      Müşteri Türü:
                    </label>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="musteriFiltre"
                          value="bilanco-sirket"
                          checked={musteriFiltreTuru === 'bilanco-sirket'}
                          onChange={(e) => setMusteriFiltreTuru(e.target.value)}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Bilanço/Şirket (Varsayılan)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="musteriFiltre"
                          value="tumu"
                          checked={musteriFiltreTuru === 'tumu'}
                          onChange={(e) => setMusteriFiltreTuru(e.target.value)}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Tümü</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Müşteri Seçici Dropdown */}
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                      Müşteri Seç:
                    </label>
                    <select
                      value={selectedMusteriForVergiler || 'all'}
                      onChange={(e) => setSelectedMusteriForVergiler(e.target.value === 'all' ? null : e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">Tüm Müşteriler</option>
                      {musteriler
                        .filter(musteri => {
                          if (musteriFiltreTuru === 'bilanco-sirket') {
                            return musteri.musteri_turu === 'BİLANÇO' || musteri.musteri_turu === 'ŞİRKET';
                          }
                          return true; // Tümü
                        })
                        .map(musteri => (
                          <option key={musteri.id} value={musteri.id}>
                            {musteri.ad_soyad || `${musteri.ad || ''} ${musteri.soyad || ''}`}
                          </option>
                        ))
                      }
                    </select>
                  </div>
                  
                  {/* Vergi Türü Filtresi */}
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                      Gösterilecek Vergiler:
                    </label>
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* Tümü Checkbox */}
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedVergiTurleri.length === 6}
                          onChange={(e) => handleTumVergiTurleri(e.target.checked)}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-semibold text-blue-600">Tümü</span>
                      </label>
                      {/* KDV Checkbox */}
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedVergiTurleri.includes('KDV')}
                          onChange={() => handleVergiTuruToggle('KDV')}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">KDV</span>
                      </label>
                      {/* KDV-2 Checkbox */}
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedVergiTurleri.includes('KDV-2')}
                          onChange={() => handleVergiTuruToggle('KDV-2')}
                          className="w-4 h-4 text-purple-600 focus:ring-2 focus:ring-purple-500"
                        />
                        <span className="text-sm font-medium text-purple-700">KDV-2</span>
                      </label>
                      {/* Stopaj Checkbox */}
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedVergiTurleri.includes('Stopaj')}
                          onChange={() => handleVergiTuruToggle('Stopaj')}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Stopaj</span>
                      </label>
                      {/* SSK Checkbox */}
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedVergiTurleri.includes('SSK')}
                          onChange={() => handleVergiTuruToggle('SSK')}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">SSK</span>
                      </label>
                      {/* Geçici Vergi Checkbox */}
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedVergiTurleri.includes('Geçici Vergi')}
                          onChange={() => handleVergiTuruToggle('Geçici Vergi')}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Geçici Vergi</span>
                      </label>
                      {/* Gelir Vergisi Checkbox */}
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedVergiTurleri.includes('Gelir Vergisi')}
                          onChange={() => handleVergiTuruToggle('Gelir Vergisi')}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Gelir Vergisi</span>
                      </label>
                      {/* Vergisi Verilmeyenler Checkbox */}
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={vergisiVerilmeyenler}
                          onChange={(e) => handleVergisiVerilmeyenlerToggle(e.target.checked)}
                          className="w-4 h-4 text-red-600 focus:ring-2 focus:ring-red-500"
                        />
                        <span className="text-sm font-medium text-red-700">Vergisi Verilmeyenler</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Yıl ve Ay Filtreleri */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                  {/* Yıl Filtresi ve Önceki/Sonraki Ay Butonları */}
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                      Yıl:
                    </label>
                    <Input
                      type="number"
                      value={yuklenenVergilerSelectedYil}
                      onChange={(e) => setYuklenenVergilerSelectedYil(parseInt(e.target.value))}
                      className="w-[120px] h-9 text-sm"
                    />
                    
                    {/* Önceki/Sonraki Ay Butonları */}
                    <div className="flex items-center gap-1 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg px-2 py-1 border border-blue-200">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 rounded-full bg-white/60 hover:bg-white text-blue-700 transition-all duration-200 hover:scale-105"
                        onClick={() => {
                          const currentAyIndex = yuklenenVergilerSelectedAylar.length === 1 ? AYLAR.indexOf(yuklenenVergilerSelectedAylar[0]) : -1;
                          if (currentAyIndex !== -1) {
                            let newAyIndex = currentAyIndex - 1;
                            let newYil = yuklenenVergilerSelectedYil;
                            if (newAyIndex < 0) {
                              newAyIndex = 11;
                              newYil = yuklenenVergilerSelectedYil - 1;
                            }
                            setYuklenenVergilerSelectedYil(newYil);
                            setYuklenenVergilerSelectedAylar([AYLAR[newAyIndex]]);
                          } else {
                            const firstAyIndex = Math.min(...yuklenenVergilerSelectedAylar.map(a => AYLAR.indexOf(a)));
                            let newAyIndex = firstAyIndex - 1;
                            let newYil = yuklenenVergilerSelectedYil;
                            if (newAyIndex < 0) {
                              newAyIndex = 11;
                              newYil = yuklenenVergilerSelectedYil - 1;
                            }
                            setYuklenenVergilerSelectedYil(newYil);
                            setYuklenenVergilerSelectedAylar([AYLAR[newAyIndex]]);
                          }
                        }}
                        title="Önceki Ay"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center gap-1 min-w-[70px] justify-center">
                        <span className="text-blue-800 font-bold text-xs">
                          {yuklenenVergilerSelectedAylar.length === 1 ? yuklenenVergilerSelectedAylar[0] : `${yuklenenVergilerSelectedAylar.length} ay`}
                        </span>
                        <span className="text-blue-600 text-xs font-medium">
                          {yuklenenVergilerSelectedYil}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 rounded-full bg-white/60 hover:bg-white text-blue-700 transition-all duration-200 hover:scale-105"
                        onClick={() => {
                          const currentAyIndex = yuklenenVergilerSelectedAylar.length === 1 ? AYLAR.indexOf(yuklenenVergilerSelectedAylar[0]) : -1;
                          if (currentAyIndex !== -1) {
                            let newAyIndex = currentAyIndex + 1;
                            let newYil = yuklenenVergilerSelectedYil;
                            if (newAyIndex > 11) {
                              newAyIndex = 0;
                              newYil = yuklenenVergilerSelectedYil + 1;
                            }
                            setYuklenenVergilerSelectedYil(newYil);
                            setYuklenenVergilerSelectedAylar([AYLAR[newAyIndex]]);
                          } else {
                            const lastAyIndex = Math.max(...yuklenenVergilerSelectedAylar.map(a => AYLAR.indexOf(a)));
                            let newAyIndex = lastAyIndex + 1;
                            let newYil = yuklenenVergilerSelectedYil;
                            if (newAyIndex > 11) {
                              newAyIndex = 0;
                              newYil = yuklenenVergilerSelectedYil + 1;
                            }
                            setYuklenenVergilerSelectedYil(newYil);
                            setYuklenenVergilerSelectedAylar([AYLAR[newAyIndex]]);
                          }
                        }}
                        title="Sonraki Ay"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Ay Seçimi */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium">Aylar</Label>
                      {/* Tüm Aylar Checkbox */}
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={yuklenenVergilerSelectedAylar.length === AYLAR.length}
                          onChange={(e) => handleYuklenenVergilerTumAylar(e.target.checked)}
                          className="w-3 h-3"
                        />
                        <span className="text-xs font-semibold text-blue-600">Tüm Aylar</span>
                      </label>
                    </div>
                    
                    {/* 4 Sütunlu Grid - Her sütunda 3 ay */}
                    <div className="grid grid-cols-4 gap-2">
                      {/* Sütun 1: Ocak, Şubat, Mart */}
                      <div className="space-y-1">
                        {['Ocak', 'Şubat', 'Mart'].map((ay) => (
                          <label 
                            key={ay} 
                            className="flex items-center gap-1 cursor-pointer hover:bg-slate-100 px-1 py-0.5 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={yuklenenVergilerSelectedAylar.includes(ay)}
                              onChange={() => handleYuklenenVergilerAyToggle(ay)}
                              className="w-3 h-3"
                            />
                            <span className="text-xs">{ay}</span>
                          </label>
                        ))}
                      </div>
                      
                      {/* Sütun 2: Nisan, Mayıs, Haziran */}
                      <div className="space-y-1">
                        {['Nisan', 'Mayıs', 'Haziran'].map((ay) => (
                          <label 
                            key={ay} 
                            className="flex items-center gap-1 cursor-pointer hover:bg-slate-100 px-1 py-0.5 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={yuklenenVergilerSelectedAylar.includes(ay)}
                              onChange={() => handleYuklenenVergilerAyToggle(ay)}
                              className="w-3 h-3"
                            />
                            <span className="text-xs">{ay}</span>
                          </label>
                        ))}
                      </div>
                      
                      {/* Sütun 3: Temmuz, Ağustos, Eylül */}
                      <div className="space-y-1">
                        {['Temmuz', 'Ağustos', 'Eylül'].map((ay) => (
                          <label 
                            key={ay} 
                            className="flex items-center gap-1 cursor-pointer hover:bg-slate-100 px-1 py-0.5 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={yuklenenVergilerSelectedAylar.includes(ay)}
                              onChange={() => handleYuklenenVergilerAyToggle(ay)}
                              className="w-3 h-3"
                            />
                            <span className="text-xs">{ay}</span>
                          </label>
                        ))}
                      </div>
                      
                      {/* Sütun 4: Ekim, Kasım, Aralık */}
                      <div className="space-y-1">
                        {['Ekim', 'Kasım', 'Aralık'].map((ay) => (
                          <label 
                            key={ay} 
                            className="flex items-center gap-1 cursor-pointer hover:bg-slate-100 px-1 py-0.5 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={yuklenenVergilerSelectedAylar.includes(ay)}
                              onChange={() => handleYuklenenVergilerAyToggle(ay)}
                              className="w-3 h-3"
                            />
                            <span className="text-xs">{ay}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Yüklenen Vergiler Tablosu - Her vergi türü ayrı satırda, SSK işyerleri dinamik */}
                <div className="border rounded-lg relative">
                  {/* Sticky header ve scrollbar stilleri */}
                  <style>
                    {`
                      #yuklenen-vergiler-table-container {
                        max-height: calc(100vh - 380px);
                        overflow: auto;
                        scrollbar-gutter: stable;
                      }
                      #yuklenen-vergiler-table-container::-webkit-scrollbar {
                        height: 14px;
                        width: 14px;
                      }
                      #yuklenen-vergiler-table-container::-webkit-scrollbar-track {
                        background: #e2e8f0;
                        border-radius: 7px;
                      }
                      #yuklenen-vergiler-table-container::-webkit-scrollbar-thumb {
                        background: linear-gradient(180deg, #8b5cf6 0%, #6366f1 100%);
                        border-radius: 7px;
                        border: 2px solid #e2e8f0;
                      }
                      #yuklenen-vergiler-table-container::-webkit-scrollbar-thumb:hover {
                        background: linear-gradient(180deg, #7c3aed 0%, #4f46e5 100%);
                      }
                      #yuklenen-vergiler-table-container::-webkit-scrollbar-corner {
                        background: #e2e8f0;
                      }
                      #yuklenen-vergiler-table-container thead,
                      #yuklenen-vergiler-table-container thead tr {
                        position: sticky !important;
                        top: 0 !important;
                        z-index: 20 !important;
                      }
                      #yuklenen-vergiler-table-container thead th {
                        position: sticky !important;
                        top: 0 !important;
                        background: #f9fafb !important;
                        z-index: 20 !important;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                      }
                    `}
                  </style>
                  <div id="yuklenen-vergiler-table-container" className="overflow-auto">
                    <table className="w-full min-w-max">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            checked={sortedYuklenenVergiler.length > 0 && selectedVergiIds.length === sortedYuklenenVergiler.length}
                            onChange={(e) => handleSelectAllVergiler(e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                          />
                        </th>
                        <th 
                          className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleYuklenenVergilerSort('ad_soyad')}
                        >
                          <div className="flex items-center gap-1">
                            Müşteri İsmi
                            {yuklenenVergilerSortConfig.key === 'ad_soyad' && (
                              <span>{yuklenenVergilerSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-3 py-3 text-center text-xs font-medium text-blue-700 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-50 select-none"
                          onClick={() => handleYuklenenVergilerSort('vergi_turu')}
                        >
                          <div className="flex items-center justify-center gap-1">
                            Vergi Türü
                            {yuklenenVergilerSortConfig.key === 'vergi_turu' && (
                              <span>{yuklenenVergilerSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleYuklenenVergilerSort('vade_tarihi')}
                        >
                          <div className="flex items-center justify-center gap-1">
                            Vade Tarihi
                            {yuklenenVergilerSortConfig.key === 'vade_tarihi' && (
                              <span>{yuklenenVergilerSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleYuklenenVergilerSort('kdv_tutar')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            KDV (1015+1016)
                            {yuklenenVergilerSortConfig.key === 'kdv_tutar' && (
                              <span>{yuklenenVergilerSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleYuklenenVergilerSort('kdv2_tutar')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            KDV 2 (4017)
                            {yuklenenVergilerSortConfig.key === 'kdv2_tutar' && (
                              <span>{yuklenenVergilerSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleYuklenenVergilerSort('kira_stopaj')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Kira Stopaj
                            {yuklenenVergilerSortConfig.key === 'kira_stopaj' && (
                              <span>{yuklenenVergilerSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleYuklenenVergilerSort('smk_stopaj')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            SMK Stopaj
                            {yuklenenVergilerSortConfig.key === 'smk_stopaj' && (
                              <span>{yuklenenVergilerSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleYuklenenVergilerSort('isci_dv')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            İşçi D.V.
                            {yuklenenVergilerSortConfig.key === 'isci_dv' && (
                              <span>{yuklenenVergilerSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleYuklenenVergilerSort('isci_gelir_vergisi')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            İşçi Gel.Vergisi
                            {yuklenenVergilerSortConfig.key === 'isci_gelir_vergisi' && (
                              <span>{yuklenenVergilerSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleYuklenenVergilerSort('gecici_vergi')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Geçici Vergi
                            {yuklenenVergilerSortConfig.key === 'gecici_vergi' && (
                              <span>{yuklenenVergilerSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        {/* 5035 D.V. - Tek sütun (eski + yeni alanların toplamı) */}
                        <th 
                          className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleYuklenenVergilerSort('damga_vergisi_5035')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            5035 D.V.
                            {yuklenenVergilerSortConfig.key === 'damga_vergisi_5035' && (
                              <span>{yuklenenVergilerSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        {/* Dinamik SSK İşyeri Kolonları - Müşteri seçimine göre */}
                        {(() => {
                          // Tüm SSK kayıtlarından maksimum işyeri sayısını bul
                          const sskKayitlari = sortedYuklenenVergiler.filter(v => v.vergi_turu === 'SSK');
                          
                          // Tek müşteri seçili mi kontrol et
                          const tekMusteriSecili = selectedMusteriForVergiler && selectedMusteriForVergiler !== 'all';
                          
                          if (tekMusteriSecili) {
                            // TEK MÜŞTERİ SEÇİLİ - Gerçek işyeri isimlerini göster
                            const isyeriAdlari = new Set();
                            sskKayitlari.forEach(kayit => {
                              (kayit.ssk_isyerleri || []).forEach(isyeri => {
                                isyeriAdlari.add(isyeri.isyeri_adi || 'MERKEZ');
                              });
                            });
                            
                            const sortedIsyeriAdlari = Array.from(isyeriAdlari).sort((a, b) => {
                              if (a === 'MERKEZ') return -1;
                              if (b === 'MERKEZ') return 1;
                              return a.localeCompare(b, 'tr-TR');
                            });
                            
                            return sortedIsyeriAdlari.map((isyeriAdi, idx) => (
                              <React.Fragment key={idx}>
                                <th className="px-2 py-2 text-right text-xs font-medium text-orange-700 uppercase tracking-wider whitespace-nowrap border-l-2 border-orange-300">
                                  {isyeriAdi}<br/>Prim
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-medium text-orange-700 uppercase tracking-wider whitespace-nowrap">
                                  {isyeriAdi}<br/>İşveren
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-medium text-orange-700 uppercase tracking-wider whitespace-nowrap">
                                  {isyeriAdi}<br/>D.Primi
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-medium text-orange-700 uppercase tracking-wider whitespace-nowrap">
                                  {isyeriAdi}<br/>E.Prim
                                </th>
                              </React.Fragment>
                            ));
                          } else {
                            // TÜM MÜŞTERİLER SEÇİLİ - Sabit kolon isimleri: Merkez, Şube 1, Şube 2, Şube 3
                            // Maksimum işyeri sayısını bul (max 4)
                            let maxIsyeriSayisi = 0;
                            sskKayitlari.forEach(kayit => {
                              const isyeriSayisi = (kayit.ssk_isyerleri || []).length;
                              if (isyeriSayisi > maxIsyeriSayisi) maxIsyeriSayisi = isyeriSayisi;
                            });
                            maxIsyeriSayisi = Math.min(maxIsyeriSayisi, 4); // Maksimum 4 işyeri
                            
                            const kolonIsimleri = ['Merkez', 'Şube 1', 'Şube 2', 'Şube 3'];
                            
                            return kolonIsimleri.slice(0, maxIsyeriSayisi || 1).map((kolonAdi, idx) => (
                              <React.Fragment key={idx}>
                                <th className="px-2 py-2 text-right text-xs font-medium text-orange-700 uppercase tracking-wider whitespace-nowrap border-l-2 border-orange-300">
                                  {kolonAdi}<br/>Prim
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-medium text-orange-700 uppercase tracking-wider whitespace-nowrap">
                                  {kolonAdi}<br/>İşveren
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-medium text-orange-700 uppercase tracking-wider whitespace-nowrap">
                                  {kolonAdi}<br/>D.Primi
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-medium text-orange-700 uppercase tracking-wider whitespace-nowrap">
                                  {kolonAdi}<br/>E.Prim
                                </th>
                              </React.Fragment>
                            ));
                          }
                        })()}
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap border-l-2 border-gray-300">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedYuklenenVergiler.length > 0 ? (
                        sortedYuklenenVergiler.map((vergi) => {
                          // Vergi türüne göre satır renklendirme
                          const getRowClass = (tur) => {
                            if (tur === 'KDV' || tur === 'KDV-2') return 'hover:bg-blue-50 bg-blue-25';
                            if (tur.startsWith('Stopaj')) return 'hover:bg-green-50 bg-green-25';
                            if (tur === 'SSK') return 'hover:bg-orange-50 bg-orange-25';
                            if (tur === 'Geçici Vergi') return 'hover:bg-purple-50 bg-purple-25';
                            return 'hover:bg-gray-50';
                          };
                          
                          return (
                            <tr key={vergi.id} className={getRowClass(vergi.vergi_turu)}>
                              <td className="px-3 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedVergiIds.includes(vergi.id)}
                                  onChange={() => handleSelectVergi(vergi.id)}
                                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                />
                              </td>
                              <td className="px-3 py-3 text-sm text-left text-gray-900 font-medium">
                                {vergi.ad_soyad || '-'}
                              </td>
                              <td className="px-3 py-3 text-sm text-center font-semibold text-blue-700">
                                {vergi.vergi_turu}
                                {vergi.vergi_turu === 'SSK' && vergi.ssk_isyeri_adi && (
                                  <span className="text-xs text-gray-500 block">{vergi.ssk_isyeri_adi}</span>
                                )}
                              </td>
                              <td className="px-3 py-3 text-sm text-center text-gray-600">
                                {vergi.vade_tarihi}
                              </td>
                              {/* KDV Kolonları */}
                              <td className="px-3 py-3 text-sm text-right text-gray-900">
                                {vergi.kdv_tutar > 0 ? `₺${vergi.kdv_tutar.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                              </td>
                              <td className="px-3 py-3 text-sm text-right text-gray-900">
                                {vergi.kdv2_tutar > 0 ? `₺${vergi.kdv2_tutar.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                              </td>
                              
                              {/* Stopaj Kolonları */}
                              <td className="px-3 py-3 text-sm text-right text-gray-900">
                                {vergi.kira_stopaj > 0 ? `₺${vergi.kira_stopaj.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                              </td>
                              <td className="px-3 py-3 text-sm text-right text-gray-900">
                                {vergi.smk_stopaj > 0 ? `₺${vergi.smk_stopaj.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                              </td>
                              <td className="px-3 py-3 text-sm text-right text-gray-900">
                                {vergi.isci_dv > 0 ? `₺${vergi.isci_dv.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                              </td>
                              <td className="px-3 py-3 text-sm text-right text-gray-900">
                                {vergi.isci_gelir_vergisi > 0 ? `₺${vergi.isci_gelir_vergisi.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                              </td>
                              
                              {/* Geçici Vergi */}
                              <td className="px-3 py-3 text-sm text-right text-gray-900">
                                {vergi.gecici_vergi > 0 ? `₺${vergi.gecici_vergi.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                              </td>
                              
                              {/* 5035 D.V - Tek sütun (ayrıştırılmış alanlardan) */}
                              <td className="px-3 py-3 text-sm text-right text-gray-600">
                                {(() => {
                                  // Sadece ayrıştırılmış alanları topla (dv_5035_xxx)
                                  // damga_vergisi_5035 eski alan, artık kullanılmıyor
                                  const toplam5035 = (vergi.dv_5035_kdv || 0) + 
                                    (vergi.dv_5035_kdv2 || 0) +
                                    (vergi.dv_5035_stopaj || 0) + 
                                    (vergi.dv_5035_gecici || 0) + 
                                    (vergi.dv_5035_gelir || 0);
                                  // Eğer ayrıştırılmış alanlar boşsa eski alanı kullan (geriye uyumluluk)
                                  const sonuc = toplam5035 > 0 ? toplam5035 : (vergi.damga_vergisi_5035 || 0);
                                  return sonuc > 0 ? `₺${sonuc.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-';
                                })()}
                              </td>
                              
                              {/* Dinamik SSK İşyeri Kolonları - Müşteri seçimine göre */}
                              {(() => {
                                const sskKayitlari = yuklenenVergiler.filter(v => v.vergi_turu === 'SSK');
                                const tekMusteriSecili = selectedMusteriForVergiler && selectedMusteriForVergiler !== 'all';
                                
                                if (tekMusteriSecili) {
                                  // TEK MÜŞTERİ SEÇİLİ - Gerçek işyeri isimlerine göre eşleştir
                                  const isyeriAdlari = new Set();
                                  sskKayitlari.forEach(kayit => {
                                    (kayit.ssk_isyerleri || []).forEach(isyeri => {
                                      isyeriAdlari.add(isyeri.isyeri_adi || 'MERKEZ');
                                    });
                                  });
                                  
                                  const sortedIsyeriAdlari = Array.from(isyeriAdlari).sort((a, b) => {
                                    if (a === 'MERKEZ') return -1;
                                    if (b === 'MERKEZ') return 1;
                                    return a.localeCompare(b, 'tr-TR');
                                  });
                                  
                                  if (vergi.vergi_turu === 'SSK') {
                                    return sortedIsyeriAdlari.map((isyeriAdi, idx) => {
                                      const isyeriIndex = (vergi.ssk_isyerleri || []).findIndex(i => i.isyeri_adi === isyeriAdi);
                                      const isyeri = isyeriIndex >= 0 ? vergi.ssk_isyerleri[isyeriIndex] : null;
                                      if (isyeri) {
                                        return (
                                          <React.Fragment key={idx}>
                                            <td className="px-2 py-2 text-sm text-right font-semibold text-orange-700">
                                              {isyeri.prim > 0 ? `₺${isyeri.prim.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                                            </td>
                                            <td className="px-2 py-2 text-sm text-right text-orange-600">
                                              {isyeri.isveren > 0 ? `₺${isyeri.isveren.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                                            </td>
                                            <td className="px-1 py-1 text-sm text-right">
                                              <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                className="w-24 px-2 py-1 text-right text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                defaultValue={isyeri.destek_primi || 0}
                                                onBlur={(e) => updateDestekPrimi(vergi.id, isyeriIndex, e.target.value)}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    e.target.blur();
                                                  }
                                                }}
                                                placeholder="0.00"
                                              />
                                            </td>
                                            <td className="px-2 py-2 text-sm text-right text-orange-600">
                                              {isyeri.emekli_prim > 0 ? `₺${isyeri.emekli_prim.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                                            </td>
                                          </React.Fragment>
                                        );
                                      } else {
                                        return (
                                          <React.Fragment key={idx}>
                                            <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                            <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                            <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                            <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                          </React.Fragment>
                                        );
                                      }
                                    });
                                  } else {
                                    return sortedIsyeriAdlari.map((_, idx) => (
                                      <React.Fragment key={idx}>
                                        <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                        <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                        <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                        <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                      </React.Fragment>
                                    ));
                                  }
                                } else {
                                  // TÜM MÜŞTERİLER SEÇİLİ - Sıra bazlı: Merkez, Şube 1, Şube 2, Şube 3
                                  let maxIsyeriSayisi = 0;
                                  sskKayitlari.forEach(kayit => {
                                    const isyeriSayisi = (kayit.ssk_isyerleri || []).length;
                                    if (isyeriSayisi > maxIsyeriSayisi) maxIsyeriSayisi = isyeriSayisi;
                                  });
                                  maxIsyeriSayisi = Math.min(maxIsyeriSayisi, 4) || 1;
                                  
                                  if (vergi.vergi_turu === 'SSK') {
                                    const isyerleri = vergi.ssk_isyerleri || [];
                                    return Array.from({length: maxIsyeriSayisi}, (_, idx) => {
                                      const isyeri = isyerleri[idx];
                                      if (isyeri) {
                                        return (
                                          <React.Fragment key={idx}>
                                            <td className="px-2 py-2 text-sm text-right font-semibold text-orange-700" title={isyeri.isyeri_adi}>
                                              {isyeri.prim > 0 ? `₺${isyeri.prim.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                                            </td>
                                            <td className="px-2 py-2 text-sm text-right text-orange-600">
                                              {isyeri.isveren > 0 ? `₺${isyeri.isveren.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                                            </td>
                                            <td className="px-1 py-1 text-sm text-right">
                                              <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                className="w-24 px-2 py-1 text-right text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                defaultValue={isyeri.destek_primi || 0}
                                                onBlur={(e) => updateDestekPrimi(vergi.id, idx, e.target.value)}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    e.target.blur();
                                                  }
                                                }}
                                                placeholder="0.00"
                                              />
                                            </td>
                                            <td className="px-2 py-2 text-sm text-right text-orange-600">
                                              {isyeri.emekli_prim > 0 ? `₺${isyeri.emekli_prim.toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                                            </td>
                                          </React.Fragment>
                                        );
                                      } else {
                                        return (
                                          <React.Fragment key={idx}>
                                            <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                            <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                            <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                            <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                          </React.Fragment>
                                        );
                                      }
                                    });
                                  } else {
                                    return Array.from({length: maxIsyeriSayisi}, (_, idx) => (
                                      <React.Fragment key={idx}>
                                        <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                        <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                        <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                        <td className="px-2 py-2 text-sm text-right text-gray-400">-</td>
                                      </React.Fragment>
                                    ));
                                  }
                                }
                              })()}
                              <td className="px-3 py-3 text-center">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                  onClick={() => deleteYuklenenVergi(vergi.id)}
                                >
                                  🗑️
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={(() => {
                            const sskKayitlari = sortedYuklenenVergiler.filter(v => v.vergi_turu === 'SSK');
                            const isyeriAdlari = new Set();
                            sskKayitlari.forEach(kayit => {
                              (kayit.ssk_isyerleri || []).forEach(isyeri => {
                                isyeriAdlari.add(isyeri.isyeri_adi || 'MERKEZ');
                              });
                            });
                            return 13 + (isyeriAdlari.size * 4); // 12 sabit kolon + SSK kolonları (4 per işyeri) + işlemler
                          })()} className="px-4 py-8 text-center text-gray-500">
                            <div className="flex flex-col items-center gap-2">
                              <span className="text-4xl">📭</span>
                              <p className="text-sm">Henüz yüklenmiş vergi kaydı bulunmuyor</p>
                              <p className="text-xs text-gray-400">Kayıtlar sekmesinden "Toplu PDF Yükleme" ile vergi tahakkukları yükleyebilirsiniz</p>
                            </div>
                          </td>
                        </tr>
                      )}
                      
                      {/* Örnek Veri - KDV (Geliştirme için) */}
                      {false && (
                      <>
                      {/* Örnek Veri - KDV */}
                      <tr className="hover:bg-blue-50">
                        <td className="px-3 py-3 text-sm text-gray-900 font-medium">
                          UZMAN GİYİM TEKSTİL
                        </td>
                        <td className="px-3 py-3 text-sm text-center text-gray-600">
                          28/11/2025
                        </td>
                        <td className="px-3 py-3 text-sm text-center text-gray-600">
                          10/2025
                        </td>
                        <td className="px-3 py-3 text-sm text-right font-semibold text-blue-700">
                          ₺56,900.51
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-600">
                          ₺665.00
                        </td>
                        {/* SSK Boş Kolonları */}
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-center">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            🗑️
                          </Button>
                        </td>
                      </tr>

                      {/* Örnek Veri - STOPAJ */}
                      <tr className="hover:bg-purple-50">
                        <td className="px-3 py-3 text-sm text-gray-900 font-medium">
                          MERTER VD
                        </td>
                        <td className="px-3 py-3 text-sm text-center text-gray-600">
                          26/11/2025
                        </td>
                        <td className="px-3 py-3 text-sm text-center text-gray-600">
                          10/2025
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right font-semibold text-purple-700">
                          ₺7,000.00
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-600">₺0.00</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-600">₺656.14</td>
                        <td className="px-3 py-3 text-sm text-right font-semibold text-purple-700">
                          ₺3,360.38
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        {/* SSK Boş Kolonları */}
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-center">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            🗑️
                          </Button>
                        </td>
                      </tr>

                      {/* Örnek Veri - GEÇİCİ VERGİ */}
                      <tr className="hover:bg-green-50">
                        <td className="px-3 py-3 text-sm text-gray-900 font-medium">
                          BAŞAKŞEHIR VD
                        </td>
                        <td className="px-3 py-3 text-sm text-center text-gray-600">
                          17/11/2025
                        </td>
                        <td className="px-3 py-3 text-sm text-center text-gray-600">
                          09/2025
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right font-semibold text-green-700">
                          ₺238,207.96
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-600">
                          ₺912.40
                        </td>
                        {/* SSK Boş Kolonları */}
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-center">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            🗑️
                          </Button>
                        </td>
                      </tr>

                      {/* Örnek Veri - SSK (Dinamik İşyerleri) */}
                      <tr className="hover:bg-orange-50">
                        <td className="px-3 py-3 text-sm text-gray-900 font-medium">
                          ABDULLAHCANSİZ
                        </td>
                        <td className="px-3 py-3 text-sm text-center text-gray-600">
                          30/11/2025
                        </td>
                        <td className="px-3 py-3 text-sm text-center text-gray-600">
                          10/2025
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-400">-</td>
                        {/* SSK MERKEZ İşyeri */}
                        <td className="px-3 py-3 text-sm text-right font-semibold text-orange-700" title="SSK Merkez Prim">
                          ₺7,996.70
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-orange-600" title="SSK Merkez İşveren">
                          ₺780.17
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-600" title="SSK Merkez Destek Primi">
                          ₺0.00
                        </td>
                        {/* SSK ŞUBE1 İşyeri */}
                        <td className="px-3 py-3 text-sm text-right font-semibold text-orange-700" title="SSK Şube1 Prim">
                          ₺5,000.00
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-orange-600" title="SSK Şube1 İşveren">
                          ₺500.00
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-600" title="SSK Şube1 Destek Primi">
                          ₺0.00
                        </td>
                        <td className="px-3 py-3 text-center">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            🗑️
                          </Button>
                        </td>
                      </tr>
                      </>
                      )}
                      
                      {/* Veri yoksa */}
                      {false && false && (
                        <tr>
                          <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                            <div className="flex flex-col items-center gap-2">
                              <span className="text-4xl">📭</span>
                              <p className="text-sm">Henüz yüklenmiş vergi kaydı bulunmuyor</p>
                              <p className="text-xs text-gray-400">Kayıtlar sekmesinden "Toplu PDF Yükleme" ile vergi tahakkukları yükleyebilirsiniz</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* GİB Sekmesi */}
          <TabsContent value="gib" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>GİB Bilgileri</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowPasswordsGib(!showPasswordsGib)}
                >
                  {showPasswordsGib ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  {showPasswordsGib ? 'Şifreleri Gizle' : 'Şifreleri Göster'}
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[150px]">Vergi No</TableHead>
                        <TableHead>Ad Soyad</TableHead>
                        <TableHead>GİB Giriş Kodu</TableHead>
                        <TableHead>GİB Parola</TableHead>
                        <TableHead>GİB Şifre</TableHead>
                        <TableHead className="w-[100px]">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {musteriler.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                            Müşteri bulunamadı
                          </TableCell>
                        </TableRow>
                      ) : (
                        musteriler.map((musteri) => (
                          <TableRow key={musteri.id}>
                            <TableCell className="font-medium">{musteri.vergi_no}</TableCell>
                            <TableCell>{musteri.ad_soyad}</TableCell>
                            <TableCell>{musteri.gib_giris_kodu || '-'}</TableCell>
                            <TableCell>
                              <span>{showPasswordsGib ? (musteri.gib_parola || '-') : '••••••'}</span>
                            </TableCell>
                            <TableCell>
                              <span>{showPasswordsGib ? (musteri.gib_sifre || '-') : '••••••'}</span>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingMusteri(musteri);
                                  setMusteriEditDialogOpen(true);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SGK Sekmesi */}
          <TabsContent value="sgk" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <CardTitle>SGK İşyerleri</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => {
                          setEditingSgkIsyeri(null);
                          setYeniSgkIsyeri({
                            vergi_no: '',
                            ad_soyad: '',
                            isyeri_adi: 'MERKEZ',
                            sgk_sicil_no: '',
                            aktif: true
                          });
                          setSgkDialogOpen(true);
                        }}
                        data-testid="sgk-yeni-isyeri-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Yeni İşyeri Ekle
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await axios.get(`${API}/sgk-isyerleri/excel-export`, {
                              responseType: 'blob'
                            });
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `sgk_isyerleri_${new Date().toISOString().split('T')[0]}.xlsx`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                            toast.success('Excel dosyası indirildi');
                          } catch (error) {
                            console.error('Excel export hatası:', error);
                            toast.error(error.response?.data?.detail || 'Excel dışa aktarma hatası');
                          }
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Dışa Aktar
                      </Button>
                      <label>
                        <Button variant="outline" size="sm" asChild>
                          <span>
                            <FileUp className="w-4 h-4 mr-2" />
                            İçeri Aktar
                          </span>
                        </Button>
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              
                              const response = await axios.post(`${API}/sgk-isyerleri/excel-import`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              
                              const { imported, updated, errors } = response.data;
                              
                              if (imported > 0 || updated > 0) {
                                toast.success(`${imported} yeni kayıt eklendi, ${updated} kayıt güncellendi`);
                                fetchSgkIsyerleri(); // Listeyi yenile
                              }
                              
                              if (errors && errors.length > 0) {
                                toast.warning(`${errors.length} satırda hata oluştu`);
                                console.warn('Import hataları:', errors);
                              }
                            } catch (error) {
                              console.error('Excel import hatası:', error);
                              toast.error(error.response?.data?.detail || 'Excel içe aktarma hatası');
                            }
                            
                            e.target.value = ''; // Input'u sıfırla
                          }}
                        />
                      </label>
                    </div>
                  </div>
                  
                  {/* Müşteri Seçici - Sadece SGK kaydı olan müşteriler */}
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium whitespace-nowrap">Müşteri:</label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={selectedSgkMusteri}
                      onChange={(e) => setSelectedSgkMusteri(e.target.value)}
                    >
                      <option value="">Tüm Müşteriler</option>
                      {/* SGK kaydı olan müşterilerin vergi numaralarını al */}
                      {(() => {
                        const sgkVergiNolar = [...new Set(sgkIsyerleri.map(s => s.vergi_no))];
                        return musteriler
                          .filter(m => sgkVergiNolar.includes(m.vergi_no))
                          .map(m => (
                            <option key={m.id} value={m.vergi_no}>{m.ad_soyad} ({m.vergi_no})</option>
                          ));
                      })()}
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* SGK İşyerleri Tablosu */}
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => {
                            if (sgkSortField === 'musteri_ad') {
                              setSgkSortDirection(sgkSortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSgkSortField('musteri_ad');
                              setSgkSortDirection('asc');
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            Müşteri
                            {sgkSortField === 'musteri_ad' ? (
                              sgkSortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                            ) : (
                              <ArrowUpDown className="w-4 h-4 opacity-30" />
                            )}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => {
                            if (sgkSortField === 'isyeri_adi') {
                              setSgkSortDirection(sgkSortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSgkSortField('isyeri_adi');
                              setSgkSortDirection('asc');
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            İşyeri Adı
                            {sgkSortField === 'isyeri_adi' ? (
                              sgkSortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                            ) : (
                              <ArrowUpDown className="w-4 h-4 opacity-30" />
                            )}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => {
                            if (sgkSortField === 'sgk_sicil_no') {
                              setSgkSortDirection(sgkSortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSgkSortField('sgk_sicil_no');
                              setSgkSortDirection('asc');
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            SGK Sicil No
                            {sgkSortField === 'sgk_sicil_no' ? (
                              sgkSortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                            ) : (
                              <ArrowUpDown className="w-4 h-4 opacity-30" />
                            )}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => {
                            if (sgkSortField === 'aktif') {
                              setSgkSortDirection(sgkSortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSgkSortField('aktif');
                              setSgkSortDirection('asc');
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            Durum
                            {sgkSortField === 'aktif' ? (
                              sgkSortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                            ) : (
                              <ArrowUpDown className="w-4 h-4 opacity-30" />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="w-[120px]">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        // Seçilen müşteriye göre filtrele - sgkIsyerleri state'inden oku
                        let allIsyerleri = sgkIsyerleri || [];
                        
                        if (selectedSgkMusteri) {
                          allIsyerleri = allIsyerleri.filter(i => i.vergi_no === selectedSgkMusteri);
                        }
                        
                        // Müşteri bilgilerini ekle
                        allIsyerleri = allIsyerleri.map(isyeri => {
                          const musteri = musteriler.find(m => m.vergi_no === isyeri.vergi_no);
                          return {
                            ...isyeri,
                            musteri_ad: isyeri.ad_soyad || musteri?.ad_soyad || 'Bilinmiyor',
                            musteri_vergi_no: isyeri.vergi_no,
                            musteri_id: musteri?.id
                          };
                        });
                        
                        // Sıralama uygula
                        allIsyerleri.sort((a, b) => {
                          let aVal = a[sgkSortField];
                          let bVal = b[sgkSortField];
                          
                          // String değerleri küçük harfe çevir
                          if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                          if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                          
                          // Boolean değerleri sayıya çevir (aktif için)
                          if (typeof aVal === 'boolean') aVal = aVal ? 1 : 0;
                          if (typeof bVal === 'boolean') bVal = bVal ? 1 : 0;
                          
                          if (aVal < bVal) return sgkSortDirection === 'asc' ? -1 : 1;
                          if (aVal > bVal) return sgkSortDirection === 'asc' ? 1 : -1;
                          return 0;
                        });
                        
                        return allIsyerleri.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                              {selectedSgkMusteri ? 'Bu müşteriye ait SGK işyeri bulunamadı' : 'SGK işyeri bulunamadı'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          allIsyerleri.map((isyeri, idx) => (
                            <TableRow key={`${isyeri.musteri_id}-${idx}`}>
                              <TableCell className="font-medium">{isyeri.musteri_ad}</TableCell>
                              <TableCell>{isyeri.isyeri_adi || 'MERKEZ'}</TableCell>
                              <TableCell className="font-mono text-xs">{isyeri.sgk_sicil_no || '-'}</TableCell>
                              <TableCell>
                                <Badge variant={isyeri.aktif ? "success" : "secondary"}>
                                  {isyeri.aktif ? 'Aktif' : 'Pasif'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      // Düzenleme modunu aç
                                      const musteri = musteriler.find(m => m.vergi_no === isyeri.vergi_no);
                                      setEditingSgkIsyeri({
                                        ...isyeri,
                                        musteri_id: musteri?.id || null,
                                        musteri_vergi_no: musteri?.vergi_no || isyeri.vergi_no,
                                        original_sicil_no: isyeri.sgk_sicil_no
                                      });
                                      setYeniSgkIsyeri({
                                        vergi_no: musteri?.vergi_no || isyeri.vergi_no,
                                        ad_soyad: musteri?.ad_soyad || isyeri.ad_soyad,
                                        isyeri_adi: isyeri.isyeri_adi || 'MERKEZ',
                                        sgk_sicil_no: isyeri.sgk_sicil_no || '',
                                        aktif: isyeri.aktif !== false
                                      });
                                      setSgkDialogOpen(true);
                                    }}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                      // Aktif/Pasif toggle - sgk_dosyalari koleksiyonunda
                                      try {
                                        await axios.patch(`${API}/sgk-isyerleri/${isyeri.id}/aktif`, {
                                          aktif: !isyeri.aktif
                                        });
                                        toast.success('İşyeri durumu güncellendi');
                                        fetchSgkIsyerleri();
                                      } catch (error) {
                                        console.error('Güncelleme hatası:', error);
                                        toast.error('İşyeri güncellenemedi: ' + (error.response?.data?.detail || error.message));
                                      }
                                    }}
                                  >
                                    {isyeri.aktif ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                      if (confirm(`${isyeri.isyeri_adi} işyerini silmek istediğinize emin misiniz?`)) {
                                        try {
                                          // sgk_dosyalari koleksiyonundan sil
                                          await axios.delete(`${API}/sgk-isyerleri/${isyeri.id}`);
                                          toast.success('İşyeri silindi');
                                          fetchSgkIsyerleri();
                                        } catch (error) {
                                          console.error('Silme hatası:', error);
                                          toast.error('İşyeri silinemedi: ' + (error.response?.data?.detail || error.message));
                                        }
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        );
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Kasa Virman Sekmesi */}
          <TabsContent value="kasa-virman" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  💰 Kasa Bakiye Optimizasyonu
                </CardTitle>
                <p className="text-sm text-gray-600">
                  Günlük bakiye kontrolü ve otomatik virman önerileri
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Excel Yükleme ve Limit Girişi */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Sol: Excel Yükleme */}
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">
                      📄 Excel Dosyası Yükle
                    </label>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setKasaExcelFile(e.target.files[0]);
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {kasaExcelFile && (
                      <p className="text-xs text-green-600">✓ {kasaExcelFile.name}</p>
                    )}
                  </div>

                  {/* Sağ: Limit Girişi */}
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">
                      💵 Bakiye Limitleri (TL)
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Minimum</label>
                        <input
                          type="number"
                          value={kasaMinLimit}
                          onChange={(e) => setKasaMinLimit(parseFloat(e.target.value))}
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Maksimum</label>
                        <input
                          type="number"
                          value={kasaMaxLimit}
                          onChange={(e) => setKasaMaxLimit(parseFloat(e.target.value))}
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="20000"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analiz Butonu */}
                <Button
                  onClick={handleKasaOptimize}
                  disabled={!kasaExcelFile || kasaLoading}
                  className="w-full"
                >
                  {kasaLoading ? '⏳ İşleniyor...' : '🔍 Analiz Et ve Virman Önerisi Oluştur'}
                </Button>

                {/* Sonuç */}
                {kasaResult && (
                  <div className="space-y-4 p-6 bg-green-50 border border-green-200 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-800 flex items-center gap-2">
                      ✅ Analiz Tamamlandı
                    </h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="bg-white p-4 rounded-lg">
                        <p className="text-gray-600">Toplam Virman</p>
                        <p className="text-2xl font-bold text-blue-600">{kasaResult.toplam_virman}</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <p className="text-gray-600">Toplam Giren</p>
                        <p className="text-2xl font-bold text-green-600">
                          ₺{kasaResult.toplam_giren?.toLocaleString('tr-TR', {minimumFractionDigits: 2}) || '0.00'}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <p className="text-gray-600">Toplam Çıkan</p>
                        <p className="text-2xl font-bold text-red-600">
                          ₺{kasaResult.toplam_cikan?.toLocaleString('tr-TR', {minimumFractionDigits: 2}) || '0.00'}
                        </p>
                      </div>
                    </div>

                    {/* Virman Detayları */}
                    {kasaResult.virman_kayitlari && kasaResult.virman_kayitlari.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-semibold mb-2">Virman Detayları:</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm bg-white rounded-lg">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-2 text-left">Tarih</th>
                                <th className="px-4 py-2 text-right">Eski Bakiye</th>
                                <th className="px-4 py-2 text-right">Virman</th>
                                <th className="px-4 py-2 text-right">Yeni Bakiye</th>
                              </tr>
                            </thead>
                            <tbody>
                              {kasaResult.virman_kayitlari.map((virman, idx) => (
                                <tr key={idx} className="border-t">
                                  <td className="px-4 py-2">{virman.tarih}</td>
                                  <td className="px-4 py-2 text-right">₺{virman.eski_bakiye.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</td>
                                  <td className={`px-4 py-2 text-right font-semibold ${virman.virman_miktari < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {virman.virman_miktari < 0 ? '-' : '+'}₺{Math.abs(virman.virman_miktari).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                                  </td>
                                  <td className="px-4 py-2 text-right font-bold text-blue-600">₺{virman.yeni_bakiye.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* İndirme Butonu */}
                    {kasaResult.excel_data && (
                      <Button
                        onClick={handleKasaExcelDownload}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        📥 Virman Excel Dosyasını İndir
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* E-Beyanname Sekmesi */}
          <TabsContent value="ebeyanname" className="space-y-6">
            {/* GİB Webview Tahakkuk İndirme - YENİ */}
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  🌐 GİB Webview - Manuel Tahakkuk İndirme
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-white border-blue-300">
                  <AlertDescription>
                    <strong className="text-blue-700">Nasıl Çalışır?</strong><br/>
                    1. "GİB Sayfasını Aç" butonuna tıklayın<br/>
                    2. Açılan pencerede GİB'e manuel giriş yapın (CAPTCHA dahil)<br/>
                    3. Dönem seçin ve tahakkukları listeleyin<br/>
                    4. "Tahakkukları İndir ve Kaydet" butonuna tıklayın<br/>
                    5. Sistem tüm tahakkukları otomatik indirir ve Kayıtlara ekler!
                  </AlertDescription>
                </Alert>
                
                <div className="flex gap-2 flex-wrap">
                  {!gibWebviewSessionId ? (
                    <Button 
                      onClick={startGibWebview}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      🌐 GİB Sayfasını Aç
                    </Button>
                  ) : (
                    <>
                      <Button 
                        onClick={downloadGibTahakkuklar}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        📥 Tahakkukları İndir ve Kaydet
                      </Button>
                      <Button 
                        onClick={closeGibWebview}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        ❌ Kapat
                      </Button>
                      <Badge variant="default" className="bg-green-600 text-white px-3 py-1">
                        ✅ GİB Sayfası Açık
                      </Badge>
                    </>
                  )}
                </div>
                
                {/* Live Screenshot Viewer */}
                {gibWebviewSessionId && gibScreenshot && (
                  <div className="mt-4 border-2 border-blue-300 rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-semibold text-gray-700">Canlı Görünüm (Tıklanabilir)</span>
                      </div>
                      <span className="text-xs text-gray-500 font-mono">{gibCurrentUrl}</span>
                    </div>
                    
                    {/* Keyboard Shortcuts */}
                    <div className="mb-2 flex gap-2 flex-wrap">
                      <Input
                        type="text"
                        placeholder="Metin yaz ve Enter'a bas..."
                        className="flex-1 text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleTypeText(e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <Button size="sm" variant="outline" onClick={() => handleKeyPress('Tab')}>Tab</Button>
                      <Button size="sm" variant="outline" onClick={() => handleKeyPress('Enter')}>Enter</Button>
                      <Button size="sm" variant="outline" onClick={() => handleKeyPress('Backspace')}>⌫</Button>
                    </div>
                    
                    <div className="border rounded overflow-hidden cursor-pointer hover:border-blue-500 transition-colors">
                      <img 
                        src={gibScreenshot} 
                        alt="GİB Sayfası" 
                        className="w-full h-auto"
                        style={{ maxHeight: '600px', objectFit: 'contain' }}
                        onClick={handleScreenshotClick}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      💡 <strong>Tıklama:</strong> Ekrana tıklayarak form alanlarını seçin. <strong>Yazma:</strong> Yukarıdaki kutuda yazıp Enter'a basın.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* GİB API Token Yönetimi */}
            <Card className="border-2 border-emerald-200 bg-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  🔐 GİB API Token Yönetimi (Otomatik Tahakkuk İndirme)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-white border-emerald-300">
                  <AlertDescription>
                    <strong className="text-emerald-700">Nasıl Çalışır?</strong><br/>
                    1. GİB Dijital Vergi Dairesi'ne giriş yapın<br/>
                    2. "Entegrasyon Yönetimi" → "Token Oluştur" seçin<br/>
                    3. Oluşan token'ı buraya yapıştırın<br/>
                    4. Sistem otomatik olarak tahakkukları indirir ve parse eder!
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">GİB API Token</Label>
                  <Input 
                    type="password"
                    placeholder="GİB API Token'ınızı buraya yapıştırın..."
                    value={gibApiToken || ''}
                    onChange={(e) => setGibApiToken(e.target.value)}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-600">
                    Token'ınız güvenli şekilde saklanacak ve sadece tahakkuk indirmek için kullanılacaktır.
                  </p>
                </div>
                
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    onClick={saveGibToken}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={!gibApiToken || gibApiToken.length < 10}
                  >
                    💾 Token'ı Kaydet
                  </Button>
                  
                  {gibApiTokenSaved && (
                    <Button 
                      onClick={testGibConnection}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      🔌 Bağlantıyı Test Et
                    </Button>
                  )}
                  
                  <Button 
                    onClick={fetchGibTahakkuklar}
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={!gibApiTokenSaved}
                  >
                    📥 Tahakkukları İndir
                  </Button>
                  
                  {gibApiTokenSaved && (
                    <Button 
                      onClick={() => {
                        setGibApiToken('');
                        setGibApiTokenSaved(false);
                      }}
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      🗑️ Token'ı Sil
                    </Button>
                  )}
                </div>
                
                {gibApiTokenSaved && (
                  <Alert className="bg-green-50 border-green-300">
                    <AlertDescription className="text-green-800">
                      ✅ <strong>Token başarıyla kaydedildi!</strong> Artık "Tahakkukları İndir" butonuna tıklayarak otomatik indirme yapabilirsiniz.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Mali Müşavir GİB Bilgileri - Kompakt */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-blue-900">📋 Mali Müşavir GİB Bilgileri</span>
                  <span className="text-xs text-blue-600 italic">💡 İpucu: Bilgileri düzenlemek için çift tıklayın</span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="h-7 text-xs border-blue-300 hover:bg-blue-100"
                  onClick={() => setShowPasswordsMaliMusavir(!showPasswordsMaliMusavir)}
                >
                  {showPasswordsMaliMusavir ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                  {showPasswordsMaliMusavir ? 'Gizle' : 'Göster'}
                </Button>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                {/* Kullanıcı Kodu */}
                <div>
                  <label className="text-xs font-medium text-blue-900">Kullanıcı Kodu</label>
                  {editingMaliMusavirField === 'kullanici_kodu' ? (
                    <Input
                      autoFocus
                      value={tempMaliMusavirValue}
                      onChange={(e) => setTempMaliMusavirValue(e.target.value)}
                      onBlur={async () => {
                        try {
                          const updated = {...maliMusavir, kullanici_kodu: tempMaliMusavirValue};
                          await axios.put(`${API}/mali-musavir`, updated);
                          setMaliMusavir(updated);
                          toast.success('Kullanıcı kodu güncellendi');
                        } catch (error) {
                          toast.error('Güncelleme başarısız');
                        }
                        setEditingMaliMusavirField(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.blur();
                        if (e.key === 'Escape') {
                          setEditingMaliMusavirField(null);
                          setTempMaliMusavirValue('');
                        }
                      }}
                      className="mt-1 h-8 text-sm"
                    />
                  ) : (
                    <div 
                      className="mt-1 px-2 py-1.5 bg-white rounded border border-blue-200 cursor-pointer hover:bg-blue-50 transition-colors text-sm"
                      onDoubleClick={() => {
                        setEditingMaliMusavirField('kullanici_kodu');
                        setTempMaliMusavirValue(maliMusavir.kullanici_kodu || '');
                      }}
                    >
                      {maliMusavir.kullanici_kodu || '-'}
                    </div>
                  )}
                </div>

                {/* Şifre */}
                <div>
                  <label className="text-xs font-medium text-blue-900">Şifre</label>
                  {editingMaliMusavirField === 'sifre' ? (
                    <Input
                      autoFocus
                      type="text"
                      value={tempMaliMusavirValue}
                      onChange={(e) => setTempMaliMusavirValue(e.target.value)}
                      onBlur={async () => {
                        try {
                          const updated = {...maliMusavir, sifre: tempMaliMusavirValue};
                          await axios.put(`${API}/mali-musavir`, updated);
                          setMaliMusavir(updated);
                          toast.success('Şifre güncellendi');
                        } catch (error) {
                          toast.error('Güncelleme başarısız');
                        }
                        setEditingMaliMusavirField(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.blur();
                        if (e.key === 'Escape') {
                          setEditingMaliMusavirField(null);
                          setTempMaliMusavirValue('');
                        }
                      }}
                      className="mt-1 h-8 text-sm"
                    />
                  ) : (
                    <div 
                      className="mt-1 px-2 py-1.5 bg-white rounded border border-blue-200 cursor-pointer hover:bg-blue-50 transition-colors text-sm"
                      onDoubleClick={() => {
                        setEditingMaliMusavirField('sifre');
                        setTempMaliMusavirValue(maliMusavir.sifre || '');
                      }}
                    >
                      {showPasswordsMaliMusavir ? (maliMusavir.sifre || '-') : '••••••'}
                    </div>
                  )}
                </div>

                {/* Parola */}
                <div>
                  <label className="text-xs font-medium text-blue-900">Parola</label>
                  {editingMaliMusavirField === 'parola' ? (
                    <Input
                      autoFocus
                      type="text"
                      value={tempMaliMusavirValue}
                      onChange={(e) => setTempMaliMusavirValue(e.target.value)}
                      onBlur={async () => {
                        try {
                          const updated = {...maliMusavir, parola: tempMaliMusavirValue};
                          await axios.put(`${API}/mali-musavir`, updated);
                          setMaliMusavir(updated);
                          toast.success('Parola güncellendi');
                        } catch (error) {
                          toast.error('Güncelleme başarısız');
                        }
                        setEditingMaliMusavirField(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.blur();
                        if (e.key === 'Escape') {
                          setEditingMaliMusavirField(null);
                          setTempMaliMusavirValue('');
                        }
                      }}
                      className="mt-1 h-8 text-sm"
                    />
                  ) : (
                    <div 
                      className="mt-1 px-2 py-1.5 bg-white rounded border border-blue-200 cursor-pointer hover:bg-blue-50 transition-colors text-sm"
                      onDoubleClick={() => {
                        setEditingMaliMusavirField('parola');
                        setTempMaliMusavirValue(maliMusavir.parola || '');
                      }}
                    >
                      {showPasswordsMaliMusavir ? (maliMusavir.parola || '-') : '••••••'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* GİB İşlemleri */}
            <Card>
              <CardHeader>
                <CardTitle>GİB E-Beyanname İşlemleri</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Adım 1: GİB'e Giriş + Otomasyon */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">📍 Adım 1: GİB'e Giriş Yapın</h3>
                  
                  {/* Manuel Giriş */}
                  <div className="mb-3">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>1. GİB'e giriş yapın (Manuel):</strong>
                    </p>
                    <Button
                      onClick={() => {
                        window.open('https://dijital.gib.gov.tr/', '_blank');
                        toast.info('GİB sistemi yeni sekmede açıldı');
                      }}
                      className="w-full sm:w-auto"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      GİB Sistemine Git
                    </Button>
                    <div className="mt-2 text-xs text-gray-500 space-y-1">
                      <p>• Kullanıcı Kodu: <strong className="text-gray-700">{maliMusavir.kullanici_kodu || '(boş)'}</strong></p>
                      <p>• Şifre: <strong className="text-gray-700">{showPasswordsMaliMusavir ? (maliMusavir.sifre || '(boş)') : '••••••'}</strong></p>
                      <p>• CAPTCHA'yı girin ve giriş yapın</p>
                    </div>
                  </div>

                  <Separator className="my-3" />

                  {/* Otomasyon Scripti */}
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>2. Tahakkuk İndirme Scriptini Çalıştırın:</strong>
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const script = `
// GİB Tahakkuk Otomatik ZIP İndirme Scripti (Sadece T harfi olanlar)
(async function() {
  console.log('🚀 Tahakkuk ZIP indirme başlatıldı...');
  console.log('📌 Sadece "T" (Tahakkuk) harfi olanlar indirilecek');
  console.log('📦 JSZip kütüphanesi yükleniyor...');
  
  // JSZip kütüphanesini yükle
  if (typeof JSZip === 'undefined') {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
    document.head.appendChild(script);
    await new Promise(resolve => script.onload = resolve);
    console.log('✅ JSZip yüklendi');
  }
  
  const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  const zip = new JSZip();
  let toplamIndirilen = 0;
  let atlandiSayisi = 0;
  
  try {
    // Sayfa bilgisini al
    const getSayfaBilgisi = () => {
      const sayfaText = document.body.textContent;
      const match = sayfaText.match(/(\\d+)\\s*-\\s*(\\d+)\\s*\\/\\s*(\\d+)/);
      if (match) {
        return {
          baslangic: parseInt(match[1]),
          bitis: parseInt(match[2]),
          toplam: parseInt(match[3])
        };
      }
      return null;
    };
    
    // Tahakkuk PDF'lerini al ve ZIP'e ekle (SADECE T harfi olanlar)
    const tahakkuklariZipEkle = async () => {
      // Tablodaki tüm satırları bul
      const rows = Array.from(document.querySelectorAll('table tr'));
      console.log(\`📊 Tabloda \${rows.length} satır bulundu\`);
      
      const tahakkukLinks = [];
      
      // Her satırı kontrol et
      for (const row of rows) {
        // Satırda "T" harfi var mı kontrol et (Adobe logo'nun yanında)
        const rowText = row.textContent;
        const cells = row.querySelectorAll('td');
        
        // Son sütunlarda "T" harfi ara (genelde tahakkuk/beyanname göstergeleri sonda)
        let tHarfiVar = false;
        
        // Yöntem 1: Hücrelerde sadece "T" yazısı olan var mı
        for (const cell of cells) {
          const text = cell.textContent.trim();
          if (text === 'T' || text === 'T ') {
            tHarfiVar = true;
            break;
          }
        }
        
        // Yöntem 2: Satırda <img> içinde "T" olan bir element ara
        if (!tHarfiVar) {
          const imgs = row.querySelectorAll('img');
          for (const img of imgs) {
            const alt = img.alt || '';
            const title = img.title || '';
            // Adobe PDF logosunun yanında T harfi kontrolü
            const nextElement = img.nextElementSibling;
            const prevElement = img.previousElementSibling;
            const parent = img.parentElement;
            
            if (alt.includes('T') || title.includes('T') || 
                (nextElement && nextElement.textContent.trim() === 'T') ||
                (prevElement && prevElement.textContent.trim() === 'T') ||
                (parent && parent.textContent.includes('T'))) {
              tHarfiVar = true;
              break;
            }
          }
        }
        
        if (tHarfiVar) {
          // Bu satırda PDF linki bul
          // Adobe logo veya PDF ikonu ara
          const pdfLinks = Array.from(row.querySelectorAll('a, img')).filter(el => {
            const src = el.src || '';
            const href = el.href || '';
            const alt = el.alt || '';
            const onclick = el.getAttribute('onclick') || '';
            
            return src.includes('pdf') || src.includes('adobe') || 
                   href.includes('pdf') || href.includes('tahakkuk') ||
                   alt.includes('pdf') || alt.includes('Adobe') ||
                   onclick.includes('pdf') || onclick.includes('tahakkuk');
          });
          
          // Link'leri topla
          for (const el of pdfLinks) {
            if (el.tagName === 'A') {
              tahakkukLinks.push({ link: el, row: row });
            } else {
              const parentLink = el.closest('a');
              if (parentLink) {
                tahakkukLinks.push({ link: parentLink, row: row });
              }
            }
          }
        } else {
          // B harfi veya başka bir şey - atla
          const hasB = Array.from(cells).some(cell => cell.textContent.trim() === 'B');
          if (hasB) {
            atlandiSayisi++;
          }
        }
      }
      
      console.log(\`📄 Bu sayfada \${tahakkukLinks.length} TAHAKKUk (T) bulundu\`);
      console.log(\`⏭️  Bu sayfada \${atlandiSayisi} BEYANNAME (B) atlandı\`);
      
      // Unique yap (aynı link birden fazla bulunabilir)
      const uniqueLinks = Array.from(new Set(tahakkukLinks.map(item => item.link)));
      console.log(\`🔗 \${uniqueLinks.length} benzersiz PDF linki işlenecek\`);
      
      for (let i = 0; i < uniqueLinks.length; i++) {
        try {
          console.log(\`⬇️  İşleniyor: \${i + 1}/\${uniqueLinks.length}\`);
          
          const link = uniqueLinks[i];
          const href = link.href;
          const onclick = link.getAttribute('onclick');
          
          // Müşteri adını ve tarih bilgisini al (dosya adı için)
          const row = link.closest('tr');
          let fileName = \`tahakkuk_\${toplamIndirilen + 1}.pdf\`;
          
          if (row) {
            const cells = row.querySelectorAll('td');
            if (cells.length >= 3) {
              const beyannameAd = cells[0]?.textContent?.trim() || '';
              const vkn = cells[1]?.textContent?.trim() || '';
              const donem = cells[3]?.textContent?.trim() || '';
              
              // VKN ve dönemi kullanarak dosya adı oluştur
              if (vkn && donem) {
                fileName = \`\${vkn}_\${donem.replace(/\\//g, '-')}.pdf\`.replace(/[^a-zA-Z0-9._-]/g, '_');
              } else if (beyannameAd) {
                fileName = \`\${beyannameAd.substring(0, 20)}_\${toplamIndirilen + 1}.pdf\`.replace(/[^a-zA-Z0-9._-]/g, '_');
              }
            }
          }
          
          // PDF'i indir
          if (href && href.includes('http')) {
            // Direkt URL varsa fetch ile al
            try {
              const response = await fetch(href);
              if (response.ok) {
                const blob = await response.blob();
                zip.file(fileName, blob);
                toplamIndirilen++;
                console.log(\`✅ ZIP'e eklendi: \${fileName}\`);
              } else {
                throw new Error('Fetch başarısız: ' + response.status);
              }
            } catch (fetchError) {
              console.log(\`⚠️  Fetch hatası: \${fetchError.message}\`);
              console.log(\`ℹ️  onclick ile deneniyor...\`);
              // Fetch çalışmazsa onclick dene
              if (onclick) {
                try {
                  eval(onclick);
                  console.log(\`⬇️  Tarayıcı indirme başlatıldı: \${fileName}\`);
                  console.log(\`⚠️  Bu dosyayı manuel ZIP'e eklemeniz gerekebilir\`);
                } catch (clickError) {
                  console.error(\`❌ onclick hatası: \${clickError.message}\`);
                }
              }
            }
          } else if (onclick) {
            // onclick varsa değerlendir
            try {
              eval(onclick);
              console.log(\`⬇️  Tarayıcı indirme başlatıldı: \${fileName}\`);
              console.log(\`⚠️  Bu dosyayı manuel ZIP'e eklemeniz gerekebilir\`);
            } catch (clickError) {
              console.error(\`❌ onclick hatası: \${clickError.message}\`);
            }
          }
          
          await wait(1000); // Her indirme arasında 1 saniye bekle
        } catch (e) {
          console.error(\`❌ Genel hata: \${e.message}\`);
        }
      }
      
      return uniqueLinks.length;
    };
    
    // SADECE MEVCUT SAYFAYI İŞLE (çok sayfalı işlem şimdilik devre dışı)
    console.log(\`\\n📑 Mevcut sayfa işleniyor...\`);
    
    const sayfaBilgi = getSayfaBilgisi();
    if (sayfaBilgi) {
      console.log(\`📊 Sayfa: \${sayfaBilgi.baslangic}-\${sayfaBilgi.bitis} / Toplam: \${sayfaBilgi.toplam}\`);
    }
    
    // Bu sayfadaki tahakkukları işle
    const indirilenSayi = await tahakkuklariZipEkle();
    
    if (indirilenSayi === 0) {
      console.log('⚠️  Bu sayfada "T" harfli tahakkuk bulunamadı.');
      console.log('ℹ️  Lütfen doğru sayfada olduğunuzdan emin olun.');
    }
    
    console.log(\`\\n📦 ZIP dosyası oluşturuluyor...\`);
    
    // ZIP'i oluştur ve indir
    if (toplamIndirilen > 0) {
      try {
        const zipBlob = await zip.generateAsync({
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: { level: 6 }
        });
        
        // ZIP'i indir
        const link = document.createElement('a');
        link.href = URL.createObjectURL(zipBlob);
        const tarih = new Date().toISOString().split('T')[0];
        link.download = \`gib_tahakkuklar_\${tarih}.zip\`;
        link.click();
        
        console.log(\`\\n🎉 ZIP indirme tamamlandı!\`);
        console.log(\`📥 ZIP'e eklenen: \${toplamIndirilen} tahakkuk PDF'i\`);
        console.log(\`⏭️  Atlanan: \${atlandiSayisi} beyanname (B harfi)\`);
        console.log(\`📂 Dosya adı: gib_tahakkuklar_\${tarih}.zip\`);
        console.log(\`\\n📋 Sonraki adım: ZIP dosyasını sisteme yükleyin.\`);
      } catch (zipError) {
        console.error('❌ ZIP oluşturma hatası:', zipError);
        console.log('ℹ️  Downloads klasöründeki PDF\\'leri manuel olarak ZIP\\'leyin.');
      }
    } else {
      console.log('⚠️ ZIP\\'e eklenecek dosya bulunamadı.');
      console.log('ℹ️ Downloads klasöründeki PDF\\'leri manuel olarak ZIP\\'leyin.');
    }
    
  } catch (error) {
    console.error('❌ Hata:', error);
    console.log(\`ℹ️ Toplam \${toplamIndirilen} tahakkuk işlendi (hata öncesi).\`);
  }
})();
`.trim();
                        
                        navigator.clipboard.writeText(script);
                        toast.success('Script panoya kopyalandı!');
                      }}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Otomasyon Scriptini Kopyala
                    </Button>
                    <div className="mt-2 text-xs text-gray-500 space-y-1">
                      <p>• Beyanname listesi sayfasındayken <kbd className="px-1 bg-gray-200 rounded">F12</kbd> ile Console'u açın</p>
                      <p>• Console'a <code className="px-1 bg-gray-200 rounded text-red-600">allow pasting</code> yazıp Enter</p>
                      <p>• Script'i yapıştırın ve <kbd className="px-1 bg-gray-200 rounded">Enter</kbd> tuşuna basın</p>
                      <p>• Script otomatik olarak:</p>
                      <p className="ml-3">✅ JSZip kütüphanesini yükler</p>
                      <p className="ml-3">✅ Her satırdaki "T" (Tahakkuk) PDF'ini alır</p>
                      <p className="ml-3">✅ Tüm sayfaları gezer (25 tahakkuk/sayfa)</p>
                      <p className="ml-3">✅ Tüm tahakkukları tek ZIP'e koyar</p>
                      <p className="ml-3">✅ ZIP'i otomatik indirir (gib_tahakkuklar_YYYY-MM-DD.zip)</p>
                      <p className="text-green-600 font-medium">🎉 ZIP otomatik inecek - direkt "Tahakkuk ZIP Yükle" ile yükleyin!</p>
                    </div>
                  </div>
                </div>

                {/* Adım 2: ZIP Yükleme */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 mb-2">📍 Adım 2: Tahakkuk ZIP'ini Yükleyin</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    İndirdiğiniz ZIP dosyasını yükleyin, sistem otomatik işleyecek
                  </p>
                  <Button
                    onClick={() => document.getElementById('tahakkuk-zip-upload').click()}
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    <FileUp className="w-4 h-4 mr-2" />
                    Tahakkuk ZIP/RAR Yükle
                  </Button>
                  <input
                    id="tahakkuk-zip-upload"
                    type="file"
                    accept=".zip,.rar"
                    style={{display: 'none'}}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      // Dosya boyutu kontrolü (100MB max)
                      if (file.size > 100 * 1024 * 1024) {
                        toast.error('Dosya boyutu 100MB\'dan büyük olamaz');
                        return;
                      }
                      
                      setLoading(true);
                      toast.info(`${file.name} yükleniyor...`);
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      try {
                        const response = await axios.post(`${API}/yuklenen-vergiler/upload-zip`, formData, {
                          headers: {'Content-Type': 'multipart/form-data'},
                          timeout: 300000, // 5 dakika timeout
                          onUploadProgress: (progressEvent) => {
                            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            if (percent % 20 === 0) {
                              console.log(`Yükleme: ${percent}%`);
                            }
                          }
                        });
                        
                        setTahakkukSonuc(response.data);
                        toast.success(`${response.data.basarili} PDF başarıyla işlendi!`);
                        fetchYuklenenVergiler(); // Yüklenen Vergileri yenile
                        fetchBeyannamesiOlmayanlar(); // Listeyi güncelle
                      } catch (error) {
                        console.error('ZIP yükleme hatası:', error);
                        if (error.response) {
                          toast.error(`Hata: ${error.response.data?.detail || error.response.statusText}`);
                        } else if (error.code === 'ECONNABORTED') {
                          toast.error('Yükleme zaman aşımına uğradı. Daha küçük dosya deneyin.');
                        } else {
                          toast.error('ZIP işlenemedi: ' + error.message);
                        }
                      } finally {
                        setLoading(false);
                      }
                      e.target.value = '';
                    }}
                  />
                  
                  {/* Sonuç Gösterimi */}
                  {tahakkukSonuc && (
                    <div className="mt-3 p-3 bg-white rounded border text-sm">
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <div>
                          <span className="text-gray-600">Toplam PDF:</span>
                          <strong className="ml-1">{tahakkukSonuc.toplam_pdf}</strong>
                        </div>
                        <div>
                          <span className="text-green-600">Başarılı:</span>
                          <strong className="ml-1 text-green-700">{tahakkukSonuc.basarili}</strong>
                        </div>
                        <div>
                          <span className="text-red-600">Hatalı:</span>
                          <strong className="ml-1 text-red-700">{tahakkukSonuc.hatali}</strong>
                        </div>
                      </div>
                      {tahakkukSonuc.islenmeyen_musteriler && tahakkukSonuc.islenmeyen_musteriler.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-orange-600 font-medium">
                            İşlenemeyen Müşteriler ({tahakkukSonuc.islenmeyen_musteriler.length})
                          </summary>
                          <ul className="mt-2 space-y-1 text-xs">
                            {tahakkukSonuc.islenmeyen_musteriler.map((m, idx) => (
                              <li key={idx} className="text-gray-600">
                                VKN: {m.vergi_no} - {m.sebep}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                </div>

                {/* Adım 3: Beyannamesi Olmayanlar */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-orange-900">📍 Beyannamesi Olmayan Müşteriler</h3>
                    <div className="flex gap-2">
                      <Select value={tahakkukAy} onValueChange={setTahakkukAy}>
                        <SelectTrigger className="w-32 h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                            'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'].map(ay => (
                            <SelectItem key={ay} value={ay}>{ay}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={String(tahakkukYil)} onValueChange={(v) => setTahakkukYil(Number(v))}>
                        <SelectTrigger className="w-24 h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2024, 2025, 2026].map(yil => (
                            <SelectItem key={yil} value={String(yil)}>{yil}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={fetchBeyannamesiOlmayanlar}>
                        Listele
                      </Button>
                    </div>
                  </div>
                  
                  {beyannamesiOlmayanlar && beyannamesiOlmayanlar.liste && beyannamesiOlmayanlar.liste.length > 0 ? (
                    <div>
                      <div className="mb-2 text-sm">
                        <span className="text-orange-600 font-medium">
                          {beyannamesiOlmayanlar.beyannamesi_olmayan} müşteri
                        </span>
                        <span className="text-gray-600"> (Toplam: {beyannamesiOlmayanlar.toplam_musteri})</span>
                      </div>
                      <div className="rounded-md border max-h-60 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Vergi No</TableHead>
                              <TableHead>Ad Soyad</TableHead>
                              <TableHead>Telefon</TableHead>
                              <TableHead>Türü</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {beyannamesiOlmayanlar.liste.map((musteri, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-mono text-sm">{musteri.vergi_no}</TableCell>
                                <TableCell>{musteri.ad_soyad}</TableCell>
                                <TableCell>{musteri.telefon || '-'}</TableCell>
                                <TableCell>
                                  <Badge variant="outline">{musteri.musteri_turu}</Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ) : beyannamesiOlmayanlar && beyannamesiOlmayanlar.liste ? (
                    <p className="text-sm text-gray-600">
                      🎉 Tüm müşterilerin beyannamesi var!
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600">
                      Listelemek için "Listele" butonuna tıklayın
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admin Panel Tab */}
          {currentUser?.is_admin && (
            <TabsContent value="admin" className="space-y-6">
              <AdminPanel currentUser={currentUser} />
              <KurulumIndirmeleri />
              <VideoYonetimi />
            </TabsContent>
          )}

        </Tabs>
      </main>

      {/* Edit Dialog */}
      {editingKayit && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="edit-record-dialog">
            <DialogHeader>
              <DialogTitle>Kayıt Düzenle</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleKayitGuncelle}>
              <div className="grid grid-cols-2 gap-4 py-4">
                <div>
                  <Label>Bakiye</Label>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="edit-bakiye-input"
                    value={editingKayit.bakiye}
                    onChange={(e) => setEditingKayit({...editingKayit, bakiye: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div>
                  <Label>Muhasebe</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingKayit.muhasebe}
                    onChange={(e) => setEditingKayit({...editingKayit, muhasebe: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div>
                  <Label>KDV</Label>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="edit-kdv-input"
                    value={editingKayit.kdv}
                    onChange={(e) => setEditingKayit({...editingKayit, kdv: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div>
                  <Label>Stopaj</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingKayit.stopaj}
                    onChange={(e) => setEditingKayit({...editingKayit, stopaj: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div>
                  <Label>SSK</Label>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="edit-ssk-input"
                    value={editingKayit.ssk}
                    onChange={(e) => setEditingKayit({...editingKayit, ssk: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div>
                  <Label>BAĞKUR</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingKayit.bagkur}
                    onChange={(e) => setEditingKayit({...editingKayit, bagkur: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div>
                  <Label>Geçici Vergi</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingKayit.gecici_vergi}
                    onChange={(e) => setEditingKayit({...editingKayit, gecici_vergi: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div>
                  <Label>Gelir Vergisi</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingKayit.gelir_vergisi}
                    onChange={(e) => setEditingKayit({...editingKayit, gelir_vergisi: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div>
                  <Label>Diğer 1</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingKayit.diger1}
                    onChange={(e) => setEditingKayit({...editingKayit, diger1: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div>
                  <Label>Diğer 2</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingKayit.diger2}
                    onChange={(e) => setEditingKayit({...editingKayit, diger2: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div>
                  <Label>Diğer 3</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingKayit.diger3}
                    onChange={(e) => setEditingKayit({...editingKayit, diger3: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div>
                  <Label>Ödenen</Label>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="edit-odenen-input"
                    value={editingKayit.odenen}
                    onChange={(e) => setEditingKayit({...editingKayit, odenen: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={loading} data-testid="update-record-btn">
                  Güncelle
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Toplu Veri Girişi Dialog */}
      <Dialog open={topluVeriDialogOpen} onOpenChange={setTopluVeriDialogOpen}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto" data-testid="bulk-input-dialog">
          <DialogHeader>
            <DialogTitle>Toplu Veri Girişi - Excel Benzeri</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Ay ve Yıl Seçimi - ÜST KISIM */}
            <div className="flex gap-4 items-end bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
              <div className="flex-1">
                <Label className="text-base font-semibold">Ay Seçimi (Tüm Müşteriler İçin)</Label>
                <Select value={topluVeriAy} onValueChange={handleTopluVeriAyDegisti}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Ay Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {AYLAR.map((ay) => (
                      <SelectItem key={ay} value={ay}>{ay}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-base font-semibold">Yıl</Label>
                <Input
                  type="number"
                  value={topluVeriYil}
                  onChange={(e) => setTopluVeriYil(parseInt(e.target.value))}
                  className="mt-2"
                />
              </div>
            </div>
            
            <Alert>
              <AlertDescription>
                💡 <strong>Excel Gibi Kullanım:</strong> Tab/Enter ile ilerle, Shift+Tab ile geri git, Copy-Paste yapabilirsiniz.
              </AlertDescription>
            </Alert>
            
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-56 min-w-[14rem]">Müşteri</TableHead>
                    <TableHead className="text-right min-w-[120px]">Bakiye</TableHead>
                    <TableHead className="text-right min-w-[120px]">Muhasebe</TableHead>
                    <TableHead className="text-right min-w-[120px]">KDV</TableHead>
                    <TableHead className="text-right min-w-[120px]">Stopaj</TableHead>
                    <TableHead className="text-right min-w-[120px]">SSK</TableHead>
                    <TableHead className="text-right min-w-[120px]">BAĞKUR</TableHead>
                    <TableHead className="text-right min-w-[130px]">Geçici Vergi</TableHead>
                    <TableHead className="text-right min-w-[130px]">Gelir Vergisi</TableHead>
                    <TableHead className="text-right min-w-[120px]">Diğer 1</TableHead>
                    <TableHead className="text-right min-w-[120px]">Diğer 2</TableHead>
                    <TableHead className="text-right min-w-[120px]">Diğer 3</TableHead>
                    <TableHead className="text-right bg-blue-100 min-w-[140px] font-bold">Toplam</TableHead>
                    <TableHead className="text-right bg-red-100 min-w-[120px] font-bold">Ödenen</TableHead>
                    <TableHead className="text-right bg-green-100 min-w-[140px] font-bold">Kalan</TableHead>
                    <TableHead className="min-w-[200px]">Not</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topluVeriKayitlar.map((kayit, idx) => {
                    const updateField = (field, value) => {
                      const yeni = [...topluVeriKayitlar];
                      // RAW değeri sakla - string olarak
                      yeni[idx][field] = value;
                      setTopluVeriKayitlar(yeni);
                    };
                    
                    const handleBlurField = (field, value) => {
                      const yeni = [...topluVeriKayitlar];
                      // Blur olunca parse et ve Türkçe formatla (15.000,50)
                      const parsed = parsePara(value);
                      const formatted = formatPara(parsed);
                      yeni[idx][field] = formatted;
                      setTopluVeriKayitlar(yeni);
                    };
                    
                    const handleFocusField = (e) => {
                      // Focus olunca tüm değeri seç
                      e.target.select();
                    };
                    
                    // Müşteri bilgisini bul
                    const musteri = musteriler.find(m => m.vergi_no === kayit.vergi_no);
                    const musteriTuru = musteri?.musteri_turu || 'İŞLETME';
                    const stopajPeriyot = musteri?.stopaj_periyot || 'Aylık';
                    
                    // Alan görünürlük kontrolü
                    const stopajGorunur = vergiAlaniGorunurMu(musteriTuru, stopajPeriyot, topluVeriAy, 'stopaj');
                    const geciciVergiGorunur = vergiAlaniGorunurMu(musteriTuru, stopajPeriyot, topluVeriAy, 'gecici_vergi');
                    const gelirVergisiGorunur = vergiAlaniGorunurMu(musteriTuru, stopajPeriyot, topluVeriAy, 'gelir_vergisi');
                    
                    // Toplam hesapla
                    const toplam = (kayit.bakiye || 0) + 
                                  (kayit.muhasebe || 0) + 
                                  (kayit.kdv || 0) + 
                                  (stopajGorunur ? (kayit.stopaj || 0) : 0) + 
                                  (kayit.ssk || 0) + 
                                  (kayit.bagkur || 0) + 
                                  (geciciVergiGorunur ? (kayit.gecici_vergi || 0) : 0) + 
                                  (gelirVergisiGorunur ? (kayit.gelir_vergisi || 0) : 0) + 
                                  (kayit.diger1 || 0) + 
                                  (kayit.diger2 || 0) + 
                                  (kayit.diger3 || 0);
                    
                    const kalan = toplam - (kayit.odenen || 0);
                    
                    const handleKeyDown = (e, field, colIdx) => {
                      if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        if (idx > 0) {
                          const nextInput = document.getElementById(`cell-${idx-1}-${colIdx}`);
                          if (nextInput) nextInput.focus();
                        }
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        if (idx < topluVeriKayitlar.length - 1) {
                          const nextInput = document.getElementById(`cell-${idx+1}-${colIdx}`);
                          if (nextInput) nextInput.focus();
                        }
                      } else if (e.key === 'ArrowLeft') {
                        e.preventDefault();
                        if (colIdx > 0) {
                          const nextInput = document.getElementById(`cell-${idx}-${colIdx-1}`);
                          if (nextInput) nextInput.focus();
                        }
                      } else if (e.key === 'ArrowRight') {
                        e.preventDefault();
                        if (colIdx < 12) { // 13 sütun var (0-12), NOT kolonu dahil
                          const nextInput = document.getElementById(`cell-${idx}-${colIdx+1}`);
                          if (nextInput) nextInput.focus();
                        }
                      }
                    };
                    
                    const excelInputClass = "border-0 rounded-none text-right h-12 px-3 text-base focus:bg-yellow-100 focus:ring-2 focus:ring-blue-400 transition-colors";
                    const notInputClass = "border-0 rounded-none h-12 px-3 text-base focus:bg-yellow-100 focus:ring-2 focus:ring-blue-400 transition-colors";
                    
                    return (
                    <TableRow key={kayit.vergi_no} className="hover:bg-blue-50">
                      <TableCell className="font-medium bg-slate-100 sticky left-0 px-4 py-3 text-base">{kayit.ad_soyad}</TableCell>
                      <TableCell className="p-0">
                        <Input id={`cell-${idx}-0`} type="text" value={kayit.bakiye || ""} onChange={(e) => updateField('bakiye', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('bakiye', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'bakiye', 0)} className={excelInputClass} />
                      </TableCell>
                      <TableCell className="p-0">
                        <Input id={`cell-${idx}-1`} type="text" value={kayit.muhasebe || ""} onChange={(e) => updateField('muhasebe', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('muhasebe', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'muhasebe', 1)} className={excelInputClass} />
                      </TableCell>
                      <TableCell className="p-0">
                        <Input id={`cell-${idx}-2`} type="text" value={kayit.kdv || ""} onChange={(e) => updateField('kdv', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('kdv', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'kdv', 2)} className={excelInputClass} />
                      </TableCell>
                      {stopajGorunur && (
                        <TableCell className="p-0">
                          <Input id={`cell-${idx}-3`} type="text" value={kayit.stopaj || ""} onChange={(e) => updateField('stopaj', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('stopaj', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'stopaj', 3)} className={excelInputClass} />
                        </TableCell>
                      )}
                      {!stopajGorunur && (
                        <TableCell className="p-0 bg-gray-100">
                          <div className="text-center text-gray-400 py-3">-</div>
                        </TableCell>
                      )}
                      <TableCell className="p-0">
                        <Input id={`cell-${idx}-4`} type="text" value={kayit.ssk || ""} onChange={(e) => updateField('ssk', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('ssk', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'ssk', 4)} className={excelInputClass} />
                      </TableCell>
                      <TableCell className="p-0">
                        <Input id={`cell-${idx}-5`} type="text" value={kayit.bagkur || ""} onChange={(e) => updateField('bagkur', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('bagkur', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'bagkur', 5)} className={excelInputClass} />
                      </TableCell>
                      {geciciVergiGorunur && (
                        <TableCell className="p-0">
                          <Input id={`cell-${idx}-6`} type="text" value={kayit.gecici_vergi || ""} onChange={(e) => updateField('gecici_vergi', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('gecici_vergi', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'gecici_vergi', 6)} className={excelInputClass} />
                        </TableCell>
                      )}
                      {!geciciVergiGorunur && (
                        <TableCell className="p-0 bg-gray-100">
                          <div className="text-center text-gray-400 py-3">-</div>
                        </TableCell>
                      )}
                      {gelirVergisiGorunur && (
                        <TableCell className="p-0">
                          <Input id={`cell-${idx}-7`} type="text" value={kayit.gelir_vergisi || ""} onChange={(e) => updateField('gelir_vergisi', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('gelir_vergisi', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'gelir_vergisi', 7)} className={excelInputClass} />
                        </TableCell>
                      )}
                      {!gelirVergisiGorunur && (
                        <TableCell className="p-0 bg-gray-100">
                          <div className="text-center text-gray-400 py-3">-</div>
                        </TableCell>
                      )}
                      <TableCell className="p-0">
                        <Input id={`cell-${idx}-8`} type="text" value={kayit.diger1 || ""} onChange={(e) => updateField('diger1', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('diger1', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'diger1', 8)} className={excelInputClass} />
                      </TableCell>
                      <TableCell className="p-0">
                        <Input id={`cell-${idx}-9`} type="text" value={kayit.diger2 || ""} onChange={(e) => updateField('diger2', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('diger2', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'diger2', 9)} className={excelInputClass} />
                      </TableCell>
                      <TableCell className="p-0">
                        <Input id={`cell-${idx}-10`} type="text" value={kayit.diger3 || ""} onChange={(e) => updateField('diger3', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('diger3', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'diger3', 10)} className={excelInputClass} />
                      </TableCell>
                      <TableCell className="p-0 bg-blue-50">
                        <div className="text-right font-bold px-4 py-3 text-base">{formatPara(toplam)}</div>
                      </TableCell>
                      <TableCell className="p-0 bg-red-50">
                        <Input id={`cell-${idx}-11`} type="text" value={kayit.odenen || ""} onChange={(e) => updateField('odenen', e.target.value)} onFocus={handleFocusField} onBlur={(e) => handleBlurField('odenen', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'odenen', 11)} className={excelInputClass + " bg-red-50 font-semibold"} />
                      </TableCell>
                      <TableCell className="p-0 bg-green-50">
                        <div className="text-right font-bold px-4 py-3 text-base">{formatPara(kalan)}</div>
                      </TableCell>
                      <TableCell className="p-0">
                        <Input id={`cell-${idx}-12`} type="text" value={kayit.notlar || ''} onChange={(e) => updateField('notlar', e.target.value)} onKeyDown={(e) => handleKeyDown(e, 'notlar', 12)} className={notInputClass} placeholder="Not ekle..." />
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleTopluVeriKaydet} disabled={loading}>
              Tümünü Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Müşteri Düzenleme Dialog */}
      {editingMusteri && (
        <Dialog open={musteriEditDialogOpen} onOpenChange={setMusteriEditDialogOpen}>
          <DialogContent className="w-[95vw] max-w-2xl h-[95vh] md:h-auto md:max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 md:p-6" data-testid="edit-customer-dialog">
            <DialogHeader>
              <DialogTitle className="text-lg md:text-xl">Müşteri Düzenle</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleMusteriGuncelle}>
              <div className="py-2 md:py-4">
                {/* İlk Satır - Temel Bilgiler (Mobilde 1, Masaüstünde 3 Kolon) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4">
                  <div>
                    <Label htmlFor="edit_vergi_no">Vergi Numarası *</Label>
                    <Input
                      id="edit_vergi_no"
                      defaultValue={editingMusteri.vergi_no}
                      onBlur={(e) => editingMusteri.vergi_no = e.target.value}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_ad_soyad">Ad Soyad *</Label>
                    <Input
                      id="edit_ad_soyad"
                      defaultValue={editingMusteri.ad_soyad}
                      onBlur={(e) => editingMusteri.ad_soyad = e.target.value}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_telefon">Telefon (WhatsApp)</Label>
                    <Input
                      id="edit_telefon"
                      placeholder="905551234567"
                      defaultValue={editingMusteri.telefon || ''}
                      onBlur={(e) => editingMusteri.telefon = e.target.value}
                    />
                  </div>
                </div>
                
                {/* İkinci Satır - Seçimler (Mobilde 1, Masaüstünde 3 Kolon) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4">
                  <div>
                    <Label className="font-semibold">Müşteri Türü *</Label>
                    <div className="flex flex-col gap-2 mt-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="edit_musteri_turu"
                          defaultChecked={(editingMusteri.musteri_turu || 'İŞLETME') === 'İŞLETME'}
                          onChange={() => { editingMusteri.musteri_turu = 'İŞLETME'; }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">İŞLETME</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="edit_musteri_turu"
                          defaultChecked={(editingMusteri.musteri_turu || 'İŞLETME') === 'BİLANÇO'}
                          onChange={() => { editingMusteri.musteri_turu = 'BİLANÇO'; }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">BİLANÇO</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="edit_musteri_turu"
                          defaultChecked={(editingMusteri.musteri_turu || 'İŞLETME') === 'ŞİRKET'}
                          onChange={() => { editingMusteri.musteri_turu = 'ŞİRKET'; }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">ŞİRKET</span>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="font-semibold">Stopaj & Durum</Label>
                    <div className="flex flex-col gap-2 mt-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          defaultChecked={(editingMusteri.stopaj_periyot || 'Aylık') === '3 Aylık'}
                          onChange={(e) => { editingMusteri.stopaj_periyot = e.target.checked ? '3 Aylık' : 'Aylık'; }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Stopaj 3 Aylık</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          defaultChecked={editingMusteri.aktif !== false}
                          onChange={(e) => { editingMusteri.aktif = e.target.checked; }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Aktif</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          defaultChecked={editingMusteri.favori || false}
                          onChange={(e) => { editingMusteri.favori = e.target.checked; }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Favori ⭐</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label className="font-semibold">Kendi Vergisini Ödeyen</Label>
                    <label className="flex items-center space-x-2 cursor-pointer mt-2 mb-2 p-1 bg-blue-50 rounded">
                      <input
                        type="checkbox"
                        defaultChecked={editingMusteri.kdv_kendisi && editingMusteri.stopaj_kendisi && editingMusteri.ssk_kendisi && editingMusteri.bagkur_kendisi && editingMusteri.gecici_vergi_kendisi && editingMusteri.gelir_vergisi_kendisi}
                        onChange={(e) => {
                          const all = e.target.checked;
                          editingMusteri.kdv_kendisi = all;
                          editingMusteri.stopaj_kendisi = all;
                          editingMusteri.ssk_kendisi = all;
                          editingMusteri.bagkur_kendisi = all;
                          editingMusteri.gecici_vergi_kendisi = all;
                          editingMusteri.gelir_vergisi_kendisi = all;
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-bold text-blue-700">Tümü</span>
                    </label>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <label className="flex items-center space-x-1"><input type="checkbox" defaultChecked={editingMusteri.kdv_kendisi || false} onChange={(e) => { editingMusteri.kdv_kendisi = e.target.checked; }} className="w-3 h-3"/><span>KDV</span></label>
                      <label className="flex items-center space-x-1"><input type="checkbox" defaultChecked={editingMusteri.stopaj_kendisi || false} onChange={(e) => { editingMusteri.stopaj_kendisi = e.target.checked; }} className="w-3 h-3"/><span>Stopaj</span></label>
                      <label className="flex items-center space-x-1"><input type="checkbox" defaultChecked={editingMusteri.ssk_kendisi || false} onChange={(e) => { editingMusteri.ssk_kendisi = e.target.checked; }} className="w-3 h-3"/><span>SSK</span></label>
                      <label className="flex items-center space-x-1"><input type="checkbox" defaultChecked={editingMusteri.bagkur_kendisi || false} onChange={(e) => { editingMusteri.bagkur_kendisi = e.target.checked; }} className="w-3 h-3"/><span>Bağkur</span></label>
                      <label className="flex items-center space-x-1"><input type="checkbox" defaultChecked={editingMusteri.gecici_vergi_kendisi || false} onChange={(e) => { editingMusteri.gecici_vergi_kendisi = e.target.checked; }} className="w-3 h-3"/><span>Geçici V</span></label>
                      <label className="flex items-center space-x-1"><input type="checkbox" defaultChecked={editingMusteri.gelir_vergisi_kendisi || false} onChange={(e) => { editingMusteri.gelir_vergisi_kendisi = e.target.checked; }} className="w-3 h-3"/><span>Gelir V</span></label>
                    </div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <Label className="text-base font-semibold">Görünür Aylar</Label>
                  <p className="text-sm text-muted-foreground mb-2">Bu müşterinin kayıtlar sekmesinde hangi aylarda görüneceğini seçin. Boş bırakılırsa her ay gösterilir.</p>
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      className={`px-2 py-1 text-xs rounded border ${
                        !editingMusteri.gorunur_aylar || editingMusteri.gorunur_aylar.length === 0
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                      }`}
                      onClick={() => {
                        editingMusteri.gorunur_aylar = [];
                        // Force re-render
                        setEditingMusteri({...editingMusteri, gorunur_aylar: []});
                      }}
                    >
                      Tüm Aylar
                    </button>
                    {AYLAR.map(ay => {
                      const secili = editingMusteri.gorunur_aylar && editingMusteri.gorunur_aylar.includes(ay);
                      return (
                        <button
                          key={ay}
                          type="button"
                          className={`px-2 py-1 text-xs rounded border ${
                            secili
                              ? 'bg-green-600 text-white border-green-600'
                              : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                          }`}
                          onClick={() => {
                            let yeniAylar = [...(editingMusteri.gorunur_aylar || [])];
                            if (secili) {
                              yeniAylar = yeniAylar.filter(a => a !== ay);
                            } else {
                              yeniAylar.push(ay);
                            }
                            editingMusteri.gorunur_aylar = yeniAylar;
                            setEditingMusteri({...editingMusteri, gorunur_aylar: yeniAylar});
                          }}
                        >
                          {ay.substring(0, 3)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-base font-semibold">Görünecek Vergi Kayıtları</Label>
                  <p className="text-sm text-muted-foreground mb-3">Bu müşteri için hangi vergi türlerinin görüneceğini seçin</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_bakiye_gorunur"
                        defaultChecked={editingMusteri.bakiye_gorunur !== false}
                        onChange={(e) => { editingMusteri.bakiye_gorunur = e.target.checked; }}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="edit_bakiye_gorunur" className="cursor-pointer">Bakiye</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_muhasebe_gorunur"
                        defaultChecked={editingMusteri.muhasebe_gorunur !== false}
                        onChange={(e) => { editingMusteri.muhasebe_gorunur = e.target.checked; }}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="edit_muhasebe_gorunur" className="cursor-pointer">Muhasebe</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_kdv_gorunur"
                        defaultChecked={editingMusteri.kdv_gorunur !== false}
                        onChange={(e) => { editingMusteri.kdv_gorunur = e.target.checked; }}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="edit_kdv_gorunur" className="cursor-pointer">KDV</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_stopaj_gorunur"
                        defaultChecked={editingMusteri.stopaj_gorunur !== false}
                        onChange={(e) => { editingMusteri.stopaj_gorunur = e.target.checked; }}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="edit_stopaj_gorunur" className="cursor-pointer">Stopaj</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_ssk_gorunur"
                        defaultChecked={editingMusteri.ssk_gorunur !== false}
                        onChange={(e) => { editingMusteri.ssk_gorunur = e.target.checked; }}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="edit_ssk_gorunur" className="cursor-pointer">SSK</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_bagkur_gorunur"
                        defaultChecked={editingMusteri.bagkur_gorunur !== false}
                        onChange={(e) => { editingMusteri.bagkur_gorunur = e.target.checked; }}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="edit_bagkur_gorunur" className="cursor-pointer">BAĞKUR</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_gecici_vergi_gorunur"
                        defaultChecked={editingMusteri.gecici_vergi_gorunur !== false}
                        onChange={(e) => { editingMusteri.gecici_vergi_gorunur = e.target.checked; }}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="edit_gecici_vergi_gorunur" className="cursor-pointer">Geçici Vergi</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_gelir_vergisi_gorunur"
                        defaultChecked={editingMusteri.gelir_vergisi_gorunur !== false}
                        onChange={(e) => { editingMusteri.gelir_vergisi_gorunur = e.target.checked; }}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="edit_gelir_vergisi_gorunur" className="cursor-pointer">Gelir Vergisi</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_diger1_gorunur"
                        checked={editingMusteri.diger1_gorunur !== false}
                        onChange={(e) => setEditingMusteri({...editingMusteri, diger1_gorunur: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="edit_diger1_gorunur" className="cursor-pointer">Diğer 1</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_diger2_gorunur"
                        checked={editingMusteri.diger2_gorunur !== false}
                        onChange={(e) => setEditingMusteri({...editingMusteri, diger2_gorunur: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="edit_diger2_gorunur" className="cursor-pointer">Diğer 2</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_diger3_gorunur"
                        checked={editingMusteri.diger3_gorunur !== false}
                        onChange={(e) => setEditingMusteri({...editingMusteri, diger3_gorunur: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="edit_diger3_gorunur" className="cursor-pointer">Diğer 3</Label>
                    </div>
                  </div>
                </div>
                
                {/* GİB Bilgileri */}
                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-3">GİB Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                    <div>
                      <Label htmlFor="edit_gib_giris_kodu">GİB Giriş Kodu</Label>
                      <Input
                        id="edit_gib_giris_kodu"
                        value={editingMusteri.gib_giris_kodu || ''}
                        onChange={(e) => setEditingMusteri({...editingMusteri, gib_giris_kodu: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_gib_parola">GİB Parola</Label>
                      <Input
                        id="edit_gib_parola"
                        value={editingMusteri.gib_parola || ''}
                        onChange={(e) => setEditingMusteri({...editingMusteri, gib_parola: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_gib_sifre">GİB Şifre</Label>
                      <Input
                        id="edit_gib_sifre"
                        value={editingMusteri.gib_sifre || ''}
                        onChange={(e) => setEditingMusteri({...editingMusteri, gib_sifre: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Ortak Bilgileri (Geçici Vergi ve Gelir Vergisi için) */}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">Ortak Bilgileri</h4>
                      <p className="text-xs text-gray-500">Geçici Vergi ve Gelir Vergisi ortakların TC'sine çıkıyorsa</p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        const ortaklar = editingMusteri.ortaklar || [];
                        setEditingMusteri({
                          ...editingMusteri,
                          ortaklar: [...ortaklar, { tc_no: '', ad_soyad: '' }]
                        });
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ortak Ekle
                    </Button>
                  </div>
                  
                  {editingMusteri.ortaklar && editingMusteri.ortaklar.length > 0 && (
                    <div className="space-y-2">
                      {editingMusteri.ortaklar.map((ortak, index) => (
                        <div key={index} className="grid grid-cols-12 gap-2 items-end p-2 border rounded">
                          <div className="col-span-5">
                            <Label className="text-xs">TC Kimlik No</Label>
                            <Input
                              placeholder="12345678901"
                              maxLength={11}
                              value={ortak.tc_no || ''}
                              onChange={(e) => {
                                const newOrtaklar = [...editingMusteri.ortaklar];
                                newOrtaklar[index].tc_no = e.target.value;
                                setEditingMusteri({...editingMusteri, ortaklar: newOrtaklar});
                              }}
                            />
                          </div>
                          <div className="col-span-6">
                            <Label className="text-xs">Ad Soyad</Label>
                            <Input
                              placeholder="Ahmet Yılmaz"
                              value={ortak.ad_soyad || ''}
                              onChange={(e) => {
                                const newOrtaklar = [...editingMusteri.ortaklar];
                                newOrtaklar[index].ad_soyad = e.target.value;
                                setEditingMusteri({...editingMusteri, ortaklar: newOrtaklar});
                              }}
                            />
                          </div>
                          <div className="col-span-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                const newOrtaklar = editingMusteri.ortaklar.filter((_, i) => i !== index);
                                setEditingMusteri({...editingMusteri, ortaklar: newOrtaklar});
                              }}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {(!editingMusteri.ortaklar || editingMusteri.ortaklar.length === 0) && (
                    <div className="text-center py-4 text-gray-500 text-sm border-2 border-dashed rounded">
                      Ortak bilgisi yok. "Ortak Ekle" butonuyla ekleyebilirsiniz.
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={loading}>
                  Güncelle
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}


      {/* Toplu Güncelleme Dialog */}
      <Dialog open={topluGuncellemeDialogOpen} onOpenChange={setTopluGuncellemeDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Toplu Müşteri Güncelleme ({selectedMusteriler.length} müşteri)</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <Alert>
              <AlertDescription>
                Seçili {selectedMusteriler.length} müşteri için değiştirmek istediğiniz alanları seçin. Boş bırakılan alanlar değiştirilmez.
              </AlertDescription>
            </Alert>

            {/* Durum */}
            <div>
              <Label className="text-base font-semibold">Durum</Label>
              <div className="flex gap-4 mt-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_durum_none"
                    name="toplu_durum"
                    checked={topluGuncellemeData.aktif === null}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, aktif: null})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_durum_none" className="cursor-pointer font-normal">Değiştirme</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_durum_aktif"
                    name="toplu_durum"
                    checked={topluGuncellemeData.aktif === true}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, aktif: true})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_durum_aktif" className="cursor-pointer font-normal">Aktif</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_durum_pasif"
                    name="toplu_durum"
                    checked={topluGuncellemeData.aktif === false}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, aktif: false})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_durum_pasif" className="cursor-pointer font-normal">Pasif</Label>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-base font-semibold">Müşteri Türü</Label>
              <div className="flex gap-4 mt-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_tur_none"
                    name="toplu_musteri_turu"
                    checked={!topluGuncellemeData.musteri_turu}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, musteri_turu: ''})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_tur_none" className="cursor-pointer font-normal">Değiştirme</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_tur_isletme"
                    name="toplu_musteri_turu"
                    checked={topluGuncellemeData.musteri_turu === 'İŞLETME'}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, musteri_turu: 'İŞLETME'})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_tur_isletme" className="cursor-pointer font-normal">İŞLETME</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_tur_bilanco"
                    name="toplu_musteri_turu"
                    checked={topluGuncellemeData.musteri_turu === 'BİLANÇO'}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, musteri_turu: 'BİLANÇO'})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_tur_bilanco" className="cursor-pointer font-normal">BİLANÇO</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_tur_sirket"
                    name="toplu_musteri_turu"
                    checked={topluGuncellemeData.musteri_turu === 'ŞİRKET'}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, musteri_turu: 'ŞİRKET'})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_tur_sirket" className="cursor-pointer font-normal">ŞİRKET</Label>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-base font-semibold">Stopaj Periyodu</Label>
              <div className="flex gap-4 mt-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_stopaj_none"
                    name="toplu_stopaj"
                    checked={!topluGuncellemeData.stopaj_periyot}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, stopaj_periyot: ''})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_stopaj_none" className="cursor-pointer font-normal">Değiştirme</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_stopaj_aylik"
                    name="toplu_stopaj"
                    checked={topluGuncellemeData.stopaj_periyot === 'Aylık'}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, stopaj_periyot: 'Aylık'})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_stopaj_aylik" className="cursor-pointer font-normal">Aylık</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_stopaj_3aylik"
                    name="toplu_stopaj"
                    checked={topluGuncellemeData.stopaj_periyot === '3 Aylık'}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, stopaj_periyot: '3 Aylık'})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_stopaj_3aylik" className="cursor-pointer font-normal">3 Aylık</Label>
                </div>
              </div>
            </div>

            {/* Favori */}
            <div>
              <Label className="text-base font-semibold">Favori</Label>
              <div className="flex gap-4 mt-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_favori_none"
                    name="toplu_favori"
                    checked={topluGuncellemeData.favori === null}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, favori: null})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_favori_none" className="cursor-pointer font-normal">Değiştirme</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_favori_evet"
                    name="toplu_favori"
                    checked={topluGuncellemeData.favori === true}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, favori: true})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_favori_evet" className="cursor-pointer font-normal">Favori Yap ⭐</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_favori_hayir"
                    name="toplu_favori"
                    checked={topluGuncellemeData.favori === false}
                    onChange={() => setTopluGuncellemeData({...topluGuncellemeData, favori: false})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_favori_hayir" className="cursor-pointer font-normal">Favoriden Çıkar</Label>
                </div>
              </div>
            </div>

            {/* Kendi Vergisini Ödeyen */}
            <div>
              <Label className="text-base font-semibold">Görünür Aylar</Label>
              <p className="text-sm text-muted-foreground mb-2">Seçili müşterilerin kayıtlar sekmesinde hangi aylarda görüneceğini belirleyin</p>
              <div className="flex flex-wrap gap-1 mt-2">
                <button
                  type="button"
                  className={`px-2 py-1 text-xs rounded border ${
                    topluGuncellemeData.gorunur_aylar === null
                      ? 'bg-slate-200 text-slate-700 border-slate-400'
                      : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={() => setTopluGuncellemeData({...topluGuncellemeData, gorunur_aylar: null})}
                >
                  Değiştirme
                </button>
                <button
                  type="button"
                  className={`px-2 py-1 text-xs rounded border ${
                    topluGuncellemeData.gorunur_aylar !== null && topluGuncellemeData.gorunur_aylar.length === 0
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={() => setTopluGuncellemeData({...topluGuncellemeData, gorunur_aylar: []})}
                >
                  Tüm Aylar
                </button>
                {AYLAR.map(ay => {
                  const secili = topluGuncellemeData.gorunur_aylar !== null && topluGuncellemeData.gorunur_aylar.includes(ay);
                  return (
                    <button
                      key={`toplu_ay_${ay}`}
                      type="button"
                      className={`px-2 py-1 text-xs rounded border ${
                        secili
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                      }`}
                      onClick={() => {
                        let yeniAylar = [...(topluGuncellemeData.gorunur_aylar || [])];
                        if (secili) {
                          yeniAylar = yeniAylar.filter(a => a !== ay);
                        } else {
                          yeniAylar.push(ay);
                        }
                        setTopluGuncellemeData({...topluGuncellemeData, gorunur_aylar: yeniAylar});
                      }}
                    >
                      {ay.substring(0, 3)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Kendi Vergisini Ödeyen */}
            <div>
              <Label className="text-base font-semibold">Kendi Vergisini Ödeyen</Label>
              <p className="text-sm text-muted-foreground mb-2">Seçili müşteriler için tüm vergileri toplu olarak ayarlayın</p>
              
              <div className="flex gap-4 mt-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_vergi_none"
                    name="toplu_vergi"
                    checked={topluGuncellemeData.kdv_kendisi === null}
                    onChange={() => setTopluGuncellemeData({
                      ...topluGuncellemeData,
                      kdv_kendisi: null,
                      stopaj_kendisi: null,
                      ssk_kendisi: null,
                      bagkur_kendisi: null,
                      gecici_vergi_kendisi: null,
                      gelir_vergisi_kendisi: null
                    })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_vergi_none" className="cursor-pointer font-normal">Değiştirme</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_vergi_evet"
                    name="toplu_vergi"
                    checked={topluGuncellemeData.kdv_kendisi === true}
                    onChange={() => setTopluGuncellemeData({
                      ...topluGuncellemeData,
                      kdv_kendisi: true,
                      stopaj_kendisi: true,
                      ssk_kendisi: true,
                      bagkur_kendisi: true,
                      gecici_vergi_kendisi: true,
                      gelir_vergisi_kendisi: true
                    })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_vergi_evet" className="cursor-pointer font-normal text-green-700 font-semibold">Tümüne Evet (Kendisi Ödüyor)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="toplu_vergi_hayir"
                    name="toplu_vergi"
                    checked={topluGuncellemeData.kdv_kendisi === false}
                    onChange={() => setTopluGuncellemeData({
                      ...topluGuncellemeData,
                      kdv_kendisi: false,
                      stopaj_kendisi: false,
                      ssk_kendisi: false,
                      bagkur_kendisi: false,
                      gecici_vergi_kendisi: false,
                      gelir_vergisi_kendisi: false
                    })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="toplu_vergi_hayir" className="cursor-pointer font-normal text-red-700 font-semibold">Tümüne Hayır (Kendisi Ödemiyor)</Label>
                </div>
              </div>
              
              <div className="mt-3 p-3 bg-blue-50 rounded text-sm text-blue-800">
                <strong>Not:</strong> Bu ayar KDV, Stopaj, SSK, Bağkur, Geçici Vergi ve Gelir Vergisi alanlarının tümünü birden değiştirir.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopluGuncellemeDialogOpen(false)}>
              İptal
            </Button>
            <Button onClick={handleTopluGuncelleme} disabled={loading}>
              {loading ? 'Güncelleniyor...' : 'Güncelle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Müşteri Excel Import Dialog */}
      <Dialog open={musteriExcelDialogOpen} onOpenChange={setMusteriExcelDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Toplu Müşteri Ekle (Excel)</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMusteriExcelImport}>
            <div className="space-y-4 py-4">
              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Excel şablonunu indirip doldurun, sonra yükleyin</span>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={handleMusteriSablonIndir}
                      className="ml-4"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Örnek Şablon İndir
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
              
              <Alert>
                <AlertDescription className="text-xs">
                  <strong>Excel Formatı:</strong> Vergi No, Ad Soyad, Telefon, Müşteri Türü (İŞLETME/BİLANÇO/ŞİRKET), Stopaj Periyodu (Aylık/3 Aylık), Favori (EVET/HAYIR), Kendi Vergisini Ödeyen (EVET/HAYIR)
                </AlertDescription>
              </Alert>
              
              <div>
                <Label>Excel Dosyası</Label>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setMusteriExcelFile(e.target.files[0])}
                  required
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Şablonu indirip doldurup yükleyin (.xlsx veya .xls)
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={loading}>
                <FileUp className="w-4 h-4 mr-2" />
                {loading ? 'Yükleniyor...' : 'İçe Aktar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {/* Rapor Dialog - Büyütülebilir */}
      <Dialog open={raporDialogOpen} onOpenChange={(open) => {
        // Sadece false olduğunda (kapanırken) işlem yap
        if (open === false) {
          // Kapanıyor flag'ini set et
          setRaporDialogClosing(true);
          setRaporDialogOpen(false);
          setRaporData(null);
          // 500ms sonra flag'i sıfırla
          setTimeout(() => {
            setRaporDialogClosing(false);
          }, 500);
        }
      }}>
        <DialogContent 
          className="w-[98vw] h-[98vh] max-w-[900px] md:w-auto md:max-w-[900px] md:h-auto md:max-h-[98vh] overflow-y-auto overflow-x-auto print:max-w-[148mm] resize p-2 md:p-6" 
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onCloseClick={() => {
            setRaporDialogClosing(true);
            setRaporDialogOpen(false);
            setRaporData(null);
            setTimeout(() => {
              setRaporDialogClosing(false);
            }, 500);
          }}
        >
          <DialogHeader className="print:hidden py-1 border-b-2 border-blue-600">
            {/* Klavye Kısayolları İpucu - Masaüstünde görünür, mobilde gizli */}
            <div className="hidden md:flex items-center justify-center gap-3 mb-2 pb-2 border-b border-blue-200">
              <div className="flex items-center gap-2 text-xs bg-blue-50 px-3 py-1 rounded-md">
                <kbd className="px-2 py-1 bg-white border border-blue-300 rounded shadow-sm font-semibold text-blue-700">F2</kbd>
                <span className="text-blue-700">Müşteri Düzenle</span>
              </div>
              <div className="flex items-center gap-2 text-xs bg-green-50 px-3 py-1 rounded-md">
                <kbd className="px-2 py-1 bg-white border border-green-300 rounded shadow-sm font-semibold text-green-700">F3</kbd>
                <span className="text-green-700">Vergi Raporu Aç</span>
              </div>
            </div>
            
            {/* Ana Başlık - Düzenlenebilir */}
            <div className="text-center mb-2 relative group">
              {baslikDuzenle ? (
                <div className="flex items-center justify-center gap-2">
                  <Input
                    type="text"
                    defaultValue={raporBaslik}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleBaslikKaydet(e.target.value);
                      } else if (e.key === 'Escape') {
                        setBaslikDuzenle(false);
                      }
                    }}
                    className="text-2xl font-bold text-blue-700 text-center max-w-2xl"
                    autoFocus
                  />
                  <Button 
                    size="sm" 
                    onClick={(e) => {
                      const input = e.target.closest('div').querySelector('input');
                      handleBaslikKaydet(input.value);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Kaydet
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <h1 
                    className={`text-lg md:text-2xl font-bold cursor-pointer ${raporBaslik ? 'text-blue-700' : 'text-gray-400 italic'}`}
                    onClick={() => setBaslikDuzenle(true)}
                    title="Başlığı Düzenle"
                  >
                    {raporBaslik || 'Muhasebe unvanı eklemek için tıklayınız'}
                  </h1>
                  {raporBaslik && (
                    <button
                      onClick={() => setBaslikDuzenle(true)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-blue-100 rounded"
                      title="Başlığı Düzenle"
                    >
                      <Pencil className="w-4 h-4 text-blue-600" />
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Müşteri ve Ay Seçimi - Mobilde tek sütun */}
            {raporData && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 mb-3">
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Müşteri Seç</Label>
                  <Select
                    value={raporSelectedMusteri}
                    onValueChange={(value) => {
                      const musteri = musteriler.find(m => m.vergi_no === value);
                      if (musteri) {
                        setRaporSelectedMusteri(value);
                        handleRaporOlustur(value, musteri.ad_soyad);
                      }
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Müşteri seçin" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {musteriler
                        .filter(m => m.aktif !== false)
                        .sort((a, b) => (a.ad_soyad || '').localeCompare(b.ad_soyad || '', 'tr'))
                        .map(musteri => (
                          <SelectItem key={musteri.vergi_no} value={musteri.vergi_no}>
                            {musteri.ad_soyad}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Ay Seç</Label>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="h-9 flex-1 justify-between text-left font-normal"
                          data-testid="rapor-ay-sec-btn"
                        >
                          <span className="truncate">
                            {raporSelectedAylar.length === 0 
                              ? 'Ay seçin' 
                              : raporSelectedAylar.length === 1 
                                ? raporSelectedAylar[0]
                                : `${raporSelectedAylar.length} ay seçili`}
                          </span>
                          <ChevronDown className="w-4 h-4 ml-1 opacity-50 shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-48 p-2" align="start">
                        <div className="space-y-1 max-h-[280px] overflow-y-auto">
                          {AYLAR.map(ay => (
                            <label 
                              key={ay} 
                              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 cursor-pointer text-sm"
                              data-testid={`rapor-ay-${ay}`}
                            >
                              <Checkbox
                                checked={raporSelectedAylar.includes(ay)}
                                onCheckedChange={(checked) => {
                                  let yeniAylar;
                                  if (checked) {
                                    yeniAylar = [...raporSelectedAylar, ay];
                                  } else {
                                    yeniAylar = raporSelectedAylar.filter(a => a !== ay);
                                  }
                                  // Ay sırasına göre sırala
                                  const aySirasi = {'Ocak':1,'Şubat':2,'Mart':3,'Nisan':4,'Mayıs':5,'Haziran':6,'Temmuz':7,'Ağustos':8,'Eylül':9,'Ekim':10,'Kasım':11,'Aralık':12};
                                  yeniAylar.sort((a, b) => aySirasi[a] - aySirasi[b]);
                                  setRaporSelectedAylar(yeniAylar);
                                  setRaporSelectedAy(yeniAylar[0] || '');
                                  if (yeniAylar.length > 0 && raporData) {
                                    handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad, yeniAylar);
                                  }
                                }}
                              />
                              {ay}
                            </label>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  <Button
                    onClick={handleOncekiVeriKopyala}
                    variant="outline"
                    size="sm"
                    className="h-9 whitespace-nowrap bg-purple-50 hover:bg-purple-100 border-purple-300 text-purple-700"
                    title="Önceki aydan SSK ve Bağkur verilerini kopyalar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                    </svg>
                    Önceki Veriyi Kopyala
                  </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Dönem ve Müşteri Bilgileri - Mavi Kısım */}
            {raporData && (
              <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-2 rounded-lg border-2 border-blue-700 shadow-md">
                <DialogTitle className="text-base font-bold text-white flex items-center gap-3">
                  <span>📅 {raporData.tekAy 
                    ? `${raporData.aylar[0]} ${raporData.yil}`
                    : `${raporData.aylar[0]} - ${raporData.aylar[raporData.aylar.length - 1]} ${raporData.yil}`
                  }</span>
                  <span className="text-xl">-</span>
                  <span>{raporData.adSoyad}</span>
                  <span className="text-xl">-</span>
                  <span>Vergi No: {raporData.vergiNo}</span>
                </DialogTitle>
              </div>
            )}
          </DialogHeader>
          
          {raporData && (
            <div id="whatsapp-rapor-content">
            <div id="vergi-raporu-content" className="space-y-2 py-2 print:text-sm text-sm">
              {/* Başlık - Sadece Print'te */}
              <div className="hidden print:block text-center mb-4">
                <h1 className="text-lg font-bold">{raporData.adSoyad}</h1>
                <p className="text-sm text-slate-600">
                  {raporData.tekAy 
                    ? `${raporData.aylar[0]} ${raporData.yil}`
                    : `${raporData.aylar[0]} - ${raporData.aylar[raporData.aylar.length - 1]} ${raporData.yil}`
                  } - Vergi No: {raporData.vergiNo}
                </p>
              </div>

              {/* SSK/Bağkur Önceki Aydan Aktarıldı Uyarısı */}
              {(raporData.ssk_onceki_aydan || raporData.bagkur_onceki_aydan) && (
                <div className="bg-orange-100 border-2 border-orange-400 rounded-lg px-4 py-3 print:hidden">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className="font-bold text-orange-800">Önceki Aydan Veri Aktarıldı!</p>
                      <p className="text-sm text-orange-700">
                        {raporData.ssk_onceki_aydan && raporData.bagkur_onceki_aydan 
                          ? 'SSK ve Bağkur tutarları önceki aydan aktarıldı. Lütfen kontrol edin.'
                          : raporData.ssk_onceki_aydan 
                            ? 'SSK tutarı önceki aydan aktarıldı. Lütfen kontrol edin.'
                            : 'Bağkur tutarı önceki aydan aktarıldı. Lütfen kontrol edin.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Ödeme Girişi - EN ÜSTTE */}
              {raporData.kayitlar && raporData.kayitlar.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded px-2 py-2 print:bg-gray-100 print:border-black print:hidden">
                  <div className="flex justify-start items-start gap-4 flex-wrap">
                    {/* Sol taraf - Ödeme Girişi */}
                    <div className="space-y-1">
                      <p className="text-xs font-semibold">
                        Ödeme Girişi 
                        <span className="text-xs font-normal text-slate-500 ml-1">
                          (En son ay: {raporData.kayitlar[raporData.kayitlar.length - 1].ay})
                        </span>
                      </p>
                      <div className="flex items-center gap-2">
                        <Label htmlFor="rapor-odenen" className="text-xs min-w-[70px]">Ödenen:</Label>
                        <Input
                          id="rapor-odenen"
                          type="text"
                          value={raporOdenenInput}
                          onChange={(e) => setRaporOdenenInput(e.target.value)}
                          onBlur={handleRaporOdenenKaydet}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleRaporOdenenKaydet();
                            }
                          }}
                          className="w-32 text-right text-xs h-7 py-1"
                          placeholder="0,00"
                        />
                        <Button 
                          onClick={handleRaporOdenenKaydet}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-xs h-7 px-2"
                        >
                          Kaydet
                        </Button>
                        <Button 
                          onClick={async () => {
                            // Toplam borcu hesapla - kendisi_odemis olan vergileri hariç tut
                            const toplamBorc = raporData.bakiye + 
                              raporFormData.muhasebe + 
                              (!raporData.kdv_kendisi_odemis ? raporFormData.kdv : 0) + 
                              (!raporData.stopaj_kendisi_odemis ? raporFormData.stopaj : 0) + 
                              (!raporData.ssk_kendisi_odemis ? raporFormData.ssk : 0) + 
                              (!raporData.bagkur_kendisi_odemis ? raporFormData.bagkur : 0) + 
                              (!raporData.gecici_vergi_kendisi_odemis ? raporFormData.gecici_vergi : 0) + 
                              (!raporData.gelir_vergisi_kendisi_odemis ? raporFormData.gelir_vergisi : 0) + 
                              raporFormData.diger1 + 
                              raporFormData.diger2 + 
                              raporFormData.diger3;
                            
                            // Ödenen alanına toplam borcu yaz
                            const toplamBorcFormatted = toplamBorc.toFixed(2).replace('.', ',');
                            setRaporOdenenInput(toplamBorcFormatted);
                            
                            // Doğrudan API'ye kaydet (state güncellemesini beklemeden)
                            if (!raporData || !raporData.kayitlar || raporData.kayitlar.length === 0) return;
                            
                            try {
                              const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                              
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, {
                                odenen: toplamBorc
                              });
                              
                              // Raporu yeniden oluştur
                              await handleRaporOlustur(raporData.vergiNo, raporData.adSoyad);
                              toast.success('Bakiye kapatıldı - Ödeme kaydedildi');
                            } catch (error) {
                              console.error('Bakiye kapatma hatası:', error);
                              toast.error('Bakiye kapatılamadı');
                            }
                          }}
                          size="sm"
                          className="bg-orange-600 hover:bg-orange-700 text-xs h-7 px-2"
                          title="Toplam borcu ödenen'e yaz ve hesabı kapat"
                        >
                          Bakiye Kapat
                        </Button>
                      </div>
                    </div>
                    
                    {/* Sağ taraf - Butonlar */}
                    <div className="flex items-center gap-2">
                      {/* Tahakkuk PDF Seçimi */}
                      {tahakkukPdfs.length > 0 && (
                        <div className="flex items-center gap-2 mr-2 p-1 bg-blue-50 rounded border border-blue-200">
                          <span className="text-xs text-blue-600 font-medium">📎 Tahakkuk:</span>
                          {tahakkukPdfs.map((pdf, idx) => (
                            <label key={pdf.id} className="flex items-center gap-1 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedTahakkukPdfs.includes(pdf.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedTahakkukPdfs([...selectedTahakkukPdfs, pdf.id]);
                                  } else {
                                    setSelectedTahakkukPdfs(selectedTahakkukPdfs.filter(id => id !== pdf.id));
                                  }
                                }}
                                className="w-3 h-3 accent-blue-600"
                              />
                              <span className="text-gray-700">{pdf.vergi_turu}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      <Button 
                        onClick={handleWhatsAppGonder}
                        size="sm"
                        className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white h-7 shadow-md"
                        title="WhatsApp ile Gönder"
                        data-testid="rapor-whatsapp-btn"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        WhatsApp
                      </Button>
                      <Button 
                        onClick={async () => {
                          if (raporData && raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, {
                                muhasebe: raporFormData.muhasebe,
                                kdv: raporFormData.kdv,
                                stopaj: raporFormData.stopaj,
                                ssk: raporFormData.ssk,
                                bagkur: raporFormData.bagkur,
                                gecici_vergi: raporFormData.gecici_vergi,
                                gelir_vergisi: raporFormData.gelir_vergisi,
                                diger1: raporFormData.diger1,
                                diger2: raporFormData.diger2,
                                diger3: raporFormData.diger3,
                                odenen: raporFormData.odenen
                              });
                              toast.success('Kaydedildi');
                              fetchKayitlar();
                            } catch (err) {
                              console.error('Kaydetme hatası:', err);
                              toast.error('Kaydetme hatası');
                            }
                          }
                        }}
                        size="sm"
                        className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white h-7 shadow-md"
                      >
                        Kaydet
                      </Button>
                      <Button 
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          // Önce kaydet (fire-and-forget)
                          if (raporData && raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, {
                                muhasebe: raporFormData.muhasebe,
                                kdv: raporFormData.kdv,
                                stopaj: raporFormData.stopaj,
                                ssk: raporFormData.ssk,
                                bagkur: raporFormData.bagkur,
                                gecici_vergi: raporFormData.gecici_vergi,
                                gelir_vergisi: raporFormData.gelir_vergisi,
                                diger1: raporFormData.diger1,
                                diger2: raporFormData.diger2,
                                diger3: raporFormData.diger3,
                                odenen: raporFormData.odenen
                              });
                            } catch (err) {
                              console.error('Kaydetme hatası:', err);
                            }
                          }
                          
                          // Sonra kapat
                          setRaporDialogClosing(true);
                          setRaporDialogOpen(false);
                          setRaporData(null);
                          
                          // Kayıtları yenile
                          fetchKayitlar();
                          
                          setTimeout(() => {
                            setRaporDialogClosing(false);
                          }, 500);
                        }}
                        size="sm"
                        className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white h-7 shadow-md"
                      >
                        Kaydet ve Kapat
                      </Button>
                      <Button 
                        onClick={() => window.print()}
                        size="sm"
                        className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white h-7 shadow-md"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 6 2 18 2 18 9"></polyline>
                          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                          <rect x="6" y="14" width="12" height="8"></rect>
                        </svg>
                        Yazdır
                      </Button>
                      <Button
                        onClick={() => {
                          if (raporData?.vergiNo) {
                            openTahsilatOzeti(raporData.vergiNo, raporData.adSoyad, raporData.yil || new Date().getFullYear());
                          }
                        }}
                        size="sm"
                        className="bg-gradient-to-r from-fuchsia-600 to-pink-600 hover:from-fuchsia-700 hover:to-pink-700 text-white h-7 shadow-md"
                        title="Tahsilat Özeti"
                        data-testid="rapor-tahsilat-ozeti-btn"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                        Tahsilat Özeti
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Rapor FORM - Düzenli Tablo */}
              <div className="border-2 border-gray-400 rounded-lg overflow-hidden max-w-2xl">
                {/* Bakiye - Çift tıklama ile düzenlenebilir */}
                <div className="flex items-center border-b-2 border-gray-400 bg-slate-200">
                  <div className="w-1/2 px-3 py-2 font-bold text-sm border-r-2 border-gray-400">BAKİYE</div>
                  <div 
                    className="w-1/2 px-3 py-2 text-right text-sm font-semibold cursor-pointer hover:bg-blue-50" 
                    onDoubleClick={() => {
                      setEditingRaporBakiye(true);
                      setRaporBakiyeValue(raporData.bakiye || 0);
                    }}
                  >
                    {editingRaporBakiye ? (
                      <Input
                        type="number"
                        value={raporBakiyeValue}
                        onChange={(e) => setRaporBakiyeValue(e.target.value)}
                        onBlur={async () => {
                          // Bakiye'yi kaydet - İlk ayın bakiye değerini güncelle
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const ilkKayit = raporData.kayitlar[0];
                            try {
                              await axios.put(`${API}/vergi-kayit/${ilkKayit.id}`, {
                                bakiye: parseFloat(raporBakiyeValue) || 0
                              });
                              toast.success('Bakiye güncellendi');
                              // Raporu yeniden oluştur
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                            } catch (error) {
                              console.error('Bakiye güncelleme hatası:', error);
                              toast.error('Bakiye güncellenemedi');
                            }
                          }
                          setEditingRaporBakiye(false);
                        }}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter') {
                            // Bakiye'yi kaydet
                            if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                              const ilkKayit = raporData.kayitlar[0];
                              try {
                                await axios.put(`${API}/vergi-kayit/${ilkKayit.id}`, {
                                  bakiye: parseFloat(raporBakiyeValue) || 0
                                });
                                toast.success('Bakiye güncellendi');
                                await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              } catch (error) {
                                console.error('Bakiye güncelleme hatası:', error);
                                toast.error('Bakiye güncellenemedi');
                              }
                            }
                            setEditingRaporBakiye(false);
                          } else if (e.key === 'Escape') {
                            setEditingRaporBakiye(false);
                          }
                        }}
                        autoFocus
                        className="h-8 text-sm text-right"
                      />
                    ) : (
                      formatPara(raporData.bakiye)
                    )}
                  </div>
                </div>

                {/* Düzenlenebilir Alanlar - Düzenli Tablo */}
                <div key={`vergi-form-${raporData?.vergiNo}`}>
                <div className="flex items-center border-b border-gray-300 hover:bg-gray-50">
                  <div className="w-1/2 px-3 py-1.5 text-sm font-medium border-r border-gray-300">MUHASEBE ÜCRETİ</div>
                  <div className="w-1/2 px-3 py-1.5">
                    <Input
                      key={`muhasebe-${raporData?.vergiNo}`}
                      type="text"
                      value={raporFormInputs.muhasebe || '0,00'}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, muhasebe: e.target.value}))}
                      onBlur={(e) => handleRaporInputBlur('muhasebe', e.target.value, e.target)}
                      onFocus={(e) => e.target.select()}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-right text-sm h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>

                {/* KDV - Müşteri kendisi ödüyorsa gri arka plan */}
                <div className={`flex items-center border-b border-gray-300 hover:bg-gray-50 ${
                  raporData.musteri?.kdv_kendisi
                    ? (raporData.kdv_aktif !== false ? 'bg-blue-100' : 'bg-gray-100')
                    : (raporData.kdv_kendisi_odemis ? 'bg-emerald-100' : '')
                }`}>
                  <div className="w-1/2 px-3 py-1.5 text-sm font-medium border-r border-gray-300 flex items-center justify-between">
                    <span>
                      KDV
                      {raporData.musteri?.kdv_kendisi && !(raporData.kdv_aktif !== false) && <span className="ml-2 text-xs text-orange-600 font-semibold">(Normalde Kendisi Ödüyor)</span>}
                      {raporData.musteri?.kdv_kendisi && (raporData.kdv_aktif !== false) && <span className="ml-2 text-xs text-blue-700 font-bold">(Bu Ay Ben Ödedim)</span>}
                      {!raporData.musteri?.kdv_kendisi && raporData.kdv_kendisi_odemis && <span className="ml-2 text-xs text-emerald-700 font-bold">(Bu Ay Kendisi Ödedi)</span>}
                    </span>
                    {raporData?.tekAy && raporData.musteri?.kdv_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.kdv_aktif !== false ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.kdv_aktif !== false ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-400 text-blue-700 hover:bg-blue-100'}`}
                        onClick={async () => {
                          const newAktif = !(raporData.kdv_aktif !== false);
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { kdv_aktif: newAktif, kdv_kendisi_odemis: !newAktif });
                              toast.success(newAktif ? 'KDV - Bu ay ben ödedim (hesaba dahil)' : 'KDV - Yine kendisi ödüyor (hesap dışı)');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.kdv_aktif !== false ? 'Bu ayı da kendisi ödüyor olarak işaretle' : 'Bu ay ben ödedim olarak işaretle'}
                        data-testid="kdv-ben-odedim-btn"
                      >
                        {raporData.kdv_aktif !== false ? '✓ Ben Ödedim' : 'Ben Ödedim'}
                      </Button>
                    )}
                    {raporData?.tekAy && !raporData.musteri?.kdv_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.kdv_kendisi_odemis ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.kdv_kendisi_odemis ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-100'}`}
                        onClick={async () => {
                          const newValue = !raporData.kdv_kendisi_odemis;
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { kdv_kendisi_odemis: newValue });
                              toast.success(newValue ? 'KDV - Kendisi Ödedi olarak işaretlendi' : 'KDV işareti kaldırıldı');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.kdv_kendisi_odemis ? 'İşareti kaldır' : 'Kendisi Ödedi olarak işaretle'}
                        data-testid="kdv-kendisi-odedi-btn"
                      >
                        {raporData.kdv_kendisi_odemis ? '✓ K.Ödedi' : 'K.Ödedi'}
                      </Button>
                    )}
                  </div>
                  <div className="w-1/2 px-3 py-1.5">
                    <Input
                      key={`kdv-${raporData?.vergiNo}`}
                      type="text"
                      value={raporFormInputs.kdv || '0,00'}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, kdv: e.target.value}))}
                      onBlur={(e) => handleRaporInputBlur('kdv', e.target.value, e.target)}
                      onFocus={(e) => e.target.select()}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-right text-sm h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''} ${
                        raporData.musteri?.kdv_kendisi
                          ? (raporData.kdv_aktif !== false ? 'bg-blue-50 font-semibold' : 'bg-yellow-50')
                          : (raporData.kdv_kendisi_odemis ? 'bg-emerald-200 line-through' : '')
                      }`}
                      placeholder={raporData.musteri?.kdv_kendisi ? (raporData.kdv_aktif !== false ? 'Değer girin' : 'Kendisi ödüyor') : '0,00'}
                    />
                  </div>
                </div>

                {/* STOPAJ - Müşteri kendisi ödüyorsa gri arka plan */}
                <div className={`flex items-center border-b border-gray-300 hover:bg-gray-50 ${
                  raporData.musteri?.stopaj_kendisi
                    ? (raporData.stopaj_aktif !== false ? 'bg-blue-100' : 'bg-gray-100')
                    : (raporData.stopaj_kendisi_odemis ? 'bg-emerald-100' : '')
                }`}>
                  <div className="w-1/2 px-3 py-1.5 text-sm font-medium border-r border-gray-300 flex items-center justify-between">
                    <span>
                      STOPAJ
                      {raporData.musteri?.stopaj_kendisi && !(raporData.stopaj_aktif !== false) && <span className="ml-2 text-xs text-orange-600 font-semibold">(Normalde Kendisi Ödüyor)</span>}
                      {raporData.musteri?.stopaj_kendisi && (raporData.stopaj_aktif !== false) && <span className="ml-2 text-xs text-blue-700 font-bold">(Bu Ay Ben Ödedim)</span>}
                      {!raporData.musteri?.stopaj_kendisi && raporData.stopaj_kendisi_odemis && <span className="ml-2 text-xs text-emerald-700 font-bold">(Bu Ay Kendisi Ödedi)</span>}
                    </span>
                    {raporData?.tekAy && raporData.musteri?.stopaj_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.stopaj_aktif !== false ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.stopaj_aktif !== false ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-400 text-blue-700 hover:bg-blue-100'}`}
                        onClick={async () => {
                          const newAktif = !(raporData.stopaj_aktif !== false);
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { stopaj_aktif: newAktif, stopaj_kendisi_odemis: !newAktif });
                              toast.success(newAktif ? 'Stopaj - Bu ay ben ödedim (hesaba dahil)' : 'Stopaj - Yine kendisi ödüyor (hesap dışı)');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.stopaj_aktif !== false ? 'Bu ayı da kendisi ödüyor olarak işaretle' : 'Bu ay ben ödedim olarak işaretle'}
                        data-testid="stopaj-ben-odedim-btn"
                      >
                        {raporData.stopaj_aktif !== false ? '✓ Ben Ödedim' : 'Ben Ödedim'}
                      </Button>
                    )}
                    {raporData?.tekAy && !raporData.musteri?.stopaj_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.stopaj_kendisi_odemis ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.stopaj_kendisi_odemis ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-100'}`}
                        onClick={async () => {
                          const newValue = !raporData.stopaj_kendisi_odemis;
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { stopaj_kendisi_odemis: newValue });
                              toast.success(newValue ? 'Stopaj - Kendisi Ödedi olarak işaretlendi' : 'Stopaj işareti kaldırıldı');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.stopaj_kendisi_odemis ? 'İşareti kaldır' : 'Kendisi Ödedi olarak işaretle'}
                        data-testid="stopaj-kendisi-odedi-btn"
                      >
                        {raporData.stopaj_kendisi_odemis ? '✓ K.Ödedi' : 'K.Ödedi'}
                      </Button>
                    )}
                  </div>
                  <div className="w-1/2 px-3 py-1.5">
                    <Input
                      type="text"
                      key={`stopaj-${raporData?.vergiNo}`}
                      value={raporFormInputs.stopaj || '0,00'}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, stopaj: e.target.value}))}
                      
                      onBlur={(e) => handleRaporInputBlur('stopaj', e.target.value, e.target)}
                      onFocus={(e) => e.target.select()}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-right text-sm h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''} ${
                        raporData.musteri?.stopaj_kendisi
                          ? (raporData.stopaj_aktif !== false ? 'bg-blue-50 font-semibold' : 'bg-yellow-50')
                          : (raporData.stopaj_kendisi_odemis ? 'bg-emerald-200 line-through' : '')
                      }`}
                      placeholder={raporData.musteri?.stopaj_kendisi ? (raporData.stopaj_aktif !== false ? 'Değer girin' : 'Kendisi ödüyor') : '0,00'}
                    />
                  </div>
                </div>

                {/* SSK - Müşteri kendisi ödüyorsa gri arka plan */}
                <div className={`flex items-center border-b border-gray-300 hover:bg-gray-50 ${
                  raporData.musteri?.ssk_kendisi
                    ? (raporData.ssk_aktif !== false ? 'bg-blue-100' : 'bg-gray-100')
                    : (raporData.ssk_kendisi_odemis ? 'bg-emerald-100' : '')
                }`}>
                  <div className="w-1/2 px-3 py-1.5 text-sm font-medium border-r border-gray-300 flex items-center justify-between">
                    <span>
                      SSK
                      {raporData.musteri?.ssk_kendisi && !(raporData.ssk_aktif !== false) && <span className="ml-2 text-xs text-orange-600 font-semibold">(Normalde Kendisi Ödüyor)</span>}
                      {raporData.musteri?.ssk_kendisi && (raporData.ssk_aktif !== false) && <span className="ml-2 text-xs text-blue-700 font-bold">(Bu Ay Ben Ödedim)</span>}
                      {!raporData.musteri?.ssk_kendisi && raporData.ssk_kendisi_odemis && <span className="ml-2 text-xs text-emerald-700 font-bold">(Bu Ay Kendisi Ödedi)</span>}
                    </span>
                    {raporData?.tekAy && raporData.musteri?.ssk_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.ssk_aktif !== false ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.ssk_aktif !== false ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-400 text-blue-700 hover:bg-blue-100'}`}
                        onClick={async () => {
                          const newAktif = !(raporData.ssk_aktif !== false);
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { ssk_aktif: newAktif, ssk_kendisi_odemis: !newAktif });
                              toast.success(newAktif ? 'SSK - Bu ay ben ödedim (hesaba dahil)' : 'SSK - Yine kendisi ödüyor (hesap dışı)');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.ssk_aktif !== false ? 'Bu ayı da kendisi ödüyor olarak işaretle' : 'Bu ay ben ödedim olarak işaretle'}
                        data-testid="ssk-ben-odedim-btn"
                      >
                        {raporData.ssk_aktif !== false ? '✓ Ben Ödedim' : 'Ben Ödedim'}
                      </Button>
                    )}
                    {raporData?.tekAy && !raporData.musteri?.ssk_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.ssk_kendisi_odemis ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.ssk_kendisi_odemis ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-100'}`}
                        onClick={async () => {
                          const newValue = !raporData.ssk_kendisi_odemis;
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { ssk_kendisi_odemis: newValue });
                              toast.success(newValue ? 'SSK - Kendisi Ödedi olarak işaretlendi' : 'SSK işareti kaldırıldı');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.ssk_kendisi_odemis ? 'İşareti kaldır' : 'Kendisi Ödedi olarak işaretle'}
                        data-testid="ssk-kendisi-odedi-btn"
                      >
                        {raporData.ssk_kendisi_odemis ? '✓ K.Ödedi' : 'K.Ödedi'}
                      </Button>
                    )}
                  </div>
                  <div className="w-1/2 px-3 py-1.5">
                    <Input
                      type="text"
                      key={`ssk-${raporData?.vergiNo}`}
                      value={raporFormInputs.ssk || '0,00'}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, ssk: e.target.value}))}
                      
                      onBlur={(e) => handleRaporInputBlur('ssk', e.target.value, e.target)}
                      onFocus={(e) => e.target.select()}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-right text-sm h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''} ${
                        raporData.musteri?.ssk_kendisi
                          ? (raporData.ssk_aktif !== false ? 'bg-blue-50 font-semibold' : 'bg-yellow-50')
                          : (raporData.ssk_kendisi_odemis ? 'bg-emerald-200 line-through' : '')
                      }`}
                      placeholder={raporData.musteri?.ssk_kendisi ? (raporData.ssk_aktif !== false ? 'Değer girin' : 'Kendisi ödüyor') : '0,00'}
                    />
                  </div>
                </div>

                {/* BAĞKUR - Müşteri kendisi ödüyorsa gri arka plan */}
                <div className={`flex items-center border-b border-gray-300 hover:bg-gray-50 ${
                  raporData.musteri?.bagkur_kendisi
                    ? (raporData.bagkur_aktif !== false ? 'bg-blue-100' : 'bg-gray-100')
                    : (raporData.bagkur_kendisi_odemis ? 'bg-emerald-100' : '')
                }`}>
                  <div className="w-1/2 px-3 py-1.5 text-sm font-medium border-r border-gray-300 flex items-center justify-between">
                    <span>
                      BAĞKUR
                      {raporData.musteri?.bagkur_kendisi && !(raporData.bagkur_aktif !== false) && <span className="ml-2 text-xs text-orange-600 font-semibold">(Normalde Kendisi Ödüyor)</span>}
                      {raporData.musteri?.bagkur_kendisi && (raporData.bagkur_aktif !== false) && <span className="ml-2 text-xs text-blue-700 font-bold">(Bu Ay Ben Ödedim)</span>}
                      {!raporData.musteri?.bagkur_kendisi && raporData.bagkur_kendisi_odemis && <span className="ml-2 text-xs text-emerald-700 font-bold">(Bu Ay Kendisi Ödedi)</span>}
                    </span>
                    {raporData?.tekAy && raporData.musteri?.bagkur_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.bagkur_aktif !== false ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.bagkur_aktif !== false ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-400 text-blue-700 hover:bg-blue-100'}`}
                        onClick={async () => {
                          const newAktif = !(raporData.bagkur_aktif !== false);
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { bagkur_aktif: newAktif, bagkur_kendisi_odemis: !newAktif });
                              toast.success(newAktif ? 'Bağkur - Bu ay ben ödedim (hesaba dahil)' : 'Bağkur - Yine kendisi ödüyor (hesap dışı)');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.bagkur_aktif !== false ? 'Bu ayı da kendisi ödüyor olarak işaretle' : 'Bu ay ben ödedim olarak işaretle'}
                        data-testid="bagkur-ben-odedim-btn"
                      >
                        {raporData.bagkur_aktif !== false ? '✓ Ben Ödedim' : 'Ben Ödedim'}
                      </Button>
                    )}
                    {raporData?.tekAy && !raporData.musteri?.bagkur_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.bagkur_kendisi_odemis ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.bagkur_kendisi_odemis ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-100'}`}
                        onClick={async () => {
                          const newValue = !raporData.bagkur_kendisi_odemis;
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { bagkur_kendisi_odemis: newValue });
                              toast.success(newValue ? 'Bağkur - Kendisi Ödedi olarak işaretlendi' : 'Bağkur işareti kaldırıldı');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.bagkur_kendisi_odemis ? 'İşareti kaldır' : 'Kendisi Ödedi olarak işaretle'}
                        data-testid="bagkur-kendisi-odedi-btn"
                      >
                        {raporData.bagkur_kendisi_odemis ? '✓ K.Ödedi' : 'K.Ödedi'}
                      </Button>
                    )}
                  </div>
                  <div className="w-1/2 px-3 py-1.5">
                    <Input
                      type="text"
                      key={`bagkur-${raporData?.vergiNo}`}
                      value={raporFormInputs.bagkur || '0,00'}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, bagkur: e.target.value}))}
                      
                      onBlur={(e) => handleRaporInputBlur('bagkur', e.target.value, e.target)}
                      onFocus={(e) => e.target.select()}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-right text-sm h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''} ${
                        raporData.musteri?.bagkur_kendisi
                          ? (raporData.bagkur_aktif !== false ? 'bg-blue-50 font-semibold' : 'bg-yellow-50')
                          : (raporData.bagkur_kendisi_odemis ? 'bg-emerald-200 line-through' : '')
                      }`}
                      placeholder={raporData.musteri?.bagkur_kendisi ? (raporData.bagkur_aktif !== false ? 'Değer girin' : 'Kendisi ödüyor') : '0,00'}
                    />
                  </div>
                </div>

                {/* GEÇİCİ VERGİ - Müşteri kendisi ödüyorsa gri arka plan */}
                <div className={`flex items-center border-b border-gray-300 hover:bg-gray-50 ${
                  raporData.musteri?.gecici_vergi_kendisi
                    ? (raporData.gecici_vergi_aktif !== false ? 'bg-blue-100' : 'bg-gray-100')
                    : (raporData.gecici_vergi_kendisi_odemis ? 'bg-emerald-100' : '')
                }`}>
                  <div className="w-1/2 px-3 py-1.5 text-sm font-medium border-r border-gray-300 flex items-center justify-between">
                    <span>
                      GEÇİCİ VERGİ
                      {raporData.musteri?.gecici_vergi_kendisi && !(raporData.gecici_vergi_aktif !== false) && <span className="ml-2 text-xs text-orange-600 font-semibold">(Normalde Kendisi Ödüyor)</span>}
                      {raporData.musteri?.gecici_vergi_kendisi && (raporData.gecici_vergi_aktif !== false) && <span className="ml-2 text-xs text-blue-700 font-bold">(Bu Ay Ben Ödedim)</span>}
                      {!raporData.musteri?.gecici_vergi_kendisi && raporData.gecici_vergi_kendisi_odemis && <span className="ml-2 text-xs text-emerald-700 font-bold">(Bu Ay Kendisi Ödedi)</span>}
                    </span>
                    {raporData?.tekAy && raporData.musteri?.gecici_vergi_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.gecici_vergi_aktif !== false ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.gecici_vergi_aktif !== false ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-400 text-blue-700 hover:bg-blue-100'}`}
                        onClick={async () => {
                          const newAktif = !(raporData.gecici_vergi_aktif !== false);
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { gecici_vergi_aktif: newAktif, gecici_vergi_kendisi_odemis: !newAktif });
                              toast.success(newAktif ? 'Geçici Vergi - Bu ay ben ödedim (hesaba dahil)' : 'Geçici Vergi - Yine kendisi ödüyor (hesap dışı)');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.gecici_vergi_aktif !== false ? 'Bu ayı da kendisi ödüyor olarak işaretle' : 'Bu ay ben ödedim olarak işaretle'}
                        data-testid="gecici-vergi-ben-odedim-btn"
                      >
                        {raporData.gecici_vergi_aktif !== false ? '✓ Ben Ödedim' : 'Ben Ödedim'}
                      </Button>
                    )}
                    {raporData?.tekAy && !raporData.musteri?.gecici_vergi_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.gecici_vergi_kendisi_odemis ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.gecici_vergi_kendisi_odemis ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-100'}`}
                        onClick={async () => {
                          const newValue = !raporData.gecici_vergi_kendisi_odemis;
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { gecici_vergi_kendisi_odemis: newValue });
                              toast.success(newValue ? 'Geçici Vergi - Kendisi Ödedi olarak işaretlendi' : 'Geçici Vergi işareti kaldırıldı');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.gecici_vergi_kendisi_odemis ? 'İşareti kaldır' : 'Kendisi Ödedi olarak işaretle'}
                        data-testid="gecici-vergi-kendisi-odedi-btn"
                      >
                        {raporData.gecici_vergi_kendisi_odemis ? '✓ K.Ödedi' : 'K.Ödedi'}
                      </Button>
                    )}
                  </div>
                  <div className="w-1/2 px-3 py-1.5">
                    <Input
                      type="text"
                      key={`gecici_vergi-${raporData?.vergiNo}`}
                      value={raporFormInputs.gecici_vergi || '0,00'}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, gecici_vergi: e.target.value}))}
                      
                      onBlur={(e) => handleRaporInputBlur('gecici_vergi', e.target.value, e.target)}
                      onFocus={(e) => e.target.select()}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-right text-sm h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''} ${
                        raporData.musteri?.gecici_vergi_kendisi
                          ? (raporData.gecici_vergi_aktif !== false ? 'bg-blue-50 font-semibold' : 'bg-yellow-50')
                          : (raporData.gecici_vergi_kendisi_odemis ? 'bg-emerald-200 line-through' : '')
                      }`}
                      placeholder={raporData.musteri?.gecici_vergi_kendisi ? (raporData.gecici_vergi_aktif !== false ? 'Değer girin' : 'Kendisi ödüyor') : '0,00'}
                    />
                  </div>
                </div>

                {/* GELİR VERGİSİ - Müşteri kendisi ödüyorsa gri arka plan */}
                <div className={`flex items-center border-b border-gray-300 hover:bg-gray-50 ${
                  raporData.musteri?.gelir_vergisi_kendisi
                    ? (raporData.gelir_vergisi_aktif !== false ? 'bg-blue-100' : 'bg-gray-100')
                    : (raporData.gelir_vergisi_kendisi_odemis ? 'bg-emerald-100' : '')
                }`}>
                  <div className="w-1/2 px-3 py-1.5 text-sm font-medium border-r border-gray-300 flex items-center justify-between">
                    <span>
                      GELİR VERGİSİ
                      {raporData.musteri?.gelir_vergisi_kendisi && !(raporData.gelir_vergisi_aktif !== false) && <span className="ml-2 text-xs text-orange-600 font-semibold">(Normalde Kendisi Ödüyor)</span>}
                      {raporData.musteri?.gelir_vergisi_kendisi && (raporData.gelir_vergisi_aktif !== false) && <span className="ml-2 text-xs text-blue-700 font-bold">(Bu Ay Ben Ödedim)</span>}
                      {!raporData.musteri?.gelir_vergisi_kendisi && raporData.gelir_vergisi_kendisi_odemis && <span className="ml-2 text-xs text-emerald-700 font-bold">(Bu Ay Kendisi Ödedi)</span>}
                    </span>
                    {raporData?.tekAy && raporData.musteri?.gelir_vergisi_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.gelir_vergisi_aktif !== false ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.gelir_vergisi_aktif !== false ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-400 text-blue-700 hover:bg-blue-100'}`}
                        onClick={async () => {
                          const newAktif = !(raporData.gelir_vergisi_aktif !== false);
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { gelir_vergisi_aktif: newAktif, gelir_vergisi_kendisi_odemis: !newAktif });
                              toast.success(newAktif ? 'Gelir Vergisi - Bu ay ben ödedim (hesaba dahil)' : 'Gelir Vergisi - Yine kendisi ödüyor (hesap dışı)');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.gelir_vergisi_aktif !== false ? 'Bu ayı da kendisi ödüyor olarak işaretle' : 'Bu ay ben ödedim olarak işaretle'}
                        data-testid="gelir-vergisi-ben-odedim-btn"
                      >
                        {raporData.gelir_vergisi_aktif !== false ? '✓ Ben Ödedim' : 'Ben Ödedim'}
                      </Button>
                    )}
                    {raporData?.tekAy && !raporData.musteri?.gelir_vergisi_kendisi && (
                      <Button
                        size="sm"
                        variant={raporData.gelir_vergisi_kendisi_odemis ? "default" : "outline"}
                        className={`h-5 px-2 text-[10px] ml-2 ${raporData.gelir_vergisi_kendisi_odemis ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-100'}`}
                        onClick={async () => {
                          const newValue = !raporData.gelir_vergisi_kendisi_odemis;
                          if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                            const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                            try {
                              await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, { gelir_vergisi_kendisi_odemis: newValue });
                              toast.success(newValue ? 'Gelir Vergisi - Kendisi Ödedi olarak işaretlendi' : 'Gelir Vergisi işareti kaldırıldı');
                              await handleRaporOlustur(raporSelectedMusteri, raporData.adSoyad);
                              fetchKayitlar();
                            } catch (error) {
                              toast.error('Güncelleme hatası');
                            }
                          }
                        }}
                        title={raporData.gelir_vergisi_kendisi_odemis ? 'İşareti kaldır' : 'Kendisi Ödedi olarak işaretle'}
                        data-testid="gelir-vergisi-kendisi-odedi-btn"
                      >
                        {raporData.gelir_vergisi_kendisi_odemis ? '✓ K.Ödedi' : 'K.Ödedi'}
                      </Button>
                    )}
                  </div>
                  <div className="w-1/2 px-3 py-1.5">
                    <Input
                      type="text"
                      key={`gelir_vergisi-${raporData?.vergiNo}`}
                      value={raporFormInputs.gelir_vergisi || '0,00'}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, gelir_vergisi: e.target.value}))}
                      
                      onBlur={(e) => handleRaporInputBlur('gelir_vergisi', e.target.value, e.target)}
                      onFocus={(e) => e.target.select()}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-right text-sm h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''} ${
                        raporData.musteri?.gelir_vergisi_kendisi
                          ? (raporData.gelir_vergisi_aktif !== false ? 'bg-blue-50 font-semibold' : 'bg-yellow-50')
                          : (raporData.gelir_vergisi_kendisi_odemis ? 'bg-emerald-200 line-through' : '')
                      }`}
                      placeholder={raporData.musteri?.gelir_vergisi_kendisi ? (raporData.gelir_vergisi_aktif !== false ? 'Değer girin' : 'Kendisi ödüyor') : '0,00'}
                    />
                  </div>
                </div>

                {/* DİĞER Alanlar - İsim Değiştirilebilir */}
                <div className="flex items-center border-b border-gray-300 hover:bg-gray-50">
                  <div className="w-1/2 px-3 py-1.5 border-r border-gray-300">
                    <Input
                      type="text"
                      value={digerIsimler.diger1}
                      onChange={(e) => setDigerIsimler({...digerIsimler, diger1: e.target.value})}
                      onBlur={async (e) => {
                        // ÇOKLU AY SEÇİMİNDE KAYDETME DEVRE DIŞI
                        if (!raporData?.tekAy) return;
                        // Input'tan çıkıldığında otomatik kaydet
                        const yeniIsim = e.target.value;
                        if (raporData?.kayitlar?.length > 0) {
                          const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                          try {
                            await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, {
                              diger1_isim: yeniIsim !== 'DİĞER 1' ? yeniIsim : null
                            });
                            console.log('DİĞER 1 ismi kaydedildi:', yeniIsim);
                          } catch (err) {
                            console.error('DİĞER 1 kaydetme hatası:', err);
                          }
                        }
                      }}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-sm font-medium h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                      placeholder="DİĞER 1"
                    />
                  </div>
                  <div className="w-1/2 px-3 py-1.5">
                    <Input
                      type="text"
                      value={raporFormInputs.diger1 || '0,00'}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, diger1: e.target.value}))}
                      onBlur={(e) => handleRaporInputBlur('diger1', e.target.value, e.target)}
                      onFocus={(e) => e.target.select()}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-right text-sm h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>

                <div className="flex items-center border-b border-gray-300 hover:bg-gray-50">
                  <div className="w-1/2 px-3 py-1.5 border-r border-gray-300">
                    <Input
                      type="text"
                      value={digerIsimler.diger2}
                      onChange={(e) => setDigerIsimler({...digerIsimler, diger2: e.target.value})}
                      onBlur={async (e) => {
                        // ÇOKLU AY SEÇİMİNDE KAYDETME DEVRE DIŞI
                        if (!raporData?.tekAy) return;
                        const yeniIsim = e.target.value;
                        if (raporData?.kayitlar?.length > 0) {
                          const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                          try {
                            await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, {
                              diger2_isim: yeniIsim !== 'DİĞER 2' ? yeniIsim : null
                            });
                            console.log('DİĞER 2 ismi kaydedildi:', yeniIsim);
                          } catch (err) {
                            console.error('DİĞER 2 kaydetme hatası:', err);
                          }
                        }
                      }}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-sm font-medium h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                      placeholder="DİĞER 2"
                    />
                  </div>
                  <div className="w-1/2 px-3 py-1.5">
                    <Input
                      type="text"
                      value={raporFormInputs.diger2 || '0,00'}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, diger2: e.target.value}))}
                      onBlur={(e) => handleRaporInputBlur('diger2', e.target.value, e.target)}
                      onFocus={(e) => e.target.select()}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-right text-sm h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>

                <div className="flex items-center border-b-2 border-gray-400 hover:bg-gray-50">
                  <div className="w-1/2 px-3 py-1.5 border-r border-gray-300">
                    <Input
                      type="text"
                      value={digerIsimler.diger3}
                      onChange={(e) => setDigerIsimler({...digerIsimler, diger3: e.target.value})}
                      onBlur={async (e) => {
                        // ÇOKLU AY SEÇİMİNDE KAYDETME DEVRE DIŞI
                        if (!raporData?.tekAy) return;
                        const yeniIsim = e.target.value;
                        if (raporData?.kayitlar?.length > 0) {
                          const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                          try {
                            await axios.put(`${API}/vergi-kayit/${sonKayit.id}`, {
                              diger3_isim: yeniIsim !== 'DİĞER 3' ? yeniIsim : null
                            });
                            console.log('DİĞER 3 ismi kaydedildi:', yeniIsim);
                          } catch (err) {
                            console.error('DİĞER 3 kaydetme hatası:', err);
                          }
                        }
                      }}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-sm font-medium h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                      placeholder="DİĞER 3"
                    />
                  </div>
                  <div className="w-1/2 px-3 py-1.5">
                    <Input
                      type="text"
                      value={raporFormInputs.diger3 || '0,00'}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, diger3: e.target.value}))}
                      onBlur={(e) => handleRaporInputBlur('diger3', e.target.value, e.target)}
                      onFocus={(e) => e.target.select()}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-right text-sm h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>

                {/* Toplamlar - Hesaplanan */}
                <div className="flex items-center bg-blue-100 border-b-2 border-blue-400">
                  <div className="w-1/2 px-3 py-2.5 font-bold text-sm text-blue-900 border-r-2 border-blue-400">TOPLAM</div>
                  <div className="w-1/2 px-3 py-2.5 text-right text-sm font-bold text-blue-900">
                    {formatPara(
                      raporData.bakiye + 
                      raporFormData.muhasebe + 
                      (!raporData.kdv_kendisi_odemis ? raporFormData.kdv : 0) + 
                      (!raporData.stopaj_kendisi_odemis ? raporFormData.stopaj : 0) + 
                      (!raporData.ssk_kendisi_odemis ? raporFormData.ssk : 0) + 
                      (!raporData.bagkur_kendisi_odemis ? raporFormData.bagkur : 0) + 
                      (!raporData.gecici_vergi_kendisi_odemis ? raporFormData.gecici_vergi : 0) + 
                      (!raporData.gelir_vergisi_kendisi_odemis ? raporFormData.gelir_vergisi : 0) + 
                      raporFormData.diger1 + 
                      raporFormData.diger2 + 
                      raporFormData.diger3
                    )}
                  </div>
                </div>

                <div className="flex items-center bg-red-100 border-b-2 border-red-400">
                  <div className="w-1/2 px-3 py-2 font-bold text-sm text-red-900 border-r-2 border-red-400">ÖDENEN</div>
                  <div className="w-1/2 px-3 py-2">
                    <Input
                      type="text"
                      key={`odenen-${raporData?.vergiNo}`}
                      value={raporFormInputs.odenen || '0,00'}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, odenen: e.target.value}))}
                      
                      onBlur={(e) => handleRaporInputBlur('odenen', e.target.value, e.target)}
                      onFocus={(e) => e.target.select()}
                      readOnly={!raporData?.tekAy}
                      className={`w-full text-right text-sm font-bold h-7 border-gray-300 ${!raporData?.tekAy ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>

                <div className="flex items-center bg-green-100">
                  <div className="w-1/2 px-3 py-2.5 font-bold text-sm text-green-900 border-r-2 border-green-400">KALAN</div>
                  <div className="w-1/2 px-3 py-2.5 text-right text-sm font-bold text-green-900">
                    {formatPara(
                      (raporData.bakiye + 
                      raporFormData.muhasebe + 
                      (!raporData.kdv_kendisi_odemis ? raporFormData.kdv : 0) + 
                      (!raporData.stopaj_kendisi_odemis ? raporFormData.stopaj : 0) + 
                      (!raporData.ssk_kendisi_odemis ? raporFormData.ssk : 0) + 
                      (!raporData.bagkur_kendisi_odemis ? raporFormData.bagkur : 0) + 
                      (!raporData.gecici_vergi_kendisi_odemis ? raporFormData.gecici_vergi : 0) + 
                      (!raporData.gelir_vergisi_kendisi_odemis ? raporFormData.gelir_vergisi : 0) + 
                      raporFormData.diger1 + 
                      raporFormData.diger2 + 
                      raporFormData.diger3) - raporFormData.odenen
                    )}
                  </div>
                </div>
                </div>
                {/* Vergi form wrapper div kapanışı */}

                {/* NOT Alanı - Tam Genişlik */}
                <div className="border-t-2 border-amber-400 bg-amber-50">
                  <div className="px-3 py-2 font-bold text-sm text-amber-900 border-b border-amber-300">NOT</div>
                  <div className="px-3 py-2">
                    <textarea
                      value={raporFormInputs.notlar || ''}
                      onChange={(e) => setRaporFormInputs(prev => ({...prev, notlar: e.target.value}))}
                      onBlur={(e) => {
                        // ÇOKLU AY SEÇİMİNDE NOT KAYDETME DEVRE DIŞI
                        if (!raporData.tekAy) {
                          console.log('Çoklu ay seçimi - not kaydedilmedi');
                          return;
                        }
                        // Notu kaydet - fire-and-forget
                        if (raporData.kayitlar && raporData.kayitlar.length > 0) {
                          const sonKayit = raporData.kayitlar[raporData.kayitlar.length - 1];
                          axios.put(`${API}/vergi-kayit/${sonKayit.id}`, {
                            notlar: e.target.value
                          }).catch((error) => {
                            console.error('Not kaydetme hatası:', error);
                            toast.error('Not kaydedilemedi');
                          });
                        }
                      }}
                      placeholder={!raporData.tekAy ? "Çoklu ay seçiminde düzenleme yapılamaz" : "Müşteri için not ekleyin..."}
                      readOnly={!raporData.tekAy}
                      className={`w-full text-sm border rounded px-3 py-2 min-h-[80px] resize-y focus:outline-none focus:ring-2 ${!raporData.tekAy ? 'border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed' : 'border-amber-300 focus:ring-amber-400'}`}
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Seçili Aylar Listesi - Kompakt */}
              {!raporData.tekAy && raporData.aylar.length > 1 && (
                <div className="bg-slate-50 p-2 rounded text-xs print:hidden">
                  <p className="font-semibold text-slate-700 mb-1">Kapsam:</p>
                  <div className="flex flex-wrap gap-1">
                    {raporData.aylar.map(ay => (
                      <span key={ay} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                        {ay}
                      </span>
                    ))}
                  </div>
                  {/* Çoklu Ay Uyarısı */}
                  <div className="mt-2 p-2 bg-amber-100 border border-amber-300 rounded text-amber-800 text-xs">
                    ⚠️ <strong>Çoklu ay seçimi:</strong> Bu görünüm sadece rapor amaçlıdır. Veri düzenlemek için tek ay seçin.
                  </div>
                </div>
              )}
              
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Ödeme Dialog */}
      {/* Tahsilat Dialog - Memo ile optimize edilmiş */}
      <TahsilatDialog
        open={odemeDialogOpen}
        onOpenChange={setOdemeDialogOpen}
        kayit={odemeKayit}
        onSave={handleTahsilatSaved}
      />

      {/* Tahsilat Özeti Dialog - Yıllık rapor (yıl seçilebilir) */}
      <Dialog open={tahsilatOzetiDialogOpen} onOpenChange={setTahsilatOzetiDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tahsilat-ozeti-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <span>Tahsilat Özeti</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Müşteri bilgisi + yıl seçici */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 border rounded-lg p-3">
              <div>
                <div className="text-xs text-slate-500">Müşteri</div>
                <div className="font-semibold text-slate-800" data-testid="tahsilat-ozeti-musteri">
                  {tahsilatOzetiAdSoyad} <span className="text-xs text-slate-500 font-normal">({tahsilatOzetiVergiNo})</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-600">Yıl:</Label>
                <select
                  value={tahsilatOzetiYil}
                  onChange={(e) => refreshTahsilatOzeti(parseInt(e.target.value))}
                  className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="tahsilat-ozeti-yil-select"
                >
                  {(() => {
                    const cur = new Date().getFullYear();
                    const yillar = [];
                    for (let y = cur + 1; y >= cur - 10; y--) yillar.push(y);
                    return yillar.map(y => <option key={y} value={y}>{y}</option>);
                  })()}
                </select>
              </div>
            </div>

            {/* Yıllık Tahsilat Tablosu */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-slate-800 text-white px-3 py-2 text-sm font-semibold">
                {tahsilatOzetiYil} Yılı Tahsilat Özeti
              </div>
              <div className="overflow-x-auto">
                {tahsilatOzetiLoading ? (
                  <div className="p-6 text-center text-slate-500 text-sm">Yükleniyor...</div>
                ) : (
                  <table className="w-full text-sm" data-testid="tahsilat-ozeti-tablo">
                    <thead>
                      <tr className="bg-slate-100 border-b">
                        <th className="px-3 py-2 text-left font-semibold">Ay</th>
                        <th className="px-3 py-2 text-right font-semibold">Toplam</th>
                        <th className="px-3 py-2 text-right font-semibold">Tahsilat</th>
                        <th className="px-3 py-2 text-right font-semibold">Kalan</th>
                        <th className="px-3 py-2 text-left font-semibold">Not</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const aySirasi = {'Ocak':1,'Şubat':2,'Mart':3,'Nisan':4,'Mayıs':5,'Haziran':6,'Temmuz':7,'Ağustos':8,'Eylül':9,'Ekim':10,'Kasım':11,'Aralık':12};
                        const yilKayitlari = (tahsilatOzetiKayitlari || [])
                          .filter(k => k.vergi_no === tahsilatOzetiVergiNo)
                          .sort((a, b) => (aySirasi[a.ay] || 0) - (aySirasi[b.ay] || 0));

                        if (yilKayitlari.length === 0) {
                          return (
                            <tr>
                              <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                                Bu yıl için kayıt bulunamadı.
                              </td>
                            </tr>
                          );
                        }

                        let genelToplam = 0, genelOdenen = 0, genelKalan = 0;
                        const rows = yilKayitlari.map(kayit => {
                          const toplam = (Number(kayit.bakiye) || 0) +
                            (Number(kayit.muhasebe) || 0) +
                            (kayit.kdv_aktif !== false && !kayit.kdv_kendisi_odemis ? (Number(kayit.kdv) || 0) : 0) +
                            (kayit.stopaj_aktif !== false && !kayit.stopaj_kendisi_odemis ? (Number(kayit.stopaj) || 0) : 0) +
                            (kayit.ssk_aktif !== false && !kayit.ssk_kendisi_odemis ? (Number(kayit.ssk) || 0) : 0) +
                            (kayit.bagkur_aktif !== false && !kayit.bagkur_kendisi_odemis ? (Number(kayit.bagkur) || 0) : 0) +
                            (kayit.gecici_vergi_aktif !== false && !kayit.gecici_vergi_kendisi_odemis ? (Number(kayit.gecici_vergi) || 0) : 0) +
                            (kayit.gelir_vergisi_aktif !== false && !kayit.gelir_vergisi_kendisi_odemis ? (Number(kayit.gelir_vergisi) || 0) : 0) +
                            (Number(kayit.diger1) || 0) + (Number(kayit.diger2) || 0) + (Number(kayit.diger3) || 0);
                          const odenen = Number(kayit.odenen) || 0;
                          const kalan = toplam - odenen;
                          genelToplam += toplam;
                          genelOdenen += odenen;
                          genelKalan += kalan;
                          return (
                            <tr key={kayit.id} className="border-b hover:bg-slate-50">
                              <td className="px-3 py-2 font-medium">{kayit.ay}</td>
                              <td className="px-3 py-2 text-right">{toplam.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</td>
                              <td className="px-3 py-2 text-right text-green-700">{odenen.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</td>
                              <td className={`px-3 py-2 text-right font-medium ${kalan > 0 ? 'text-red-600' : 'text-green-600'}`}>{kalan.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</td>
                              <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate" title={kayit.notlar || ''}>{kayit.notlar || '-'}</td>
                            </tr>
                          );
                        });
                        return (
                          <>
                            {rows}
                            <tr className="bg-slate-200 font-bold border-t-2">
                              <td className="px-3 py-2">TOPLAM</td>
                              <td className="px-3 py-2 text-right" data-testid="tahsilat-ozeti-genel-toplam">{genelToplam.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</td>
                              <td className="px-3 py-2 text-right text-green-700" data-testid="tahsilat-ozeti-genel-odenen">{genelOdenen.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</td>
                              <td className={`px-3 py-2 text-right ${genelKalan > 0 ? 'text-red-600' : 'text-green-600'}`} data-testid="tahsilat-ozeti-genel-kalan">{genelKalan.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</td>
                              <td className="px-3 py-2"></td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                onClick={handleTahsilatOzetiWhatsApp}
                size="sm"
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-md"
                data-testid="tahsilat-ozeti-whatsapp-btn"
                disabled={tahsilatOzetiLoading || !tahsilatOzetiKayitlari || tahsilatOzetiKayitlari.length === 0}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp ile Gönder
              </Button>
              <Button
                onClick={() => setTahsilatOzetiDialogOpen(false)}
                size="sm"
                variant="outline"
                data-testid="tahsilat-ozeti-kapat-btn"
              >
                Kapat
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Güvenlik / 2FA Ayarları Dialog */}
      <Dialog open={securityDialogOpen} onOpenChange={(open) => {
        setSecurityDialogOpen(open);
        if (!open) {
          setTwoFAPasswordInput('');
          setTwoFAToggleError('');
        }
      }}>
        <DialogContent className="max-w-md" data-testid="security-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🛡️ <span>Güvenlik Ayarları</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 border rounded-lg p-4" data-testid="profil-telefon-bolumu">
              <h4 className="font-semibold text-slate-800">Telefon Numarası</h4>
              <p className="text-xs text-slate-500 mb-2">
                Kayıtlı: <span className="font-medium text-slate-700" data-testid="profil-telefon-mevcut">{currentUser?.telefon || 'Henüz eklenmedi'}</span>
              </p>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="05xx xxx xx xx"
                  value={profilTelefon}
                  onChange={(e) => setProfilTelefon(e.target.value)}
                  maxLength={20}
                  data-testid="profil-telefon-input"
                />
                <Button onClick={handleProfilTelefonKaydet} disabled={profilTelefonLoading || !profilTelefon.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="profil-telefon-kaydet">
                  {profilTelefonLoading ? '⏳' : 'Kaydet'}
                </Button>
              </div>
            </div>
            <div className="bg-slate-50 border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-semibold text-slate-800">İki Faktörlü Doğrulama</h4>
                  <p className="text-xs text-slate-500">Girişte e-postanıza 6 haneli kod gönderilir</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${currentUser?.two_factor_enabled ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`} data-testid="2fa-status-badge">
                  {currentUser?.two_factor_enabled ? '✅ Aktif' : '⚪ Kapalı'}
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-2">
                {currentUser?.two_factor_enabled
                  ? 'Hesabınız ekstra güvenlik katmanı ile korunuyor. Kapatmak için aşağıdaki şifrenizi girin.'
                  : 'Hesabınızı korumak için iki faktörlü doğrulamayı aktifleştirin. Girişte size kod gönderilecektir.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Mevcut Şifreniz</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={twoFAPasswordInput}
                onChange={(e) => setTwoFAPasswordInput(e.target.value)}
                data-testid="2fa-password-input"
              />
            </div>

            {twoFAToggleError && (
              <Alert className={`border-${twoFAToggleError.startsWith('✅') ? 'emerald' : 'red'}-300`}>
                <AlertDescription className={twoFAToggleError.startsWith('✅') ? 'text-emerald-700' : 'text-red-700'} data-testid="2fa-toggle-error">
                  {twoFAToggleError}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setSecurityDialogOpen(false)}
                className="flex-1"
                data-testid="2fa-close-btn"
              >
                Kapat
              </Button>
              <Button
                onClick={async () => {
                  setTwoFAToggleError('');
                  if (!twoFAPasswordInput) {
                    setTwoFAToggleError('Lütfen şifrenizi girin.');
                    return;
                  }
                  setTwoFAToggleLoading(true);
                  try {
                    const endpoint = currentUser?.two_factor_enabled ? '/auth/2fa/disable' : '/auth/2fa/enable';
                    const resp = await axios.post(`${API}${endpoint}`, { password: twoFAPasswordInput });
                    setTwoFAToggleError('✅ ' + resp.data.message);
                    // Kullanıcı bilgisini güncelle
                    const updatedUser = { ...currentUser, two_factor_enabled: !currentUser?.two_factor_enabled };
                    setCurrentUser(updatedUser);
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    setTwoFAPasswordInput('');
                    setTimeout(() => setSecurityDialogOpen(false), 1500);
                  } catch (err) {
                    setTwoFAToggleError(err.response?.data?.detail || 'İşlem yapılamadı');
                  } finally {
                    setTwoFAToggleLoading(false);
                  }
                }}
                disabled={twoFAToggleLoading}
                className={`flex-1 ${currentUser?.two_factor_enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`}
                data-testid="2fa-toggle-btn"
              >
                {twoFAToggleLoading ? '⏳ ...' : currentUser?.two_factor_enabled ? '🚫 Kapat' : '✅ Aktifleştir'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SGK İşyeri Ekle/Düzenle Dialog */}
      <Dialog open={sgkDialogOpen} onOpenChange={setSgkDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingSgkIsyeri ? 'SGK İşyeri Düzenle' : 'Yeni SGK İşyeri Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              if (editingSgkIsyeri) {
                // Düzenleme: sgk_dosyalari koleksiyonunda güncelle
                const musteri = musteriler.find(m => m.vergi_no === editingSgkIsyeri.vergi_no);
                await axios.put(`${API}/sgk-isyerleri/${editingSgkIsyeri.id}`, {
                  vergi_no: editingSgkIsyeri.vergi_no,
                  ad_soyad: musteri?.ad_soyad || editingSgkIsyeri.ad_soyad,
                  sgk_sicil_no: yeniSgkIsyeri.sgk_sicil_no,
                  isyeri_adi: yeniSgkIsyeri.isyeri_adi,
                  aktif: yeniSgkIsyeri.aktif
                });
                toast.success('İşyeri güncellendi');
              } else {
                // Yeni ekleme: sgk_dosyalari koleksiyonuna ekle
                const musteri = musteriler.find(m => m.vergi_no === yeniSgkIsyeri.vergi_no);
                if (!musteri) {
                  toast.error('Müşteri bulunamadı');
                  return;
                }
                
                await axios.post(`${API}/sgk-isyerleri`, {
                  vergi_no: musteri.vergi_no,
                  ad_soyad: musteri.ad_soyad,
                  sgk_sicil_no: yeniSgkIsyeri.sgk_sicil_no,
                  isyeri_adi: yeniSgkIsyeri.isyeri_adi,
                  aktif: true
                });
                toast.success('İşyeri eklendi');
              }
              setSgkDialogOpen(false);
              setEditingSgkIsyeri(null);
              fetchSgkIsyerleri(); // sgk_dosyalari'dan yeniden yükle
            } catch (error) {
              toast.error(editingSgkIsyeri ? 'İşyeri güncellenemedi' : 'İşyeri eklenemedi');
            }
          }}>
            <div className="space-y-4 py-4">
              {/* Müşteri Seçimi - Hem yeni ekleme hem düzenleme için */}
              <div>
                <Label htmlFor="sgk_musteri">Müşteri Seç *</Label>
                <Select 
                  value={editingSgkIsyeri ? editingSgkIsyeri.vergi_no : yeniSgkIsyeri.vergi_no} 
                  onValueChange={(value) => {
                    const musteri = musteriler.find(m => m.vergi_no === value);
                    if (editingSgkIsyeri) {
                      setEditingSgkIsyeri({
                        ...editingSgkIsyeri,
                        vergi_no: value,
                        ad_soyad: musteri?.ad_soyad || editingSgkIsyeri.ad_soyad
                      });
                    } else {
                      setYeniSgkIsyeri({
                        ...yeniSgkIsyeri, 
                        vergi_no: value,
                        ad_soyad: musteri?.ad_soyad || ''
                      });
                    }
                  }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Müşteri seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {musteriler.map((musteri) => (
                      <SelectItem key={musteri.id} value={musteri.vergi_no}>
                        {musteri.ad_soyad} ({musteri.vergi_no})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editingSgkIsyeri && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Mevcut: {editingSgkIsyeri.ad_soyad}
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="isyeri_adi">İşyeri Adı *</Label>
                <Input
                  id="isyeri_adi"
                  value={yeniSgkIsyeri.isyeri_adi}
                  onChange={(e) => setYeniSgkIsyeri({...yeniSgkIsyeri, isyeri_adi: e.target.value})}
                  placeholder="MERKEZ, ŞUBE 1, vs."
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="sgk_sicil_no">SGK Sicil No *</Label>
                <Input
                  id="sgk_sicil_no"
                  value={yeniSgkIsyeri.sgk_sicil_no}
                  onChange={(e) => setYeniSgkIsyeri({...yeniSgkIsyeri, sgk_sicil_no: e.target.value})}
                  placeholder="215120101140299803429-52/000"
                  required
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="sgk_aktif"
                  checked={yeniSgkIsyeri.aktif}
                  onChange={(e) => setYeniSgkIsyeri({...yeniSgkIsyeri, aktif: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="sgk_aktif" className="cursor-pointer">Aktif</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSgkDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">
                {editingSgkIsyeri ? 'Güncelle' : 'Ekle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
  
  
    </div>
  );
}

export default App;  